import React from "react";
import { useState, useEffect, useCallback, useRef } from "react";

// ─── FIREBASE ─────────────────────────────────────────────────────────────────
// Run: npm install firebase
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup,
         signInWithEmailLink, sendSignInLinkToEmail,
         isSignInWithEmailLink, signOut, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc, collection, addDoc, getDocs, query, orderBy, limit, onSnapshot, serverTimestamp } from "firebase/firestore";

const FIREBASE_CONFIG = {
  apiKey:            "AIzaSyBHF_9oytCEU3jRZPs8lS1CF6bvvs5iY0E",
  authDomain:        "folio-a67a5.firebaseapp.com",
  projectId:         "folio-a67a5",
  storageBucket:     "folio-a67a5.firebasestorage.app",
  messagingSenderId: "67761995649",
  appId:             "1:67761995649:web:75cf9080e8c4a8fb467280",
  measurementId:     "G-77CM4MEZVX",
};

let _app, _auth, _db, _provider;
const isFirebaseConfigured = !FIREBASE_CONFIG.apiKey.includes("PASTE");

if (isFirebaseConfigured) {
  _app      = initializeApp(FIREBASE_CONFIG);
  _auth     = getAuth(_app);
  _db       = getFirestore(_app);
  _provider = new GoogleAuthProvider();
  _provider.setCustomParameters({ prompt: "select_account" });
}

// Save/load user tier from Firestore
const saveTierToFirestore = async (uid, tier) => {
  if (!_db || !uid) return;
  try {
    await setDoc(doc(_db, "users", uid), { tier, updatedAt: new Date().toISOString() }, { merge: true });
  } catch(e) { console.warn("Firestore write failed", e); }
};

const loadTierFromFirestore = async (uid) => {
  if (!_db || !uid) return null;
  try {
    const snap = await getDoc(doc(_db, "users", uid));
    return snap.exists() ? snap.data().tier : null;
  } catch(e) { return null; }
};

// ══════════════════════════════════════════════════════════════════════════════
// MODULAR BOX SYSTEM — Per-box subscription architecture
// ══════════════════════════════════════════════════════════════════════════════

// ── BOX CATALOG ──────────────────────────────────────────────────────────────
// Single source of truth for all purchasable boxes.
const BOX_CATALOG = {
  oil_desk:     { title: "Oil Desk",           price: 19, color: "#f6a623" },
  weather_intel:{ title: "Weather Intel",      price: 14, color: "#48bb78" },
  ai_brief:     { title: "AI Market Brief",    price: 9,  color: "#68d391" },
  predictions:  { title: "Prediction Markets", price: 9,  color: "#4299e1" },
  markets:      { title: "Markets",            price: 12, color: "#68d391" },
  news:         { title: "News Feed",          price: 0,  color: "#a0aec0" },
};

// ── useSubscription ───────────────────────────────────────────────────────────
// Real-time listener on users/{uid} — exposes active_subscriptions array.
// Falls back gracefully when user is logged out or Firestore unavailable.
const useSubscription = (uid) => {
  const [subs, setSubs]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!uid || !_db) { setLoading(false); return; }

    const ref = doc(_db, "users", uid);
    const unsub = onSnapshot(ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          // Support both legacy "tier" field and new "active_subscriptions" array
          const activeSubs = data.active_subscriptions || [];
          // If user has a paid tier, grant all boxes (backwards compat)
          if (data.tier === "pulse" || data.tier === "pro") {
            setSubs(Object.keys(BOX_CATALOG));
          } else {
            setSubs(activeSubs);
          }
        } else {
          setSubs([]);
        }
        setLoading(false);
      },
      (err) => { console.warn("[useSubscription]", err); setLoading(false); }
    );

    return () => unsub();
  }, [uid]);

  const hasBox = React.useCallback((boxId) => subs.includes(boxId), [subs]);

  return { subs, loading, hasBox };
};

// ── saveBoxSubscription ───────────────────────────────────────────────────────
// Adds a boxId to the user's active_subscriptions in Firestore.
// Called after successful Stripe checkout confirmation.
const saveBoxSubscription = async (uid, boxId) => {
  if (!_db || !uid || !boxId) return;
  try {
    const ref = doc(_db, "users", uid);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? (snap.data().active_subscriptions || []) : [];
    if (!existing.includes(boxId)) {
      await setDoc(ref, {
        active_subscriptions: [...existing, boxId],
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    }
  } catch(e) { console.warn("[saveBoxSubscription]", e); }
};

// ── PaywalledBox ─────────────────────────────────────────────────────────────
// Wraps any dashboard module with subscription gating.
// Props:
//   boxId      — key from BOX_CATALOG
//   uid        — current user uid (null if logged out)
//   subs       — active subscriptions array from useSubscription
//   onUnlock   — callback to trigger Stripe checkout for this boxId
//   children   — the live component to render when unlocked
//   teaser     — optional React node to show blurred behind lock overlay
// ══════════════════════════════════════════════════════════════════════════════
// COMMAND PALETTE — Cmd+K / Ctrl+K global navigation
// ══════════════════════════════════════════════════════════════════════════════
const COMMANDS = [
  // Navigation
  { id:"markets",     label:"Markets",            sub:"Equity & Macro · Live quotes",         icon:"📈", group:"Desks",    view:"markets"     },
  { id:"oildesk",     label:"Oil Desk",           sub:"WTI · Brent · EIA inventory",          icon:"🛢️", group:"Desks",    view:"oildesk"     },
  { id:"freight",     label:"Freight",            sub:"DAT rates · Diesel · Capacity",        icon:"🚛", group:"Desks",    view:"freight"     },
  { id:"weather",     label:"Weather Intel",      sub:"NOAA alerts · Commodity impact",       icon:"🌩️", group:"Desks",    view:"weather"     },
  { id:"predictions", label:"Predictions",        sub:"Polymarket · Kalshi · Divergence",     icon:"🎯", group:"Desks",    view:"predictions" },
  { id:"ai_brief",    label:"AI Intelligence Brief", sub:"Claude synthesis · All desks",     icon:"⚡", group:"Desks",    view:"markets",    brief:true },
  // Data queries
  { id:"wti",         label:"WTI Crude Price",    sub:"Live spot price",                      icon:"💹", group:"Lookup",   view:"oildesk"     },
  { id:"vix",         label:"VIX Index",          sub:"Volatility gauge",                     icon:"📊", group:"Lookup",   view:"markets"     },
  { id:"corn",        label:"Corn Futures",       sub:"CBOT ZC · NOAA drought alerts",        icon:"🌽", group:"Lookup",   view:"weather"     },
  { id:"wheat",       label:"Wheat Futures",      sub:"CBOT ZW · Weather impact",             icon:"🌾", group:"Lookup",   view:"weather"     },
  { id:"ng",          label:"Natural Gas",        sub:"Henry Hub · Storage data",             icon:"🔥", group:"Lookup",   view:"oildesk"     },
  { id:"suez",        label:"Suez Canal Status",  sub:"Transit times · Congestion",           icon:"🚢", group:"Lookup",   view:"freight"     },
  { id:"panama",      label:"Panama Canal",       sub:"Water levels · Delay index",           icon:"🌊", group:"Lookup",   view:"freight"     },
  { id:"diesel",      label:"Diesel Index",       sub:"DOE national average",                 icon:"⛽", group:"Lookup",   view:"freight"     },
  // Account
  { id:"pricing",     label:"Pricing & Plans",    sub:"Unlock modules",                       icon:"💳", group:"Account",  view:"pricing"     },
  { id:"settings",    label:"Settings",           sub:"Account & preferences",                icon:"⚙️", group:"Account",  view:"settings"    },
];

const CommandPalette = ({ open, onClose, onNavigate, onBrief }) => {
  const [query, setQuery] = React.useState("");
  const inputRef = React.useRef(null);
  const MONO = "'IBM Plex Mono', monospace";

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Filter commands
  const filtered = query.trim() === ""
    ? COMMANDS
    : COMMANDS.filter(c =>
        c.label.toLowerCase().includes(query.toLowerCase()) ||
        c.sub.toLowerCase().includes(query.toLowerCase()) ||
        c.group.toLowerCase().includes(query.toLowerCase())
      );

  // Group results
  const groups = filtered.reduce((acc, cmd) => {
    if (!acc[cmd.group]) acc[cmd.group] = [];
    acc[cmd.group].push(cmd);
    return acc;
  }, {});

  const handleSelect = (cmd) => {
    if (cmd.brief) { onBrief?.(); }
    else { onNavigate(cmd.view); }
    onClose();
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:9999,
        background:"rgba(0,0,0,0.75)",
        backdropFilter:"blur(4px)",
        display:"flex", alignItems:"flex-start", justifyContent:"center",
        paddingTop:"12vh",
      }}
    >
      <div
        onClick={e=>e.stopPropagation()}
        style={{
          width:"100%", maxWidth:580,
          background:"#0d0d0d",
          border:"1px solid #2a2a2a",
          borderRadius:10,
          overflow:"hidden",
          boxShadow:"0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)",
          fontFamily:MONO,
        }}
      >
        {/* Search input */}
        <div style={{
          display:"flex", alignItems:"center", gap:12,
          padding:"14px 18px",
          borderBottom:"1px solid #1a1a1a",
        }}>
          <span style={{fontSize:14, opacity:0.4}}>⌘</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e=>setQuery(e.target.value)}
            onKeyDown={e=>{
              if (e.key==="Escape") onClose();
              if (e.key==="Enter" && filtered.length>0) handleSelect(filtered[0]);
            }}
            placeholder="Search desks, data, commands..."
            style={{
              flex:1, background:"transparent", border:"none", outline:"none",
              fontFamily:MONO, fontSize:13, color:"#ffffff",
              letterSpacing:".03em",
            }}
          />
          <span style={{
            fontFamily:MONO, fontSize:8, color:"#333",
            border:"1px solid #222", borderRadius:3,
            padding:"2px 6px", letterSpacing:".1em",
          }}>ESC</span>
        </div>

        {/* Results */}
        <div style={{maxHeight:400, overflowY:"auto"}}>
          {Object.keys(groups).length === 0 ? (
            <div style={{padding:"24px 18px",fontFamily:MONO,fontSize:11,color:"#333",textAlign:"center",letterSpacing:".1em"}}>
              NO RESULTS
            </div>
          ) : Object.entries(groups).map(([group, cmds]) => (
            <div key={group}>
              <div style={{
                padding:"8px 18px 4px",
                fontFamily:MONO, fontSize:8, color:"#333",
                letterSpacing:".2em", textTransform:"uppercase",
              }}>{group}</div>
              {cmds.map((cmd, i) => (
                <div
                  key={cmd.id}
                  onClick={()=>handleSelect(cmd)}
                  style={{
                    display:"flex", alignItems:"center", gap:12,
                    padding:"10px 18px", cursor:"pointer",
                    transition:"background 0.08s",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background="#141414"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                >
                  <span style={{fontSize:16, flexShrink:0}}>{cmd.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:MONO,fontSize:12,color:"#ffffff",letterSpacing:".03em",marginBottom:2}}>
                      {cmd.label}
                    </div>
                    <div style={{fontFamily:MONO,fontSize:9,color:"#444",letterSpacing:".05em"}}>
                      {cmd.sub}
                    </div>
                  </div>
                  <span style={{fontFamily:MONO,fontSize:8,color:"#222",letterSpacing:".1em"}}>→</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{
          padding:"8px 18px",
          borderTop:"1px solid #141414",
          display:"flex", gap:16,
        }}>
          {[["↵","Select"],["↑↓","Navigate"],["ESC","Close"]].map(([key,label])=>(
            <div key={key} style={{display:"flex",alignItems:"center",gap:5}}>
              <span style={{fontFamily:MONO,fontSize:8,color:"#333",border:"1px solid #222",borderRadius:2,padding:"1px 5px"}}>{key}</span>
              <span style={{fontFamily:MONO,fontSize:8,color:"#2a2a2a",letterSpacing:".08em"}}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MASTER-DETAIL DRILL-DOWN PANEL
// ══════════════════════════════════════════════════════════════════════════════
const DrillDownPanel = ({ moduleId, onClose, tier, subs, onPaywall, onUnlock,
  liveOilData, visibleWx, liveMacro, liveGainers, liveLosers, user }) => {
  const MONO = "'IBM Plex Mono', monospace";
  const SERIF = "'Playfair Display', Georgia, serif";

  const META = {
    markets:     { title:"MARKETS",     color:"#4ade80", icon:"📈", source:"Yahoo Finance · Alpha Vantage" },
    oildesk:     { title:"OIL DESK",    color:"#d4a017", icon:"🛢️", source:"EIA · CFTC · Baker Hughes"    },
    freight:     { title:"FREIGHT",     color:"#38bdf8", icon:"🚛", source:"DAT · FreightWaves · DOE"      },
    weather:     { title:"WEATHER",     color:"#818cf8", icon:"🌩️", source:"NOAA · NHC · USDA"             },
    predictions: { title:"PREDICTIONS", color:"#a78bfa", icon:"🎯", source:"Polymarket · Kalshi"           },
    ai_brief:    { title:"INTELLIGENCE",color:"#f59e0b", icon:"⚡", source:"Claude Sonnet · All Desks"     },
  };
  const meta = META[moduleId] || META.markets;

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:8888,
        background:"rgba(0,0,0,0.85)",
        backdropFilter:"blur(6px)",
        display:"flex", alignItems:"stretch", justifyContent:"flex-end",
      }}
    >
      <div
        onClick={e=>e.stopPropagation()}
        style={{
          width:"100%", maxWidth:820,
          background:"#080808",
          borderLeft:"1px solid #1a1a1a",
          display:"flex", flexDirection:"column",
          overflow:"hidden",
          animation:"slideInRight 0.2s ease",
        }}
      >
        {/* Header */}
        <div style={{
          padding:"18px 24px",
          borderBottom:"1px solid #141414",
          display:"flex", alignItems:"center", gap:14, flexShrink:0,
          background:"#0a0a0a",
        }}>
          <span style={{fontSize:20}}>{meta.icon}</span>
          <div style={{flex:1}}>
            <div style={{
              fontFamily:MONO, fontSize:16, color:"#ffffff",
              fontWeight:700, letterSpacing:".08em",
            }}>{meta.title}</div>
            <div style={{fontFamily:MONO,fontSize:8,color:"#333",letterSpacing:".15em",marginTop:2}}>
              {meta.source}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginRight:8}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:meta.color,animation:"breathe 2s infinite"}}/>
            <span style={{fontFamily:MONO,fontSize:8,color:meta.color,letterSpacing:".15em"}}>LIVE</span>
          </div>
          <button onClick={onClose} style={{
            background:"transparent", border:"1px solid #222",
            color:"#555", fontFamily:MONO, fontSize:10,
            padding:"5px 10px", cursor:"pointer", borderRadius:4,
            letterSpacing:".1em",
          }}>✕ CLOSE</button>
        </div>

        {/* Content — scrollable */}
        <div style={{flex:1, overflowY:"auto", padding:"24px"}}>
          {moduleId==="oildesk"   && <OilDeskPage tier={tier} onPaywall={onPaywall}/>}
          {moduleId==="freight"   && <FreightDeskPage tier={tier} onPaywall={onPaywall} user={user} onSignIn={()=>{}}/>}
          {moduleId==="weather"   && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
              {visibleWx.map((wx,i)=>(
                <WeatherCard key={wx.id} wx={wx} index={i} onRead={()=>{}} tier={tier}/>
              ))}
            </div>
          )}
          {moduleId==="predictions" && <PredictionsPage tier={tier} onPaywall={onPaywall} user={user} onSignIn={()=>{}}/>}
          {moduleId==="markets"   && (
            <div>
              <AIBriefCard macro={liveMacro} gainers={liveGainers} losers={liveLosers}
                weather={visibleWx}
                predictions={[
                  {label:"Fed cuts May FOMC?",poly:31,kalshi:40},
                  {label:"Recession by Dec 2025?",poly:22,kalshi:31},
                  {label:"S&P 500 > 5800 EOY?",poly:55,kalshi:62},
                ]}
                tier={tier} onPaywall={onPaywall}
              />
              <div style={{marginTop:20}}>
                <EarningsPanel tier={tier} onUpgrade={onPaywall}/>
                <SectorPanel/>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaywalledBox = ({ boxId, uid, subs, onUnlock, children, teaser }) => {
  const box = BOX_CATALOG[boxId] || {};
  const isUnlocked = box.price === 0 || subs.includes(boxId);

  // Dot color: green if unlocked, amber if locked
  const dotColor = isUnlocked ? "var(--up)" : "#d69e2e";
  const statusLabel = isUnlocked ? "LIVE" : "LOCKED";

  return (
    <div style={{
      background:"var(--card)",
      border:"1px solid var(--border)",
      borderRadius:10,
      overflow:"hidden",
      minHeight:320,
      display:"flex",
      flexDirection:"column",
      position:"relative",
    }}>
      {/* Header bar */}
      <div style={{
        padding:"11px 16px",
        borderBottom:"1px solid var(--border)",
        display:"flex",
        alignItems:"center",
        gap:8,
        background:"var(--raised)",
        flexShrink:0,
      }}>
        <div style={{width:6,height:6,borderRadius:"50%",
          background:dotColor,
          animation:isUnlocked?"breathe 2s infinite":"none"}}/>
        <span style={{fontFamily:"var(--M)",fontSize:9,color:dotColor,
          letterSpacing:".12em",fontWeight:600}}>{box.title?.toUpperCase()}</span>
        <span style={{marginLeft:"auto",fontFamily:"var(--M)",fontSize:8,
          color: isUnlocked ? "var(--muted)" : "#d69e2e",
          letterSpacing:".08em"}}>{statusLabel}</span>
      </div>

      {/* Content area */}
      <div style={{flex:1,position:"relative",overflow:"hidden"}}>
        {isUnlocked ? (
          // ── UNLOCKED: render live children
          <div style={{padding:"14px 16px",height:"100%"}}>{children}</div>
        ) : (
          // ── LOCKED: blurred teaser + unlock overlay
          <>
            {/* Blurred teaser */}
            <div style={{padding:"14px 16px",filter:"blur(6px)",
              userSelect:"none",pointerEvents:"none",opacity:0.6}}>
              {teaser || children}
            </div>
            {/* Lock overlay */}
            <div style={{
              position:"absolute",inset:0,
              display:"flex",flexDirection:"column",
              alignItems:"center",justifyContent:"center",
              gap:12,
              background:"rgba(7,6,10,0.65)",
              backdropFilter:"blur(2px)",
            }}>
              <div style={{
                width:36,height:36,borderRadius:"50%",
                background:"rgba(214,158,46,0.12)",
                border:"1px solid rgba(214,158,46,0.3)",
                display:"flex",alignItems:"center",justifyContent:"center",
              }}>
                <Icon name="lock" size={15} color="#d69e2e"/>
              </div>
              <div style={{textAlign:"center",maxWidth:220}}>
                <div style={{fontFamily:"var(--D)",fontSize:15,
                  color:"var(--cream)",marginBottom:6}}>
                  {box.title}
                </div>
                <div style={{fontFamily:"var(--P)",fontSize:11,
                  color:"var(--muted)",lineHeight:1.55}}>
                  Real-time intelligence module.
                  Subscribe to unlock live data.
                </div>
              </div>
              <button
                onClick={() => onUnlock(boxId)}
                style={{
                  background:"#d69e2e",
                  color:"#07060a",
                  border:"none",
                  borderRadius:7,
                  padding:"9px 22px",
                  fontFamily:"var(--M)",
                  fontSize:10,
                  fontWeight:700,
                  cursor:"pointer",
                  letterSpacing:".1em",
                  transition:"opacity .15s",
                }}
                onMouseEnter={e=>e.currentTarget.style.opacity=".85"}
                onMouseLeave={e=>e.currentTarget.style.opacity="1"}
              >
                UNLOCK — ${box.price}/MO
              </button>
              {!uid && (
                <div style={{fontFamily:"var(--M)",fontSize:8,
                  color:"var(--muted)",letterSpacing:".08em"}}>
                  SIGN IN REQUIRED
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Authenticated fetch helper ───────────────────────────────────────────────
// Attaches the Firebase ID token to every /api/brief call.
// Falls back gracefully if auth isn't available (cron, demo mode).
const authedBriefFetch = async (body) => {
  const headers = { "Content-Type": "application/json" };
  try {
    if (_auth?.currentUser) {
      const token = await _auth.currentUser.getIdToken(/* forceRefresh */ false);
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch(e) {
    console.warn("[brief] Could not get ID token:", e.message);
  }
  return fetch("/api/brief", { method: "POST", headers, body: JSON.stringify(body) });
};

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GS = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=IM+Fell+English:ital@0;1&family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@300;400;500;600&display=swap');

    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    :root{
      --void:#070809;--base:#0d1114;--surface:#141a1f;--raised:#1c2428;
      --border:#202830;--border2:#2e3840;--muted:#7a8898;--dim:#9aaab8;
      --soft:#b8c8d4;--text:#dce8f0;--bright:#f0f6fa;--cream:#f5edd8;
      --up:#c4a44a;--up2:#dfc070;--up-bg:rgba(196,164,74,.09);--up-b:rgba(196,164,74,.22);
      --dn:#b83248;--dn2:#d84860;--dn-bg:rgba(184,50,72,.09);--dn-b:rgba(184,50,72,.22);
      --vio:#6a8a9a;--vio2:#8fb0c0;--vio-bg:rgba(106,138,154,.08);--vio-b:rgba(106,138,154,.2);
      --rose:#7a9aaa;--rose2:#9abbc8;--rose-bg:rgba(122,154,170,.08);--rose-b:rgba(122,154,170,.2);
      --amb:#b87830;--amb-bg:rgba(184,120,48,.09);--amb-b:rgba(184,120,48,.22);
      --neu:#607080;--neu-bg:rgba(96,112,128,.09);--neu-b:rgba(96,112,128,.2);
      --D:'Playfair Display',Georgia,serif;
      --M:'IBM Plex Mono',monospace;
      --P:'Inter',system-ui,sans-serif;
    }
    html,body{background:var(--void);color:var(--text);font-family:var(--P);-webkit-font-smoothing:antialiased;line-height:1.6}

    /* Paper texture on cards */
    .card{
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    }

    /* ── LIGHT MODE ────────────────────────────────────────────────────── */
    [data-theme="light"]{
      --void:#f4f0e8;--base:#ece7dc;--surface:#e5dfd2;--raised:#dbd3c4;
      --border:rgba(0,0,0,.1);--border2:rgba(0,0,0,.16);--border3:rgba(0,0,0,.22);
      --muted:#7a8090;--dim:#4e5862;--soft:#303c48;--text:#1a2128;--bright:#0d1318;--cream:#100800;
      --up:#7a5c00;--up2:#9a7410;--up-bg:rgba(122,92,0,.08);--up-b:rgba(122,92,0,.2);
      --dn:#9a1828;--dn2:#c02040;--dn-bg:rgba(154,24,40,.07);--dn-b:rgba(154,24,40,.2);
      --amb:#904e00;--amb-bg:rgba(144,78,0,.07);--amb-b:rgba(144,78,0,.2);
      --vio:#2a5060;--vio2:#1e3a48;--vio-bg:rgba(42,80,96,.07);--vio-b:rgba(42,80,96,.18);
      --neu:#406070;--neu-bg:rgba(64,96,112,.07);--neu-b:rgba(64,96,112,.18);
    }
    [data-theme="light"] .card{background:var(--surface) !important;border-color:rgba(0,0,0,.1) !important;box-shadow:0 1px 4px rgba(0,0,0,.06)}
    [data-theme="light"] .tick-wrap{background:var(--base) !important}
    [data-theme="light"] header{background:var(--base) !important}
    [data-theme="light"] .tbl th{background:var(--raised) !important;color:var(--dim) !important}
    [data-theme="light"] .tbl tr:hover td{background:rgba(0,0,0,.03)}
    [data-theme="light"] .btn.b-ghost{border-color:rgba(0,0,0,.15) !important;color:var(--dim) !important}
    [data-theme="light"] .btn.b-ghost:hover{background:rgba(0,0,0,.05) !important}
    [data-theme="light"] .nav{color:var(--dim) !important}
    [data-theme="light"] .n-on{background:rgba(0,0,0,.08) !important;color:var(--bright) !important}
    [data-theme="light"] .mac-cell{border-color:rgba(0,0,0,.08) !important}
    [data-theme="light"] .fut-cell{border-color:rgba(0,0,0,.08) !important}
    [data-theme="light"] .fut-cell:hover{background:rgba(0,0,0,.04) !important}
    [data-theme="light"] .news-card{background:var(--raised) !important}
    [data-theme="light"] .tag.t-up{background:rgba(122,92,0,.1) !important;color:#6a4e00 !important;border-color:rgba(122,92,0,.25) !important}
    [data-theme="light"] .tag.t-dn{background:rgba(154,24,40,.08) !important;color:#8a1020 !important;border-color:rgba(154,24,40,.2) !important}
    [data-theme="light"] .tag.t-amb{background:rgba(144,78,0,.08) !important;color:#7a4000 !important;border-color:rgba(144,78,0,.2) !important}
    [data-theme="light"] .tag.t-neu{background:rgba(0,0,0,.06) !important;color:var(--dim) !important;border-color:rgba(0,0,0,.12) !important}
    [data-theme="light"] input{background:var(--raised) !important;color:var(--text) !important;border-color:rgba(0,0,0,.18) !important}
    ::-webkit-scrollbar{width:2px;height:2px}
    ::-webkit-scrollbar-thumb{background:var(--border2);border-radius:2px}

    @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes slideInRight{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
    @keyframes breathe{0%,100%{opacity:.9}50%{opacity:.28}}
    @keyframes ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
    .skel{background:linear-gradient(90deg,var(--raised) 25%,var(--border) 50%,var(--raised) 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;}
    @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
    [style*="animation: shimmer"],[style*="animation:shimmer"]{
      background:linear-gradient(90deg,var(--border) 25%,var(--raised) 50%,var(--border) 75%) !important;
      background-size:200% 100% !important;
    }
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
    @keyframes tileFloat{0%,100%{transform:translateY(0) rotate(0deg)}33%{transform:translateY(-4px) rotate(.08deg)}66%{transform:translateY(-2px) rotate(-.05deg)}}
    @keyframes tileGlow{0%,100%{box-shadow:0 4px 24px rgba(0,0,0,.4),inset 0 1px 0 rgba(255,255,255,.04)}50%{box-shadow:0 8px 40px rgba(0,0,0,.6),inset 0 1px 0 rgba(255,255,255,.08)}}
    @keyframes gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
    @keyframes riseIn{from{opacity:0;transform:scale(.97) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
    @keyframes needleSpin{from{transform:rotate(var(--from))}to{transform:rotate(var(--to))}}
    @keyframes slideRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
    @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    @keyframes slideLeft{from{transform:translateX(-100%)}to{transform:translateX(0)}}
    @keyframes pulseRed{0%,100%{box-shadow:0 0 0 0 rgba(192,54,74,0),inset 0 0 0 1px rgba(192,54,74,.35)}50%{box-shadow:0 0 20px 5px rgba(192,54,74,.25),inset 0 0 0 1px rgba(192,54,74,.8)}}
    @keyframes pulseGold{0%,100%{box-shadow:0 0 0 0 rgba(200,169,74,0),inset 0 0 0 1px rgba(200,169,74,.3)}50%{box-shadow:0 0 18px 4px rgba(200,169,74,.2),inset 0 0 0 1px rgba(200,169,74,.7)}}
    @keyframes pulseAmber{0%,100%{box-shadow:0 0 0 0 rgba(176,120,48,0),inset 0 0 0 1px rgba(176,120,48,.3)}50%{box-shadow:0 0 14px 3px rgba(176,120,48,.18),inset 0 0 0 1px rgba(176,120,48,.6)}}
    @keyframes glowUp{0%,100%{text-shadow:0 0 8px rgba(200,169,74,0)}50%{text-shadow:0 0 14px rgba(200,169,74,.6)}}
    @keyframes glowDn{0%,100%{text-shadow:0 0 8px rgba(192,54,74,0)}50%{text-shadow:0 0 14px rgba(192,54,74,.6)}}
    @keyframes needleGlow{0%,100%{filter:drop-shadow(0 0 2px rgba(200,169,74,.2))}50%{filter:drop-shadow(0 0 8px rgba(200,169,74,.8))}}
    @keyframes newsPop{0%{transform:translateY(0) scale(1);box-shadow:0 4px 12px rgba(0,0,0,.2)}100%{transform:translateY(-6px) scale(1.025);box-shadow:0 20px 50px rgba(0,0,0,.65)}}
    .news-card{transition:transform .22s cubic-bezier(.34,1.56,.64,1),box-shadow .22s ease,border-left-width .12s ease,border-left-color .12s ease,background .15s !important}
    .news-card:hover{transform:translateY(-6px) scale(1.025) !important;box-shadow:0 20px 50px rgba(0,0,0,.65) !important;border-left-width:4px !important;background:var(--raised) !important}
    .pred-tile{transition:transform .3s cubic-bezier(.34,1.3,.64,1),box-shadow .3s ease,border-color .2s !important;animation:tileFloat var(--float-dur,8s) ease-in-out infinite;animation-delay:var(--float-delay,0s)}
    .pred-tile:hover{transform:translateY(-8px) scale(1.02) rotate(.3deg) !important;box-shadow:0 24px 60px rgba(0,0,0,.7) !important;border-color:rgba(196,164,74,.5) !important;animation-play-state:paused !important}
    .chat-input:focus{border-color:rgba(196,164,74,.5) !important;outline:none !important}
    .pulse-critical{animation:pulseRed 1.8s ease-in-out infinite}
    .pulse-warning{animation:pulseGold 2.2s ease-in-out infinite}
    .pulse-watch{animation:pulseAmber 2.6s ease-in-out infinite}
    .glow-up{animation:glowUp 2.8s ease-in-out infinite}
    .glow-dn{animation:glowDn 2.8s ease-in-out infinite}
    .needle-glow{animation:needleGlow 2s ease-in-out infinite}

    .fu{animation:fadeUp .48s cubic-bezier(.22,1,.36,1) both}
    .fi{animation:fadeIn .3s ease both}
    .breathe{animation:breathe 3s ease infinite}

    .tick-wrap{overflow:hidden;background:var(--base);border-bottom:1px solid var(--border)}
    .tick-track{display:flex;white-space:nowrap;animation:ticker 65s linear infinite}
    .tick-track:hover{animation-play-state:paused}

    .card{background:var(--surface);border:1px solid var(--border);border-radius:13px;overflow:hidden;transition:border-color .22s,box-shadow .22s,transform .22s}
    .click:hover{cursor:pointer;border-color:var(--border2);box-shadow:0 10px 40px rgba(0,0,0,.55);transform:translateY(-2px)}
    .news-card{transition:all .18s cubic-bezier(.22,1,.36,1);border-left-width:3px!important}
    .news-card:hover{transform:translateY(-3px) scale(1.012);box-shadow:0 14px 44px rgba(0,0,0,.6);border-color:var(--border2)!important;z-index:2;border-left-width:4px!important}

    .tag{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-family:var(--M);font-size:10px;font-weight:500;letter-spacing:.07em;text-transform:uppercase;white-space:nowrap}
    .t-up{background:var(--up-bg);color:var(--up);border:1px solid var(--up-b)}
    .t-dn{background:var(--dn-bg);color:var(--dn);border:1px solid var(--dn-b)}
    .t-vio{background:var(--vio-bg);color:var(--vio2);border:1px solid var(--vio-b)}
    .t-ros{background:var(--rose-bg);color:var(--rose2);border:1px solid var(--rose-b)}
    .t-amb{background:var(--amb-bg);color:var(--amb);border:1px solid var(--amb-b)}
    .t-neu{background:var(--neu-bg);color:var(--neu);border:1px solid var(--neu-b)}

    .tbl{width:100%;border-collapse:collapse;font-family:var(--M);font-size:12px}
    .tbl thead th{padding:9px 14px;text-align:left;border-bottom:1px solid var(--border);color:var(--muted);font-weight:400;font-size:9px;letter-spacing:.09em;text-transform:uppercase;background:rgba(0,0,0,.28);position:sticky;top:0;z-index:1}
    .tbl tbody td{padding:10px 14px;border-bottom:1px solid rgba(30,28,42,.7);vertical-align:middle}
    .tbl tbody tr{transition:background .14s}
    .tbl tbody tr:hover{background:rgba(255,255,255,.02)}

    .skel{border-radius:4px;background:linear-gradient(90deg,var(--surface) 25%,var(--raised) 50%,var(--surface) 75%);background-size:300% 100%;animation:shimmer 1.8s ease infinite}
    .lk{filter:blur(5px);user-select:none;pointer-events:none;opacity:.35}
    .overlay{position:fixed;inset:0;background:rgba(4,3,8,.93);backdrop-filter:blur(18px) saturate(160%);z-index:300;display:flex;align-items:center;justify-content:center;animation:fadeIn .2s ease}

    .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:11px 22px;border-radius:9px;font-family:var(--M);font-size:11px;font-weight:500;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;border:none;transition:all .18s;white-space:nowrap}
    .b-gold{background:linear-gradient(135deg,#c4a44a,#9c7c2c);color:#080500;box-shadow:0 4px 18px rgba(196,164,74,.26)}
    .b-gold:hover{box-shadow:0 6px 28px rgba(196,164,74,.42);transform:translateY(-1px)}
    .b-vio{background:linear-gradient(135deg,#3a6070,#254555);color:#e0eaf0;box-shadow:0 4px 18px rgba(58,96,112,.22)}
    .b-vio:hover{box-shadow:0 6px 26px rgba(58,96,112,.38);transform:translateY(-1px)}
    .b-ghost{background:transparent;border:1px solid var(--border2);color:var(--dim)}
    .b-ghost:hover{border-color:var(--soft);color:var(--text)}
    .b-rose{background:linear-gradient(135deg,#3a5060,#253545);color:#d8eaf0;box-shadow:0 4px 18px rgba(58,80,96,.2)}
    .b-rose:hover{box-shadow:0 6px 26px rgba(58,80,96,.35);transform:translateY(-1px)}

    .nav{padding:7px 16px;border-radius:7px;font-family:var(--M);font-size:10px;letter-spacing:.07em;text-transform:uppercase;cursor:pointer;border:none;transition:all .15s;white-space:nowrap}
    .n-on{background:var(--raised);color:var(--cream);border:1px solid var(--border2)}
    .n-off{background:transparent;color:var(--muted);border:1px solid transparent}
    .n-off:hover{color:var(--soft)}

    .reader-body{overflow-y:auto;flex:1;padding:28px 32px}
    .reader-body p{font-size:15px;line-height:1.92;color:rgba(224,216,238,.82);margin-bottom:14px;font-family:var(--P)}
    .reader-body h3{font-family:var(--D);font-size:20px;font-weight:600;color:var(--cream);margin:24px 0 8px}

    .wx-critical{border-color:rgba(192,54,74,.6)!important;animation:pulseRed 1.8s ease-in-out infinite}
    .wx-warning{border-color:rgba(200,169,74,.55)!important;animation:pulseGold 2.2s ease-in-out infinite}
    .wx-watch{border-color:rgba(176,120,48,.5)!important;animation:pulseAmber 2.6s ease-in-out infinite}
    .wx-normal{border-color:var(--border2)!important}

    .sec-bg{flex:1;height:3px;background:var(--raised);border-radius:2px;overflow:hidden}
    .sec-fill{height:100%;border-radius:2px;transition:width .7s ease}
    .mac-cell{display:flex;flex-direction:column;gap:3px;padding:12px 18px;border-right:1px solid var(--border);min-width:90px;flex-shrink:0}
    .fut-cell{display:flex;flex-direction:column;gap:3px;padding:12px 18px;border-right:1px solid var(--border);min-width:96px;flex-shrink:0;cursor:pointer;transition:background .15s}
    .fut-cell:hover{background:rgba(255,255,255,.022)}

    .inp{background:var(--raised);border:1px solid var(--border2);color:var(--bright);border-radius:8px;padding:11px 14px;font-family:var(--M);font-size:12px;outline:none;transition:border-color .18s;letter-spacing:.03em}
    .inp:focus{border-color:var(--up)}
    .inp::placeholder{color:var(--muted)}

    .g-btn{display:flex;align-items:center;gap:13px;padding:13px 20px;border-radius:10px;background:#fff;border:none;cursor:pointer;font-family:var(--M);font-size:12px;font-weight:500;color:#1a1a1a;box-shadow:0 2px 12px rgba(0,0,0,.4);transition:all .2s;width:100%;letter-spacing:.04em}
    .g-btn:hover{box-shadow:0 4px 20px rgba(0,0,0,.5);transform:translateY(-1px)}

    /* Settings drawer */
    .drawer{position:fixed;right:0;top:0;bottom:0;width:340px;background:var(--base);border-left:1px solid var(--border2);z-index:250;animation:slideRight .3s cubic-bezier(.22,1,.36,1);overflow-y:auto;padding:28px 24px}

    /* Toggle */
    .toggle-wrap{display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid rgba(30,28,42,.6)}
    .toggle{width:36px;height:20px;border-radius:10px;cursor:pointer;transition:background .2s;position:relative;border:none;flex-shrink:0}
    .toggle-dot{width:14px;height:14px;border-radius:50%;background:#fff;position:absolute;top:3px;transition:left .2s}

    /* Gauge needle animation */
    .needle{transform-origin:50% 100%;transition:transform 1.2s cubic-bezier(.22,1,.36,1)}

    /* Tech news card */
    .news-card{padding:14px 18px;border-bottom:1px solid rgba(30,28,42,.6);cursor:pointer;transition:background .15s}
    .news-card:hover{background:rgba(255,255,255,.02)}
    .news-card:last-child{border-bottom:none}

    /* Persona card */
    .persona{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:26px 24px;flex:1}
  `}</style>
);

// ─── SVG ICON LIBRARY (monochrome line art) ───────────────────────────────────

// ─── FIRESTORE HELPERS ────────────────────────────────────────────────────────
const saveWaitlistToFirestore = async (email) => {
  if (!_db) return;
  try {
    const id = email.replace(/[^a-zA-Z0-9]/g, "_");
    await setDoc(doc(_db, "waitlist", id), {
      email, joinedAt: new Date().toISOString(), source:"invite_gate", status:"pending",
    }, { merge:true });
  } catch(e) { console.warn("Waitlist save:", e); }
};

const saveSubscriberToFirestore = async (email, uid) => {
  if (!_db) return;
  try {
    const id = uid || email.replace(/[^a-zA-Z0-9]/g, "_");
    await setDoc(doc(_db, "subscribers", id), {
      email, uid:uid||null, subscribedAt:new Date().toISOString(), digest:true,
    }, { merge:true });
  } catch(e) { console.warn("Subscriber save:", e); }
};

const Icon = ({ name, size=18, color="currentColor", strokeWidth=1.4 }) => {
  const s = { width:size, height:size, display:"block", flexShrink:0 };
  const p = { fill:"none", stroke:color, strokeWidth, strokeLinecap:"round", strokeLinejoin:"round" };

  const icons = {
    barrel:   <svg style={s} viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="8" ry="3" {...p}/><path d="M4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5" {...p}/><path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" {...p}/><path d="M4 8.5c0 .83 1.79 1.5 4 1.5" {...p}/></svg>,
    flame:    <svg style={s} viewBox="0 0 24 24"><path d="M8.5 14.5A5.5 5.5 0 0019 12c0-4-3-7-7.5-7C7.5 7 5 10 5 12a7 7 0 003.5 6.062" {...p}/><path d="M12 19a3 3 0 100-6 3 3 0 000 6z" {...p}/></svg>,
    grain:    <svg style={s} viewBox="0 0 24 24"><path d="M12 2C6 8 6 14 12 22c6-8 6-14 0-20z" {...p}/><path d="M12 8v14" {...p}/><path d="M8 11c1.5-1 3-1.5 4-1.5" {...p}/><path d="M16 11c-1.5-1-3-1.5-4-1.5" {...p}/><path d="M8 15c1.5-1 3-1.5 4-1.5" {...p}/><path d="M16 15c-1.5-1-3-1.5-4-1.5" {...p}/></svg>,
    coffee:   <svg style={s} viewBox="0 0 24 24"><path d="M6 2s0 3 3 3 3 3 3 3" {...p}/><path d="M4 10h12v7a3 3 0 01-3 3H7a3 3 0 01-3-3v-7z" {...p}/><path d="M16 12h2a2 2 0 010 4h-2" {...p}/></svg>,
    wheat:    <svg style={s} viewBox="0 0 24 24"><path d="M12 22V6" {...p}/><path d="M7 12c1.5-2 2.5-2.5 5-3" {...p}/><path d="M17 12c-1.5-2-2.5-2.5-5-3" {...p}/><path d="M7 8c1.5-2 2.5-2.5 5-3" {...p}/><path d="M17 8c-1.5-2-2.5-2.5-5-3" {...p}/><path d="M7 16c1.5-2 2.5-2.5 5-3" {...p}/><path d="M17 16c-1.5-2-2.5-2.5-5-3" {...p}/></svg>,
    bolt:     <svg style={s} viewBox="0 0 24 24"><path d="M13 2L4.09 12.96A.5.5 0 004.5 14H11l-1 8 8.91-10.96A.5.5 0 0018.5 10H12l1-8z" {...p}/></svg>,
    bean:     <svg style={s} viewBox="0 0 24 24"><path d="M10.5 2.5c-4.5 1.5-7 5.5-6.5 10C4.5 17 7.5 20.5 12 21c3.5.5 7-1.5 8.5-5C21.5 12 19 8.5 15 7c-2-.8-3.5-.5-4.5 1.5-1 2 .5 4.5 3 4.5" {...p}/></svg>,
    oj:       <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="13" r="7" {...p}/><path d="M12 6V2" {...p}/><path d="M8.5 9.5L5 6" {...p}/><path d="M15.5 9.5L19 6" {...p}/><path d="M9 13h6" {...p}/><path d="M12 10v6" {...p}/></svg>,
    cow:      <svg style={s} viewBox="0 0 24 24"><path d="M5 8H3a1 1 0 000 2h2" {...p}/><path d="M19 8h2a1 1 0 010 2h-2" {...p}/><path d="M5 8c0-3.31 3.13-6 7-6s7 2.69 7 6v6c0 3.31-3.13 6-7 6s-7-2.69-7-6V8z" {...p}/><path d="M9 14c.83 1 2.17 1.5 3 1.5s2.17-.5 3-1.5" {...p}/><circle cx="9.5" cy="10.5" r="1" fill={color} stroke="none"/><circle cx="14.5" cy="10.5" r="1" fill={color} stroke="none"/></svg>,
    snowflake:<svg style={s} viewBox="0 0 24 24"><path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07" {...p}/><circle cx="12" cy="12" r="2" {...p}/></svg>,
    sun:      <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" {...p}/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" {...p}/></svg>,
    moon:     <svg style={s} viewBox="0 0 24 24"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" {...p}/></svg>,
    storm:    <svg style={s} viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" {...p}/></svg>,
    cloud:    <svg style={s} viewBox="0 0 24 24"><path d="M18 10h-1.26A8 8 0 009 4a8 8 0 00-8 8 8 8 0 008 8h11a5 5 0 000-10z" {...p}/></svg>,
    chart:    <svg style={s} viewBox="0 0 24 24"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12" {...p}/></svg>,
    cpu:      <svg style={s} viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" {...p}/><rect x="9" y="9" width="6" height="6" {...p}/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" {...p}/></svg>,
    atom:     <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="1.5" fill={color} stroke="none"/><ellipse cx="12" cy="12" rx="10" ry="4" {...p}/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" {...p}/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" {...p}/></svg>,
    newspaper:<svg style={s} viewBox="0 0 24 24"><path d="M4 3h16a1 1 0 011 1v14a2 2 0 01-2 2H5a2 2 0 01-2-2V4a1 1 0 011-1z" {...p}/><path d="M8 7h8M8 11h8M8 15h5" {...p}/></svg>,
    settings: <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" {...p}/><path d="M12 1v3M12 20v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M1 12h3M20 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" {...p}/></svg>,
    user:     <svg style={s} viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" {...p}/><circle cx="12" cy="7" r="4" {...p}/></svg>,
    lock:     <svg style={s} viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" {...p}/><path d="M7 11V7a5 5 0 0110 0v4" {...p}/></svg>,
    chevron:  <svg style={s} viewBox="0 0 24 24"><polyline points="6,9 12,15 18,9" {...p}/></svg>,
    external: <svg style={s} viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" {...p}/><polyline points="15,3 21,3 21,9" {...p}/><line x1="10" y1="14" x2="21" y2="3" {...p}/></svg>,
    close:    <svg style={s} viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" {...p}/><line x1="6" y1="6" x2="18" y2="18" {...p}/></svg>,
    check:    <svg style={s} viewBox="0 0 24 24"><polyline points="20,6 9,17 4,12" {...p}/></svg>,
    "external-link": <svg style={s} viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" {...p}/><polyline points="15,3 21,3 21,9" {...p}/><line x1="10" y1="14" x2="21" y2="3" {...p}/></svg>,
    folio: <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" {...p}/><path d="M12 2v20M2 12h20" {...p}/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" {...p}/></svg>,
    shield:   <svg style={s} viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" {...p}/></svg>,
    oildrop:  <svg style={s} viewBox="0 0 24 24"><path d="M12 2C6 9 5 12.5 5 14a7 7 0 0014 0c0-1.5-1-5-7-12z" {...p}/></svg>,
    trend:    <svg style={s} viewBox="0 0 24 24"><polyline points="22,7 13.5,15.5 8.5,10.5 2,17" {...p}/><polyline points="16,7 22,7 22,13" {...p}/></svg>,
    down:     <svg style={s} viewBox="0 0 24 24"><polyline points="22,17 13.5,8.5 8.5,13.5 2,7" {...p}/><polyline points="16,17 22,17 22,11" {...p}/></svg>,
    calendar: <svg style={s} viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" {...p}/><line x1="16" y1="2" x2="16" y2="6" {...p}/><line x1="8" y1="2" x2="8" y2="6" {...p}/><line x1="3" y1="10" x2="21" y2="10" {...p}/></svg>,
    signal:   <svg style={s} viewBox="0 0 24 24"><path d="M1.5 8.5A11 11 0 0122.5 8.5" {...p}/><path d="M5 12a7.5 7.5 0 0114 0" {...p}/><path d="M8.5 15.5a4 4 0 017 0" {...p}/><circle cx="12" cy="19" r="1.5" fill={color} stroke="none"/></svg>,
    rocket:   <svg style={s} viewBox="0 0 24 24"><path d="M12 2C6.5 8 5 13 7 17l3-3 2 5 2-5 3 3c2-4 .5-9-5-15z" {...p}/><path d="M9 17c0 2.5 1.5 4 3 5 1.5-1 3-2.5 3-5" {...p}/></svg>,
    parachute:<svg style={s} viewBox="0 0 24 24"><path d="M12 22V12" {...p}/><path d="M12 12L8 22" {...p}/><path d="M12 12l4 10" {...p}/><path d="M3 10A9 9 0 0021 10" {...p}/><path d="M3 10a9 9 0 009-8 9 9 0 009 8" {...p}/></svg>,
    gem:      <svg style={s} viewBox="0 0 24 24"><path d="M12 2l3.5 5H21l-3 4.5L21 17H3l3.5-5.5L3 7h5.5L12 2z" {...p}/><path d="M3 7l9 15 9-15" {...p}/></svg>,
    compass:  <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" {...p}/><path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" {...p}/></svg>,
    layers:   <svg style={s} viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z" {...p}/><path d="M2 17l10 5 10-5" {...p}/><path d="M2 12l10 5 10-5" {...p}/></svg>,
    eye:      <svg style={s} viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" {...p}/><circle cx="12" cy="12" r="3" {...p}/></svg>,
    briefcase:<svg style={s} viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" {...p}/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" {...p}/><line x1="12" y1="12" x2="12" y2="12" strokeWidth="2" {...p}/><path d="M2 12h20" {...p}/></svg>,
    zap:      <svg style={s} viewBox="0 0 24 24"><polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" {...p}/></svg>,
    "bar-chart":<svg style={s} viewBox="0 0 24 24"><rect x="18" y="3" width="4" height="18" {...p}/><rect x="10" y="8" width="4" height="13" {...p}/><rect x="2" y="13" width="4" height="8" {...p}/></svg>,
    rss:      <svg style={s} viewBox="0 0 24 24"><path d="M4 11a9 9 0 019 9" {...p}/><path d="M4 4a16 16 0 0116 16" {...p}/><circle cx="5" cy="19" r="1" fill={color} stroke="none"/></svg>,
    refresh:  <svg style={s} viewBox="0 0 24 24"><path d="M23 4v6h-6" {...p}/><path d="M1 20v-6h6" {...p}/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" {...p}/></svg>,
    bell:     <svg style={s} viewBox="0 0 24 24"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" {...p}/><path d="M13.73 21a2 2 0 01-3.46 0" {...p}/></svg>,
    bookmark: <svg style={s} viewBox="0 0 24 24"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" {...p}/></svg>,
    discord:  <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.032.057a19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>,
  };
  return icons[name] || <svg style={s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" {...p}/></svg>;
};

// ─── COMMODITY ICON MAP ───────────────────────────────────────────────────────
const commodityIcon = id => {
  const map = {
    "oil-gulf":"barrel","natgas-polar":"flame","corn-drought":"grain",
    "coffee-frost":"coffee","wheat-ukraine":"wheat","electricity-heat":"bolt"
  };
  return map[id] || "chart";
};

// ─── DATA ─────────────────────────────────────────────────────────────────────

// Seed data shown while live data loads
const MACRO_SEED = [
  {k:"S&P 500",v:"—",c:"—",up:null},{k:"Nasdaq",v:"—",c:"—",up:null},
  {k:"WTI",v:"—",c:"—",up:null},{k:"Nat Gas",v:"—",c:"—",up:null},
  {k:"Gold",v:"—",c:"—",up:null},{k:"BTC",v:"—",c:"—",up:null},
  {k:"VIX",v:"—",c:"—",up:null},{k:"10Y",v:"—",c:"—",up:null},
  {k:"Fed",v:"4.25–4.50%",c:"Hold",up:null},{k:"DXY",v:"—",c:"—",up:null},
];

const TAPE_SEED = [
  "SPY —","QQQ —","NVDA —","TSLA —","AAPL —","MSFT —","AMZN —","META —",
  "GLD —","BTC —","ETH —","SPX —","VIX —","DXY —","TNX —","COIN —",
];

// ─── YAHOO FINANCE LIVE QUOTES ────────────────────────────────────────────────
// Fetches via allorigins CORS proxy → Yahoo Finance v7 JSON API
const QUOTE_SYMBOLS = ["SPY","QQQ","NVDA","TSLA","AAPL","MSFT","AMZN","META","GLD","BTC-USD","ETH-USD","^GSPC","^VIX","DX-Y.NYB","^TNX","COIN"];
const TAPE_LABELS   = ["SPY","QQQ","NVDA","TSLA","AAPL","MSFT","AMZN","META","GLD","BTC","ETH","SPX","VIX","DXY","10Y","COIN"];

const useYahooTape = () => {
  const [tape, setTape] = useState(TAPE_SEED);

  const fetchQuotes = async () => {
    try {
      const syms = QUOTE_SYMBOLS.join(",");
      const url = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${syms}&fields=symbol,regularMarketPrice,regularMarketChangePercent`;
      const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
      const res = await fetch(proxy, { signal: AbortSignal.timeout(12000) });
      if (!res.ok) return;
      const data = await res.json();
      const quotes = data?.quoteResponse?.result || [];
      if (quotes.length < 3) return;
      const newTape = quotes.map((q, i) => {
        const lbl = TAPE_LABELS[i] || q.symbol;
        const price = q.regularMarketPrice;
        const chg = q.regularMarketChangePercent;
        if (price == null) return `${lbl} —`;
        const chgStr = chg != null
          ? (chg >= 0 ? ` +${chg.toFixed(2)}%` : ` ${chg.toFixed(2)}%`)
          : "";
        const priceStr = price >= 10000 ? `$${(price/1000).toFixed(1)}K`
          : price >= 1 ? `$${price.toFixed(2)}`
          : `$${(price||0).toFixed(4)}`;
        return `${lbl} ${priceStr}${chgStr}`;
      });
      setTape(newTape);
    } catch(e) {
      // silently fall back to seed
    }
  };

  useEffect(() => {
    fetchQuotes();
    const t = setInterval(fetchQuotes, 60 * 1000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  return tape;
};

// ─── STRIPE PAYMENT LINKS ─────────────────────────────────────────────────────
// After creating products in Stripe dashboard, paste your Payment Link URLs here.
// dashboard.stripe.com → Products → Payment Links → Create
const STRIPE_LINKS = {
  pulse:     "https://buy.stripe.com/28E00l2QMcnG35Xb6UdZ601",    // $14.99/mo Pulse
  pulse_yr:  "https://buy.stripe.com/28E00l2QMcnG35Xb6UdZ601",    // $149/yr Pulse (same link for now)
  pro:       "https://buy.stripe.com/9B67sN4YU3Ra4a15MAdZ600",    // $39/mo Pro
  pro_yr:    "https://buy.stripe.com/9B67sN4YU3Ra4a15MAdZ600",    // $390/yr Pro (same link for now)
};
// Add ?prefilled_email=EMAIL to pre-fill checkout (optional enhancement)
const stripeCheckout = (tierId, annual=false) => {
  const key = tierId + (annual ? "_yr" : "");
  const url = STRIPE_LINKS[key] || STRIPE_LINKS[tierId];
  if (!url || url.includes("REPLACE_WITH")) {
    alert("Stripe not yet configured. Contact support@getfolio.ink to subscribe.");
    return;
  }
  window.open(url, "_blank");
};

// ─── ALPHA VANTAGE LIVE HOOK ──────────────────────────────────────────────────
// /api/prices returns { macro: [...], tape: [...] } already formatted.
// Frontend just consumes them directly — no client-side parsing needed.

const useLiveMarkets = () => {
  const [macro, setMacro] = useState(MACRO_SEED);
  const [isLive, setIsLive] = useState(false);
  const yahooTape = useYahooTape(); // live 60s quotes

  const load = async () => {
    try {
      const res = await fetch("/api/prices", { signal: AbortSignal.timeout(14000) });
      if (!res.ok) return;
      const d = await res.json();

      // API returns pre-formatted macro + tape arrays
      if (d.macro && Array.isArray(d.macro) && d.macro.filter(m => m.v !== "—").length >= 2) {
        setMacro(d.macro);
        setIsLive(true);
      }
    } catch(e) {
      // Stay on seed data silently
    }
  };

  useEffect(() => { load(); }, []);
  // Refresh every 5 min — server-side cache handles the AV quota
  useEffect(() => {
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  return { macro, tape: yahooTape, isLive };
};

// Keep static MACRO and TAPE as fallback references
const MACRO = MACRO_SEED;
const TAPE  = TAPE_SEED;

// ─── FUTURES STRIP — Live commodity prices ────────────────────────────────────
// Symbols mapped to Yahoo Finance. Prices injected by useFuturesStrip hook.
const FUTURES_STRIP_CONFIG = [
  {k:"WTI",     sym:"CL=F",    icon:"barrel", label:"WTI Crude"},
  {k:"Nat Gas", sym:"NG=F",    icon:"flame",  label:"Natural Gas"},
  {k:"RBOB",    sym:"RB=F",    icon:"barrel", label:"RBOB Gas"},
  {k:"Heat. Oil",sym:"HO=F",   icon:"flame",  label:"Heating Oil"},
  {k:"Corn",    sym:"ZC=F",    icon:"grain",  label:"Corn"},
  {k:"Soybeans",sym:"ZS=F",    icon:"bean",   label:"Soybeans"},
  {k:"Wheat",   sym:"ZW=F",    icon:"wheat",  label:"Wheat"},
  {k:"Coffee",  sym:"KC=F",    icon:"coffee", label:"Arabica Coffee"},
  {k:"OJ",      sym:"OJ=F",    icon:"oj",     label:"OJ Futures"},
  {k:"Cattle",  sym:"LE=F",    icon:"cow",    label:"Live Cattle"},
];

const useFuturesStrip = () => {
  const [strip, setStrip] = React.useState(
    FUTURES_STRIP_CONFIG.map(c => ({...c, v:"—", c_:"—", up:null, wx:""}))
  );
  React.useEffect(() => {
    let alive = true;
    const go = async () => {
      try {
        const syms  = FUTURES_STRIP_CONFIG.map(c => c.sym).join(",");
        const url   = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${syms}&fields=symbol,regularMarketPrice,regularMarketChangePercent`;
        const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        const res   = await fetch(proxy, { signal: AbortSignal.timeout(10000) });
        const d     = await res.json();
        const quotes = d?.quoteResponse?.result || [];
        const qmap   = Object.fromEntries(quotes.map(q => [q.symbol, q]));
        if (alive) setStrip(FUTURES_STRIP_CONFIG.map(c => {
          const q   = qmap[c.sym] || {};
          const p   = q.regularMarketPrice;
          const chg = q.regularMarketChangePercent;
          const isUp = chg != null ? chg >= 0 : null;
          const pStr = p != null ? (p > 100 ? `$${p.toFixed(0)}` : `$${p.toFixed(2)}`) : "—";
          const cStr = chg != null ? `${isUp?"+":""}${chg.toFixed(2)}%` : "—";
          return {...c, v:pStr, c_:cStr, up:isUp, wx:""};
        }));
      } catch(e) {}
    };
    go();
    const t = setInterval(go, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return strip;
};

// ─── WEATHER INTELLIGENCE — LIVE SYSTEM ──────────────────────────────────────
// Fetches active NOAA alerts mapped to commodity futures.
// Falls back to curated seasonal scenarios when no alerts are active.
// ─────────────────────────────────────────────────────────────────────────────

// Seasonal fallback cards — shown when no active NOAA alerts match commodities.
// These represent perennial risk scenarios, not specific events.
const WEATHER_FALLBACK = [
  {
    id:"wti-seasonal", commodity:"WTI Crude Oil", symbol:"CL=F",
    futures:"—", futChange:"—", futUp:null,
    alert:"NORMAL", alertCls:"wx-normal",
    headline:"Gulf of Mexico Season — No Active Alerts",
    sub:"Monitoring Gulf weather patterns for potential refinery disruption. Hurricane season runs June–November.",
    weather:{ event:"Seasonal Monitoring", location:"Gulf of Mexico / Texas Coast", severity:"Normal", source:"NOAA NHC" },
    marketImpact:{ direction:"neutral", magnitude:"LOW", shortTerm:"No current weather premium in WTI.", medium:"Hurricane season peak: Aug–Oct." },
    gaugeSignal: 50,
    background: "Gulf of Mexico handles ~17% of US crude production. Harvey (2017) shut 4.4M bbl/day.",
    hedges: "USO calls, VLO, PSX, MPC",
    seasonal: true,
  },
  {
    id:"natgas-seasonal", commodity:"Natural Gas", symbol:"NG=F",
    futures:"—", futChange:"—", futUp:null,
    alert:"NORMAL", alertCls:"wx-normal",
    headline:"Winter/Summer Demand Outlook — Monitoring Model Runs",
    sub:"GFS and Euro model divergence tracking. HDD/CDD anomalies watched for positioning opportunities.",
    weather:{ event:"Seasonal Demand Monitor", location:"Midwest/Northeast US", severity:"Normal", source:"NOAA/GFS, ECMWF" },
    marketImpact:{ direction:"neutral", magnitude:"LOW", shortTerm:"No weather premium. Storage in focus.", medium:"Temperature anomalies key." },
    gaugeSignal: 50,
    background: "Heating demand 36% of US nat gas use. Northeast basis spreads spike on cold extremes.",
    hedges: "UNG, BOIL, EQT, AR",
    seasonal: true,
  },
  {
    id:"corn-seasonal", commodity:"Corn (CBOT)", symbol:"ZC=F",
    futures:"—", futChange:"—", futUp:null,
    alert:"NORMAL", alertCls:"wx-normal",
    headline:"Corn Belt — Crop Condition Monitoring",
    sub:"USDA crop condition reports weekly. Pollination window (July) is critical. Drought monitor tracked for Corn Belt.",
    weather:{ event:"Crop Condition Monitor", location:"Iowa, Illinois, Indiana, Nebraska", severity:"Normal", source:"USDA Drought Monitor" },
    marketImpact:{ direction:"neutral", magnitude:"LOW", shortTerm:"No weather premium in corn futures.", medium:"USDA WASDE monthly." },
    gaugeSignal: 50,
    background: "Iowa + Illinois = 30% of US crop. D2+ drought during pollination costs 8-14 bu/acre.",
    hedges: "CORN ETF, CBOT futures, DE, CTVA, MOS",
    seasonal: true,
  },
];

// ─── useWeatherData hook ─────────────────────────────────────────────────────
// Fetches live cards from /api/weather, injects real Yahoo Finance prices.
const useWeatherData = () => {
  const [cards,     setCards]     = React.useState(WEATHER_FALLBACK);
  const [loading,   setLoading]   = React.useState(true);
  const [isLive,    setIsLive]    = React.useState(false);
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [prices,    setPrices]    = React.useState({});

  // Fetch live prices for fallback cards
  React.useEffect(() => {
    const syms = [...new Set(WEATHER_FALLBACK.map(c => c.symbol))].join(",");
    const url   = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${syms}&fields=symbol,regularMarketPrice,regularMarketChangePercent`;
    const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
    fetch(proxy, { signal: AbortSignal.timeout(8000) })
      .then(r => r.json())
      .then(d => {
        const qs = d?.quoteResponse?.result || [];
        const map = {};
        qs.forEach(q => { map[q.symbol] = q; });
        setPrices(map);
      })
      .catch(() => {});
  }, []);

  // Fetch live NOAA-mapped cards from API
  React.useEffect(() => {
    let alive = true;
    const go = async () => {
      try {
        const res  = await fetch("/api/weather", { signal: AbortSignal.timeout(12000) });
        const json = await res.json();
        if (!alive) return;
        if (json.ok && json.cards && json.cards.length > 0) {
          setCards(json.cards);
          setIsLive(true);
          setUpdatedAt(json.updatedAt);
        }
        // If no active alerts, we stay on fallback (fine)
      } catch(e) {}
      if (alive) setLoading(false);
    };
    go();
    const t = setInterval(go, 10 * 60 * 1000); // refresh every 10 min
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Inject live prices into fallback cards
  const enrichedCards = React.useMemo(() => {
    if (isLive) return cards; // API cards already have prices
    return cards.map(c => {
      const q = prices[c.symbol] || {};
      const p = q.regularMarketPrice;
      const chg = q.regularMarketChangePercent;
      if (!p) return c;
      return {
        ...c,
        futures:   `$${p > 100 ? p.toFixed(0) : p.toFixed(2)}`,
        futChange: `${chg >= 0 ? "+" : ""}${(chg || 0).toFixed(2)}%`,
        futUp:     chg >= 0,
        livePrice: true,
      };
    });
  }, [cards, prices, isLive]);

  return { cards: enrichedCards, loading, isLive, updatedAt };
};

const GAINERS = [
  {r:1,t:"SOUN",n:"SoundHound AI",p:"$8.42",c:"+47.3%",v:"284.2M",sq:94,sig:"Strong Buy",up:true},
  {r:2,t:"MARA",n:"Marathon Digital",p:"$19.87",c:"+31.2%",v:"142.8M",sq:88,sig:"Buy",up:true},
  {r:3,t:"CLSK",n:"CleanSpark",p:"$14.23",c:"+28.7%",v:"96.4M",sq:91,sig:"Strong Buy",up:true},
  {r:4,t:"SMCI",n:"Super Micro",p:"$812",c:"+19.8%",v:"18.2M",sq:72,sig:"Buy",up:true},
  {r:5,t:"NVAX",n:"Novavax",p:"$6.18",c:"+18.4%",v:"48.6M",sq:96,sig:"Strong Buy",up:true},
  {r:6,t:"ASTS",n:"AST SpaceMobile",p:"$11.24",c:"+15.6%",v:"62.3M",sq:84,sig:"Buy",up:true},
  {r:7,t:"RKLB",n:"Rocket Lab",p:"$6.84",c:"+13.2%",v:"41.7M",sq:58,sig:"Buy",up:true},
  {r:8,t:"MSTR",n:"MicroStrategy",p:"$1,284",c:"+12.8%",v:"8.4M",sq:77,sig:"Buy",up:true},
  {r:9,t:"IONQ",n:"IonQ",p:"$14.62",c:"+11.4%",v:"38.2M",sq:69,sig:"Neutral",up:true},
  {r:10,t:"RXRX",n:"Recursion",p:"$9.84",c:"+10.8%",v:"28.4M",sq:61,sig:"Neutral",up:true},
];

const LOSERS = [
  {r:1,t:"BYND",n:"Beyond Meat",p:"$7.12",c:"−28.4%",v:"24.8M",sq:12,sig:"Strong Sell",up:false},
  {r:2,t:"UPST",n:"Upstart",p:"$24.18",c:"−22.1%",v:"18.6M",sq:18,sig:"Sell",up:false},
  {r:3,t:"LCID",n:"Lucid Group",p:"$2.84",c:"−18.6%",v:"84.2M",sq:22,sig:"Sell",up:false},
  {r:4,t:"WISH",n:"ContextLogic",p:"$1.24",c:"−16.2%",v:"44.8M",sq:8,sig:"Strong Sell",up:false},
  {r:5,t:"CLOV",n:"Clover Health",p:"$1.88",c:"−14.8%",v:"28.4M",sq:15,sig:"Sell",up:false},
  {r:6,t:"SPCE",n:"Virgin Galactic",p:"$1.12",c:"−13.4%",v:"38.2M",sq:24,sig:"Sell",up:false},
  {r:7,t:"RIVN",n:"Rivian",p:"$10.24",c:"−11.8%",v:"42.6M",sq:31,sig:"Sell",up:false},
  {r:8,t:"BBBY",n:"Beyond Brands",p:"$0.42",c:"−10.6%",v:"18.4M",sq:19,sig:"Strong Sell",up:false},
  {r:9,t:"NKLA",n:"Nikola",p:"$0.84",c:"−9.8%",v:"62.4M",sq:28,sig:"Sell",up:false},
  {r:10,t:"SKLZ",n:"Skillz",p:"$3.14",c:"−8.4%",v:"8.6M",sq:22,sig:"Sell",up:false},
];

const SECTORS = [
  {n:"Technology",c:"+2.1%",v:2.1},{n:"Energy",c:"+1.8%",v:1.8},
  {n:"Comm. Svcs",c:"+1.4%",v:1.4},{n:"Financials",c:"+0.9%",v:0.9},
  {n:"Healthcare",c:"+0.4%",v:0.4},{n:"Cons. Staples",c:"+0.1%",v:0.1},
  {n:"Industrials",c:"−0.2%",v:-0.2},{n:"Consumer Disc.",c:"−0.6%",v:-0.6},
  {n:"Materials",c:"−0.8%",v:-0.8},{n:"Real Estate",c:"−1.2%",v:-1.2},
  {n:"Utilities",c:"−1.6%",v:-1.6},
];

const PRICING_TIERS = [
  {
    id:"free",name:"Free",price:0,annual:0,tagline:"Begin here.",color:"var(--dim)",
    features:["Top 3 movers preview","Hero story access","Live macro ribbon","Market status"],
    locked:["Full tables","AI analysis","Weather Intelligence","Squeeze scores","Tech news","Customization"],
    cta:"Current Plan",style:"b-ghost",
  },
  {
    id:"pulse",name:"Pulse",price:14.99,annual:149,tagline:"Founding rate — yours forever.",
    color:"var(--up)",badge:"✦ FOUNDING  ·  384 LEFT",
    features:["Full 10-row data tables","All AI-written stories","Weather Intelligence","Squeeze scores","Earnings whispers","Sector heat map","1 price alert","Tech & Quantum news"],
    note:"$14.99/mo forever · First 500 members. After: $19/mo.",
    cta:"Request Full Access",style:"b-gold",featured:true,
  },
  {
    id:"pro",name:"Pulse Pro",price:39,annual:390,tagline:"For those who need the edge.",
    color:"var(--vio2)",
    features:["Everything in Pulse, plus:","SEC insider transactions (Form 4)","FRED macro dashboard","Unlimited price alerts","Squeeze watchlist (25)","Daily 10am brief (email)","API — 1,000 calls/day","Full weather forecast stack","Customization presets"],
    cta:"Request Full Access",style:"b-vio",
  },
];

const DEFAULT_SETTINGS = {
  persona:"everyday", // everyday | trader
  commodities:{ oil:true, natgas:true, corn:true, coffee:true, wheat:true, electricity:true },
  sections:{ weather:true, stocks:true, tech:true, gauge:true },
  alertThreshold:5, // % move to alert
  accentColor:"gold", // gold | violet | rose
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getMarket = () => {
  const et = new Date(new Date().toLocaleString("en-US",{timeZone:"America/New_York"}));
  const h=et.getHours(),m=et.getMinutes(),day=et.getDay();
  if(day===0||day===6) return{status:"weekend",label:"WEEKEND",color:"var(--muted)"};
  const mins=h*60+m;
  if(mins<10*60) return{status:"pre",label:"PRE-MARKET",color:"var(--up)",minsLeft:10*60-mins};
  if(mins>=16*60) return{status:"closed",label:"CLOSED",color:"var(--muted)"};
  return{status:"open",label:"LIVE",color:"var(--up)"};
};

const sqColor = s => s>=80?"var(--up)":s>=60?"var(--up2)":s>=40?"var(--amb)":s>=20?"var(--amb)":"var(--dn)";
const alertColor = a => a==="CRITICAL"?"var(--dn)":a==="WARNING"?"var(--up)":a==="WATCH"?"var(--amb)":"var(--dim)";

const parseStory = text => text.split('\n').map((line,i)=>{
  if(!line.trim()) return <div key={i} style={{height:10}}/>;
  const bold = line.match(/^\*\*(.+)\*\*$/);
  if(bold) return <h3 key={i}>{bold[1]}</h3>;
  return <p key={i}>{line.replace(/\*\*/g,"").replace(/\*/g,"")}</p>;
});

// ─── OIL FUTURES GAUGE ───────────────────────────────────────────────────────
// ─── MINI ARC GAUGE (squeeze scores) ─────────────────────────────────────────
const MiniGauge = ({ value, size = 44 }) => {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDisplayed(value), 200 + Math.random() * 400);
    return () => clearTimeout(t);
  }, [value]);

  const cx = 24, cy = 24, r = 18;
  const rad = d => d * Math.PI / 180;
  const pct = displayed / 100;
  const angle = 180 - pct * 180; // 180=left(0%) → 0=right(100%)
  const ex = cx + r * Math.cos(rad(angle));
  const ey = cy + r * Math.sin(rad(angle));
  const largeArc = pct > 0.5 ? 1 : 0;
  const color = value >= 75 ? "var(--up)" : value >= 45 ? "var(--amb)" : "var(--dn)";
  const needleDeg = -90 + pct * 180;

  return (
    <svg viewBox="6 6 36 22" width={size} height={size * 0.56}
      style={{ display: "block", overflow: "visible" }}>
      {/* Track */}
      <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`}
        fill="none" stroke="var(--border2)" strokeWidth="3.5" strokeLinecap="round"/>
      {/* Fill */}
      {pct > 0.02 && (
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
          fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"
          style={{ transition: "all .9s cubic-bezier(.22,1,.36,1)", filter: `drop-shadow(0 0 3px ${color})` }}
          opacity=".85"/>
      )}
      {/* Needle */}
      <g style={{ transformOrigin: `${cx}px ${cy}px`, transform: `rotate(${needleDeg}deg)`,
        transition: "transform .9s cubic-bezier(.22,1,.36,1)" }}>
        <line x1={cx} y1={cy} x2={cx} y2={cy - 14}
          stroke="var(--cream)" strokeWidth="1" strokeLinecap="round" opacity=".9"/>
        <circle cx={cx} cy={cy} r="2" fill="var(--surface)" stroke="var(--cream)" strokeWidth=".8"/>
      </g>
      {/* Value */}
      <text x={cx} y={cy + 10} textAnchor="middle" fill={color}
        fontSize="7" fontFamily="IBM Plex Mono,monospace" fontWeight="700"
        style={{ filter: `drop-shadow(0 0 3px ${color})` }}>
        {displayed}
      </text>
    </svg>
  );
};

// ─── VIX FEAR/GREED GAUGE ────────────────────────────────────────────────────
const WeatherAlertsSidebar = ({ wcs=[], onRead }) => {
  if(!wcs||wcs.length===0) return null;
  return (
    <div className="card" style={{padding:"14px 16px",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}>
        <Icon name="alert-triangle" size={13} color="var(--amb)" strokeWidth={1.4}/>
        <div style={{fontFamily:"var(--D)",fontSize:13,color:"var(--dim)"}}>Weather Alerts</div>
      </div>
      {wcs.slice(0,3).map((w,i)=>(
        <div key={i} onClick={()=>onRead&&onRead(w)} style={{cursor:"pointer",
          padding:"6px 0",borderBottom:i<2?"1px solid var(--border)":"none"}}>
          <div style={{fontFamily:"var(--D)",fontSize:12,color:"var(--fg)"}}>{w.headline||w.title||"Alert"}</div>
        </div>
      ))}
    </div>
  );
};

const EarningsPanel = ({ tier, onUpgrade }) => {
  const earnings = [
    { ticker:"COST",  date:"Mar 6",  est:"$4.11", prior:"$3.71", beat:null  },
    { ticker:"ORCL",  date:"Mar 10", est:"$1.49", prior:"$1.41", beat:null  },
    { ticker:"ADBE",  date:"Mar 12", est:"$4.97", prior:"$4.48", beat:null  },
    { ticker:"FDX",   date:"Mar 19", est:"$4.19", prior:"$3.86", beat:null  },
    { ticker:"NKE",   date:"Mar 20", est:"$0.29", prior:"$0.77", beat:null  },
  ];
  return (
    <div className="card" style={{padding:"14px 16px",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}>
        <Icon name="calendar" size={13} color="var(--dim)" strokeWidth={1.4}/>
        <div style={{fontFamily:"var(--D)",fontSize:13,color:"var(--dim)"}}>Earnings Calendar</div>
      </div>
      {earnings.map((e,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"6px 0",borderBottom:i<earnings.length-1?"1px solid var(--border)":"none"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{fontFamily:"var(--M)",fontSize:12,color:"var(--fg)",minWidth:44}}>{e.ticker}</div>
            <div style={{fontFamily:"var(--D)",fontSize:11,color:"var(--dim)"}}>{e.date}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontFamily:"var(--M)",fontSize:11,color:"var(--dim)"}}>Est {e.est}</div>
            {e.beat!==null&&<div style={{fontSize:10,padding:"1px 5px",borderRadius:3,
              background:e.beat?"rgba(74,196,100,0.15)":"rgba(196,74,74,0.15)",
              color:e.beat?"var(--up)":"var(--dn)",fontFamily:"var(--M)"}}>
              {e.beat?"BEAT":"MISS"}
            </div>}
          </div>
        </div>
      ))}
    </div>
  );
};

const SectorPanel = () => {
  const sectors = [
    { name:"Technology",  chg:+1.24 },
    { name:"Energy",      chg:-0.87 },
    { name:"Financials",  chg:+0.43 },
    { name:"Healthcare",  chg:+0.19 },
    { name:"Materials",   chg:-1.10 },
  ];
  return (
    <div className="card" style={{padding:"14px 16px",marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}>
        <Icon name="pie-chart" size={13} color="var(--dim)" strokeWidth={1.4}/>
        <div style={{fontFamily:"var(--D)",fontSize:13,color:"var(--dim)"}}>Sector Performance</div>
      </div>
      {sectors.map((s,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"5px 0",borderBottom:i<sectors.length-1?"1px solid var(--border)":"none"}}>
          <div style={{fontFamily:"var(--D)",fontSize:12,color:"var(--fg)"}}>{s.name}</div>
          <div style={{fontFamily:"var(--M)",fontSize:12,color:s.chg>=0?"var(--up)":"var(--dn)"}}>
            {s.chg>=0?"+":""}{(s.chg||0).toFixed(2)}%
          </div>
        </div>
      ))}
    </div>
  );
};

const VixGauge = () => {
  const [vix,    setVix]    = useState(null);
  const [change, setChange] = useState(null);

  // Fetch VIX live from Yahoo Finance via allorigins proxy
  React.useEffect(() => {
    let alive = true;
    const go = async () => {
      try {
        const url   = "https://query1.finance.yahoo.com/v7/finance/quote?symbols=%5EVIX&fields=regularMarketPrice,regularMarketChangePercent";
        const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        const res   = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
        const d     = await res.json();
        const q     = d?.quoteResponse?.result?.[0];
        if (alive && q) {
          setVix(q.regularMarketPrice);
          setChange(q.regularMarketChangePercent);
        }
      } catch(e) {}
    };
    go();
    const t = setInterval(go, 5 * 60 * 1000); // refresh every 5 min
    return () => { alive = false; clearInterval(t); };
  }, []);

  const [displayed, setDisplayed] = useState(50);
  useEffect(() => {
    if (vix == null) return;
    // VIX: 10=extreme greed(0%), 20=neutral(50%), 40+=extreme fear(100%)
    const pct = Math.min(100, Math.max(0, ((vix - 10) / 30) * 100));
    const t = setTimeout(() => setDisplayed(pct), 400);
    return () => clearTimeout(t);
  }, [vix]);

  const cx = 100, cy = 100, r = 72;
  const rad = d => d * Math.PI / 180;
  const pct = displayed / 100;
  const fillDeg = 180 - pct * 180;
  const ex = cx + r * Math.cos(rad(fillDeg));
  const ey = cy + r * Math.sin(rad(fillDeg));
  const largeArc = pct > 0.5 ? 1 : 0;
  const needleDeg = -90 + pct * 180;
  const nx = cx + 54 * Math.cos(rad(needleDeg - 90));
  const ny = cy + 54 * Math.sin(rad(needleDeg - 90));

  const fearPct = Math.min(100, Math.max(0, ((vix - 10) / 30) * 100));
  const label = fearPct >= 75 ? "EXTREME FEAR" : fearPct >= 55 ? "FEAR" : fearPct >= 45 ? "NEUTRAL" : fearPct >= 25 ? "GREED" : "EXTREME GREED";
  const color = fearPct >= 75 ? "var(--dn)" : fearPct >= 55 ? "var(--amb)" : fearPct >= 45 ? "var(--dim)" : "var(--up)";
  const tagClass = fearPct >= 55 ? "t-dn" : fearPct >= 45 ? "t-neu" : "t-up";

  // Build ticks
  const ticks = [];
  for(let i=0; i<=10; i++){
    const deg = 180 - i * 18;
    const inner = i%5===0 ? r-16 : r-10;
    const x1 = cx + r * Math.cos(rad(deg)), y1 = cy + r * Math.sin(rad(deg));
    const x2 = cx + inner * Math.cos(rad(deg)), y2 = cy + inner * Math.sin(rad(deg));
    ticks.push({x1,y1,x2,y2,major:i%5===0});
  }

  // Zone gradient arcs
  const zones = [
    {from:0, to:25, color:"#c4a44a"},   // extreme greed - gold
    {from:25,to:50, color:"#8a7a3a"},   // greed - muted gold
    {from:50,to:75, color:"#b07830"},   // fear - amber
    {from:75,to:100,color:"#b83248"},   // extreme fear - crimson
  ];

  return (
    <div className="card" style={{padding:0,overflow:"hidden"}}>
      <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",
        display:"flex",alignItems:"center",gap:10}}>
        <Icon name="zap" size={16} color={color} strokeWidth={1.5}/>
        <div style={{fontFamily:"var(--D)",fontSize:15,color:"var(--cream)"}}>
          Market Sentiment
        </div>
        <span className={`tag ${tagClass}`} style={{marginLeft:"auto"}}>{label}</span>
      </div>
      <div style={{padding:"8px 20px 4px",background:"rgba(0,0,0,.18)"}}>
        <svg viewBox="20 30 160 90" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
          {/* Zone track segments */}
          {zones.map((z,i) => {
            const startA = 180 - (z.from/100)*180;
            const endA   = 180 - (z.to/100)*180;
            const sx = cx + r * Math.cos(rad(startA)), sy = cy + r * Math.sin(rad(startA));
            const ex2 = cx + r * Math.cos(rad(endA)),   ey2 = cy + r * Math.sin(rad(endA));
            const la = (z.to - z.from) > 50 ? 1 : 0;
            return <path key={i}
              d={`M ${sx} ${sy} A ${r} ${r} 0 ${la} 0 ${ex2} ${ey2}`}
              fill="none" stroke={z.color} strokeWidth="8" strokeLinecap="butt" opacity=".25"/>;
          })}
          {/* Active fill */}
          {pct > 0.02 && (
            <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
              fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
              style={{transition:"all 1.1s cubic-bezier(.22,1,.36,1)",filter:`drop-shadow(0 0 4px ${color})`}}
              opacity=".8"/>
          )}
          {ticks.map((t,i)=>(
            <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke={t.major?"var(--dim)":"var(--border2)"}
              strokeWidth={t.major?1.2:.7}/>
          ))}
          {/* Labels */}
          <text x={cx-r-10} y={cy+4} fill="var(--up)" fontSize="9"
            fontFamily="IBM Plex Mono,monospace" fontWeight="600" textAnchor="middle">G</text>
          <text x={cx+r+10} y={cy+4} fill="var(--dn)" fontSize="9"
            fontFamily="IBM Plex Mono,monospace" fontWeight="600" textAnchor="middle">F</text>
          <text x={cx-r+4}  y={cy-8} fill="var(--up)" fontSize="6.5"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" opacity=".7">GREED</text>
          <text x={cx+r-4}  y={cy-8} fill="var(--dn)" fontSize="6.5"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" opacity=".7">FEAR</text>
          {/* Needle */}
          <g style={{transformOrigin:`${cx}px ${cy}px`,transform:`rotate(${needleDeg}deg)`,
            transition:"transform 1.2s cubic-bezier(.22,1,.36,1)"}}>
            <line x1={cx} y1={cy} x2={cx} y2={cy-54}
              stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round"
              className="needle-glow"/>
            <circle cx={cx} cy={cy} r="4" fill="var(--raised)" stroke="var(--cream)" strokeWidth="1"/>
          </g>
          {/* Center label */}
          <text x={cx} y={cy-20} fill="var(--muted)" fontSize="7"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" letterSpacing="1.5">VIX</text>
        </svg>
      </div>
      {/* Odometer readout */}
      <div style={{margin:"0 20px 16px",background:"#030304",border:"1px solid var(--border)",
        borderRadius:6,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--soft)",letterSpacing:.8,marginBottom:3}}>VIX INDEX</div>
          <div style={{fontFamily:"var(--M)",fontSize:22,fontWeight:700,color:"var(--cream)",letterSpacing:"-0.5px",
            textShadow:`0 0 12px ${color}44`}}>{vix != null ? vix.toFixed(2) : "—"}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",letterSpacing:1,marginBottom:3}}>SESSION</div>
          <div style={{fontFamily:"var(--M)",fontSize:14,fontWeight:700,
            color:change<0?"var(--up)":"var(--dn)"}}>{change>0?"+":""}{change}%</div>
          <div style={{fontFamily:"var(--P)",fontSize:9,color:"var(--muted)",marginTop:2}}>
            {change<0?"Fear declining":"Fear rising"}
          </div>
        </div>
      </div>
      <div style={{padding:"0 20px 14px"}}>
        <div style={{display:"flex",gap:4}}>
          {[{l:"Extreme\nGreed",c:"var(--up)"},{l:"Greed",c:"#8a7a3a"},{l:"Neutral",c:"var(--dim)"},{l:"Fear",c:"var(--amb)"},{l:"Extreme\nFear",c:"var(--dn)"}].map((z,i)=>(
            <div key={i} style={{flex:1,height:3,borderRadius:2,background:z.c,
              opacity: fearPct >= i*25 && fearPct < (i+1)*25 ? 1 : 0.25,
              transition:"opacity .6s",boxShadow: fearPct >= i*25 && fearPct < (i+1)*25 ? `0 0 6px ${z.c}` : "none"}}/>
          ))}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
          <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>Extreme Greed</span>
          <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>Extreme Fear</span>
        </div>
      </div>
    </div>
  );
};

// ─── REUSABLE SEMICIRCLE GAUGE ────────────────────────────────────────────────
const SemicircleGauge = ({
  label, value, minLabel, maxLabel, minColor, maxColor,
  zones, signalLabel, signalClass, readout, subReadout, icon, liveTag
}) => {
  const [displayed, setDisplayed] = useState(50);
  useEffect(() => {
    const t = setTimeout(() => setDisplayed(value), 350);
    return () => clearTimeout(t);
  }, [value]);

  const cx = 100, cy = 100, r = 72;
  const rad = d => d * Math.PI / 180;
  const pct = displayed / 100;
  const needleDeg = -90 + pct * 180;
  const fillDeg = 180 - pct * 180;
  const ex = cx + r * Math.cos(rad(fillDeg));
  const ey = cy + r * Math.sin(rad(fillDeg));
  const largeArc = pct > 0.5 ? 1 : 0;

  const ticks = [];
  for(let i=0; i<=10; i++){
    const deg = 180 - i*18;
    const inner = i%5===0 ? r-16 : r-10;
    const x1 = cx+r*Math.cos(rad(deg)), y1 = cy+r*Math.sin(rad(deg));
    const x2 = cx+inner*Math.cos(rad(deg)), y2 = cy+inner*Math.sin(rad(deg));
    ticks.push({x1,y1,x2,y2,major:i%5===0});
  }

  // Determine active zone color
  const activeZone = zones.find(z => pct*100 >= z.from && pct*100 < z.to) || zones[zones.length-1];
  const needleColor = activeZone?.color || "var(--cream)";

  return (
    <div className="card" style={{padding:0,overflow:"hidden"}}>
      <div style={{padding:"14px 18px",borderBottom:"1px solid var(--border)",
        display:"flex",alignItems:"center",gap:8}}>
        {icon && <Icon name={icon} size={14} color={needleColor} strokeWidth={1.5}/>}
        <div style={{fontFamily:"var(--D)",fontSize:14,color:"var(--cream)",flex:1}}>{label}</div>
        {liveTag && <span style={{fontFamily:"var(--M)",fontSize:7,color:"#00a0dd",letterSpacing:".1em",
          border:"1px solid rgba(0,160,221,.3)",borderRadius:3,padding:"1px 5px"}}>LIVE</span>}
        <span className={`tag ${signalClass}`} style={{fontSize:8}}>{signalLabel}</span>
      </div>
      <div style={{padding:"8px 18px 4px",background:"rgba(0,0,0,.18)"}}>
        <svg viewBox="20 30 160 90" style={{width:"100%",maxWidth:260,display:"block",margin:"0 auto"}}>
          {zones.map((z,i) => {
            const startA = 180 - (z.from/100)*180, endA = 180 - (z.to/100)*180;
            const sx = cx+r*Math.cos(rad(startA)), sy = cy+r*Math.sin(rad(startA));
            const ex2 = cx+r*Math.cos(rad(endA)), ey2 = cy+r*Math.sin(rad(endA));
            const la = (z.to-z.from) > 50 ? 1 : 0;
            return <path key={i} d={`M ${sx} ${sy} A ${r} ${r} 0 ${la} 0 ${ex2} ${ey2}`}
              fill="none" stroke={z.color} strokeWidth="8" strokeLinecap="butt" opacity=".22"/>;
          })}
          {pct > 0.02 && (
            <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
              fill="none" stroke={needleColor} strokeWidth="8" strokeLinecap="round"
              style={{transition:"all 1.1s cubic-bezier(.22,1,.36,1)",filter:`drop-shadow(0 0 4px ${needleColor})`}}
              opacity=".85"/>
          )}
          {ticks.map((t,i) => (
            <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke={t.major?"var(--dim)":"var(--border2)"} strokeWidth={t.major?1.2:.7}/>
          ))}
          <text x={cx-r-10} y={cy+4} fill={minColor} fontSize="8"
            fontFamily="IBM Plex Mono,monospace" fontWeight="600" textAnchor="middle">{minLabel[0]}</text>
          <text x={cx+r+10} y={cy+4} fill={maxColor} fontSize="8"
            fontFamily="IBM Plex Mono,monospace" fontWeight="600" textAnchor="middle">{maxLabel[0]}</text>
          <text x={cx-r+4} y={cy-8} fill={minColor} fontSize="6"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" opacity=".6">{minLabel}</text>
          <text x={cx+r-4} y={cy-8} fill={maxColor} fontSize="6"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" opacity=".6">{maxLabel}</text>
          <g style={{transformOrigin:`${cx}px ${cy}px`,transform:`rotate(${needleDeg}deg)`,
            transition:"transform 1.2s cubic-bezier(.22,1,.36,1)"}}>
            <line x1={cx} y1={cy} x2={cx} y2={cy-54}
              stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round" className="needle-glow"/>
            <circle cx={cx} cy={cy} r="4" fill="var(--raised)" stroke="var(--cream)" strokeWidth="1"/>
          </g>
          <text x={cx} y={cy-20} fill="var(--muted)" fontSize="7"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" letterSpacing="1.5">{label.toUpperCase()}</text>
        </svg>
      </div>
      <div style={{margin:"0 18px 14px",background:"#030304",border:"1px solid var(--border)",
        borderRadius:6,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--soft)",letterSpacing:.8,marginBottom:3}}>{label.toUpperCase()}</div>
          <div style={{fontFamily:"var(--M)",fontSize:20,fontWeight:700,color:"var(--cream)",letterSpacing:"-0.5px",
            textShadow:`0 0 10px ${needleColor}44`}}>{readout}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",letterSpacing:1,marginBottom:3}}>SIGNAL</div>
          <div style={{fontFamily:"var(--M)",fontSize:13,fontWeight:700,color:needleColor}}>{signalLabel}</div>
          {subReadout && <div style={{fontFamily:"var(--P)",fontSize:9,color:"var(--muted)",marginTop:2}}>{subReadout}</div>}
        </div>
      </div>
    </div>
  );
};

// ─── LIVE GAUGE PANEL (Gold · BTC · NatGas · SPX) ─────────────────────────────
const useLiveGaugeData = () => {
  const [data, setData] = useState({ gold:null, btc:null, natgas:null, spx:null });

  const fetch_ = async () => {
    try {
      const syms = "GLD,BTC-USD,UNG,^GSPC";
      const url = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${syms}&fields=symbol,regularMarketPrice,regularMarketChangePercent,fiftyDayAverage`;
      const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
      const res = await fetch(proxy, { signal: AbortSignal.timeout(10000) });
      const d = await res.json();
      const quotes = d?.quoteResponse?.result || [];
      const find = sym => quotes.find(q => q.symbol === sym);
      const gld = find("GLD"), btc = find("BTC-USD"), ung = find("UNG"), spx = find("^GSPC");
      setData({
        gold:   gld  ? { price: gld.regularMarketPrice,  chg: gld.regularMarketChangePercent,  ma50: gld.fiftyDayAverage }  : null,
        btc:    btc  ? { price: btc.regularMarketPrice,  chg: btc.regularMarketChangePercent,  ma50: btc.fiftyDayAverage }  : null,
        natgas: ung  ? { price: ung.regularMarketPrice,  chg: ung.regularMarketChangePercent,  ma50: ung.fiftyDayAverage }  : null,
        spx:    spx  ? { price: spx.regularMarketPrice,  chg: spx.regularMarketChangePercent,  ma50: spx.fiftyDayAverage }  : null,
      });
    } catch(e) {}
  };

  useEffect(() => { fetch_(); const t = setInterval(fetch_, 90000); return ()=>clearInterval(t); }, []);
  return data;
};

// Convert % change to 0-100 gauge signal (0=-10%+, 50=0%, 100=+10%+)
const chgToSignal = (chg) => Math.min(100, Math.max(0, 50 + (chg||0) * 5));
// Convert price vs MA50 to signal
const maSignal = (price, ma50) => {
  if (!price || !ma50) return 50;
  const dev = ((price - ma50) / ma50) * 100;
  return Math.min(100, Math.max(0, 50 + dev * 4));
};

const GoldGauge = ({ liveData }) => {
  const g = liveData?.gold;
  const signal = g ? maSignal(g.price, g.ma50) : 50;
  const pf = (n, d=2) => n != null ? `$${n.toFixed(d)}` : "—";
  const chgStr = g?.chg != null ? `${g.chg>=0?"+":""}${g.chg.toFixed(2)}%` : "—";
  const label = signal>=68?"SAFE HAVEN":signal>=52?"MILD BID":signal>=40?"NEUTRAL":signal>=25?"RISK-ON":"SELL-OFF";
  const cls = signal>=52?"t-up":signal>=40?"t-neu":"t-dn";
  return <SemicircleGauge
    label="Gold" icon="gem" liveTag={!!g}
    value={signal} signalLabel={label} signalClass={cls}
    minLabel="RISK" maxLabel="SAFE" minColor="var(--dn)" maxColor="var(--up)"
    zones={[
      {from:0,to:25,color:"#b83248"},{from:25,to:45,color:"#b07830"},
      {from:45,to:55,color:"#666"},{from:55,to:75,color:"#8a7a3a"},{from:75,to:100,color:"#c4a44a"}
    ]}
    readout={pf(g?.price)} subReadout={`${chgStr} today`}
  />;
};

const BTCGauge = ({ liveData }) => {
  const b = liveData?.btc;
  const signal = b ? maSignal(b.price, b.ma50) : 50;
  const fmtBTC = p => p!=null ? (p>=10000?`$${(p/1000).toFixed(1)}K`:`$${p.toFixed(0)}`) : "—";
  const chgStr = b?.chg != null ? `${b.chg>=0?"+":""}${b.chg.toFixed(2)}%` : "—";
  const label = signal>=70?"RISK ON":signal>=55?"MOMENTUM":signal>=40?"NEUTRAL":signal>=22?"CAUTION":"RISK OFF";
  const cls = signal>=55?"t-up":signal>=40?"t-neu":"t-dn";
  return <SemicircleGauge
    label="BTC" icon="zap" liveTag={!!b}
    value={signal} signalLabel={label} signalClass={cls}
    minLabel="BEAR" maxLabel="BULL" minColor="var(--dn)" maxColor="var(--up)"
    zones={[
      {from:0,to:25,color:"#b83248"},{from:25,to:45,color:"#b07830"},
      {from:45,to:55,color:"#666"},{from:55,to:80,color:"#8a7a3a"},{from:80,to:100,color:"#c4a44a"}
    ]}
    readout={fmtBTC(b?.price)} subReadout={`${chgStr} today`}
  />;
};

const NatGasGauge = ({ liveData }) => {
  const n = liveData?.natgas;
  const signal = n ? maSignal(n.price, n.ma50) : 50;
  const pf = p => p!=null ? `$${p.toFixed(2)}` : "—";
  const chgStr = n?.chg != null ? `${n.chg>=0?"+":""}${n.chg.toFixed(2)}%` : "—";
  const label = signal>=70?"SUPPLY CRUNCH":signal>=55?"TIGHT":signal>=40?"BALANCED":signal>=22?"OVERSUPPLY":"GLUT";
  const cls = signal>=55?"t-up":signal>=40?"t-neu":"t-dn";
  return <SemicircleGauge
    label="Nat Gas" icon="flame" liveTag={!!n}
    value={signal} signalLabel={label} signalClass={cls}
    minLabel="GLUT" maxLabel="CRUNCH" minColor="var(--dn)" maxColor="var(--amb)"
    zones={[
      {from:0,to:25,color:"#4a8ab8"},{from:25,to:45,color:"#666"},
      {from:45,to:55,color:"#666"},{from:55,to:80,color:"#b07830"},{from:80,to:100,color:"#c4a44a"}
    ]}
    readout={pf(n?.price)} subReadout={`${chgStr} today`}
  />;
};

const SPXGauge = ({ liveData }) => {
  const s = liveData?.spx;
  const signal = s ? maSignal(s.price, s.ma50) : 50;
  const pf = p => p!=null ? (p>=1000?`${p.toFixed(0)}`:`$${p.toFixed(2)}`) : "—";
  const chgStr = s?.chg != null ? `${s.chg>=0?"+":""}${s.chg.toFixed(2)}%` : "—";
  const label = signal>=72?"BREAKOUT":signal>=57?"BULL":signal>=42?"NEUTRAL":signal>=25?"PULLBACK":"BREAKDOWN";
  const cls = signal>=57?"t-up":signal>=42?"t-neu":"t-dn";
  return <SemicircleGauge
    label="S&P 500" icon="bar-chart" liveTag={!!s}
    value={signal} signalLabel={label} signalClass={cls}
    minLabel="BEAR" maxLabel="BULL" minColor="var(--dn)" maxColor="var(--up)"
    zones={[
      {from:0,to:25,color:"#b83248"},{from:25,to:42,color:"#b07830"},
      {from:42,to:57,color:"#666"},{from:57,to:80,color:"#8a7a3a"},{from:80,to:100,color:"#c4a44a"}
    ]}
    readout={pf(s?.price)} subReadout={`${chgStr} today`}
  />;
};

const GaugesPanel = () => {
  const liveGaugeData = useLiveGaugeData();
  return (
  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:13,marginBottom:13}}>
    <GoldGauge   liveData={liveGaugeData}/>
    <BTCGauge    liveData={liveGaugeData}/>
    <NatGasGauge liveData={liveGaugeData}/>
    <SPXGauge    liveData={liveGaugeData}/>
  </div>
  );
};

const OilGauge = ({ wx }) => {
  const signal = wx.gaugeSignal; // 0–100
  const [displayed, setDisplayed] = useState(50);
  const [animated, setAnimated] = useState(false);

  useEffect(()=>{
    const t = setTimeout(()=>{ setDisplayed(signal); setAnimated(true); }, 300);
    return ()=>clearTimeout(t);
  },[signal]);

  // Gauge arc: from -220deg to 40deg (260deg sweep, left=E, right=F)
  // Signal 0=E (−220deg rotation), 100=F (40deg)
  // Needle angle in degrees from vertical (0=down)
  // Arc goes from 9 o'clock (left, Empty/Sell) to 3 o'clock (right, Full/Buy)
  // Map: 0 → -90deg, 100 → +90deg (needle pointing from left to right)
  const needleDeg = -90 + (displayed / 100) * 180;
  const rad = (deg) => (deg * Math.PI) / 180;

  // SVG arc path for semicircle (bottom of circle)
  const cx = 100, cy = 100, r = 72;
  const startDeg = 180, endDeg = 0; // left to right

  // Build tick marks
  const ticks = [];
  for(let i=0; i<=10; i++){
    const deg = 180 - i*18; // 180 to 0
    const inner = i%5===0 ? r-16 : r-10;
    const x1 = cx + r * Math.cos(rad(deg));
    const y1 = cy + r * Math.sin(rad(deg));
    const x2 = cx + inner * Math.cos(rad(deg));
    const y2 = cy + inner * Math.sin(rad(deg));
    ticks.push({x1,y1,x2,y2,major:i%5===0});
  }

  // Arc color based on signal
  const gaugeColor = signal>=75?"var(--up)":signal>=45?"var(--amb)":"var(--dn)";
  const signalLabel = signal>=75?"BUY":signal>=45?"NEUTRAL":"SELL";
  const signalClass = signal>=75?"t-up":signal>=45?"t-amb":"t-dn";

  // Needle tip coords
  const needleLength = 56;
  const nx = cx + needleLength * Math.cos(rad(needleDeg - 90));
  const ny = cy + needleLength * Math.sin(rad(needleDeg - 90));

  return (
    <div className="card" style={{padding:"0",overflow:"hidden"}}>
      {/* Header */}
      <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",
        display:"flex",alignItems:"center",gap:10}}>
        <Icon name="oildrop" size={16} color="var(--up)" strokeWidth={1.5}/>
        <div style={{fontFamily:"var(--D)",fontSize:16,color:"var(--cream)",fontWeight:600}}>
          Oil Futures Signal
        </div>
        <span className={`tag ${signalClass}`} style={{marginLeft:"auto"}}>{signalLabel}</span>
      </div>

      {/* SVG Gauge */}
      <div style={{padding:"8px 20px 4px",background:"rgba(0,0,0,.18)"}}>
        <svg viewBox="20 30 160 90" style={{width:"100%",maxWidth:280,display:"block",margin:"0 auto"}}>
          {/* Background arc track */}
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none" stroke="var(--border2)" strokeWidth="8" strokeLinecap="round"
          />

          {/* Colored fill arc - progress */}
          {(() => {
            const fillEnd = Math.max(0, Math.min(100, displayed));
            const fillDeg = 180 - (fillEnd / 100) * 180;
            const fillX = cx + r * Math.cos(rad(fillDeg));
            const fillY = cy + r * Math.sin(rad(fillDeg));
            const largeArc = fillEnd > 50 ? 1 : 0;
            return (
              <path
                d={`M ${cx - r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${fillX} ${fillY}`}
                fill="none" stroke={gaugeColor} strokeWidth="8" strokeLinecap="round"
                style={{transition:"all 1.1s cubic-bezier(.22,1,.36,1)"}}
                opacity=".7"
              />
            );
          })()}

          {/* Tick marks */}
          {ticks.map((t,i)=>(
            <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke={t.major?"var(--dim)":"var(--border2)"}
              strokeWidth={t.major?1.2:.7}
            />
          ))}

          {/* E and F labels */}
          <text x={cx - r - 10} y={cy + 4} fill="var(--dn)" fontSize="10"
            fontFamily="IBM Plex Mono,monospace" fontWeight="600" textAnchor="middle">E</text>
          <text x={cx + r + 10} y={cy + 4} fill="var(--up)" fontSize="10"
            fontFamily="IBM Plex Mono,monospace" fontWeight="600" textAnchor="middle">F</text>

          {/* SELL / BUY labels */}
          <text x={cx - r + 4} y={cy - 8} fill="var(--dn)" fontSize="7"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" opacity=".7">SELL</text>
          <text x={cx + r - 4} y={cy - 8} fill="var(--up)" fontSize="7"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" opacity=".7">BUY</text>

          {/* Needle */}
          <g style={{transformOrigin:`${cx}px ${cy}px`,
            transform:`rotate(${needleDeg}deg)`,
            transition:"transform 1.2s cubic-bezier(.22,1,.36,1)"}}>
            {/* Needle shaft */}
            <line x1={cx} y1={cy} x2={cx} y2={cy - 54}
              stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round"
              className="needle-glow"/>
            {/* Needle base disk */}
            <circle cx={cx} cy={cy} r="4" fill="var(--raised)" stroke="var(--cream)" strokeWidth="1"/>
            {/* Needle tip */}
            <circle cx={cx} cy={cy - 54} r="1.5" fill="var(--cream)"/>
          </g>

          {/* Oil drop in center */}
          <text x={cx} y={cy + 16} fill="var(--muted)" fontSize="7"
            fontFamily="IBM Plex Mono,monospace" textAnchor="middle" letterSpacing=".1em">
            PREDICTIVE
          </text>
        </svg>
      </div>

      {/* Price + Mileage readout */}
      <div style={{padding:"0 20px 16px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          padding:"12px 14px",background:"rgba(0,0,0,.3)",borderRadius:9,
          border:"1px solid var(--border)",marginBottom:10}}>
          {/* Odometer style price */}
          <div style={{display:"flex",alignItems:"baseline",gap:6}}>
            <Icon name="oildrop" size={13} color="var(--dim)" strokeWidth={1.5}/>
            <span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--muted)"}}>WTI</span>
            <span style={{fontFamily:"var(--M)",fontSize:22,fontWeight:600,
              color:"var(--cream)",letterSpacing:".04em",
              background:"rgba(0,0,0,.4)",padding:"2px 8px",borderRadius:5,
              border:"1px solid var(--border)",display:"inline-block"}}>
              {wx.futures}
            </span>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginBottom:2}}>Session</div>
            <div style={{fontFamily:"var(--M)",fontSize:14,fontWeight:700,
              color:wx.futUp?"var(--up)":"var(--dn)"}}>{wx.futChange}</div>
          </div>
        </div>

        {/* Signal breakdown */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
          {[
            {l:"Supply Risk",v:88,positive:true},
            {l:"Momentum",v:72,positive:true},
            {l:"Sentiment",v:signal,positive:signal>50},
          ].map((f,i)=>(
            <div key={i} style={{textAlign:"center",padding:"8px 6px",
              background:"rgba(0,0,0,.2)",borderRadius:7,border:"1px solid var(--border)"}}>
              <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",
                textTransform:"uppercase",letterSpacing:".07em",marginBottom:4}}>{f.l}</div>
              <div style={{fontFamily:"var(--M)",fontSize:16,fontWeight:600,
                color:f.positive?"var(--up)":"var(--dn)"}}>{f.v}</div>
              <div style={{width:"100%",height:2,background:"var(--raised)",
                borderRadius:1,marginTop:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${f.v}%`,
                  background:f.positive?"var(--up)":"var(--dn)",
                  borderRadius:1,transition:"width .8s ease"}}/>
              </div>
            </div>
          ))}
        </div>

        <div style={{marginTop:10,fontSize:11,color:"var(--muted)",fontFamily:"var(--P)",lineHeight:1.5,textAlign:"center"}}>
          Signal derived from weather disruption, supply capacity,<br/>
          options flow, and momentum indicators.
        </div>
      </div>
    </div>
  );
};


// ─── AI MARKET BRIEF ─────────────────────────────────────────────────────────
const ANTHROPIC_API = "https://api.anthropic.com/v1/messages";

const buildMarketPayload = (macro, gainers, losers, weather, predictions) => {
  const macroStr = macro.map(m=>`${m.k}: ${m.v} (${m.c})`).join(", ");
  const topGainers = gainers.slice(0,3).map(g=>`${g.ticker} +${g.chg}%`).join(", ");
  const topLosers  = losers.slice(0,3).map(l=>`${l.ticker} ${l.chg}%`).join(", ");
  const wxAlert    = weather.length ? `${weather[0].alert}: ${weather[0].headline}` : "No significant alerts";
  const predStr    = predictions.slice(0,2).map(p=>`${p.question||p.label}: PM ${p.poly||p.yes_bid}% vs KS ${p.kalshi||p.no_ask}%`).join("; ");
  return `MACRO: ${macroStr} | TOP GAINERS: ${topGainers} | TOP LOSERS: ${topLosers} | WEATHER: ${wxAlert} | PREDICTIONS: ${predStr}`;
};

const useAIBrief = ({ macro, gainers, losers, weather, predictions, tier }) => {
  const [brief, setBrief]     = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError]     = React.useState(null);
  const [ts, setTs]           = React.useState(null);
  const cacheKey = "folio_brief_v2";

  const generate = React.useCallback(async (force=false) => {
    // Check cache — refresh max once per 30 min
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey)||"null");
        if (cached && Date.now() - cached.ts < 30*60*1000) {
          setBrief(cached.text);
          setTs(new Date(cached.ts));
          return;
        }
      } catch(e) {}
    }

    setLoading(true);
    setError(null);

    const payload = buildMarketPayload(macro, gainers, losers, weather, predictions);

    const systemPrompt = `You are Folio's market intelligence engine — a senior cross-asset strategist with 20 years of experience across equities, commodities, FX, and prediction markets. Write with the authority of a Goldman Sachs morning note and the clarity of Bloomberg surveillance. No fluff. No disclaimers. No "it's important to note." Just signal.`;

    const userPrompt = `Today's market snapshot: ${payload}

Write a 3-sentence Market Brief for sophisticated investors. Structure:
1. Sentence 1: The dominant macro narrative right now — what's driving risk appetite.
2. Sentence 2: The key divergence or tension worth watching (cross-asset, prediction market gap, or weather-commodity signal).
3. Sentence 3: One specific, actionable observation — where the edge might be.

Be precise, use actual numbers from the data, and write like a trader, not a journalist. No headers. No bullet points. Flowing prose only.`;

    try {
      const res = await authedBriefFetch({ macro, gainers, losers, weather, predictions });

      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      const text = data.brief || "";

      if (text) {
        setBrief(text);
        setTs(new Date());
        localStorage.setItem(cacheKey, JSON.stringify({ text, ts: Date.now() }));
      }
    } catch(e) {
      setError("Brief unavailable — markets are complex, refresh to retry.");
      console.error("AI Brief:", e);
    }
    setLoading(false);
  }, [macro, gainers, losers, weather, predictions]);

  // Auto-generate when data is ready
  React.useEffect(() => {
    if (macro?.length && (gainers?.length || losers?.length)) {
      generate();
    }
  }, []); // Only on mount — cached after that

  return { brief, loading, error, ts, refresh: ()=>generate(true) };
};


// ── useCitedBrief: reads server-generated brief from Firestore ──────────────
// Reads from cache/brief_latest (written by /api/brief every 6 hours)
// Falls back to daily_briefs/{date} for backwards compat
const useCitedBrief = () => {
  const [citedBrief, setCitedBrief] = React.useState(null);
  const [cbLoading,  setCbLoading]  = React.useState(false);

  const refreshFromServer = React.useCallback(async () => {
    try {
      const r = await fetch("/api/brief", { signal: AbortSignal.timeout(45000) });
      if (!r.ok) return;
      const d = await r.json();
      if (d?.brief) setCitedBrief(d);
    } catch(e) {}
  }, []);

  React.useEffect(() => {
    if (!_db) {
      // No Firestore — call API directly
      setCbLoading(true);
      refreshFromServer().finally(() => setCbLoading(false));
      return;
    }
    setCbLoading(true);
    // Try cache/brief_latest first (written by /api/brief)
    const { doc, getDoc } = require ? { doc: null, getDoc: null } : {};
    import("firebase/firestore").then(({ doc, getDoc, onSnapshot }) => {
      // Real-time listener on brief_latest so UI updates when new brief generates
      const ref = doc(_db, "cache", "brief_latest");
      const unsub = onSnapshot(ref, snap => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.payload?.brief) {
            setCitedBrief(data.payload);
            setCbLoading(false);
            return;
          }
        }
        // Fallback: call /api/brief directly to generate
        refreshFromServer().finally(() => setCbLoading(false));
      }, () => {
        refreshFromServer().finally(() => setCbLoading(false));
      });
      return unsub;
    }).catch(() => {
      refreshFromServer().finally(() => setCbLoading(false));
    });
  }, []);

  return { citedBrief, cbLoading, refresh: refreshFromServer };
};

const AIBriefCard = ({ macro, gainers, losers, weather, predictions, tier, onPaywall }) => {
  // Free users see a locked teaser — no API call made
  if (tier === "free") {
    return (
      <div className="card" style={{
        marginBottom:24, padding:"20px 24px",
        borderLeft:"3px solid var(--up)",
        background:"linear-gradient(135deg, var(--surface) 0%, var(--raised) 100%)",
        position:"relative", overflow:"hidden",
      }}>
        <div style={{position:"absolute",top:0,right:0,width:200,height:"100%",
          background:"radial-gradient(ellipse at top right, rgba(196,164,74,0.06) 0%, transparent 70%)",
          pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:"var(--up)"}}/>
            <span style={{fontFamily:"var(--M)",fontSize:9,letterSpacing:"0.14em",
              color:"var(--up)",fontWeight:500}}>FOLIO INTELLIGENCE BRIEF</span>
          </div>
          <span style={{marginLeft:"auto",fontFamily:"var(--M)",fontSize:8,
            color:"var(--up)",border:"1px solid var(--up-b)",borderRadius:3,
            padding:"2px 6px",letterSpacing:"0.08em"}}>PRO</span>
        </div>
        {/* Blurred teaser */}
        <div style={{position:"relative",marginBottom:18}}>
          <div style={{fontFamily:"var(--P)",fontSize:15,color:"var(--text)",lineHeight:1.75,
            filter:"blur(5px)",userSelect:"none",pointerEvents:"none"}}>
            Equity risk appetite is compressing as the front-end of the rates curve reasserts
            control — watch the 2Y10Y for the next directional cue. Prediction markets are
            pricing a 40% cut probability into May FOMC while oil COT positioning hints at
            a supply squeeze not yet reflected in spot. The edge is in the basis.
          </div>
          {/* Lock overlay */}
          <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",
            alignItems:"center",justifyContent:"center",gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <Icon name="lock" size={16} color="var(--up)"/>
              <span style={{fontFamily:"var(--D)",fontSize:16,color:"var(--cream)"}}>
                AI Market Brief
              </span>
            </div>
            <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--muted)",
              textAlign:"center",maxWidth:260,lineHeight:1.5}}>
              Cross-asset intelligence written by AI in real time — macro, commodities,
              predictions and weather in one paragraph.
            </div>
            <button onClick={onPaywall}
              style={{background:"var(--up)",color:"#000",border:"none",
                borderRadius:8,padding:"10px 24px",fontFamily:"var(--M)",
                fontSize:11,cursor:"pointer",letterSpacing:".08em",marginTop:4}}>
              UNLOCK FROM $14.99/MO →
            </button>
          </div>
        </div>
        <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",letterSpacing:".08em"}}>
          UPDATED EVERY 30 MIN · POWERED BY CLAUDE AI · CROSS-ASSET SYNTHESIS
        </div>
      </div>
    );
  }

  const { brief, loading, error, ts, refresh } = useAIBrief({ macro, gainers, losers, weather, predictions, tier });
  const { citedBrief, cbLoading } = useCitedBrief();
  const [expanded, setExpanded] = React.useState(false);

  const fmtTime = d => d?.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}) || "";

  return (
    <div className="card" style={{
      marginBottom:24,
      padding:"20px 24px",
      borderLeft:"3px solid var(--up)",
      background:"linear-gradient(135deg, var(--surface) 0%, var(--raised) 100%)",
      position:"relative",
      overflow:"hidden",
    }}>
      {/* Background texture */}
      <div style={{position:"absolute",top:0,right:0,width:200,height:"100%",
        background:"radial-gradient(ellipse at top right, rgba(196,164,74,0.04) 0%, transparent 70%)",
        pointerEvents:"none"}}/>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <div style={{width:7,height:7,borderRadius:"50%",
            background:loading?"var(--dim)":brief?"var(--up)":"var(--dn)",
            animation:loading?"breathe 1s infinite":"none"}}/>
          <span style={{fontFamily:"var(--M)",fontSize:9,letterSpacing:"0.14em",
            color:"var(--up)",fontWeight:500}}>FOLIO INTELLIGENCE BRIEF</span>
        </div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
          {ts&&<span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
            {fmtTime(ts)}
          </span>}
          {tier==="free"&&(
            <span onClick={onPaywall} style={{fontFamily:"var(--M)",fontSize:8,
              color:"var(--up)",border:"1px solid var(--up-b)",borderRadius:3,
              padding:"2px 6px",cursor:"pointer",letterSpacing:"0.08em"}}>
              PULSE
            </span>
          )}
          <button onClick={refresh} disabled={loading}
            style={{background:"transparent",border:"none",cursor:loading?"default":"pointer",
              padding:2,opacity:loading?0.4:1,display:"flex",alignItems:"center"}}>
            <Icon name="refresh" size={11} color="var(--dim)"/>
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:28,height:28,borderRadius:"50%",
            background:"linear-gradient(135deg,var(--up-bg),var(--raised))",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Icon name="zap" size={13} color="var(--up)"/>
          </div>
          <div>
            <div style={{fontFamily:"var(--P)",fontSize:14,color:"var(--dim)",lineHeight:1.6}}>
              Synthesizing market data
              <span style={{animation:"breathe 1s infinite",display:"inline-block",marginLeft:2}}>...</span>
            </div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginTop:3}}>
              Cross-referencing macro · predictions · weather
            </div>
          </div>
        </div>
      )}

      {error && !loading && (
        <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--muted)",lineHeight:1.6}}>
          {error}
        </div>
      )}

      {brief && !loading && (
        <div>
          {/* Cited brief — show if Firestore has today's server brief */}
          {citedBrief?.brief_sentences?.length > 0 ? (
            <div>
              {/* Unavailable sources warning */}
              {citedBrief.unavailable_sources?.length > 0 && (
                <div style={{background:"rgba(184,50,72,0.07)",border:"1px solid rgba(184,50,72,0.2)",
                  borderRadius:5,padding:"7px 12px",marginBottom:12,
                  fontFamily:"var(--M)",fontSize:9,color:"#b06070",letterSpacing:"0.08em"}}>
                  ⚠ DATA UNAVAILABLE: {citedBrief.unavailable_sources.join(", ")}
                </div>
              )}
              {/* Each sentence with citation */}
              {citedBrief.brief_sentences.map((s, i) => (
                <div key={i} style={{marginBottom:12,paddingBottom:12,
                  borderBottom:i < citedBrief.brief_sentences.length-1 ? "1px solid var(--border)" : "none"}}>
                  <p style={{fontFamily:"var(--P)",fontSize:15,color:"var(--text)",lineHeight:1.75,
                    fontWeight:400,letterSpacing:"0.01em",margin:"0 0 5px 0"}}>
                    {s.text}
                    <sup style={{fontFamily:"var(--M)",fontSize:8,color:"var(--up)",marginLeft:2,cursor:"default"}}
                      title={`Source: ${s.source_label||s.source_id} · Value: ${s.value??""} ${s.delta_context||""}`}>
                      [{i+1}]
                    </sup>
                  </p>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",letterSpacing:"0.07em"}}>
                      {s.source_label||s.source_id}
                    </span>
                    {s.value != null && (
                      <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--dim)"}}>· {s.value}</span>
                    )}
                    {s.delta_context && (
                      <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--dim)"}}>· {s.delta_context}</span>
                    )}
                    <span style={{fontFamily:"var(--M)",fontSize:8,letterSpacing:"0.07em",
                      color:s.confidence==="HIGH"?"var(--up)":s.confidence==="LOW"?"#b06070":"var(--amb)",
                      background:s.confidence==="HIGH"?"rgba(196,164,74,0.08)":"rgba(96,112,128,0.08)",
                      border:`1px solid ${s.confidence==="HIGH"?"rgba(196,164,74,0.2)":"rgba(96,112,128,0.2)"}`,
                      borderRadius:3,padding:"1px 5px"}}>
                      {s.confidence||"—"}
                    </span>
                  </div>
                </div>
              ))}
              {/* Overall bias + key risk */}
              <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                {citedBrief.overall_bias && (
                  <span style={{fontFamily:"var(--M)",fontSize:9,letterSpacing:"0.1em",
                    color:citedBrief.overall_bias==="BULLISH"?"var(--up)":citedBrief.overall_bias==="BEARISH"?"var(--dn)":"var(--muted)",
                    border:"1px solid",borderColor:citedBrief.overall_bias==="BULLISH"?"var(--up-b)":citedBrief.overall_bias==="BEARISH"?"rgba(184,50,72,0.3)":"var(--border)",
                    borderRadius:3,padding:"2px 7px"}}>
                    {citedBrief.overall_bias}
                  </span>
                )}
                {citedBrief.vol_regime && (
                  <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",letterSpacing:"0.07em"}}>
                    {citedBrief.vol_regime} VOL REGIME
                  </span>
                )}
                <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>·</span>
                <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
                  {citedBrief.brief_sentences.length} CITED CLAIMS · NOT FINANCIAL ADVICE
                </span>
              </div>
              {citedBrief.key_risk && (
                <div style={{marginTop:10,padding:"8px 10px",
                  background:"rgba(184,50,72,0.05)",border:"1px solid rgba(184,50,72,0.15)",borderRadius:5}}>
                  <span style={{fontFamily:"var(--M)",fontSize:8,color:"#b06070",letterSpacing:"0.1em"}}>KEY RISK: </span>
                  <span style={{fontFamily:"var(--P)",fontSize:12,color:"#c08090"}}>{citedBrief.key_risk}</span>
                </div>
              )}
            </div>
          ) : (
            /* Fallback: plain text brief (live-generated) */
            <div>
              <p style={{fontFamily:"var(--P)",fontSize:15,color:"var(--text)",lineHeight:1.75,
                fontWeight:400,letterSpacing:"0.01em",margin:0}}>
                {brief}
              </p>
              <div style={{marginTop:12,display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
                  AI SYNTHESIS · NOT FINANCIAL ADVICE
                </span>
                <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>·</span>
                <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
                  CLAUDE SONNET 4
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {!brief && !loading && !error && (
        <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--muted)",lineHeight:1.6}}>
          Market brief will generate automatically when data loads.
        </div>
      )}
    </div>
  );
};

// ─── NEWS FEED ────────────────────────────────────────────────────────────────
const NEWS_SOURCES = [
  { label:"MarketWatch",url:"https://feeds.marketwatch.com/marketwatch/topstories/",color:"#00a0dd" },
  { label:"Reuters",    url:"https://feeds.reuters.com/reuters/businessNews",      color:"#ff8000" },
  { label:"AP News",    url:"https://feeds.apnews.com/rss/apf-business",           color:"#cc0000" },
];
const RSS2JSON = "https://api.rss2json.com/v1/api.json?rss_url=";

const useNewsFeed = () => {
  const [articles, setArticles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [src, setSrc] = React.useState(0); // default: MarketWatch

  const fetchFeed = async (idx) => {
    setLoading(true);
    setError(false);
    let fetched = false;
    // Try rss2json first, then allorigins as a CORS proxy fallback
    const proxiedUrls = [
      RSS2JSON + encodeURIComponent(NEWS_SOURCES[idx].url),
      `https://api.allorigins.win/get?url=${encodeURIComponent(RSS2JSON + encodeURIComponent(NEWS_SOURCES[idx].url))}`,
    ];
    for (const fetchUrl of proxiedUrls) {
      try {
        const res = await fetch(fetchUrl, { signal: AbortSignal.timeout(12000) });
        let data = await res.json();
        // allorigins wraps response in {contents}
        if (data.contents) { try { data = JSON.parse(data.contents); } catch(_) {} }
        if (data.status === "ok" && data.items?.length) {
          setArticles(data.items.slice(0,12).map(it=>({
            title: it.title,
            url: it.link,
            source: NEWS_SOURCES[idx].label,
            color: NEWS_SOURCES[idx].color,
            date: it.pubDate ? new Date(it.pubDate) : new Date(),
            desc: it.description?.replace(/<[^>]+>/g,"").replace(/&nbsp;/g," ").replace(/&amp;/g,"&").slice(0,160)||"",
          })));
          fetched = true;
          break;
        }
      } catch(e) { /* try next proxy */ }
    }
    if (!fetched) setError(true);
    setLoading(false);
  };

  React.useEffect(()=>{ fetchFeed(src); }, [src]);
  // Auto-refresh every 4 minutes
  React.useEffect(()=>{
    const t = setInterval(()=>fetchFeed(src), 4*60*1000);
    return ()=>clearInterval(t);
  }, [src]);
  return { articles, loading, error, src, setSrc, fetchFeed };
};


// ─── STOCKTWITS MARKET PULSE ────────────────────────────────────────────────
// Read-only feed from StockTwits public API — no auth required for reads.
// Shows real financial community sentiment per symbol.
// StockTwits public endpoint: api.stocktwits.com/api/2/streams/symbol/{SYM}.json
// Proxied via allorigins to bypass browser CORS.

const useStockTwits = (symbol, enabled) => {
  const [messages, setMessages] = React.useState([]);
  const [loading,  setLoading]  = React.useState(false);
  const [bullish,  setBullish]  = React.useState(null); // pct bullish 0-100

  const fetch_ = React.useCallback(async () => {
    if (!symbol || !enabled) return;
    setLoading(true);
    try {
      const sym = symbol.replace(/[\^$]/, "").toUpperCase();
      const url = `https://api.stocktwits.com/api/2/streams/symbol/${sym}.json?limit=10`;
      const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
      const res = await fetch(proxy, { signal: AbortSignal.timeout(10000) });
      const data = await res.json();
      if (data.response?.status !== 200) throw new Error("StockTwits error");
      const msgs = data.messages || [];
      setMessages(msgs.slice(0, 8));
      // Calculate bullish sentiment
      const tagged = msgs.filter(m => m.entities?.sentiment?.basic);
      if (tagged.length > 0) {
        const bullCount = tagged.filter(m => m.entities.sentiment.basic === "Bullish").length;
        setBullish(Math.round((bullCount / tagged.length) * 100));
      }
    } catch(e) {
      setMessages([]);
    }
    setLoading(false);
  }, [symbol, enabled]);

  React.useEffect(() => {
    fetch_();
    if (!enabled) return;
    const t = setInterval(fetch_, 90000); // refresh every 90s
    return () => clearInterval(t);
  }, [fetch_]);

  return { messages, loading, bullish, refresh: fetch_ };
};

const fmtTwitsTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / 1440)}d`;
};

// Compact StockTwits panel — shows in DataTable on click, minimal footprint
const StockTwitsPanel = ({ symbol }) => {
  const [open, setOpen] = React.useState(false);
  const { messages, loading, bullish, refresh } = useStockTwits(symbol, open);

  if (!open) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          display: "flex", alignItems: "center", gap: 5, padding: "4px 10px",
          background: "rgba(0,0,0,.2)", border: "1px solid rgba(255,255,255,.08)",
          borderRadius: 5, cursor: "pointer", fontFamily: "var(--M)",
          fontSize: 9, color: "var(--muted)", letterSpacing: ".06em",
          transition: "border-color .15s",
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(0,160,221,.35)"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,.08)"}
      >
        {/* StockTwits icon */}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="#00a0dd" stroke="none">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
        </svg>
        Market Pulse
      </button>
    );
  }

  return (
    <div
      style={{
        marginTop: 10, border: "1px solid rgba(0,160,221,.2)",
        borderRadius: 8, overflow: "hidden", animation: "fadeUp .2s ease",
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
        background: "rgba(0,160,221,.07)", borderBottom: "1px solid rgba(0,160,221,.15)",
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="#00a0dd" stroke="none">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/>
        </svg>
        <span style={{ fontFamily: "var(--M)", fontSize: 9, color: "#00a0dd", letterSpacing: ".1em" }}>
          STOCKTWITS · ${symbol}
        </span>
        {bullish != null && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 6 }}>
            <div style={{
              width: 36, height: 4, borderRadius: 2, overflow: "hidden",
              background: "rgba(192,54,74,.3)",
            }}>
              <div style={{
                height: "100%", width: `${bullish}%`,
                background: bullish >= 55 ? "var(--up)" : bullish <= 40 ? "var(--dn)" : "var(--amb)",
                borderRadius: 2, transition: "width .8s ease",
              }}/>
            </div>
            <span style={{ fontFamily: "var(--M)", fontSize: 8,
              color: bullish >= 55 ? "var(--up)" : bullish <= 40 ? "var(--dn)" : "var(--amb)" }}>
              {bullish}% 🐂
            </span>
          </div>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={refresh} style={{ background: "none", border: "none",
            cursor: "pointer", color: "var(--muted)", fontSize: 10, padding: "0 4px" }}>↻</button>
          <button onClick={() => setOpen(false)} style={{ background: "none", border: "none",
            cursor: "pointer", color: "var(--muted)", fontSize: 14, padding: "0 4px", lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ maxHeight: 220, overflowY: "auto", background: "rgba(0,0,0,.3)" }}>
        {loading && (
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", margin: "0 auto",
              border: "2px solid rgba(0,160,221,.2)", borderTopColor: "#00a0dd",
              animation: "spin .7s linear infinite" }}/>
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div style={{ padding: "16px 12px", fontFamily: "var(--M)", fontSize: 9,
            color: "var(--muted)", textAlign: "center" }}>
            No recent messages on StockTwits.
          </div>
        )}
        {!loading && messages.map((m, i) => {
          const sentiment = m.entities?.sentiment?.basic;
          const sColor = sentiment === "Bullish" ? "var(--up)" : sentiment === "Bearish" ? "var(--dn)" : null;
          return (
            <div key={m.id || i} style={{
              padding: "8px 12px", borderBottom: i < messages.length - 1 ? "1px solid rgba(255,255,255,.04)" : "none",
              display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              {/* Avatar */}
              <img
                src={m.user?.avatar_url_ssl || `https://api.dicebear.com/7.x/initials/svg?seed=${m.user?.username}`}
                alt={m.user?.username}
                style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, objectFit: "cover",
                  border: sColor ? `1.5px solid ${sColor}` : "1px solid rgba(255,255,255,.1)" }}
                onError={e => { e.target.style.display = "none"; }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <a
                    href={`https://stocktwits.com/${m.user?.username}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ fontFamily: "var(--M)", fontSize: 9, color: "#00a0dd",
                      fontWeight: 700, textDecoration: "none" }}
                  >
                    @{m.user?.username}
                  </a>
                  {sentiment && (
                    <span style={{
                      fontFamily: "var(--M)", fontSize: 7, letterSpacing: ".06em",
                      color: sColor, border: `1px solid ${sColor}33`,
                      borderRadius: 3, padding: "1px 4px",
                    }}>
                      {sentiment === "Bullish" ? "🐂" : "🐻"} {sentiment.toUpperCase()}
                    </span>
                  )}
                  <span style={{ fontFamily: "var(--M)", fontSize: 8,
                    color: "var(--muted)", marginLeft: "auto" }}>
                    {fmtTwitsTime(m.created_at)}
                  </span>
                </div>
                <div style={{ fontFamily: "var(--P)", fontSize: 11, color: "var(--text)",
                  lineHeight: 1.45, wordBreak: "break-word" }}>
                  {m.body?.slice(0, 180)}{m.body?.length > 180 ? "…" : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        padding: "6px 12px", background: "rgba(0,0,0,.4)",
        borderTop: "1px solid rgba(255,255,255,.04)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ fontFamily: "var(--M)", fontSize: 8, color: "var(--muted)" }}>
          Via StockTwits · refreshes every 90s
        </span>
        <a href={`https://stocktwits.com/symbol/${symbol}`}
          target="_blank" rel="noopener noreferrer"
          style={{ fontFamily: "var(--M)", fontSize: 8, color: "#00a0dd", textDecoration: "none" }}>
          View on StockTwits →
        </a>
      </div>
    </div>
  );
};

// ─── FOLIO COMMUNITY CHAT (Predictions only — authenticated users) ─────────────
// Lightweight Firestore-backed chat, used only in prediction market tiles.
// Requires a display handle — set during onboarding.
const ChatRoom = ({ ticker, user }) => {
  const [msgs, setMsgs]     = React.useState([]);
  const [text, setText]     = React.useState("");
  const [sending, setSending] = React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    if (!isFirebaseConfigured || !_db) return;
    const q = query(
      collection(_db, `chats/${ticker}/messages`),
      orderBy("ts", "asc"), limit(40)
    );
    const unsub = onSnapshot(q, snap => {
      setMsgs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    return () => unsub();
  }, [ticker]);

  const send = async () => {
    if (!text.trim() || !user || sending) return;
    setSending(true);
    try {
      await addDoc(collection(_db, `chats/${ticker}/messages`), {
        uid: user.uid,
        handle: user.handle || user.name?.split(" ")[0] || "anon",
        text: text.trim().slice(0, 500),
        ts: serverTimestamp(),
      });
      setText("");
    } catch(e) {}
    setSending(false);
  };

  const fmtTime = ts => {
    if (!ts?.toDate) return "";
    const d = ts.toDate();
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%",
          background: "var(--up)", animation: "breathe 2s infinite" }}/>
        <span style={{ fontFamily: "var(--M)", fontSize: 8,
          color: "var(--soft)", letterSpacing: ".1em" }}>
          FOLIO COMMUNITY · #{ticker.slice(5, 20)}
        </span>
        <span style={{ fontFamily: "var(--M)", fontSize: 8,
          color: "var(--muted)", marginLeft: "auto" }}>{msgs.length} msgs</span>
      </div>
      <div style={{ height: 160, overflowY: "auto", marginBottom: 8,
        display: "flex", flexDirection: "column", gap: 5 }}>
        {msgs.length === 0 && (
          <div style={{ fontFamily: "var(--M)", fontSize: 9, color: "var(--muted)",
            textAlign: "center", paddingTop: 50 }}>
            First to comment. What do you think?
          </div>
        )}
        {msgs.map(m => (
          <div key={m.id} style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg,rgba(196,164,74,.4),rgba(196,164,74,.1))",
              border: "1px solid rgba(196,164,74,.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--M)", fontSize: 7, fontWeight: 700,
              color: "var(--up)" }}>
              {(m.handle || "?")[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 5, alignItems: "baseline", marginBottom: 2 }}>
                <span style={{ fontFamily: "var(--M)", fontSize: 9, color: "var(--cream)", fontWeight: 600 }}>
                  {m.handle}
                </span>
                <span style={{ fontFamily: "var(--M)", fontSize: 7, color: "var(--muted)" }}>
                  {fmtTime(m.ts)}
                </span>
              </div>
              <div style={{ fontFamily: "var(--P)", fontSize: 11, color: "var(--text)", lineHeight: 1.4 }}>
                {m.text}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>
      {user ? (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder={`@${user.handle || "you"} — your take…`}
            maxLength={500}
            className="chat-input"
            style={{ flex: 1, background: "rgba(0,0,0,.3)", border: "1px solid var(--border)",
              borderRadius: 6, padding: "7px 10px", fontFamily: "var(--P)",
              fontSize: 11, color: "var(--cream)" }}
          />
          <button onClick={send} disabled={sending || !text.trim()}
            style={{ padding: "7px 12px", background: "rgba(196,164,74,.15)",
              border: "1px solid rgba(196,164,74,.3)", borderRadius: 6, cursor: "pointer",
              fontFamily: "var(--M)", fontSize: 9, color: "var(--up)",
              opacity: (!text.trim() || sending) ? 0.4 : 1 }}>
            Send
          </button>
        </div>
      ) : (
        <div style={{ fontFamily: "var(--M)", fontSize: 9, color: "var(--muted)",
          textAlign: "center", padding: "8px 0" }}>
          Sign in to join the conversation.
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM 2: HOVER SPARKLINE — SVG price chart on ticker hover (Yahoo 1D data)
// ═══════════════════════════════════════════════════════════════════════════════

const sparklineCache = {};

const HoverSparkline = ({ symbol }) => {
  const [points, setPoints] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [chgPct,  setChgPct] = React.useState(null);

  React.useEffect(() => {
    if (sparklineCache[symbol]) {
      setPoints(sparklineCache[symbol].points);
      setChgPct(sparklineCache[symbol].chgPct);
      setLoading(false);
      return;
    }
    const fetch_ = async () => {
      try {
        // Yahoo Finance v8 chart — 1 day, 5-min intervals
        const sym = symbol.replace(/\^/g, "%5E");
        const url = `https://query1.finance.yahoo.com/v8/finance/chart/${sym}?interval=5m&range=1d`;
        const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        const res  = await fetch(proxy, { signal: AbortSignal.timeout(8000) });
        const data = await res.json();
        const chart = data?.chart?.result?.[0];
        const closes = chart?.indicators?.quote?.[0]?.close || [];
        const valid  = closes.filter(c => c != null);
        if (valid.length < 2) { setLoading(false); return; }

        const open  = valid[0];
        const close = valid[valid.length - 1];
        const pct   = ((close - open) / open) * 100;
        sparklineCache[symbol] = { points: valid, chgPct: pct };
        setPoints(valid);
        setChgPct(pct);
      } catch(e) {}
      setLoading(false);
    };
    fetch_();
  }, [symbol]);

  const W = 120, H = 40;

  if (loading) return (
    <div style={{width:W,height:H,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:12,height:12,borderRadius:"50%",
        border:"1.5px solid rgba(196,164,74,.2)",borderTopColor:"var(--up)",
        animation:"spin .7s linear infinite"}}/>
    </div>
  );

  if (!points) return null;

  const min  = Math.min(...points);
  const max  = Math.max(...points);
  const rng  = max - min || 1;
  const pad  = 3;
  const xs   = points.map((_, i) => pad + (i / (points.length - 1)) * (W - pad * 2));
  const ys   = points.map(v => H - pad - ((v - min) / rng) * (H - pad * 2));
  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const fill = [...xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`),
    `L${(W - pad).toFixed(1)},${H}`, `L${pad},${H}`, "Z"].join(" ");

  const isUp = chgPct >= 0;
  const col  = isUp ? "var(--up)" : "var(--dn)";

  return (
    <div style={{position:"relative"}}>
      <svg width={W} height={H} style={{display:"block"}}>
        <defs>
          <linearGradient id={`sg_${symbol}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={isUp?"#c4a44a":"#b83248"} stopOpacity=".25"/>
            <stop offset="100%" stopColor={isUp?"#c4a44a":"#b83248"} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={fill} fill={`url(#sg_${symbol})`}/>
        <path d={path} fill="none" stroke={col} strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"/>
        {/* Current price dot */}
        <circle cx={xs[xs.length-1]} cy={ys[ys.length-1]} r="2.5"
          fill={col} stroke="var(--void)" strokeWidth="1"/>
      </svg>
      <div style={{position:"absolute",bottom:-14,left:0,right:0,textAlign:"center",
        fontFamily:"var(--M)",fontSize:8,color:col,letterSpacing:".04em"}}>
        1D {chgPct != null ? `${isUp?"+":""}${chgPct.toFixed(2)}%` : ""}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM 3: ALERT STRIP — Breaking news per watchlist ticker at top of page
// Uses Google News RSS per symbol (news.google.com/rss/search?q=SYMBOL)
// Proxied via rss2json.com (free, 10k/day). Falls back to allorigins.
// Shows as dismissible pill strip below ticker tape. Clean, compact.
// ═══════════════════════════════════════════════════════════════════════════════

const alertStripCache  = {};
const alertDismissed   = new Set(); // session-only dismissed alert IDs

const useBreakingAlerts = (tickers) => {
  const [alerts, setAlerts] = React.useState([]);

  React.useEffect(() => {
    if (!tickers || tickers.length === 0) return;
    let alive = true;

    const fetchAlerts = async () => {
      const results = [];
      // Only fetch top 5 tickers to stay under rate limits
      const toFetch = tickers.slice(0, 5);

      await Promise.allSettled(toFetch.map(async (sym) => {
        // Cache per ticker, 5-min TTL
        const cached = alertStripCache[sym];
        if (cached && Date.now() - cached.ts < 5 * 60 * 1000) {
          results.push(...cached.items);
          return;
        }
        try {
          // Google News RSS — dynamic per ticker, no API key needed
          const query  = encodeURIComponent(`${sym} stock OR ${sym} market`);
          const feed   = `https://news.google.com/rss/search?q=${query}&hl=en-US&gl=US&ceid=US:en`;
          const api    = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feed)}&count=3`;
          const res    = await fetch(api, { signal: AbortSignal.timeout(8000) });
          const data   = await res.json();
          const items  = (data.items || []).slice(0, 2).map(item => ({
            id:     `${sym}_${item.guid || item.link}`,
            ticker: sym,
            title:  item.title?.replace(/<[^>]+>/g,"").slice(0, 80),
            link:   item.link,
            pubDate:item.pubDate,
            source: item.author || item.source || "Google News",
          }));
          alertStripCache[sym] = { ts: Date.now(), items };
          results.push(...items);
        } catch(e) {}
      }));

      if (alive && results.length > 0) {
        setAlerts(results.filter(a => !alertDismissed.has(a.id)));
      }
    };

    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(interval); };
  }, [tickers.slice(0,5).join(",")]);

  const dismiss = (id) => {
    alertDismissed.add(id);
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  return { alerts, dismiss };
};

// ── WORLD CLOCK BAR ──────────────────────────────────────────────────────────
// Analog clocks for major shipping/trading time zones.
// Smooth sweeping second hand — no tick, always moving.
const AnalogClock = ({ tz, city }) => {
  const [time, setTime] = React.useState(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 50); // 20fps — smooth sweep
    return () => clearInterval(id);
  }, []);

  const local = new Date(time.toLocaleString("en-US", { timeZone: tz }));
  const h = local.getHours() % 12;
  const m = local.getMinutes();
  const s = local.getSeconds();
  const ms = local.getMilliseconds();

  const sDeg  = (s + ms / 1000) * 6;           // smooth
  const mDeg  = m * 6 + (s / 60) * 6;
  const hDeg  = h * 30 + (m / 60) * 30;

  const SIZE = 52;
  const CX = SIZE / 2;
  const hand = (deg, len, width, color) => {
    const rad = (deg - 90) * Math.PI / 180;
    return {
      x2: CX + Math.cos(rad) * len,
      y2: CX + Math.sin(rad) * len,
      width, color,
    };
  };

  const hHand = hand(hDeg, CX * 0.50, 2,   "#e8e0d0");
  const mHand = hand(mDeg, CX * 0.70, 1.5, "#e8e0d0");
  const sHand = hand(sDeg, CX * 0.78, 1,   "#e53935");

  const hourMarks = Array.from({length:12},(_,i)=>{
    const a = (i/12)*2*Math.PI - Math.PI/2;
    const r = CX - 4;
    return { x: CX + Math.cos(a)*r, y: CX + Math.sin(a)*r, major: i%3===0 };
  });

  const isOpen = local.getHours()>=9 && local.getHours()<17;

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flexShrink:0 }}>
      <svg width={SIZE} height={SIZE} style={{ display:"block" }}>
        {/* Face */}
        <circle cx={CX} cy={CX} r={CX-1} fill="#0d0d0d" stroke="#2a2a2a" strokeWidth={1.5}/>
        {/* Hour marks */}
        {hourMarks.map((m,i)=>(
          <circle key={i} cx={m.x} cy={m.y} r={m.major ? 1.8 : 1}
            fill={m.major ? "#555" : "#333"}/>
        ))}
        {/* Hands */}
        <line x1={CX} y1={CX} x2={hHand.x2} y2={hHand.y2}
          stroke={hHand.color} strokeWidth={hHand.width} strokeLinecap="round"/>
        <line x1={CX} y1={CX} x2={mHand.x2} y2={mHand.y2}
          stroke={mHand.color} strokeWidth={mHand.width} strokeLinecap="round"/>
        <line x1={CX} y1={CX} x2={sHand.x2} y2={sHand.y2}
          stroke={sHand.color} strokeWidth={sHand.width} strokeLinecap="round"/>
        {/* Center dot */}
        <circle cx={CX} cy={CX} r={2} fill="#e53935"/>
      </svg>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:7, color:"#444", letterSpacing:".12em", textAlign:"center", textTransform:"uppercase" }}>
        {city}
      </div>
      <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:7,
        color: isOpen ? "#4ade80" : "#333", letterSpacing:".08em" }}>
        {local.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",hour12:false})}
      </div>
    </div>
  );
};

const WorldClockBar = () => {
  const CITIES = [
    { tz:"America/Los_Angeles", city:"Los Angeles" },
    { tz:"America/Chicago",     city:"Chicago"     },
    { tz:"America/New_York",    city:"New York"    },
    { tz:"America/Sao_Paulo",   city:"São Paulo"   },
    { tz:"Europe/London",       city:"London"      },
    { tz:"Europe/Paris",        city:"Paris"       },
    { tz:"Europe/Moscow",       city:"Moscow"      },
    { tz:"Asia/Dubai",          city:"Dubai"       },
    { tz:"Asia/Mumbai",         city:"Mumbai"      },
    { tz:"Asia/Singapore",      city:"Singapore"   },
    { tz:"Asia/Tokyo",          city:"Tokyo"       },
    { tz:"Australia/Sydney",    city:"Sydney"      },
  ];

  return (
    <div style={{
      background: "#080808",
      borderBottom: "1px solid #141414",
      padding: "10px 24px",
      display: "flex",
      alignItems: "center",
      gap: 28,
      overflowX: "auto",
      scrollbarWidth: "none",
    }}>
      {CITIES.map(c => <AnalogClock key={c.city} tz={c.tz} city={c.city}/>)}
    </div>
  );
};

const BreakingAlertStrip = ({ tickers }) => {
  const { alerts, dismiss } = useBreakingAlerts(tickers);
  const [idx, setIdx] = React.useState(0);
  const visible = alerts.slice(0, 6);

  React.useEffect(() => {
    if (visible.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % visible.length), 6000);
    return () => clearInterval(t);
  }, [visible.length]);

  if (visible.length === 0) return null;

  const cur = visible[idx] || visible[0];

  return (
    <div style={{
      background:"linear-gradient(90deg,rgba(196,164,74,.06),rgba(196,164,74,.03),rgba(196,164,74,.06))",
      borderBottom:"1px solid rgba(196,164,74,.12)",
      padding:"6px 20px",
      display:"flex",alignItems:"center",gap:10,
      overflow:"hidden",animation:"fadeIn .3s ease",
    }}>
      {/* BREAKING badge */}
      <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
        <div style={{width:5,height:5,borderRadius:"50%",
          background:"var(--dn)",animation:"breathe 1.5s infinite"}}/>
        <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--amb)",
          letterSpacing:".14em",fontWeight:700}}>BREAKING</span>
        <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",
          background:"rgba(196,164,74,.1)",border:"1px solid rgba(196,164,74,.2)",
          borderRadius:4,padding:"0 6px",marginLeft:2}}>{cur.ticker}</span>
      </div>

      {/* Headline */}
      <a href={cur.link} target="_blank" rel="noopener noreferrer"
        style={{flex:1,fontFamily:"var(--P)",fontSize:12,color:"var(--cream)",
          textDecoration:"none",lineHeight:1.4,overflow:"hidden",
          display:"-webkit-box",WebkitLineClamp:1,WebkitBoxOrient:"vertical",
          transition:"color .2s"}}
        onMouseEnter={e=>e.target.style.color="var(--up)"}
        onMouseLeave={e=>e.target.style.color="var(--cream)"}>
        {cur.title}
      </a>

      {/* Source + time */}
      <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--dim)",
        flexShrink:0,whiteSpace:"nowrap"}}>
        {cur.source}
      </span>

      {/* Pagination dots */}
      {visible.length > 1 && (
        <div style={{display:"flex",gap:3,flexShrink:0}}>
          {visible.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              style={{width:i===idx?12:4,height:4,borderRadius:2,
                background:i===idx?"var(--up)":"var(--border2)",
                border:"none",cursor:"pointer",transition:"all .25s",padding:0}}/>
          ))}
        </div>
      )}

      {/* Dismiss */}
      <button onClick={() => dismiss(cur.id)}
        style={{background:"none",border:"none",cursor:"pointer",
          color:"var(--dim)",fontSize:14,lineHeight:1,flexShrink:0,padding:"0 2px"}}>
        ×
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM 4: WATCHLIST NEWS FEED — Bottom of watchlist page, per-ticker news
// ═══════════════════════════════════════════════════════════════════════════════

const WatchlistNewsFeed = ({ tickers }) => {
  const [news,    setNews]    = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter,  setFilter]  = React.useState("ALL");

  React.useEffect(() => {
    if (!tickers.length) return;
    setLoading(true);
    const fetch_ = async () => {
      const all = [];
      await Promise.allSettled(tickers.slice(0,6).map(async sym => {
        try {
          const query = encodeURIComponent(`"${sym}" stock`);
          const feed  = `https://news.google.com/rss/search?q=${query}&hl=en-US&gl=US&ceid=US:en`;
          const api   = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(feed)}&count=4`;
          const res   = await fetch(api, { signal: AbortSignal.timeout(8000) });
          const data  = await res.json();
          (data.items || []).slice(0,3).forEach(item => {
            all.push({
              id:     item.guid || item.link,
              ticker: sym,
              title:  item.title?.replace(/<[^>]+>/g,""),
              link:   item.link,
              source: item.author || "News",
              pubDate:new Date(item.pubDate),
            });
          });
        } catch(e) {}
      }));
      // Sort by date, dedupe
      const seen = new Set();
      const deduped = all
        .filter(a => { if(seen.has(a.id)){return false;} seen.add(a.id); return true; })
        .sort((a,b) => b.pubDate - a.pubDate);
      setNews(deduped);
      setLoading(false);
    };
    fetch_();
  }, [tickers.slice(0,6).join(",")]);

  const fmtAge = d => {
    const mins = Math.floor((Date.now() - d) / 60000);
    if (mins < 60)   return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins/60)}h ago`;
    return `${Math.floor(mins/1440)}d ago`;
  };

  const filtered = filter === "ALL" ? news : news.filter(n => n.ticker === filter);
  const filterTickers = ["ALL", ...tickers.slice(0,6)];

  return (
    <div style={{marginTop:28}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <div style={{width:3,height:16,background:"var(--up)",borderRadius:2}}/>
        <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--soft)",
          letterSpacing:".1em"}}>WATCHLIST NEWS</span>
        <div style={{display:"flex",gap:5,marginLeft:"auto",flexWrap:"wrap"}}>
          {filterTickers.map(t => (
            <button key={t} onClick={() => setFilter(t)}
              style={{padding:"3px 8px",fontFamily:"var(--M)",fontSize:9,
                background:filter===t?"rgba(196,164,74,.15)":"transparent",
                border:`1px solid ${filter===t?"rgba(196,164,74,.4)":"var(--border)"}`,
                borderRadius:5,cursor:"pointer",
                color:filter===t?"var(--up)":"var(--muted)",
                transition:"all .15s"}}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{padding:"6px 0"}}>
          {[90,75,82,68,88].map((w,i)=>(
            <div key={i} style={{padding:"11px 14px",borderBottom:"1px solid var(--border)"}}>
              <div className="skel" style={{height:14,borderRadius:4,marginBottom:6,width:`${w}%`}}/>
              <div className="skel" style={{height:9,borderRadius:4,width:"40%"}}/>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:1}}>
        {filtered.map((item, i) => (
          <a key={item.id} href={item.link} target="_blank" rel="noopener noreferrer"
            style={{display:"flex",gap:12,alignItems:"flex-start",
              padding:"11px 14px",
              background:i%2===0?"var(--card)":"transparent",
              borderRadius:8,textDecoration:"none",
              transition:"background .15s",cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
            onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"var(--card)":"transparent"}>
            <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",
              background:"rgba(196,164,74,.1)",border:"1px solid rgba(196,164,74,.2)",
              borderRadius:4,padding:"2px 6px",flexShrink:0,marginTop:1}}>
              {item.ticker}
            </span>
            <span style={{fontFamily:"var(--P)",fontSize:12,color:"var(--cream)",
              lineHeight:1.45,flex:1}}>
              {item.title}
            </span>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",
              gap:2,flexShrink:0}}>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>
                {fmtAge(item.pubDate)}
              </span>
              <span style={{fontFamily:"var(--M)",fontSize:7,color:"var(--dim)",
                maxWidth:80,textAlign:"right",overflow:"hidden",
                textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {item.source}
              </span>
            </div>
          </a>
        ))}
        {!loading && filtered.length === 0 && (
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",
            textAlign:"center",padding:"24px 0"}}>
            No news found for {filter === "ALL" ? "your watchlist" : filter}.
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM 1: WATCHLIST — Firestore-backed per-user ticker list
// ═══════════════════════════════════════════════════════════════════════════════

const useWatchlist = (user) => {
  const [tickers, setTickers]   = React.useState([]);
  const [loading, setLoading]   = React.useState(false);

  // Load from Firestore on user login
  React.useEffect(() => {
    if (!user?.uid || !isFirebaseConfigured || !_db) {
      setTickers([]);
      return;
    }
    setLoading(true);
    const ref = doc(_db, "users", user.uid);
    getDoc(ref).then(snap => {
      const data = snap.exists() ? snap.data() : {};
      setTickers(data.watchlist || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user?.uid]);

  const save = async (list) => {
    setTickers(list);
    if (!user?.uid || !_db) return;
    try {
      await setDoc(doc(_db, "users", user.uid),
        { watchlist: list, updatedAt: new Date().toISOString() },
        { merge: true }
      );
    } catch(e) { console.warn("Watchlist save:", e); }
  };

  const add = (ticker) => {
    const sym = ticker.trim().toUpperCase().replace(/[^A-Z0-9.\-^]/g, "");
    if (!sym || tickers.includes(sym) || tickers.length >= 30) return;
    save([...tickers, sym]);
  };

  const remove = (ticker) => save(tickers.filter(t => t !== ticker));

  const move = (from, to) => {
    const list = [...tickers];
    const [item] = list.splice(from, 1);
    list.splice(to, 0, item);
    save(list);
  };

  return { tickers, loading, add, remove, move };
};

// ─── Watchlist Page ──────────────────────────────────────────────────────────
const WatchlistPage = ({ user, tier, onPaywall, onSignIn }) => {
  const { tickers, loading, add, remove } = useWatchlist(user);
  const [input, setInput]   = React.useState("");
  const [quotes, setQuotes] = React.useState({});
  const [hoverId, setHoverId] = React.useState(null);

  // Fetch live quotes for watchlist
  React.useEffect(() => {
    if (!tickers.length) return;
    const fetchQ = async () => {
      try {
        const syms = tickers.map(t => t.replace(/\^/g,"%5E")).join(",");
        const url = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${syms}&fields=symbol,regularMarketPrice,regularMarketChangePercent,regularMarketVolume,marketCap,fiftyTwoWeekHigh,fiftyTwoWeekLow`;
        const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        const res = await fetch(proxy, { signal: AbortSignal.timeout(12000) });
        const data = await res.json();
        const q = {};
        (data?.quoteResponse?.result || []).forEach(r => {
          q[r.symbol] = r;
        });
        setQuotes(q);
      } catch(e) {}
    };
    fetchQ();
    const t = setInterval(fetchQ, 60000);
    return () => clearInterval(t);
  }, [tickers.join(",")]);

  const handleAdd = () => {
    if (!user) { onSignIn(); return; }
    add(input);
    setInput("");
  };

  const fmtPrice = n => n != null ? `$${Number(n).toFixed(2)}` : "—";
  const fmtChg   = n => n != null ? `${n >= 0 ? "+" : ""}${Number(n).toFixed(2)}%` : "—";
  const fmtMcap  = n => {
    if (!n) return "—";
    if (n >= 1e12) return `$${(n/1e12).toFixed(1)}T`;
    if (n >= 1e9)  return `$${(n/1e9).toFixed(1)}B`;
    return `$${(n/1e6).toFixed(0)}M`;
  };

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:22}}>
        <div style={{width:3,height:22,background:"var(--up)",borderRadius:2}}/>
        <div>
          <div style={{fontFamily:"var(--D)",fontSize:20,color:"var(--cream)",fontWeight:600}}>
            My Watchlist
          </div>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",letterSpacing:".1em",marginTop:2}}>
            {tickers.length}/30 TICKERS · LIVE PRICES · BRIEFED DAILY
          </div>
        </div>
      </div>

      {/* Add ticker input */}
      {!user ? (
        <div style={{padding:"18px 20px",background:"var(--card)",border:"1px solid var(--border)",
          borderRadius:10,textAlign:"center",marginBottom:20}}>
          <div style={{fontFamily:"var(--M)",fontSize:11,color:"var(--soft)",marginBottom:10}}>
            Sign in to save your watchlist and personalize your morning brief.
          </div>
          <button className="btn b-gold" onClick={onSignIn} style={{padding:"9px 20px",fontSize:11}}>
            Sign In →
          </button>
        </div>
      ) : (
        <div style={{display:"flex",gap:8,marginBottom:20}}>
          <input
            className="inp"
            value={input}
            onChange={e => setInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            placeholder="Add ticker… AAPL, BTC-USD, ^VIX"
            style={{flex:1,fontFamily:"var(--M)",letterSpacing:".05em",fontSize:13}}
          />
          <button className="btn b-gold" onClick={handleAdd}
            style={{padding:"11px 18px",fontSize:11,flexShrink:0}}>
            + Add
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,padding:"4px 0"}}>
          {[1,2,3,4,5,6].map(i=>(
            <div key={i} style={{background:"var(--raised)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px"}}>
              <div className="skel" style={{height:13,width:"50%",borderRadius:4,marginBottom:8}}/>
              <div className="skel" style={{height:9,width:"80%",borderRadius:4,marginBottom:10}}/>
              <div className="skel" style={{height:16,width:"60%",borderRadius:4,marginBottom:4}}/>
              <div className="skel" style={{height:11,width:"40%",borderRadius:4}}/>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && tickers.length === 0 && (
        <div style={{textAlign:"center",padding:"50px 0",
          border:"1px dashed var(--border)",borderRadius:12}}>
          <div style={{fontSize:28,marginBottom:14,opacity:.5}}>📊</div>
          <div style={{fontFamily:"var(--D)",fontSize:16,color:"var(--soft)",marginBottom:6}}>
            No tickers yet
          </div>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)"}}>
            Add tickers above — they'll appear in your morning brief email
          </div>
        </div>
      )}

      {/* Ticker rows */}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {tickers.map((sym, idx) => {
          const q = quotes[sym] || quotes[sym.replace("^","%5E")] || {};
          const price  = q.regularMarketPrice;
          const chg    = q.regularMarketChangePercent;
          const isUp   = chg >= 0;
          const hi52   = q.fiftyTwoWeekHigh;
          const lo52   = q.fiftyTwoWeekLow;
          const pctRng = hi52 && lo52 && price
            ? Math.round(((price - lo52) / (hi52 - lo52)) * 100)
            : null;

          return (
            <div
              key={sym}
              style={{
                background:"var(--card)",
                border:`1px solid ${hoverId===idx?"var(--border2)":"var(--border)"}`,
                borderRadius:10,padding:"14px 16px",
                display:"grid",gridTemplateColumns:"auto 1fr auto auto auto",
                alignItems:"center",gap:12,
                transition:"border-color .15s,box-shadow .15s",
                boxShadow:hoverId===idx?"0 4px 20px rgba(0,0,0,.3)":"none",
                position:"relative",cursor:"default",
              }}
              onMouseEnter={() => setHoverId(idx)}
              onMouseLeave={() => setHoverId(null)}
            >
              {/* Rank */}
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--dim)",
                width:18,textAlign:"right"}}>{idx+1}</div>

              {/* Symbol + name */}
              <div>
                <div style={{fontFamily:"var(--M)",fontSize:13,fontWeight:700,
                  color:"var(--cream)",letterSpacing:".03em"}}>{sym}</div>
                {q.longName && (
                  <div style={{fontFamily:"var(--P)",fontSize:10,color:"var(--muted)",
                    marginTop:2,whiteSpace:"nowrap",overflow:"hidden",
                    textOverflow:"ellipsis",maxWidth:200}}>{q.longName}</div>
                )}
              </div>

              {/* Hover sparkline */}
              {hoverId === idx && (
                <HoverSparkline symbol={sym}/>
              )}

              {/* Price + change */}
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"var(--M)",fontSize:14,fontWeight:600,
                  color:"var(--cream)"}}>{fmtPrice(price)}</div>
                <div style={{fontFamily:"var(--M)",fontSize:11,
                  color:isUp?"var(--up)":"var(--dn)"}}>{fmtChg(chg)}</div>
              </div>

              {/* 52-week range bar */}
              {pctRng != null && (
                <div style={{width:60}}>
                  <div style={{height:3,background:"var(--raised)",borderRadius:2,
                    overflow:"hidden",marginBottom:3}}>
                    <div style={{height:"100%",width:`${pctRng}%`,
                      background:isUp?"var(--up)":"var(--dn)",
                      borderRadius:2,transition:"width .6s ease"}}/>
                  </div>
                  <div style={{fontFamily:"var(--M)",fontSize:7,
                    color:"var(--muted)",textAlign:"center",letterSpacing:".04em"}}>
                    {pctRng}% OF 52W
                  </div>
                </div>
              )}

              {/* Market cap */}
              <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",
                textAlign:"right",minWidth:36}}>{fmtMcap(q.marketCap)}</div>

              {/* Remove */}
              {user && (
                <button
                  onClick={() => remove(sym)}
                  style={{position:"absolute",right:8,top:8,
                    background:"none",border:"none",cursor:"pointer",
                    color:"var(--muted)",fontSize:14,lineHeight:1,
                    opacity:hoverId===idx?1:0,transition:"opacity .15s"}}
                  title="Remove">×</button>
              )}
            </div>
          );
        })}
      </div>

      {/* News feed for watchlist */}
      {tickers.length > 0 && (
        <WatchlistNewsFeed tickers={tickers}/>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM 5: PREDICTION MARKET INTELLIGENCE PANEL
// Curated reads + creator links — real content, no broken embeds
// ═══════════════════════════════════════════════════════════════════════════════

const PREDICTION_CREATORS = [
  {
    name:    "Nate Silver",
    handle:  "@NateSilver538",
    bio:     "Forecaster & probabilistic thinker. Silver Bulletin substack.",
    avatar:  "NS",
    color:   "#4da6ff",
    url:     "https://www.natesilver.net",
    ytUrl:   "https://www.youtube.com/@natesilver538",
  },
  {
    name:    "Manifold Markets",
    handle:  "@ManifoldMarkets",
    bio:     "Free-to-play prediction markets. Education & commentary.",
    avatar:  "MM",
    color:   "#a855f7",
    url:     "https://manifold.markets",
    ytUrl:   "https://www.youtube.com/@ManifoldMarkets",
  },
  {
    name:    "Kalshi",
    handle:  "@Kalshi",
    bio:     "CFTC-regulated event contracts. Official market commentary.",
    avatar:  "KL",
    color:   "#00cc88",
    url:     "https://kalshi.com",
    ytUrl:   "https://www.youtube.com/@Kalshi",
  },
  {
    name:    "Polymarket",
    handle:  "@Polymarket",
    bio:     "Largest on-chain prediction market. Real-money probability.",
    avatar:  "PM",
    color:   "#c4a44a",
    url:     "https://polymarket.com",
    ytUrl:   "https://www.youtube.com/@polymarket",
  },
];

const PREDICTION_READS = [
  { title:"How Prediction Markets Beat Polls in 2024",      url:"https://www.natesilver.net", src:"Silver Bulletin",   tag:"METHODOLOGY" },
  { title:"Kalshi vs Polymarket: Where They Diverge",        url:"https://kalshi.com/blog",    src:"Kalshi Blog",       tag:"ANALYSIS" },
  { title:"The Case for Legalizing Prediction Markets",      url:"https://manifold.markets",   src:"Manifold",          tag:"POLICY" },
  { title:"Reading Market Probabilities vs Media Headlines", url:"https://polymarket.com",      src:"Polymarket",        tag:"GUIDE" },
  { title:"Calibration: Why Sharp Bettors Beat Analysts",   url:"https://www.natesilver.net", src:"Silver Bulletin",   tag:"DEEP DIVE" },
  { title:"Event Contracts 101: How CFTC Regulation Works", url:"https://kalshi.com/blog",    src:"Kalshi Blog",       tag:"REGULATORY" },
];

const InfluencerPanel = () => {
  const [tab, setTab] = React.useState("reads"); // "reads" | "creators"

  return (
    <div className="card" style={{padding:0,overflow:"hidden"}}>
      {/* Header */}
      <div style={{padding:"12px 16px 0",borderBottom:"1px solid var(--border)"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
          <div style={{width:4,height:4,borderRadius:"50%",background:"#a855f7"}}/>
          <span style={{fontFamily:"var(--M)",fontSize:9,color:"#a855f7",letterSpacing:".12em"}}>
            PREDICTION MARKET INTELLIGENCE
          </span>
        </div>
        <div style={{display:"flex",gap:0}}>
          {[{k:"reads",l:"Curated Reads"},{k:"creators",l:"Creators"}].map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)}
              style={{fontFamily:"var(--M)",fontSize:9,padding:"8px 12px",
                background:"transparent",border:"none",cursor:"pointer",
                color:tab===t.k?"var(--cream)":"var(--muted)",letterSpacing:".08em",
                borderBottom:tab===t.k?"2px solid #a855f7":"2px solid transparent",
                transition:"color .15s"}}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Reads tab */}
      {tab === "reads" && (
        <div style={{padding:"6px 0"}}>
          {PREDICTION_READS.map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
              style={{display:"block",padding:"10px 16px",textDecoration:"none",
                borderBottom:i<PREDICTION_READS.length-1?"1px solid var(--border)":"none",
                transition:"background .12s"}}
              onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                <div style={{fontFamily:"var(--P)",fontSize:12.5,color:"var(--cream)",
                  lineHeight:1.45,flex:1}}>{r.title}</div>
                <span style={{fontFamily:"var(--M)",fontSize:7,color:"#a855f7",
                  background:"rgba(168,85,247,.08)",border:"1px solid rgba(168,85,247,.2)",
                  borderRadius:3,padding:"2px 6px",flexShrink:0,whiteSpace:"nowrap",marginTop:1}}>
                  {r.tag}
                </span>
              </div>
              <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginTop:4}}>
                {r.src}
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Creators tab */}
      {tab === "creators" && (
        <div style={{padding:"10px 14px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {PREDICTION_CREATORS.map((c, i) => (
            <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
              style={{display:"block",background:"var(--raised)",border:"1px solid var(--border)",
                borderRadius:8,padding:"12px",textDecoration:"none",transition:"border-color .15s,box-shadow .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--border2)";e.currentTarget.style.boxShadow="0 4px 14px rgba(0,0,0,.3)"}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.boxShadow="none"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{width:28,height:28,borderRadius:8,
                  background:`${c.color}22`,border:`1px solid ${c.color}44`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontFamily:"var(--M)",fontSize:9,color:c.color,fontWeight:700,
                  flexShrink:0}}>
                  {c.avatar}
                </div>
                <div>
                  <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--cream)",
                    fontWeight:600,lineHeight:1.2}}>{c.name}</div>
                  <div style={{fontFamily:"var(--M)",fontSize:8,color:c.color}}>
                    {c.handle}
                  </div>
                </div>
              </div>
              <div style={{fontFamily:"var(--P)",fontSize:10.5,color:"var(--dim)",
                lineHeight:1.5}}>{c.bio}</div>
              <div style={{marginTop:8,display:"flex",gap:6}}>
                <a href={c.url} target="_blank" rel="noopener noreferrer"
                  onClick={e=>e.stopPropagation()}
                  style={{fontFamily:"var(--M)",fontSize:8,color:c.color,
                    background:`${c.color}11`,border:`1px solid ${c.color}33`,
                    borderRadius:4,padding:"3px 8px",textDecoration:"none"}}>
                  Site ↗
                </a>
                <a href={c.ytUrl} target="_blank" rel="noopener noreferrer"
                  onClick={e=>e.stopPropagation()}
                  style={{fontFamily:"var(--M)",fontSize:8,color:"#ff4444",
                    background:"rgba(255,68,68,.08)",border:"1px solid rgba(255,68,68,.2)",
                    borderRadius:4,padding:"3px 8px",textDecoration:"none"}}>
                  YouTube ↗
                </a>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};




// ─── USERNAME SETUP MODAL ─────────────────────────────────────────────────────
// Shown after first login if user has no handle stored.
// Saves handle to Firestore users/{uid}.handle
// ─── USERNAME SETUP MODAL ─────────────────────────────────────────────────────
// Shown after first login if user has no handle stored.
// Saves handle to Firestore users/{uid}.handle
const UsernameSetupModal = ({ user, onComplete }) => {
  const [handle, setHandle]   = React.useState("");
  const [error, setError]     = React.useState("");
  const [saving, setSaving]   = React.useState(false);

  const reserved = ["admin","folio","system","moderator","support","help"];
  const valid = h => /^[a-zA-Z0-9_]{3,20}$/.test(h) && !reserved.includes(h.toLowerCase());

  const save = async () => {
    const h = handle.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
    if (!valid(h)) {
      setError("3–20 characters. Letters, numbers, underscores only.");
      return;
    }
    setSaving(true);
    try {
      if (isFirebaseConfigured && _db && user?.uid) {
        await setDoc(doc(_db, "users", user.uid), {
          handle: h,
          displayName: user.name || h,
          updatedAt: new Date().toISOString(),
        }, { merge: true });
      }
      onComplete(h);
    } catch(e) {
      setError("Failed to save. Try again.");
    }
    setSaving(false);
  };

  return (
    <div className="overlay" style={{ zIndex: 10001 }}>
      <div style={{
        background: "linear-gradient(155deg,#13111e,#0e0c18)",
        border: "1px solid rgba(196,164,74,.25)", borderRadius: 18,
        maxWidth: 380, width: "90%", padding: "36px 32px",
        boxShadow: "0 48px 96px rgba(0,0,0,.85)", animation: "riseIn .25s ease",
      }}>
        <div style={{ height: 2, background: "linear-gradient(90deg,transparent,var(--up),transparent)",
          borderRadius: "18px 18px 0 0", margin: "-36px -32px 28px" }}/>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontFamily: "var(--M)", fontSize: 9, color: "var(--up)",
            letterSpacing: ".15em", marginBottom: 12 }}>ONE LAST THING</div>
          <div style={{ fontFamily: "var(--D)", fontSize: 22, color: "var(--cream)",
            fontWeight: 600, marginBottom: 8 }}>Choose your handle</div>
          <div style={{ fontFamily: "var(--P)", fontSize: 12.5, color: "var(--soft)",
            lineHeight: 1.65 }}>
            This is how you'll appear in Folio community discussions.
            Choose wisely — it represents your edge.
          </div>
        </div>

        <div style={{ position: "relative", marginBottom: 8 }}>
          <span style={{ position: "absolute", left: 12, top: "50%",
            transform: "translateY(-50%)", fontFamily: "var(--M)",
            fontSize: 13, color: "var(--dim)" }}>@</span>
          <input
            className="inp"
            value={handle}
            onChange={e => { setHandle(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && save()}
            placeholder="yourhandle"
            maxLength={20}
            style={{ width: "100%", paddingLeft: 28, fontFamily: "var(--M)",
              letterSpacing: ".04em", fontSize: 14 }}
            autoFocus
          />
        </div>

        {error && (
          <div style={{ fontFamily: "var(--M)", fontSize: 10, color: "var(--dn)",
            marginBottom: 8, textAlign: "center" }}>{error}</div>
        )}

        <div style={{ fontFamily: "var(--M)", fontSize: 8, color: "var(--muted)",
          marginBottom: 18, textAlign: "center" }}>
          Letters, numbers, underscores · 3–20 characters
        </div>

        <button
          className="btn b-gold"
          onClick={save}
          disabled={saving || handle.trim().length < 3}
          style={{ width: "100%", padding: "13px", fontSize: 12,
            opacity: (saving || handle.trim().length < 3) ? 0.5 : 1 }}
        >
          {saving ? "Saving…" : "Set Handle →"}
        </button>

        <div style={{ fontFamily: "var(--M)", fontSize: 9, color: "var(--muted)",
          textAlign: "center", marginTop: 12 }}>
          You can update this later in Settings.
        </div>
      </div>
    </div>
  );
};

// ─── DATA TABLE WITH PER-STOCK CHAT ───────────────────────────────────────────
const DataTable = ({ rows, tier, onPaywall, user }) => {
  const [openChat, setOpenChat] = React.useState(null); // ticker string or null
  const [liveQuotes, setLiveQuotes] = React.useState({});

  // Fetch live quotes for displayed stocks every 60s
  React.useEffect(() => {
    if (!rows || rows.length === 0) return;
    const symbols = rows.map(r => r.t).join(",");
    const fetchQ = async () => {
      try {
        const url = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${symbols}&fields=symbol,regularMarketPrice,regularMarketChangePercent`;
        const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        const res = await fetch(proxy, { signal: AbortSignal.timeout(10000) });
        const data = await res.json();
        const quotes = data?.quoteResponse?.result || [];
        const map = {};
        quotes.forEach(q => {
          map[q.symbol] = {
            price: q.regularMarketPrice,
            chg: q.regularMarketChangePercent,
          };
        });
        setLiveQuotes(map);
      } catch(e) {}
    };
    fetchQ();
    const t = setInterval(fetchQ, 60000);
    return () => clearInterval(t);
  }, [rows?.map(r=>r.t).join(",")]);

  const fmtPrice = (p) => {
    if (p == null) return null;
    return p >= 1000 ? `$${(p/1000).toFixed(1)}K` : `$${p.toFixed(2)}`;
  };
  const fmtChg = (c) => {
    if (c == null) return null;
    return (c >= 0 ? `+${c.toFixed(2)}%` : `${c.toFixed(2)}%`);
  };

  // Skeleton loading state — shown while data loads
  if (!rows || rows.length === 0) {
    return (
      <div className="card" style={{overflow:"hidden"}}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{width:30}}>#</th>
              <th>Ticker</th>
              <th>Price</th>
              <th>Change</th>
              <th>Volume</th>
              <th style={{width:80}}>Signal</th>
              <th style={{width:60,textAlign:"center"}}>Chat</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({length:10}).map((_,i)=>(
              <tr key={i}>
                <td><div style={{width:14,height:10,background:"var(--border)",borderRadius:3,animation:"shimmer 1.5s infinite",animationDelay:`${i*.07}s`}}/></td>
                <td><div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <div style={{width:32,height:32,borderRadius:6,background:"var(--border)",animation:"shimmer 1.5s infinite",animationDelay:`${i*.07}s`}}/>
                  <div>
                    <div style={{width:38,height:10,background:"var(--border)",borderRadius:3,marginBottom:4,animation:"shimmer 1.5s infinite"}}/>
                    <div style={{width:60,height:8,background:"var(--border)",borderRadius:3,animation:"shimmer 1.5s infinite"}}/>
                  </div>
                </div></td>
                <td><div style={{width:48,height:10,background:"var(--border)",borderRadius:3,animation:"shimmer 1.5s infinite"}}/></td>
                <td><div style={{width:44,height:10,background:"var(--border)",borderRadius:3,animation:"shimmer 1.5s infinite"}}/></td>
                <td><div style={{width:40,height:10,background:"var(--border)",borderRadius:3,animation:"shimmer 1.5s infinite"}}/></td>
                <td><div style={{width:50,height:6,background:"var(--border)",borderRadius:6,animation:"shimmer 1.5s infinite"}}/></td>
                <td/>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="card" style={{overflow:"hidden"}}>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{width:30}}>#</th>
            <th>Ticker</th>
            <th>Price</th>
            <th>Change</th>
            <th>Volume</th>
            <th style={{width:80}}>Signal</th>
            <th style={{width:60, textAlign:"center"}}>Chat</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const live = liveQuotes[row.t];
            const displayPrice = live?.price != null ? fmtPrice(live.price) : row.p;
            const displayChg = live?.chg != null ? fmtChg(live.chg) : row.c;
            const isUp = live?.chg != null ? live.chg >= 0 : row.up;
            const isLocked = tier === "free" && i >= 3;
            const chatOpen = openChat === row.t;
            return (
              <React.Fragment key={row.t}>
                <tr onClick={isLocked ? onPaywall : undefined}
                  style={{cursor: isLocked ? "pointer" : "default", opacity: isLocked ? 0.45 : 1}}>
                  <td style={{color:"var(--muted)",fontSize:11}}>{row.r}</td>
                  <td>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:28,height:28,borderRadius:6,
                        background:isUp?"var(--up-bg)":"var(--dn-bg)",
                        border:`1px solid ${isUp?"var(--up-b)":"var(--dn-b)"}`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontFamily:"var(--M)",fontSize:8,fontWeight:700,
                        color:isUp?"var(--up)":"var(--dn)"}}>
                        {row.t?.slice(0,4)}
                      </div>
                      <div>
                        <div style={{fontFamily:"var(--M)",fontSize:12,fontWeight:600,color:"var(--text)"}}>{row.t}</div>
                        <div style={{fontFamily:"var(--P)",fontSize:10,color:"var(--muted)"}}>{row.n}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{fontFamily:"var(--M)",fontSize:13,fontWeight:600,
                      color:"var(--cream)",display:"flex",alignItems:"center",gap:5}}>
                      {displayPrice}
                      {live?.price != null && (
                        <span style={{fontFamily:"var(--M)",fontSize:7,color:"#00a0dd",
                          letterSpacing:"0.1em",padding:"1px 4px",border:"1px solid rgba(0,160,221,.3)",
                          borderRadius:3}}>LIVE</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span style={{fontFamily:"var(--M)",fontSize:12,fontWeight:600,
                      color:isUp?"var(--up)":"var(--dn)"}}>
                      {displayChg}
                    </span>
                  </td>
                  <td style={{fontFamily:"var(--M)",fontSize:11,color:"var(--dim)"}}>{row.v}</td>
                  <td>
                    <span className={`tag ${row.sig?.includes("Strong")?(isUp?"t-up":"t-dn"):row.sig==="Buy"||row.sig==="Sell"?(isUp?"t-up":"t-dn"):"t-neu"}`}
                      style={{fontSize:9}}>
                      {row.sig}
                    </span>
                  </td>
                  <td style={{textAlign:"center"}}>
                    {!isLocked && (
                      <button
                        onClick={(e)=>{ e.stopPropagation(); setOpenChat(chatOpen ? null : row.t); }}
                        style={{background:chatOpen?"rgba(0,160,221,.15)":"transparent",
                          border:`1px solid ${chatOpen?"rgba(0,160,221,.5)":"var(--border2)"}`,
                          borderRadius:6,padding:"4px 8px",cursor:"pointer",
                          fontFamily:"var(--M)",fontSize:9,
                          color:chatOpen?"#00a0dd":"var(--dim)",
                          transition:"all .15s"}}>
                        <Icon name="message" size={11} color={chatOpen?"#00a0dd":"var(--dim)"}/>
                      </button>
                    )}
                  </td>
                </tr>
                {chatOpen && (
                  <tr>
                    <td colSpan={7} style={{padding:"0 0 0 0",background:"var(--raised)"}}>
                      <div style={{padding:"0 16px 16px"}}>
                        <StockTwitsPanel symbol={row.t}/>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── LIVE GAINERS / LOSERS HOOK ───────────────────────────────────────────────
const AV_KEY = "U3B2CCGREENB6J8B";
const useGainersLosers = () => {
  const [gainers, setGainers] = useState(GAINERS);
  const [losers,  setLosers]  = useState(LOSERS);
  const [isLive,  setIsLive]  = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchTopMovers = async () => {
      try {
        const res = await fetch(
          `https://www.alphavantage.co/query?function=TOP_GAINERS_LOSERS&apikey=${AV_KEY}`
        );
        const data = await res.json();

        const normalize = (list, up) =>
          (list || []).slice(0, 10).map((item, i) => {
            const pct = item.change_percentage || "";
            const price = parseFloat(item.price || 0);
            const vol = parseFloat(item.volume || 0);
            const volFmt = vol >= 1e9 ? `${(vol/1e9).toFixed(1)}B`
                         : vol >= 1e6 ? `${(vol/1e6).toFixed(1)}M`
                         : vol >= 1e3 ? `${(vol/1e3).toFixed(1)}K`
                         : String(vol);
            const chgNum = parseFloat(pct.replace("%","").replace("+",""));
            // Squeeze score: proxy from volume rank + magnitude
            const sq = up
              ? Math.min(99, Math.round(50 + Math.abs(chgNum) * 1.5 + (10 - i) * 2))
              : Math.max(1,  Math.round(50 - Math.abs(chgNum) * 1.5 - (10 - i) * 2));
            const sig = up
              ? (sq >= 85 ? "Strong Buy" : sq >= 65 ? "Buy" : "Neutral")
              : (sq <= 15 ? "Strong Sell" : sq <= 35 ? "Sell" : "Neutral");
            return {
              r: i + 1,
              t: item.ticker,
              n: item.ticker,           // AV doesn't return company name on this endpoint
              p: price >= 1000 ? `$${(price/1000).toFixed(1)}K` : `$${price.toFixed(2)}`,
              c: pct.startsWith("-") ? pct.replace("-","−") : (pct.startsWith("+") ? pct : `+${pct}`),
              v: volFmt,
              sq,
              sig,
              up,
            };
          });

        const liveGainers = normalize(data.top_gainers, true);
        const liveLosers  = normalize(data.top_losers,  false);

        if (liveGainers.length > 0) {
          setGainers(liveGainers);
          setLosers(liveLosers);
          setIsLive(true);
          setLastUpdated(new Date());
        }
      } catch (e) {
        console.warn("Gainers/Losers fetch failed, using static fallback", e);
      }
    };

    fetchTopMovers();
    // AV free tier: refresh every 30 min to preserve quota
    const id = setInterval(fetchTopMovers, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return { gainers, losers, isLive, lastUpdated };
};

// Empty seed — prediction markets load from Polymarket/Kalshi APIs on mount
const SEED_MARKETS = [];

const usePredictionMarkets = () => {
  const [markets, setMarkets]       = useState(SEED_MARKETS);
  const [loading, setLoading]       = useState(true);
  const [isLive, setIsLive]         = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError]           = useState(null);

  const fetchPolymarket = async () => {
    const results = [];
    const categories = ["economics","energy","crypto","financials","science"];
    for (const tag of categories) {
      try {
        const r = await fetch(
          `https://gamma-api.polymarket.com/markets?active=true&tag=${tag}&limit=4&order=volume24hr&ascending=false`,
          { signal: AbortSignal.timeout(8000) }
        );
        if (!r.ok) continue;
        const raw = await r.json();
        const items = Array.isArray(raw) ? raw : (raw.markets || []);
        items.forEach((m, i) => {
          try {
            const prices   = JSON.parse(m.outcomePrices || '["0.5","0.5"]');
            const yes      = Math.round(parseFloat(prices[0]) * 100);
            const vol24    = parseFloat(m.volume24hr || 0);
            const vol7     = parseFloat(m.volume7d   || 0);
            const trend    = (vol24 > 0 && vol7 > 0)
              ? (vol24 > vol7/7
                  ? `+${Math.round((vol24/(vol7/7)-1)*100)}%`
                  : `-${Math.round((1-vol24/(vol7/7))*100)}%`)
              : null;
            const tagLabels = (m.tags||[]).map(t=>t.label||t).filter(Boolean);
            results.push({
              id:       `pm_${m.id||m.conditionId||i}`,
              source:   "Polymarket",
              image:    m.image || m.bannerUrl || null,
              category: detectCategory(m.question||m.title||"", tagLabels),
              title:    m.question || m.title || "Market",
              yes, no: 100-yes,
              volume:   fmtVol(vol24 || m.liquidity || 0),
              trend,
              endDate:  m.endDate || null,
              liquidity: parseFloat(m.liquidity||0),
              insight:  null,
            });
          } catch(_) {}
        });
      } catch(_) {}
    }
    return results;
  };

  const fetchKalshi = async () => {
    // Public tickers — no auth required
    const tickers = [
      "FED-25MAY-T4.25","INXD-25Q1-B5400","UNEMP-25JUN-T4.2","CPI-25APR-B3","GDP-25Q1-B2"
    ];
    const results = [];
    for (const ticker of tickers) {
      try {
        const r = await fetch(
          `https://api.elections.kalshi.com/trade-api/v2/markets/${ticker}`,
          { signal: AbortSignal.timeout(6000) }
        );
        if (!r.ok) continue;
        const data = await r.json();
        const m = data.market;
        if (!m) continue;
        const yes = m.yes_ask != null ? Math.round(m.yes_ask) : 50;
        results.push({
          id:       `kal_${m.ticker}`,
          source:   "Kalshi",
          category: detectCategory(m.title||""),
          title:    m.title || ticker,
          yes, no: 100-yes,
          volume:   fmtVol(m.volume||0),
          trend:    null,
          endDate:  m.close_time || null,
          liquidity: 0,
          insight:  null,
        });
      } catch(_) {}
    }
    return results;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pr, kr] = await Promise.allSettled([fetchPolymarket(), fetchKalshi()]);
      const poly = pr.status==="fulfilled" ? pr.value : [];
      const kal  = kr.status==="fulfilled" ? kr.value : [];
      const all  = [...poly, ...kal];
      if (all.length >= 3) {
        all.sort((a,b) => (b.liquidity||0)-(a.liquidity||0));
        setMarkets(all.slice(0,12));
        setIsLive(true);
        setLastUpdated(new Date());
        setError(null);
      } else {
        throw new Error("Insufficient data from APIs");
      }
    } catch(e) {
      setMarkets(SEED_MARKETS);
      setIsLive(false);
      setError("Live APIs unavailable (CORS) — showing curated markets");
    }
    setLoading(false);
  };

  useEffect(()=>{ load(); },[]);
  useEffect(()=>{ const t=setInterval(load,5*60*1000); return()=>clearInterval(t); },[]);

  return { markets, loading, isLive, lastUpdated, error, refresh:load };
};

// ─── DIVERGENCE ENGINE ────────────────────────────────────────────────────────
const computeDivergences = markets => {
  const plays = [];
  markets.forEach(m => {
    const gap = Math.abs(m.yes-50);
    if (gap >= 8) {
      plays.push({
        title:     m.title.length>65 ? m.title.slice(0,62)+"…" : m.title,
        crowd:     m.yes,
        futures:   Math.round(50+(m.yes-50)*0.55),
        gap:       Math.round(gap*0.45),
        direction: m.yes>=50?"up":"down",
        summary:   `${m.source} pricing ${m.yes}% YES on volume ${m.volume}. `+
          (m.category==="Energy"  ? "Watch commodity forward curves for convergence signal over next 2 weeks." :
           m.category==="Macro"   ? "Next data release is the binary catalyst — Fed speak and CPI prints are the variables." :
           m.category==="Crypto"  ? "ETF flow data and on-chain metrics are the leading indicators here." :
           m.category==="Equities"? "Options market vol surface gives diverging signal — watch for pin risk at key strikes." :
                                    "Cross-asset signals diverging — monitoring shipping and macro data for confirmation."),
      });
    }
  });
  plays.sort((a,b)=>b.gap-a.gap);
  return plays.slice(0,3);
};

// ─── PREDICTIONS PAGE ─────────────────────────────────────────────────────────


// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN PANEL — Folio Control Center
// Accessible at nav item "Admin" — only shown when user.email matches ADMIN_EMAILS
// ═══════════════════════════════════════════════════════════════════════════════

const ADMIN_EMAILS = ["abhilash@afcurgentcare.com","admin@getfolio.ink","buch@buchmedicine.com"];
const ADMIN_SECRET_KEY = "folio-admin-2025"; // matches ADMIN_SECRET in Vercel env

const useAdminAPI = () => {
  const call = async (action, payload) => {
    const r = await fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-secret": ADMIN_SECRET_KEY },
      body: JSON.stringify({ action, payload, secret: ADMIN_SECRET_KEY }),
    });
    return r.json();
  };
  return { call };
};



// ═══════════════════════════════════════════════════════════════════════════════
// PRICE ALERTS — User-configurable alerts with signal context
// ═══════════════════════════════════════════════════════════════════════════════

const AlertsPanel = ({ oilData, tier, onPaywall }) => {
  const STORAGE_KEY = "folio_price_alerts";
  const [alerts, setAlerts]     = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||"[]"); }
    catch { return []; }
  });
  const [form, setForm]         = React.useState({ ticker:"WTI", price:"", direction:"above", method:"email", phone:"", email:"" });
  const [triggered, setTriggered] = React.useState([]);

  const saveAlerts = (list) => {
    setAlerts(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  };

  const addAlert = () => {
    if (!form.price || !form.email) return;
    const alert = { ...form, id: Date.now(), active: true, price: parseFloat(form.price) };
    saveAlerts([...alerts, alert]);
    setForm(f => ({ ...f, price:"" }));
  };

  const removeAlert = (id) => saveAlerts(alerts.filter(a => a.id !== id));
  const toggleAlert = (id) => saveAlerts(alerts.map(a => a.id===id ? { ...a, active:!a.active } : a));

  // Check alerts against live price
  React.useEffect(() => {
    const liveWTI = oilData?.wti?.price;
    if (!liveWTI) return;
    alerts.forEach(async (alert) => {
      if (!alert.active) return;
      const triggered = alert.direction==="above" ? liveWTI >= alert.price : liveWTI <= alert.price;
      if (triggered) {
        // Fire alert via API
        try {
          await fetch("/api/alert", {
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body: JSON.stringify({
              ticker: alert.ticker,
              price: alert.price,
              direction: alert.direction,
              method: alert.method,
              to: { email: alert.email, phone: alert.phone },
              context: {
                hormuzStatus: oilData?.chokepoints?.[0]?.status,
                cotSentiment: oilData?.cot?.sentiment,
                eiaLastDraw: oilData?.inventory?.crude?.change,
                bdti: oilData?.tankerRates?.bdti?.index,
              },
            }),
          });
          // Deactivate after firing
          saveAlerts(alerts.map(a => a.id===alert.id ? {...a, active:false, firedAt: new Date().toISOString()} : a));
          setTriggered(t => [...t, alert.id]);
        } catch(e) {}
      }
    });
  }, [oilData?.wti?.price]);

  if (tier === "free") {
    return (
      <div className="card" style={{padding:"24px",textAlign:"center",borderLeft:"3px solid var(--up)"}}>
        <div style={{fontSize:28,marginBottom:12}}>⚡</div>
        <div style={{fontFamily:"var(--D)",fontSize:18,color:"var(--cream)",marginBottom:8}}>Price Alerts</div>
        <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--dim)",lineHeight:1.7,marginBottom:16}}>
          Get SMS or email when WTI crosses your target — with full signal context (Hormuz status, COT positioning, EIA data).
        </div>
        <button className="btn b-gold" onClick={onPaywall} style={{padding:"12px 24px",fontSize:12}}>
          Unlock with Pulse →
        </button>
      </div>
    );
  }

  const TICKERS = ["WTI","BRENT","SPREAD","BDTI","CRACK_321"];
  const livePrice = { WTI: oilData?.wti?.price, BRENT: oilData?.brent?.price, SPREAD: oilData?.spread, BDTI: oilData?.tankerRates?.bdti?.index };

  return (
    <div>
      {/* Add alert form */}
      <div className="card" style={{padding:"18px 20px",marginBottom:14}}>
        <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
          NEW PRICE ALERT
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:5}}>TICKER</div>
            <select className="inp" value={form.ticker} onChange={e=>setForm(f=>({...f,ticker:e.target.value}))}
              style={{width:"100%"}}>
              {TICKERS.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:5}}>DIRECTION</div>
            <select className="inp" value={form.direction} onChange={e=>setForm(f=>({...f,direction:e.target.value}))}
              style={{width:"100%"}}>
              <option value="above">Above</option>
              <option value="below">Below</option>
            </select>
          </div>
          <div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:5}}>
              TARGET PRICE {livePrice[form.ticker] ? `(live: $${livePrice[form.ticker]})` : ""}
            </div>
            <input className="inp" value={form.price} onChange={e=>setForm(f=>({...f,price:e.target.value}))}
              placeholder="85.00" type="number" style={{width:"100%"}}/>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
          <div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:5}}>NOTIFY VIA</div>
            <select className="inp" value={form.method} onChange={e=>setForm(f=>({...f,method:e.target.value}))}
              style={{width:"100%"}}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:5}}>EMAIL</div>
            <input className="inp" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}
              placeholder="you@example.com" style={{width:"100%"}}/>
          </div>
          {(form.method==="sms"||form.method==="both") && (
            <div>
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:5}}>PHONE (SMS)</div>
              <input className="inp" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}
                placeholder="+12155551234" style={{width:"100%"}}/>
            </div>
          )}
        </div>
        <button className="btn b-gold" onClick={addAlert}
          style={{width:"100%",padding:"12px",fontSize:12}}>
          Set Alert →
        </button>
      </div>

      {/* Active alerts list */}
      {alerts.length > 0 && (
        <div className="card" style={{padding:"16px 18px"}}>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:12}}>
            ACTIVE ALERTS ({alerts.filter(a=>a.active).length})
          </div>
          {alerts.map((a, i) => (
            <div key={a.id} style={{display:"flex",alignItems:"center",gap:12,
              padding:"9px 0",borderBottom:i<alerts.length-1?"1px solid var(--border)":"none",
              opacity:a.active?1:0.5}}>
              <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,
                background:a.active?"var(--up)":"var(--muted)",
                animation:a.active?"breathe 2s infinite":"none"}}/>
              <div style={{flex:1}}>
                <span style={{fontFamily:"var(--M)",fontSize:12,color:"var(--cream)"}}>
                  {a.ticker} {a.direction} ${a.price}
                </span>
                <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginLeft:8}}>
                  via {a.method} · {a.email}
                </span>
                {a.firedAt && <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",marginLeft:8}}>✓ Fired</span>}
              </div>
              <button onClick={()=>toggleAlert(a.id)}
                style={{fontFamily:"var(--M)",fontSize:9,color:a.active?"var(--amb)":"var(--up)",
                  background:"transparent",border:"1px solid var(--border)",
                  borderRadius:4,padding:"3px 8px",cursor:"pointer"}}>
                {a.active ? "Pause" : "Resume"}
              </button>
              <button onClick={()=>removeAlert(a.id)}
                style={{fontFamily:"var(--M)",fontSize:9,color:"var(--dn)",
                  background:"transparent",border:"1px solid var(--dn-b)",
                  borderRadius:4,padding:"3px 8px",cursor:"pointer"}}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// OIL DESK — Private Crude Intelligence
// Where physical meets futures.
// Data: EIA · CFTC · Baker Hughes · Baltic Exchange · CME · NOAA · AIS
// ═══════════════════════════════════════════════════════════════════════════════

const EIA_KEY = "UI1WhaOUjBKVqeNjkPNXvOXCW0rHOF9oOKjAL0vt";
const EIA_BASE = "https://api.eia.gov/v2";

// ── Static seed data (shows while APIs load) ─────────────────────────────────
const OIL_SEED = {
  wti:        { price: 83.40, chg: +1.82, chgPct: +2.24 },
  brent:      { price: 87.15, chg: +1.44, chgPct: +1.68 },
  spread:     3.75, // Brent-WTI
  contango:   false, // false = backwardation (bullish)
  m1m2spread: 0.42, // front vs next month

  inventory: {
    crude:     { actual: 432.1, prev: 435.8, draw: true,  change: -3.7,  est: -2.1  },
    gasoline:  { actual: 228.4, prev: 226.1, draw: false, change: +2.3,  est: +0.8  },
    distillate:{ actual: 117.2, prev: 118.0, draw: true,  change: -0.8,  est: -0.5  },
    cushing:   { actual: 22.8,  prev: 23.4,  draw: true,  change: -0.6,  est: null  },
    utilization: 91.2,
  },

  cot: {
    managed_long:  241800,
    managed_short:  58200,
    net:           183600,
    prev_net:      179200,
    extreme: false, // true = historically crowded
    sentiment: "BULLISH",
    week: "Feb 18, 2025",
  },

  rigCount: {
    total:       479,
    prev:        481,
    chg:          -2,
    oil:         484,
    gas:          99,
    permian:     308,
    eagle_ford:   49,
    bakken:       33,
    trend:       "declining",
    yoy:          -28,
  },

  tankerRates: {
    vlcc:       { rate: 42800,  chg: +3200,  route: "AG→China" },
    suezmax:    { rate: 28400,  chg: -800,   route: "W.Africa→Europe" },
    aframax:    { rate: 22100,  chg: +1100,  route: "N.Sea→UKC" },
    bdti:       { index: 1124,  chg: +47,    trend: "rising" },
  },

  chokepoints: [
    { name:"Strait of Hormuz", status:"WATCH",   flow:"21M bbl/day",  note:"Iran naval exercises ongoing" },
    { name:"Suez Canal",       status:"NORMAL",  flow:"5.5M bbl/day", note:"Traffic normalized post-Houthi rerouting" },
    { name:"Strait of Malacca",status:"NORMAL",  flow:"16M bbl/day",  note:"No disruptions" },
    { name:"Bosphorus",        status:"ELEVATED",flow:"2.8M bbl/day", note:"Russian Urals rerouting adding congestion" },
  ],

  crackSpreads: {
    gasoline_321: { value: 24.80, chg: +1.40, trend: "widening" },
    heating_321:  { value: 22.10, chg: -0.60, trend: "narrowing" },
    interpretation: "Gasoline demand strong, distillate softening",
  },

  opec: {
    quota:      27.0,
    actual:     26.4,
    cheat_rate: -2.2, // % below quota (negative = underproducing vs quota)
    cuts_end:   "Q2 2025",
    compliance: "HIGH",
    notable:    "Iraq -380kbbl/day below quota · Libya restored 180kbbl/day",
  },

  geopolitical: [
    { region:"Iran",    signal:"BEARISH", score:-2, note:"Nuclear talks stalled, sanction enforcement tightening" },
    { region:"Russia",  signal:"BULLISH", score:+2, note:"EU G7 price cap enforcement increasing, Urals discount widening" },
    { region:"Libya",   signal:"NEUTRAL", score: 0, note:"Political tensions but production steady at 1.1M bbl/day" },
    { region:"Houthi",  signal:"BULLISH", score:+1, note:"Red Sea rerouting adding 10-14 days to Europe-bound tankers" },
  ],

  nextEIA: "Wed (weekly · 10:30 AM ET)",
  nextCOT: "Fri (weekly · 3:30 PM ET)",
  nextBH:  "Fri (weekly · 1:00 PM ET)",
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
const useOilData = () => {
  const [data, setData] = React.useState(OIL_SEED);
  const [live, setLive] = React.useState(false);

  React.useEffect(() => {
    // Fetches from /api/oil — server-side proxy with Firestore cache
    // API key never exposed to client
    const fetchOil = async () => {
      try {
        const r = await fetch("/api/oil", { signal: AbortSignal.timeout(12000) });
        if (!r.ok) return;
        const d = await r.json();
        if (d?.inventory?.length >= 2) {
          const curr = parseFloat(d.inventory[0].value);
          const prev = parseFloat(d.inventory[1].value);
          const chg  = curr - prev;
          setData(old => ({
            ...old,
            inventory: { ...old.inventory,
              crude: { actual: curr, prev, draw: chg < 0, change: chg, est: old.inventory.crude.est }
            }
          }));
          setLive(true);
        }
        if (d?.liveWTI?.price) {
          setData(old => ({ ...old, wti: { price: d.liveWTI.price, change: d.liveWTI.change, changePct: d.liveWTI.changePct } }));
        }
      } catch(e) { /* fall through to seed */ }
    };
    fetchOil();

    fetchOil();
  }, []);
  return { data, live };
};

const useOilBrief = (data) => {
  const [brief, setBrief]     = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const cacheKey = "folio_oil_brief_v1";

  const generate = React.useCallback(async (force=false) => {
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey)||"null");
        if (cached && Date.now()-cached.ts < 30*60*1000) {
          setBrief(cached.text); return;
        }
      } catch(e) {}
    }
    setLoading(true);
    try {
      const payload = `WTI $${data.wti.price} (${data.wti.chgPct>0?"+":""}${data.wti.chgPct}%) | Brent $${data.brent.price} | B-W Spread $${data.spread} | Crude Draw: ${data.inventory.crude.change}M bbl vs est ${data.inventory.crude.est}M | Cushing: ${data.inventory.cushing.change}M bbl | Managed Money Net: ${(data.cot.net/1000).toFixed(0)}k (${data.cot.sentiment}) | Rig Count: ${data.rigCount.total} (${data.rigCount.chg} wk, ${data.rigCount.yoy} yoy) | VLCC Rates: $${data.tankerRates.vlcc.rate.toLocaleString()} | BDTI: ${data.tankerRates.bdti.index} | Hormuz: ${data.chokepoints[0].status} — ${data.chokepoints[0].note} | 3:2:1 Crack: $${data.crackSpreads.gasoline_321.value} | OPEC Compliance: ${data.opec.compliance}`;

      const res = await authedBriefFetch({
          custom_payload: payload,
          custom_system: "You are a veteran crude oil trader and physical market analyst with 25 years on the desk at BP Trading and Vitol. You see WTI and Brent futures, inventory data, tanker rates, rig counts, OPEC compliance, and geopolitical signals. Write a 3-sentence pre-market brief that synthesizes ALL signals into one directional thesis. Start with the dominant physical signal (inventory or tanker), note the key tension or divergence, and end with one specific trade observation. Use exact numbers. Write for someone who trades 50,000 barrel lots. No disclaimers. No fluff.",
        });
      const d = await res.json();
      const text = d.brief||"";
      if (text) {
        setBrief(text);
        localStorage.setItem(cacheKey, JSON.stringify({text, ts:Date.now()}));
      }
    } catch(e) {}
    setLoading(false);
  }, [data]);

  React.  useEffect(() => {
    // Delay 1.5s so Firebase auth state resolves before generating brief
    const t = setTimeout(() => generate(), 1500);
    return () => clearTimeout(t);
  }, []);
  return { brief, loading, refresh: ()=>generate(true) };
};

// ── Sub-components ────────────────────────────────────────────────────────────
const OilStatBox = ({ label, value, sub, up, hot }) => (
  <div className="card" style={{padding:"14px 16px",minWidth:0}}>
    <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:6}}>{label}</div>
    <div style={{fontFamily:"var(--M)",fontSize:22,fontWeight:500,
      color: up===true?"var(--up)": up===false?"var(--dn)":"var(--cream)",
      lineHeight:1}}>{value}</div>
    {sub&&<div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--dim)",marginTop:5}}>{sub}</div>}
  </div>
);

const SignalBar = ({ label, value, max=100, color="var(--up)", suffix="%" }) => (
  <div style={{marginBottom:8}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
      <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--dim)"}}>{label}</span>
      <span style={{fontFamily:"var(--M)",fontSize:10,color}}>{value}{suffix}</span>
    </div>
    <div style={{height:4,borderRadius:2,background:"var(--border2)"}}>
      <div style={{height:"100%",width:`${Math.min(100,(value/max)*100)}%`,
        background:color,borderRadius:2,transition:"width 0.6s ease"}}/>
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const colors = { WATCH:"var(--amb)", NORMAL:"var(--up)", ELEVATED:"var(--dn)", CRITICAL:"var(--dn)" };
  return (
    <span style={{fontFamily:"var(--M)",fontSize:8,padding:"2px 6px",borderRadius:3,
      background:`${colors[status]||"var(--muted)"}22`,
      color:colors[status]||"var(--muted)",letterSpacing:"0.08em",border:`1px solid ${colors[status]||"var(--muted)"}44`}}>
      {status}
    </span>
  );
};

// ── Main OilDesk Page ─────────────────────────────────────────────────────────

// ─── EIA REPORT COUNTDOWN ─────────────────────────────────────────────────────
const useEIACountdown = () => {
  const [state, setState] = React.useState(null);

  React.useEffect(() => {
    const calc = () => {
      const now = new Date();
      // Convert to ET
      const etOffset = -5; // EST; EDT is -4 — close enough for display
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const et  = new Date(utc + 3600000 * etOffset);

      const day    = et.getDay();    // 0=Sun, 3=Wed
      const hour   = et.getHours();
      const minute = et.getMinutes();
      const second = et.getSeconds();

      // Is it Wednesday after 10:30 AM ET?
      const isWed          = day === 3;
      const pastReleaseToday = isWed && (hour > 10 || (hour === 10 && minute >= 30));

      if (pastReleaseToday) {
        // Show "released today" until midnight, then next week countdown
        setState({ released: true, label: "Released today", sub: "Next: Wed Mar 5 · 10:30 AM ET" });
        return;
      }

      // Calculate ms until next Wednesday 10:30 AM ET
      let daysUntilWed = (3 - day + 7) % 7;
      if (daysUntilWed === 0) daysUntilWed = 7; // already past today's release

      const nextWed = new Date(et);
      nextWed.setDate(et.getDate() + daysUntilWed);
      nextWed.setHours(10, 30, 0, 0);

      const diff = nextWed - et;
      const totalSec = Math.floor(diff / 1000);
      const days    = Math.floor(totalSec / 86400);
      const hours   = Math.floor((totalSec % 86400) / 3600);
      const mins    = Math.floor((totalSec % 3600) / 60);
      const secs    = totalSec % 60;

      const pad = n => String(n).padStart(2,'0');
      const label = days > 0
        ? `${days}d ${pad(hours)}h ${pad(mins)}m`
        : `${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;

      setState({ released: false, label, sub: "EIA Petroleum Status Report · Every Wed 10:30 AM ET", days, hours, mins, secs });
    };

    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);

  return state;
};

const EIACountdownBadge = () => {
  const s = useEIACountdown();
  if (!s) return null;

  return (
    <div style={{
      display:"flex", alignItems:"center", gap:10,
      padding:"8px 14px", borderRadius:8,
      background: s.released ? "var(--up-bg)" : "var(--surface)",
      border: `1px solid ${s.released ? "var(--up-b)" : "var(--border2)"}`,
      marginBottom:16,
    }}>
      <div style={{
        width:8, height:8, borderRadius:"50%",
        background: s.released ? "var(--up)" : "var(--amb)",
        animation: s.released ? "none" : "breathe 2s infinite",
        flexShrink:0,
      }}/>
      <div>
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          <span style={{fontFamily:"var(--M)", fontSize:9, color:"var(--muted)", letterSpacing:"0.1em"}}>
            EIA REPORT
          </span>
          <span style={{fontFamily:"var(--M)", fontSize:13, fontWeight:600,
            color: s.released ? "var(--up)" : "var(--cream)"}}>
            {s.label}
          </span>
        </div>
        <div style={{fontFamily:"var(--M)", fontSize:9, color:"var(--muted)", marginTop:2}}>
          {s.sub}
        </div>
      </div>
      {!s.released && (
        <div style={{marginLeft:"auto", display:"flex", gap:6}}>
          {s.days > 0 && (
            <div style={{textAlign:"center"}}>
              <div style={{fontFamily:"var(--M)", fontSize:16, color:"var(--cream)", lineHeight:1}}>{s.days}</div>
              <div style={{fontFamily:"var(--M)", fontSize:8, color:"var(--muted)"}}>DAY</div>
            </div>
          )}
          <div style={{textAlign:"center"}}>
            <div style={{fontFamily:"var(--M)", fontSize:16, color:"var(--cream)", lineHeight:1}}>{String(s.hours).padStart(2,'0')}</div>
            <div style={{fontFamily:"var(--M)", fontSize:8, color:"var(--muted)"}}>HR</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontFamily:"var(--M)", fontSize:16, color:"var(--cream)", lineHeight:1}}>{String(s.mins).padStart(2,'0')}</div>
            <div style={{fontFamily:"var(--M)", fontSize:8, color:"var(--muted)"}}>MIN</div>
          </div>
          {s.days === 0 && (
            <div style={{textAlign:"center"}}>
              <div style={{fontFamily:"var(--M)", fontSize:16, color:"var(--amb)", lineHeight:1}}>{String(s.secs).padStart(2,'0')}</div>
              <div style={{fontFamily:"var(--M)", fontSize:8, color:"var(--muted)"}}>SEC</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OilDeskPage = ({ tier, onPaywall }) => {
  const { data, live } = useOilData();
  const { brief, loading: briefLoading, refresh: refreshBrief } = useOilBrief(data);
  const [section, setSection] = React.useState("overview");

  const SECTIONS = [
    { k:"overview",   l:"Overview"   },
    { k:"inventory",  l:"Inventory"  },
    { k:"physical",   l:"Physical"   },
    { k:"sentiment",  l:"Sentiment"  },
    { k:"geo",        l:"Geopolitical"},
  ];

  const fmtChg = n => `${n>=0?"+":""}${(n||0).toFixed(2)}`;
  const fmtK   = n => n>=1000?`${(n/1000).toFixed(1)}k`:n.toString();

  return (
    <div style={{maxWidth:1240,margin:"0 auto",padding:"28px 24px"}}>

      {/* ── Header ── */}
      <div style={{marginBottom:24}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <Icon name="oildrop" size={22} color="var(--up)" strokeWidth={1.2}/>
            <div style={{fontFamily:"var(--D)",fontSize:28,fontWeight:700,
              color:"var(--cream)",letterSpacing:"-0.5px"}}>
              Oil Desk
            </div>
          </div>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.12em",
            borderLeft:"1px solid var(--border2)",paddingLeft:12}}>
            PRIVATE CRUDE INTELLIGENCE
          </div>
          {live&&<span style={{fontFamily:"var(--M)",fontSize:8,color:"#e74c3c",
            border:"1px solid rgba(231,76,60,0.4)",borderRadius:3,padding:"2px 6px"}}>LIVE</span>}
          <div style={{marginLeft:"auto",fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
            Where physical meets futures.
          </div>
        </div>
        <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--dim)",lineHeight:1.7,maxWidth:700}}>
          Unified crude intelligence: EIA inventory · CFTC positioning · Baker Hughes rig count · Baltic tanker rates · chokepoint monitoring · OPEC compliance · geopolitical signals.
        </div>
      </div>

      {/* ── EIA Countdown ── */}
      <EIACountdownBadge/>

      {/* ── AI Brief ── */}
      <div className="card" style={{padding:"20px 24px",marginBottom:24,
        borderLeft:"3px solid var(--up)",
        background:"linear-gradient(135deg,var(--surface) 0%,var(--raised) 100%)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <div style={{width:7,height:7,borderRadius:"50%",background:"var(--up)",
            animation:briefLoading?"breathe 1s infinite":"none"}}/>
          <span style={{fontFamily:"var(--M)",fontSize:9,letterSpacing:"0.14em",color:"var(--up)"}}>
            OIL DESK INTELLIGENCE BRIEF
          </span>
          <button onClick={refreshBrief} style={{marginLeft:"auto",background:"transparent",
            border:"none",cursor:"pointer",display:"flex",alignItems:"center"}}>
            <Icon name="refresh" size={11} color="var(--dim)"/>
          </button>
        </div>
        {briefLoading ? (
          <div style={{fontFamily:"var(--P)",fontSize:14,color:"var(--dim)"}}>
            Synthesizing crude signals<span style={{animation:"breathe 1s infinite",display:"inline-block"}}>...</span>
          </div>
        ) : brief ? (
          <div>
            <p style={{fontFamily:"var(--P)",fontSize:15,color:"var(--text)",lineHeight:1.8,margin:0}}>{brief}</p>
            <div style={{marginTop:10,fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
              AI SYNTHESIS · CLAUDE SONNET 4 · NOT FINANCIAL ADVICE
            </div>
          </div>
        ) : (
          <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--muted)"}}>
            Add your Anthropic API key to enable AI briefs.
          </div>
        )}
      </div>

      {/* ── Price strip ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:20}}>
        <OilStatBox label="WTI CRUDE" value={`$${(data.wti?.price||0).toFixed(2)}`}
          sub={`${fmtChg(data.wti.chgPct)}% today`} up={data.wti.chgPct>0}/>
        <OilStatBox label="BRENT CRUDE" value={`$${(data.brent?.price||0).toFixed(2)}`}
          sub={`${fmtChg(data.brent.chgPct)}% today`} up={data.brent.chgPct>0}/>
        <OilStatBox label="BRENT-WTI SPREAD" value={`$${(data.spread||0).toFixed(2)}`}
          sub={data.spread>4?"Wide — US constrained":data.spread<2?"Tight — normal":"Moderate"} up={null}/>
        <OilStatBox label="CURVE STRUCTURE" value={data.contango?"CONTANGO":"BACKWARDATION"}
          sub={data.contango?"Market expects oversupply":"Market expects tightness"} up={!data.contango}/>
        <OilStatBox label="3:2:1 CRACK" value={`$${data.crackSpreads.gasoline_321.value}`}
          sub={`${fmtChg(data.crackSpreads.gasoline_321.chg)} · ${data.crackSpreads.gasoline_321.trend}`}
          up={data.crackSpreads.gasoline_321.chg>0}/>
        <OilStatBox label="BDTI INDEX" value={data.tankerRates.bdti.index.toString()}
          sub={`${fmtChg(data.tankerRates.bdti.chg)} · ${data.tankerRates.bdti.trend}`}
          up={data.tankerRates.bdti.trend==="rising"}/>
      </div>

      {/* ── Sub-nav ── */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:"1px solid var(--border)",paddingBottom:12,flexWrap:"wrap"}}>
        {SECTIONS.map(s=>(
          <button key={s.k} onClick={()=>setSection(s.k)}
            style={{fontFamily:"var(--M)",fontSize:10,padding:"5px 12px",borderRadius:5,
              background:section===s.k?"var(--raised)":"transparent",
              border:`1px solid ${section===s.k?"var(--border2)":"transparent"}`,
              color:section===s.k?"var(--cream)":"var(--dim)",cursor:"pointer",letterSpacing:"0.06em"}}>
            {s.l.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {section==="overview"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>

          {/* EIA Snapshot */}
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              EIA WEEKLY SNAPSHOT
            </div>
            {[
              { label:"Crude Inventory",   val:`${data.inventory.crude.actual}M bbl`,    chg:data.inventory.crude.change,    bullish:data.inventory.crude.draw   },
              { label:"Gasoline",          val:`${data.inventory.gasoline.actual}M bbl`, chg:data.inventory.gasoline.change, bullish:data.inventory.gasoline.draw},
              { label:"Distillate",        val:`${data.inventory.distillate.actual}M bbl`,chg:data.inventory.distillate.change,bullish:data.inventory.distillate.draw},
              { label:"Cushing, OK",       val:`${data.inventory.cushing.actual}M bbl`,  chg:data.inventory.cushing.change,  bullish:data.inventory.cushing.draw },
              { label:"Refinery Util.",    val:`${data.inventory.utilization}%`,          chg:null, bullish:data.inventory.utilization>90 },
            ].map((r,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                padding:"7px 0",borderBottom:i<4?"1px solid var(--border)":"none"}}>
                <span style={{fontFamily:"var(--P)",fontSize:12,color:"var(--dim)"}}>{r.label}</span>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {r.chg!==null&&<span style={{fontFamily:"var(--M)",fontSize:10,
                    color:r.bullish?"var(--up)":"var(--dn)"}}>
                    {r.chg>=0?"+":""}{r.chg.toFixed(1)}M
                  </span>}
                  <span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--text)"}}>{r.val}</span>
                </div>
              </div>
            ))}
            <div style={{marginTop:10,fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
              Next release: {data.nextEIA}
            </div>
          </div>

          {/* Tanker Rates */}
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              TANKER RATES ($/DAY)
            </div>
            {[
              { type:"VLCC", data:data.tankerRates.vlcc },
              { type:"Suezmax", data:data.tankerRates.suezmax },
              { type:"Aframax", data:data.tankerRates.aframax },
            ].map((t,i)=>(
              <div key={i} style={{padding:"8px 0",borderBottom:i<2?"1px solid var(--border)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                  <div>
                    <span style={{fontFamily:"var(--M)",fontSize:12,color:"var(--text)",fontWeight:500}}>{t.type}</span>
                    <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginLeft:8}}>{t.data.route}</span>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <span style={{fontFamily:"var(--M)",fontSize:12,color:"var(--cream)"}}>
                      ${t.data.rate.toLocaleString()}
                    </span>
                    <span style={{fontFamily:"var(--M)",fontSize:10,marginLeft:6,
                      color:t.data.chg>=0?"var(--up)":"var(--dn)"}}>
                      {t.data.chg>=0?"+":""}{t.data.chg.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div style={{height:3,borderRadius:2,background:"var(--border2)"}}>
                  <div style={{height:"100%",
                    width:`${Math.min(100,(t.data.rate/60000)*100)}%`,
                    background:t.data.chg>=0?"var(--up)":"var(--dn)",borderRadius:2}}/>
                </div>
              </div>
            ))}
            <div style={{marginTop:12,padding:"8px 10px",background:"var(--up-bg)",
              borderRadius:6,border:"1px solid var(--up-b)"}}>
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--dim)",marginBottom:3}}>BDTI</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontFamily:"var(--M)",fontSize:16,color:"var(--up)",fontWeight:500}}>
                  {data.tankerRates.bdti.index}
                </span>
                <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)"}}>
                  +{data.tankerRates.bdti.chg} · {data.tankerRates.bdti.trend}
                </span>
              </div>
            </div>
          </div>

          {/* Chokepoints */}
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              MARITIME CHOKEPOINTS
            </div>
            {data.chokepoints.map((c,i)=>(
              <div key={i} style={{padding:"8px 0",borderBottom:i<data.chokepoints.length-1?"1px solid var(--border)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontFamily:"var(--P)",fontSize:12,color:"var(--text)",fontWeight:500}}>{c.name}</span>
                  <StatusPill status={c.status}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--dim)"}}>{c.note}</span>
                  <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>{c.flow}</span>
                </div>
              </div>
            ))}
          </div>

          {/* OPEC */}
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              OPEC COMPLIANCE
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:14}}>
              <div>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:4}}>QUOTA</div>
                <div style={{fontFamily:"var(--M)",fontSize:20,color:"var(--dim)"}}>{data.opec.quota}M</div>
              </div>
              <div>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:4}}>ACTUAL</div>
                <div style={{fontFamily:"var(--M)",fontSize:20,color:"var(--cream)"}}>{data.opec.actual}M</div>
              </div>
              <div>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:4}}>COMPLIANCE</div>
                <div style={{fontFamily:"var(--M)",fontSize:20,color:"var(--up)"}}>{data.opec.compliance}</div>
              </div>
            </div>
            <SignalBar label="Production vs quota" value={Math.abs(data.opec.cheat_rate)} max={10}
              color="var(--up)" suffix="%"/>
            <div style={{marginTop:10,fontFamily:"var(--P)",fontSize:11,color:"var(--dim)",lineHeight:1.6}}>
              {data.opec.notable}
            </div>
            <div style={{marginTop:8,fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
              Cuts scheduled through {data.opec.cuts_end}
            </div>
          </div>
        </div>
      )}

      {/* ── SENTIMENT ── */}
      {section==="sentiment"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {/* COT */}
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              CFTC COMMITMENT OF TRADERS
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
              {[
                { label:"Managed Long",  val:fmtK(data.cot.managed_long), color:"var(--up)" },
                { label:"Managed Short", val:fmtK(data.cot.managed_short),color:"var(--dn)" },
                { label:"Net Position",  val:fmtK(data.cot.net),          color:"var(--cream)" },
              ].map((s,i)=>(
                <div key={i}>
                  <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginBottom:4}}>{s.label}</div>
                  <div style={{fontFamily:"var(--M)",fontSize:16,color:s.color,fontWeight:500}}>{s.val}</div>
                </div>
              ))}
            </div>
            <SignalBar label="Managed money net long %" value={Math.round((data.cot.net/(data.cot.managed_long+data.cot.managed_short))*100)} color="var(--up)"/>
            <div style={{marginTop:12,padding:"8px 10px",borderRadius:6,
              background:data.cot.sentiment==="BULLISH"?"var(--up-bg)":"var(--dn-bg)",
              border:`1px solid ${data.cot.sentiment==="BULLISH"?"var(--up-b)":"var(--dn-b)"}`}}>
              <div style={{fontFamily:"var(--M)",fontSize:10,color:data.cot.sentiment==="BULLISH"?"var(--up)":"var(--dn)"}}>
                {data.cot.sentiment} · Net {fmtK(data.cot.net)} contracts
              </div>
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginTop:2}}>
                Prev week: {fmtK(data.cot.prev_net)} · Δ {fmtK(data.cot.net-data.cot.prev_net)}
              </div>
            </div>
            <div style={{marginTop:8,fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
              Week of {data.cot.week} · Next: {data.nextCOT}
            </div>
          </div>

          {/* Rig Count */}
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              BAKER HUGHES RIG COUNT
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[
                { label:"Total US",   val:data.rigCount.total,   chg:data.rigCount.chg },
                { label:"Oil Rigs",   val:data.rigCount.oil,     chg:null },
                { label:"Permian",    val:data.rigCount.permian,  chg:null },
                { label:"Eagle Ford", val:data.rigCount.eagle_ford,chg:null },
              ].map((r,i)=>(
                <div key={i}>
                  <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginBottom:4}}>{r.label}</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                    <span style={{fontFamily:"var(--M)",fontSize:18,color:"var(--cream)",fontWeight:500}}>{r.val}</span>
                    {r.chg!==null&&<span style={{fontFamily:"var(--M)",fontSize:11,
                      color:r.chg>=0?"var(--up)":"var(--dn)"}}>
                      {r.chg>=0?"+":""}{r.chg}
                    </span>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{padding:"8px 10px",borderRadius:6,background:"var(--dn-bg)",
              border:"1px solid var(--dn-b)"}}>
              <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--dn)"}}>
                DECLINING TREND · {data.rigCount.yoy} year-over-year
              </div>
              <div style={{fontFamily:"var(--P)",fontSize:11,color:"var(--dim)",marginTop:3,lineHeight:1.5}}>
                Falling rig count is a 6-month leading indicator for production decline — bullish for WTI medium-term.
              </div>
            </div>
            <div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:"var(--amb)",animation:"breathe 2s infinite"}}/>
              <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--amb)"}}>Next: {data.nextBH}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── GEOPOLITICAL ── */}
      {section==="geo"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {data.geopolitical.map((g,i)=>(
            <div key={i} className="card" style={{padding:"16px 18px",
              borderLeft:`3px solid ${g.score>0?"var(--up)":g.score<0?"var(--dn)":"var(--border2)"}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <span style={{fontFamily:"var(--D)",fontSize:16,fontWeight:600,color:"var(--cream)"}}>{g.region}</span>
                <span style={{fontFamily:"var(--M)",fontSize:10,padding:"2px 8px",borderRadius:3,
                  background:g.score>0?"var(--up-bg)":g.score<0?"var(--dn-bg)":"var(--neu-bg)",
                  color:g.score>0?"var(--up)":g.score<0?"var(--dn)":"var(--neu)",
                  border:`1px solid ${g.score>0?"var(--up-b)":g.score<0?"var(--dn-b)":"var(--neu-b)"}`}}>
                  {g.signal}
                </span>
              </div>
              <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--dim)",lineHeight:1.65}}>
                {g.note}
              </div>
              <div style={{marginTop:10,display:"flex",alignItems:"center",gap:6}}>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>IMPACT</div>
                {[-2,-1,0,1,2].map(s=>(
                  <div key={s} style={{width:10,height:10,borderRadius:2,
                    background: Math.abs(s)<=Math.abs(g.score) && Math.sign(s)===Math.sign(g.score)
                      ? g.score>0?"var(--up)":"var(--dn)"
                      : "var(--border2)"}}/>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── INVENTORY detail ── */}
      {section==="inventory"&&(
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16}}>
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:16}}>
              EIA PETROLEUM STATUS REPORT — DETAILED
            </div>
            {[
              { label:"US Crude Oil Inventories",  actual:data.inventory.crude.actual,     chg:data.inventory.crude.change,     est:data.inventory.crude.est,     unit:"M bbl", bullish:data.inventory.crude.draw     },
              { label:"Gasoline Inventories",      actual:data.inventory.gasoline.actual,  chg:data.inventory.gasoline.change,  est:data.inventory.gasoline.est,  unit:"M bbl", bullish:data.inventory.gasoline.draw  },
              { label:"Distillate Inventories",    actual:data.inventory.distillate.actual,chg:data.inventory.distillate.change,est:data.inventory.distillate.est,unit:"M bbl", bullish:data.inventory.distillate.draw},
              { label:"Cushing, OK Stocks",        actual:data.inventory.cushing.actual,   chg:data.inventory.cushing.change,   est:null,                          unit:"M bbl", bullish:data.inventory.cushing.draw   },
            ].map((r,i)=>(
              <div key={i} style={{padding:"10px 0",borderBottom:i<3?"1px solid var(--border)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <span style={{fontFamily:"var(--P)",fontSize:13,color:"var(--text)"}}>{r.label}</span>
                  <div style={{textAlign:"right"}}>
                    <span style={{fontFamily:"var(--M)",fontSize:14,color:"var(--cream)",fontWeight:500}}>
                      {r.actual}{r.unit}
                    </span>
                  </div>
                </div>
                <div style={{display:"flex",gap:16}}>
                  <div>
                    <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>WK CHG  </span>
                    <span style={{fontFamily:"var(--M)",fontSize:11,color:r.bullish?"var(--up)":"var(--dn)"}}>
                      {r.chg>=0?"+":""}{r.chg.toFixed(1)}M
                    </span>
                  </div>
                  {r.est&&<div>
                    <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>EST  </span>
                    <span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--dim)"}}>
                      {r.est>=0?"+":""}{r.est}M
                    </span>
                  </div>}
                  {r.est&&<div>
                    <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>SURPRISE  </span>
                    <span style={{fontFamily:"var(--M)",fontSize:11,
                      color:((r.chg-r.est)<0)===r.bullish?"var(--up)":"var(--dn)"}}>
                      {((r.chg-r.est)>=0?"+":"")}{ (r.chg-r.est).toFixed(1)}M
                    </span>
                  </div>}
                </div>
              </div>
            ))}
            <div style={{marginTop:12,padding:"8px 10px",borderRadius:6,background:"var(--surface)"}}>
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>REFINERY UTILIZATION</div>
              <SignalBar label="" value={data.inventory.utilization} max={100} color="var(--up)" suffix="%"/>
            </div>
          </div>
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              CRACK SPREADS
            </div>
            {[
              { label:"Gasoline 3:2:1", data:data.crackSpreads.gasoline_321 },
              { label:"Heating Oil 3:2:1", data:data.crackSpreads.heating_321 },
            ].map((s,i)=>(
              <div key={i} style={{padding:"10px 0",borderBottom:i===0?"1px solid var(--border)":"none"}}>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:6}}>{s.label}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontFamily:"var(--M)",fontSize:20,color:"var(--cream)",fontWeight:500}}>
                    ${s.data.value}
                  </span>
                  <span style={{fontFamily:"var(--M)",fontSize:11,
                    color:s.data.chg>=0?"var(--up)":"var(--dn)"}}>
                    {s.data.chg>=0?"+":""}{s.data.chg}
                  </span>
                </div>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--dim)",marginTop:4}}>{s.data.trend}</div>
              </div>
            ))}
            <div style={{marginTop:14,fontFamily:"var(--P)",fontSize:12,color:"var(--dim)",lineHeight:1.6}}>
              {data.crackSpreads.interpretation}
            </div>
          </div>
        </div>
      )}

      {/* ── PHYSICAL ── */}
      {section==="physical"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              TANKER MARKET — PHYSICAL FLOW
            </div>
            {[
              { type:"VLCC (270k DWT)",   data:data.tankerRates.vlcc,    daily:true },
              { type:"Suezmax (150k DWT)",data:data.tankerRates.suezmax, daily:true },
              { type:"Aframax (80k DWT)", data:data.tankerRates.aframax, daily:true },
            ].map((t,i)=>(
              <div key={i} style={{padding:"10px 0",borderBottom:i<2?"1px solid var(--border)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--text)"}}>{t.type}</div>
                    <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginTop:2}}>{t.data.route}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"var(--M)",fontSize:14,color:"var(--cream)"}}>
                      ${t.data.rate.toLocaleString()}/day
                    </div>
                    <div style={{fontFamily:"var(--M)",fontSize:10,marginTop:2,
                      color:t.data.chg>=0?"var(--up)":"var(--dn)"}}>
                      {t.data.chg>=0?"+":""}{t.data.chg.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div style={{marginTop:14,padding:"10px 12px",borderRadius:6,
              background:"var(--up-bg)",border:"1px solid var(--up-b)"}}>
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",marginBottom:4}}>BDTI INTERPRETATION</div>
              <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--dim)",lineHeight:1.6}}>
                Rising tanker rates signal physical crude demand outpacing supply — typically a leading indicator for WTI strength 2–3 weeks forward.
              </div>
            </div>
          </div>
          <div className="card" style={{padding:"16px 18px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:14}}>
              CHOKEPOINT MONITOR
            </div>
            {data.chokepoints.map((c,i)=>(
              <div key={i} style={{padding:"10px 0",borderBottom:i<data.chokepoints.length-1?"1px solid var(--border)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                  <span style={{fontFamily:"var(--P)",fontSize:13,color:"var(--text)",fontWeight:500}}>{c.name}</span>
                  <StatusPill status={c.status}/>
                </div>
                <div style={{fontFamily:"var(--P)",fontSize:11,color:"var(--dim)",lineHeight:1.55,marginBottom:4}}>{c.note}</div>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>Flow: {c.flow}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Data Sources Footer ── */}
      <div style={{marginTop:24,padding:"12px 16px",borderRadius:8,background:"var(--surface)",
        border:"1px solid var(--border)",display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>DATA SOURCES:</span>
        {["EIA.gov","CFTC","Baker Hughes","Baltic Exchange","CME Group","NOAA NHC","MarineTraffic","OPEC.org"].map((s,i)=>(
          <span key={i} style={{fontFamily:"var(--M)",fontSize:9,color:"var(--dim)",
            padding:"2px 7px",borderRadius:3,border:"1px solid var(--border2)"}}>{s}</span>
        ))}
        <span style={{marginLeft:"auto",fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
          NOT FINANCIAL ADVICE · EDUCATIONAL USE ONLY
        </span>
      </div>
    </div>
  );
};

const CATEGORY_GRADIENTS = {
  Energy:   "linear-gradient(135deg,#1a0e00,#3d1f00,#1a0e00)",
  Crypto:   "linear-gradient(135deg,#070d1a,#0d1f3d,#070d1a)",
  Equities: "linear-gradient(135deg,#000d0a,#003320,#000d0a)",
  Macro:    "linear-gradient(135deg,#0d0a00,#2e2400,#0d0a00)",
  Shipping: "linear-gradient(135deg,#080010,#1a0033,#080010)",
};
const CATEGORY_ACCENT = {
  Energy:"var(--amb)", Crypto:"#4da6ff", Equities:"var(--up)",
  Macro:"var(--cream)", Shipping:"#a855f7",
};

// Fetch related Google News headlines for a market title
const useMarketNews = (title, enabled) => {
  const [news, setNews] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    if (!enabled || !title) return;
    setLoading(true);
    const keywords = title.replace(/[?!]/g,"").split(" ").filter(w=>w.length>3).slice(0,4).join("+");
    const rssUrl = `https://news.google.com/rss/search?q=${encodeURIComponent(keywords)}&hl=en-US&gl=US&ceid=US:en`;
    const fetchUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}&count=3`;
    fetch(fetchUrl, { signal: AbortSignal.timeout(8000) })
      .then(r=>r.json())
      .then(d => {
        if (d.status==="ok" && d.items?.length) {
          setNews(d.items.slice(0,3).map(it=>({
            title: it.title,
            url: it.link,
            source: it.author || "News",
            date: it.pubDate ? new Date(it.pubDate) : null,
          })));
        }
      })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, [enabled]);
  return { news, loading };
};

// Polymarket logo SVG
const PolymarketLogo = ({ size=18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="6" fill="#0E4AFF"/>
    <path d="M8 22V10l8 8 8-8v12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const KalshiLogo = ({ size=18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="6" fill="#00B050"/>
    <text x="16" y="22" fill="white" fontSize="13" fontWeight="800"
      fontFamily="IBM Plex Mono,monospace" textAnchor="middle">K</text>
  </svg>
);

const PredictionTile = ({ m, idx, tier, onPaywall, user }) => {
  const [expanded, setExpanded] = React.useState(false);
  const { news, loading: newsLoading } = useMarketNews(m.title, expanded);
  const [chatOpen, setChatOpen] = React.useState(false);

  const barColor = m.yes>=60?"var(--up)":m.yes>=45?"var(--amb)":"var(--dn)";
  const accent = CATEGORY_ACCENT[m.category] || "var(--cream)";
  const gradient = CATEGORY_GRADIENTS[m.category] || CATEGORY_GRADIENTS.Macro;
  const floatDur = [7,9,11,8,10,12,8.5,9.5][idx%8];
  const floatDel = [-1,-2.5,0,-1.5,1,-3,0.5,-2][idx%8];
  const isLocked = tier==="free" && idx >= 2;

  const fmtAge = d => {
    if(!d) return "";
    const m_ = Math.floor((Date.now()-d)/60000);
    if(m_<60) return `${m_}m`;
    if(m_<1440) return `${Math.floor(m_/60)}h`;
    return `${Math.floor(m_/1440)}d`;
  };

  return (
    <div
      className="pred-tile card"
      style={{
        "--float-dur": `${floatDur}s`,
        "--float-delay": `${floatDel}s`,
        padding:0, overflow:"hidden", cursor:"pointer",
        position:"relative", minHeight:240,
        opacity: isLocked ? 0.55 : 1,
      }}
      onClick={() => { if(isLocked){onPaywall();return;} setExpanded(e=>!e); }}
    >
      {/* Background: image or gradient */}
      {m.image ? (
        <>
          <div style={{position:"absolute",inset:0,backgroundImage:`url(${m.image})`,
            backgroundSize:"cover",backgroundPosition:"center top",filter:"brightness(.35) saturate(.7)"}}/>
          <div style={{position:"absolute",inset:0,
            background:"linear-gradient(to bottom,rgba(0,0,0,.1) 0%,rgba(0,0,0,.75) 60%,rgba(0,0,0,.96) 100%)"}}/>
        </>
      ) : (
        <div style={{position:"absolute",inset:0,background:gradient,
          backgroundSize:"200% 200%",animation:"gradientShift 8s ease infinite",
          animationDelay:`${floatDel}s`}}/>
      )}

      {/* Animated accent line */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:2,
        background:`linear-gradient(90deg,transparent,${accent},transparent)`,
        animation:"shimmer 3s ease-in-out infinite"}}/>

      {/* Content */}
      <div style={{position:"relative",padding:"16px 18px",height:"100%",display:"flex",flexDirection:"column"}}>
        {/* Top row: source + category + volume */}
        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}>
          {m.source==="Polymarket" ? <PolymarketLogo size={16}/> : <KalshiLogo size={16}/>}
          <span style={{fontFamily:"var(--M)",fontSize:9,color:accent,letterSpacing:".1em",fontWeight:700}}>
            {m.source.toUpperCase()}
          </span>
          <span className="tag t-neu" style={{fontSize:7}}>{m.category}</span>
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
            {m.trend && (
              <span style={{fontFamily:"var(--M)",fontSize:9,
                color:m.trend.startsWith("+")?"var(--up)":"var(--dn)"}}>{m.trend}</span>
            )}
            <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>{m.volume}</span>
            {isLocked && <Icon name="lock" size={11} color="var(--muted)"/>}
          </div>
        </div>

        {/* Title */}
        <div style={{fontFamily:"var(--D)",fontSize:15,color:"var(--cream)",fontWeight:600,
          lineHeight:1.32,marginBottom:14,flex:1,minHeight:52}}>{m.title}</div>

        {/* Probability bar */}
        <div style={{marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
            <div style={{display:"flex",alignItems:"baseline",gap:4}}>
              <span style={{fontFamily:"var(--M)",fontSize:24,fontWeight:700,
                color:barColor,lineHeight:1,textShadow:`0 0 12px ${barColor}66`}}>{m.yes}%</span>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>YES</span>
            </div>
            <div style={{display:"flex",alignItems:"baseline",gap:4}}>
              <span style={{fontFamily:"var(--M)",fontSize:14,fontWeight:600,color:"var(--dim)"}}>{m.no}%</span>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>NO</span>
            </div>
          </div>
          <div style={{height:4,background:"rgba(255,255,255,.08)",borderRadius:2,overflow:"hidden",
            boxShadow:"inset 0 1px 3px rgba(0,0,0,.4)"}}>
            <div style={{height:"100%",width:`${m.yes}%`,background:barColor,
              borderRadius:2,boxShadow:`0 0 8px ${barColor}55`,
              transition:"width 1.2s cubic-bezier(.22,1,.36,1)"}}/>
          </div>
        </div>

        {/* Resolve date */}
        {m.endDate && (
          <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginBottom:expanded?10:0}}>
            Resolves {new Date(m.endDate).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
          </div>
        )}

        {!expanded && (
          <div style={{fontFamily:"var(--M)",fontSize:8,color:accent,opacity:.5,marginTop:4}}>
            tap for analysis + news →
          </div>
        )}

        {/* EXPANDED SECTION */}
        {expanded && (
          <div style={{borderTop:"1px solid rgba(255,255,255,.1)",paddingTop:12,
            animation:"fadeUp .25s ease"}}
            onClick={e=>e.stopPropagation()}>

            {/* Analysis */}
            <div style={{fontFamily:"var(--M)",fontSize:8,color:accent,letterSpacing:".1em",marginBottom:6}}>
              FOLIO ANALYSIS
            </div>
            <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--text)",lineHeight:1.65,marginBottom:14}}>
              {m.insight || (
                m.yes>=65
                  ? `Strong crowd conviction at ${m.yes}%. High-volume consensus — watch for catalyst that confirms or unwinds.`
                  : m.yes<=35
                  ? `Heavy NO positioning at ${m.no}%. Asymmetric upside if an unexpected shift breaks consensus.`
                  : `Near equilibrium at ${m.yes}% YES. Binary outcome approaching — next data release will break this range.`
              )}
            </div>

            {/* Related News */}
            <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",letterSpacing:".1em",marginBottom:8}}>
              RELATED HEADLINES
            </div>
            {newsLoading ? (
              <div style={{display:"flex",gap:8,alignItems:"center",padding:"8px 0"}}>
                <div style={{width:12,height:12,borderRadius:"50%",border:"1.5px solid var(--muted)",
                  borderTopColor:"var(--up)",animation:"spin .8s linear infinite"}}/>
                <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>Fetching news…</span>
              </div>
            ) : news.length > 0 ? (
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                {news.map((n,i)=>(
                  <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
                    style={{display:"flex",gap:8,alignItems:"flex-start",padding:"7px 10px",
                      background:"rgba(255,255,255,.04)",borderRadius:6,
                      border:"1px solid rgba(255,255,255,.07)",textDecoration:"none",
                      transition:"background .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.08)"}
                    onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.04)"}>
                    <div style={{width:3,flexShrink:0,alignSelf:"stretch",
                      background:accent,borderRadius:2,marginTop:2}}/>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:"var(--D)",fontSize:11,color:"var(--text)",
                        lineHeight:1.4,marginBottom:3}}>{n.title}</div>
                      <div style={{display:"flex",gap:6}}>
                        <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{n.source}</span>
                        {n.date && <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{fmtAge(n.date)} ago</span>}
                      </div>
                    </div>
                    <Icon name="external-link" size={10} color="var(--muted)"/>
                  </a>
                ))}
              </div>
            ) : (
              <div style={{fontFamily:"var(--P)",fontSize:11,color:"var(--muted)",marginBottom:10,
                padding:"6px 0"}}>No news found for this market.</div>
            )}

            {/* Chat toggle */}
            <button
              onClick={e=>{e.stopPropagation();setChatOpen(c=>!c);}}
              style={{width:"100%",padding:"7px",background:chatOpen?"rgba(0,160,221,.1)":"rgba(255,255,255,.04)",
                border:`1px solid ${chatOpen?"rgba(0,160,221,.4)":"rgba(255,255,255,.1)"}`,
                borderRadius:6,cursor:"pointer",fontFamily:"var(--M)",fontSize:9,
                color:chatOpen?"#00a0dd":"var(--dim)",letterSpacing:".06em",
                display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
              <Icon name="message" size={10} color={chatOpen?"#00a0dd":"var(--dim)"}/>
              {chatOpen?"Hide Chat":"Live Chat · "+m.title.split(" ").slice(0,3).join(" ")+"…"}
            </button>
            {chatOpen && (
              <div style={{marginTop:10}} onClick={e=>e.stopPropagation()}>
                <ChatRoom ticker={`PRED_${m.id}`} user={user}/>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Founding member invite gate + access strategy
const FoundingGate = ({ onSignIn, tier }) => {
  const [count] = React.useState(47); // Simulate founding member count
  const remaining = 100 - count;
  return (
    <div className="fu" style={{maxWidth:580,margin:"0 auto",padding:"48px 24px",textAlign:"center"}}>
      {/* Logo mark */}
      <div style={{width:56,height:56,borderRadius:14,margin:"0 auto 20px",
        background:"linear-gradient(135deg,rgba(196,164,74,.2),rgba(196,164,74,.05))",
        border:"1px solid rgba(196,164,74,.35)",
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        <Icon name="zap" size={24} color="var(--up)" strokeWidth={1.3}/>
      </div>

      <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)",letterSpacing:".15em",marginBottom:12}}>
        PREDICTIONS INTELLIGENCE · FOUNDING ACCESS
      </div>
      <div style={{fontFamily:"var(--D)",fontSize:32,color:"var(--cream)",fontWeight:700,
        lineHeight:1.1,marginBottom:16,letterSpacing:"-0.5px"}}>
        Real money on<br/>real outcomes.
      </div>
      <div style={{fontFamily:"var(--P)",fontSize:14,color:"var(--soft)",lineHeight:1.7,
        marginBottom:28,maxWidth:420,margin:"0 auto 28px"}}>
        Crowd intelligence from Polymarket and Kalshi — where prediction markets diverge
        from futures is where the edge lives.
      </div>

      {/* Scarcity meter */}
      <div className="card" style={{padding:"16px 20px",marginBottom:20,textAlign:"left"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--cream)"}}>Founding Member Spots</span>
          <span style={{fontFamily:"var(--M)",fontSize:13,fontWeight:700,color:"var(--up)"}}>{remaining} left</span>
        </div>
        <div style={{height:6,background:"var(--border2)",borderRadius:3,overflow:"hidden",marginBottom:8}}>
          <div style={{height:"100%",width:`${count}%`,
            background:"linear-gradient(90deg,var(--up),#a07828)",
            borderRadius:3,boxShadow:"0 0 8px rgba(200,169,74,.4)",
            transition:"width 1.2s cubic-bezier(.22,1,.36,1)"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>{count} claimed</span>
          <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>100 total</span>
        </div>
      </div>

      {/* Pricing */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:24}}>
        <div className="card" style={{padding:"16px",borderColor:"rgba(196,164,74,.4)",
          background:"rgba(196,164,74,.04)"}}>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",letterSpacing:".1em",marginBottom:6}}>
            ✦ FOUNDING · NOW
          </div>
          <div style={{fontFamily:"var(--M)",fontSize:24,fontWeight:700,color:"var(--cream)"}}>$9.99</div>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>per month, locked forever</div>
          <div style={{fontFamily:"var(--P)",fontSize:11,color:"var(--dim)",marginTop:8,lineHeight:1.5}}>
            Your rate never changes. Full access. Founding badge.
          </div>
        </div>
        <div className="card" style={{padding:"16px",opacity:.5}}>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",letterSpacing:".1em",marginBottom:6}}>
            STANDARD · AFTER 100
          </div>
          <div style={{fontFamily:"var(--M)",fontSize:24,fontWeight:700,color:"var(--dim)"}}>$19.99</div>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>per month</div>
          <div style={{fontFamily:"var(--P)",fontSize:11,color:"var(--muted)",marginTop:8,lineHeight:1.5}}>
            Open access after founding round closes.
          </div>
        </div>
      </div>

      {/* What you get */}
      <div className="card" style={{padding:"16px 20px",marginBottom:24,textAlign:"left"}}>
        <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",letterSpacing:".1em",marginBottom:12}}>
          FOUNDING MEMBER INCLUDES
        </div>
        {[
          "Live Polymarket + Kalshi prediction market feed",
          "AI-generated market thesis (Claude Sonnet)",
          "Divergence signals: where crowd vs futures disagree",
          "Related news per market, refreshing live",
          "Per-market live chat rooms",
          "Founding Member badge — visible to the community",
          "Full Oil Desk + Futures intelligence",
          "Lifetime rate lock — price never increases for you",
        ].map((f,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <div style={{width:14,height:14,borderRadius:"50%",flexShrink:0,
              background:"var(--up-bg)",border:"1px solid var(--up-b)",
              display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon name="check" size={8} color="var(--up)"/>
            </div>
            <span style={{fontFamily:"var(--P)",fontSize:12,color:"var(--text)"}}>{f}</span>
          </div>
        ))}
      </div>

      <button className="btn b-gold" style={{width:"100%",padding:"14px",fontSize:12,
        letterSpacing:".08em",marginBottom:12}}
        onClick={onSignIn}>
        ✦ Claim Founding Member Rate — $9.99/mo
      </button>
      <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",lineHeight:1.6}}>
        After {remaining} spots fill, price increases to $19.99/mo. Cancel anytime.
      </div>
    </div>
  );
};

const PredictionsPage = ({ tier, onPaywall, user, onSignIn }) => {
  const {markets,loading,isLive,lastUpdated,error,refresh} = usePredictionMarkets();
  const [filter,setFilter]       = useState("All");
  const [aiLoading,setAiLoading] = useState(false);
  const [aiThesis,setAiThesis]   = useState("");
  const [aiError,setAiError]     = useState("");

  const FILTERS = ["All","Energy","Macro","Equities","Crypto","Shipping"];
  const filtered = filter==="All" ? markets : markets.filter(m=>m.category===filter);
  const divergences = computeDivergences(markets);
  const topDiv = divergences[0];

  // Show founding gate for free users
  if (tier==="free") {
    return <FoundingGate onSignIn={onSignIn} tier={tier}/>;
  }

  const generateThesis = async () => {
    setAiLoading(true); setAiThesis(""); setAiError("");
    try {
      const mSummary = markets.slice(0,6).map(m=>
        `${m.title}: YES ${m.yes}% (Vol: ${m.volume}). Source: ${m.source}. Category: ${m.category}.`
      ).join("\n");
      const dSummary = divergences.map(d=>
        `"${d.title}": Crowd ${d.crowd}% vs Futures-implied ${d.futures}% — ${d.gap}pt gap.`
      ).join("\n");
      const resp = await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:1000,
          system:"You are a senior analyst at a quantitative hedge fund specializing in prediction market arbitrage. Write precise, data-dense research notes. Every sentence must contain a specific number from the provided data.",
          messages:[{role:"user",content:`Live prediction market data:\n\nMARKETS:\n${mSummary}\n\nDIVERGENCES:\n${dSummary}\n\nWrite "Today's Market Thesis" — 4 paragraphs: (1) most actionable divergence with specific numbers, (2) what the crowd is pricing that traditional markets haven't caught up to, (3) key binary risk event in the next 48 hours, (4) one contrarian read where the crowd is likely wrong.`}]
        })
      });
      const data = await resp.json();
      if(data.content?.[0]?.text) setAiThesis(data.content[0].text);
      else setAiError("Unable to generate thesis.");
    } catch(e){ setAiError("Connection error."); }
    setAiLoading(false);
  };

  return (
    <div className="fu">
      {/* HEADER */}
      <div style={{marginBottom:24}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <PolymarketLogo size={22}/>
            <KalshiLogo size={22}/>
          </div>
          <div style={{fontFamily:"var(--D)",fontSize:30,color:"var(--cream)",
            fontWeight:600,letterSpacing:"-0.4px",lineHeight:1.1}}>Predictions Intelligence</div>
          <span className={`tag ${isLive?"t-dn":"t-neu"}`} style={{fontSize:9}}>
            {isLive?"● LIVE":"● DEMO"}
          </span>
          <div style={{marginLeft:"auto",display:"flex",gap:10,alignItems:"center"}}>
            {lastUpdated&&(
              <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
                {lastUpdated.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
              </span>
            )}
            <button onClick={refresh} disabled={loading} className="btn b-ghost"
              style={{padding:"5px 11px",fontSize:9,opacity:loading?.5:1}}>
              {loading?"Loading…":"↻ Refresh"}
            </button>
          </div>
        </div>
        <div style={{fontFamily:"var(--P)",fontSize:13.5,color:"var(--soft)",lineHeight:1.65,maxWidth:640}}>
          Real money on real outcomes. Where prediction markets diverge from futures is where
          the edge lives — crowd intelligence from Polymarket and Kalshi before it moves the tape.
        </div>
      </div>

      {loading && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
          {[1,2,3,4,5,6].map(i=>(
            <div key={i} style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 16px"}}>
              <div className="skel" style={{height:10,width:"30%",borderRadius:4,marginBottom:10}}/>
              <div className="skel" style={{height:15,width:"90%",borderRadius:4,marginBottom:8}}/>
              <div className="skel" style={{height:11,width:"70%",borderRadius:4,marginBottom:14}}/>
              <div className="skel" style={{height:6,width:"100%",borderRadius:3,marginBottom:6}}/>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:8}}>
                <div className="skel" style={{height:22,width:"42%",borderRadius:5}}/>
                <div className="skel" style={{height:22,width:"42%",borderRadius:5}}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && <>
        {/* TOP DIVERGENCE */}
        {topDiv && (
          <div className="card" style={{padding:"16px 20px",marginBottom:20,
            borderColor:"rgba(200,169,74,.4)",background:"rgba(196,164,74,.03)"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
              <Icon name="zap" size={16} color="var(--up)" strokeWidth={1.5} style={{marginTop:2}}/>
              <div style={{flex:1}}>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)",
                  letterSpacing:".1em",marginBottom:6}}>SIGNAL DIVERGENCE DETECTED</div>
                <div style={{fontFamily:"var(--D)",fontSize:17,color:"var(--cream)",
                  marginBottom:6,fontWeight:600}}>{topDiv.title} — {topDiv.gap}pt gap</div>
                <div style={{fontFamily:"var(--P)",fontSize:12.5,color:"var(--text)",lineHeight:1.6}}>
                  {topDiv.summary}
                </div>
              </div>
              <div style={{textAlign:"center",flexShrink:0,padding:"12px 18px",
                background:"rgba(0,0,0,.2)",borderRadius:8,border:"1px solid var(--border)"}}>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginBottom:6}}>CROWD vs FUTURES</div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontFamily:"var(--M)",fontSize:20,fontWeight:700,color:"var(--up)",lineHeight:1}}>{topDiv.crowd}%</div>
                    <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>CROWD</div>
                  </div>
                  <div style={{fontFamily:"var(--M)",fontSize:12,color:"var(--border2)"}}>→</div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontFamily:"var(--M)",fontSize:20,fontWeight:700,color:"var(--dim)",lineHeight:1}}>{topDiv.futures}%</div>
                    <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>FUTURES</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI THESIS */}
        <div className="card" style={{padding:"18px 20px",marginBottom:24,
          background:"rgba(0,0,0,.18)",borderColor:"rgba(196,164,74,.1)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:aiThesis?14:0}}>
            <Icon name="atom" size={15} color="var(--up)" strokeWidth={1.4}/>
            <div style={{fontFamily:"var(--D)",fontSize:16,color:"var(--cream)",fontWeight:600,flex:1}}>AI Market Thesis</div>
            <div style={{fontFamily:"var(--P)",fontSize:11,color:"var(--soft)",marginRight:10}}>
              Synthesizes {markets.length} {isLive?"live":"curated"} markets
            </div>
            <button onClick={generateThesis} disabled={aiLoading}
              style={{padding:"9px 18px",border:"1px solid rgba(196,164,74,.3)",borderRadius:8,
                background:aiLoading?"rgba(196,164,74,.04)":"linear-gradient(135deg,rgba(196,164,74,.16),rgba(196,164,74,.06))",
                fontFamily:"var(--M)",fontSize:10,letterSpacing:".1em",
                color:aiLoading?"var(--muted)":"rgba(196,164,74,.9)",
                cursor:aiLoading?"default":"pointer",whiteSpace:"nowrap"}}>
              {aiLoading?"Analyzing…":"Generate Thesis"}
            </button>
          </div>
          {aiLoading && (
            <div style={{padding:"20px 0",textAlign:"center"}}>
              <div style={{fontFamily:"var(--D)",fontSize:14,color:"var(--dim)",marginBottom:8}}>
                Reading {markets.length} prediction markets…
              </div>
              <div style={{width:"100%",height:2,background:"var(--border)",borderRadius:2,overflow:"hidden"}}>
                <div style={{height:"100%",width:"60%",
                  background:"linear-gradient(90deg,transparent,var(--up),transparent)",
                  animation:"shimmer 1.5s ease-in-out infinite"}}/>
              </div>
            </div>
          )}
          {aiThesis && (
            <div style={{borderTop:"1px solid var(--border)",paddingTop:14}}>
              <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--text)",
                lineHeight:1.8,whiteSpace:"pre-wrap"}}>{aiThesis}</div>
            </div>
          )}
          {aiError && (
            <div style={{padding:"12px 0",fontFamily:"var(--M)",fontSize:11,color:"var(--dn)",textAlign:"center"}}>{aiError}</div>
          )}
        </div>

        {/* FILTERS */}
        <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
          {FILTERS.map(f=>(
            <button key={f} onClick={()=>setFilter(f)}
              className={`btn ${filter===f?"b-gold":"b-ghost"}`}
              style={{padding:"6px 13px",fontSize:10,letterSpacing:".06em"}}>
              {f}
              {f!=="All" && (
                <span style={{marginLeft:5,opacity:.45,fontSize:9}}>
                  ({markets.filter(m=>m.category===f).length})
                </span>
              )}
            </button>
          ))}
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8,
            fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
            <span style={{color:"var(--up)"}}>●</span>YES&nbsp;
            <span style={{color:"var(--dn)"}}>●</span>NO
          </div>
        </div>

        {/* ANIMATED TILE GRID */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",
          gap:16,marginBottom:28}}>
          {filtered.map((m,i) => (
            <PredictionTile key={m.id} m={m} idx={i} tier={tier} onPaywall={onPaywall} user={user}/>
          ))}
        </div>

        {/* ATTRIBUTION */}
        <div style={{padding:"12px 18px",background:"rgba(0,0,0,.1)",
          borderRadius:10,border:"1px solid var(--border)",
          display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <PolymarketLogo size={14}/>
          <KalshiLogo size={14}/>
          <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
            Polymarket Gamma API · Kalshi Trade API v2 · AI synthesis via Claude Sonnet
          </span>
          <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--border2)",marginLeft:"auto"}}>
            Not financial advice
          </span>
        </div>

        {/* PREDICTION VOICES — influencer video panel */}
        <InfluencerPanel/>

      </>}
    </div>
  );
};

const SettingsDrawer = ({ settings, onChange, onClose, tier }) => {
  const S = settings;
  const unlocked = tier!=="free";

  const Toggle = ({label, value, onToggle, disabled=false}) => (
    <div className="toggle-wrap">
      <div>
        <div style={{fontSize:13,color:disabled?"var(--muted)":"var(--soft)",fontFamily:"var(--P)"}}>{label}</div>
        {disabled&&<div style={{fontSize:10,color:"var(--muted)",fontFamily:"var(--M)"}}>Requires Pulse</div>}
      </div>
      <button
        className="toggle"
        style={{background:value&&!disabled?"var(--up)":"var(--muted)",
          cursor:disabled?"not-allowed":"pointer"}}
        onClick={()=>!disabled&&onToggle()}>
        <div className="toggle-dot" style={{left:value&&!disabled?18:3}}/>
      </button>
    </div>
  );

  return(
    <>
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",
        zIndex:249,animation:"fadeIn .2s ease"}} onClick={onClose}/>
      <div className="drawer">
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:28}}>
          <Icon name="settings" size={16} color="var(--up)" strokeWidth={1.4}/>
          <div style={{fontFamily:"var(--D)",fontSize:20,color:"var(--cream)"}}>
            Customize Folio
          </div>
          <button className="btn b-ghost" style={{marginLeft:"auto",padding:"4px 10px",fontSize:10}}
            onClick={onClose}>
            <Icon name="close" size={14} color="var(--dim)"/>
          </button>
        </div>

        {/* Persona */}
        <div style={{marginBottom:24}}>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",
            textTransform:"uppercase",letterSpacing:".09em",marginBottom:10}}>Investor Persona</div>
          <div style={{display:"flex",gap:8}}>
            {[{k:"everyday",l:"Everyday Investor"},{k:"trader",l:"Active Trader"}].map(p=>(
              <button key={p.k}
                onClick={()=>onChange({...S,persona:p.k})}
                style={{flex:1,padding:"10px 8px",borderRadius:8,cursor:"pointer",
                  fontFamily:"var(--P)",fontSize:12,
                  background:S.persona===p.k?"var(--up-bg)":"var(--raised)",
                  border:`1px solid ${S.persona===p.k?"var(--up-b)":"var(--border)"}`,
                  color:S.persona===p.k?"var(--up)":"var(--dim)",
                  transition:"all .15s"}}>
                {p.l}
              </button>
            ))}
          </div>
        </div>

        {/* Accent color */}
        <div style={{marginBottom:24}}>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",
            textTransform:"uppercase",letterSpacing:".09em",marginBottom:10}}>Accent Color</div>
          <div style={{display:"flex",gap:8}}>
            {[
              {k:"gold",label:"Gold",color:"var(--up)"},
              {k:"violet",label:"Violet",color:"var(--vio2)"},
              {k:"rose",label:"Rose",color:"var(--rose2)"},
            ].map(c=>(
              <button key={c.k}
                onClick={()=>onChange({...S,accentColor:c.k})}
                style={{flex:1,padding:"10px 8px",borderRadius:8,cursor:"pointer",
                  fontFamily:"var(--M)",fontSize:10,
                  background:S.accentColor===c.k?`${c.color}14`:"var(--raised)",
                  border:`1px solid ${S.accentColor===c.k?c.color:"var(--border)"}`,
                  color:S.accentColor===c.k?c.color:"var(--dim)",
                  transition:"all .15s"}}>
                <div style={{width:10,height:10,borderRadius:"50%",
                  background:c.color,margin:"0 auto 4px"}}/>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div style={{marginBottom:24}}>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",
            textTransform:"uppercase",letterSpacing:".09em",marginBottom:4}}>Dashboard Sections</div>
          <Toggle label="Weather Intelligence" value={S.sections.weather}
            onToggle={()=>onChange({...S,sections:{...S.sections,weather:!S.sections.weather}})}
            disabled={!unlocked}/>
          <Toggle label="Oil Futures Gauge" value={S.sections.gauge}
            onToggle={()=>onChange({...S,sections:{...S.sections,gauge:!S.sections.gauge}})}
            disabled={!unlocked}/>
          <Toggle label="Stock Tables" value={S.sections.stocks}
            onToggle={()=>onChange({...S,sections:{...S.sections,stocks:!S.sections.stocks}})}/>
          <Toggle label="Tech & Quantum News" value={S.sections.tech}
            onToggle={()=>onChange({...S,sections:{...S.sections,tech:!S.sections.tech}})}
            disabled={!unlocked}/>
        </div>

        {/* Commodities */}
        <div style={{marginBottom:24}}>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",
            textTransform:"uppercase",letterSpacing:".09em",marginBottom:4}}>Tracked Commodities</div>
          {[
            {k:"oil",l:"WTI Crude Oil",icon:"barrel"},
            {k:"natgas",l:"Natural Gas",icon:"flame"},
            {k:"corn",l:"Corn / Grains",icon:"grain"},
            {k:"coffee",l:"Coffee Arabica",icon:"coffee"},
            {k:"wheat",l:"Wheat",icon:"wheat"},
            {k:"electricity",l:"Electricity",icon:"bolt"},
          ].map(c=>(
            <div key={c.k} className="toggle-wrap">
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Icon name={c.icon} size={14} color="var(--dim)" strokeWidth={1.3}/>
                <span style={{fontSize:13,color:"var(--soft)",fontFamily:"var(--P)"}}>{c.l}</span>
              </div>
              <button className="toggle"
                style={{background:S.commodities[c.k]?"var(--up)":"var(--muted)"}}
                onClick={()=>onChange({...S,commodities:{...S.commodities,[c.k]:!S.commodities[c.k]}})}>
                <div className="toggle-dot" style={{left:S.commodities[c.k]?18:3}}/>
              </button>
            </div>
          ))}
        </div>

        {/* Alert threshold */}
        <div style={{marginBottom:24}}>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",
            textTransform:"uppercase",letterSpacing:".09em",marginBottom:8}}>
            Price Alert Threshold: <span style={{color:"var(--up)"}}>{S.alertThreshold}%</span>
          </div>
          <input type="range" min={1} max={20} value={S.alertThreshold}
            onChange={e=>onChange({...S,alertThreshold:parseInt(e.target.value)})}
            style={{width:"100%",accentColor:"var(--up)"}}/>
          <div style={{display:"flex",justifyContent:"space-between",
            fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginTop:4}}>
            <span>1% — sensitive</span><span>20% — major moves</span>
          </div>
        </div>

        <div style={{padding:"14px",background:"rgba(196,164,74,.05)",
          border:"1px solid rgba(196,164,74,.12)",borderRadius:9}}>
          <div style={{fontSize:11,color:"var(--dim)",fontFamily:"var(--P)",lineHeight:1.6}}>
            <strong style={{color:"var(--up)"}}>Pro tip:</strong> Set your persona to "Active Trader" for
            a denser data layout with squeeze scores front and center.
            Everyday Investor mode prioritizes readable stories.
          </div>
        </div>
      </div>
    </>
  );
};

// ─── AUTH MODAL ───────────────────────────────────────────────────────────────
// Invite codes validated server-side via /api/validate-invite
// ─── hCaptcha loader ─────────────────────────────────────────────────────────
const HCAPTCHA_SITEKEY = "10000000-ffff-ffff-ffff-000000000001"; // Replace with real key from hcaptcha.com

const useHCaptcha = () => {
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    if (window.hcaptcha) { setLoaded(true); return; }
    const s = document.createElement("script");
    s.src = "https://js.hcaptcha.com/1/api.js";
    s.async = true;
    s.defer = true;
    s.onload = () => setLoaded(true);
    document.head.appendChild(s);
  }, []);
  return loaded;
};

const HCaptcha = ({ onVerify, theme = "dark" }) => {
  const ref = React.useRef(null);
  const rendered = React.useRef(false);
  React.useEffect(() => {
    if (!window.hcaptcha || rendered.current || !ref.current) return;
    rendered.current = true;
    window.hcaptcha.render(ref.current, {
      sitekey: HCAPTCHA_SITEKEY,
      theme,
      callback: onVerify,
      "expired-callback": () => onVerify(null),
    });
  }, []);
  return <div ref={ref} style={{ marginBottom: 16 }}/>;
};

const AuthModal = ({ onClose, onLogin }) => {
  const [step, setStep]           = useState("invite");
  const [inviteCode, setInvite]   = useState("");
  const [inviteErr, setInviteErr] = useState("");
  const [email, setEmail]         = useState("");
  const [waitEmail, setWaitEmail] = useState("");
  const [waitDone, setWaitDone]   = useState(false);
  const [errMsg, setErrMsg]       = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [grantedTier, setGrantedTier]   = useState("pulse");
  const captchaLoaded = useHCaptcha();

  const handleInvite = async () => {
    const code = inviteCode.trim().toUpperCase();
    if (!code) return;
    if (!captchaToken && window.hcaptcha) {
      setInviteErr("Please complete the CAPTCHA first.");
      return;
    }
    setStep("loading");
    try {
      const res = await fetch("/api/validate-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, captchaToken }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setGrantedTier(data.tier || "pulse");
        setStep("signin");
      } else {
        setStep("invite");
        setInviteErr(data.error || "Invalid code. Request access below.");
        // Reset hCaptcha
        if (window.hcaptcha) window.hcaptcha.reset();
        setCaptchaToken(null);
      }
    } catch(e) {
      setStep("invite");
      setInviteErr("Connection error. Try again.");
    }
  };

  const handleWaitlist = () => {
    if (!waitEmail.includes("@")) return;
    // Save locally
    const list = JSON.parse(localStorage.getItem("folio_waitlist")||"[]");
    if (!list.includes(waitEmail)) list.push(waitEmail);
    localStorage.setItem("folio_waitlist", JSON.stringify(list));
    // Save to Firestore for server-side cron access
    saveWaitlistToFirestore(waitEmail);
    setWaitDone(true);
  };

  const handleGoogle = async () => {
    setStep("loading");
    if (!isFirebaseConfigured) {
      setTimeout(() => onLogin({ name:"Demo User", email:"demo@getfolio.ink", avatar:"D", method:"google", uid:"demo" }), 900);
      return;
    }
    try {
      const result = await signInWithPopup(_auth, _provider);
      const u = result.user;
      const savedTier = await loadTierFromFirestore(u.uid);
      onLogin({ name:u.displayName||u.email, email:u.email, avatar:(u.displayName||u.email)[0].toUpperCase(), method:"google", uid:u.uid, photo:u.photoURL, tier:savedTier });
    } catch(e) {
      setErrMsg(e.code==="auth/popup-closed-by-user" ? "Sign-in cancelled." : "Google sign-in failed. Try again.");
      setStep("error");
    }
  };

  const handleEmail = async () => {
    if (!email.includes("@")) return;
    setStep("loading");
    if (!isFirebaseConfigured) {
      setTimeout(() => onLogin({ name:email.split("@")[0], email, avatar:email[0].toUpperCase(), method:"email", uid:"demo" }), 900);
      return;
    }
    try {
      await sendSignInLinkToEmail(_auth, email, { url: window.location.origin+"?emailSignIn=true", handleCodeInApp:true });
      localStorage.setItem("folio_signin_email", email);
      setStep("email_sent");
    } catch(e) {
      setErrMsg("Couldn't send link. Check the email and try again.");
      setStep("error");
    }
  };

  const cardStyle = {
    background:"linear-gradient(155deg,#13111e 0%,#0e0c18 100%)",
    border:"1px solid rgba(196,164,74,.2)",borderRadius:20,maxWidth:420,width:"92%",
    boxShadow:"0 48px 96px rgba(0,0,0,.85)",animation:"riseIn .28s ease",position:"relative",
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div style={cardStyle} onClick={e=>e.stopPropagation()}>
        <div style={{height:2,background:"linear-gradient(90deg,transparent,var(--up),transparent)",borderRadius:"20px 20px 0 0"}}/>
        <div style={{padding:"36px 32px 32px"}}>

          {/* LOADING */}
          {step==="loading"&&(
            <div style={{textAlign:"center",padding:"36px 0"}}>
              <div style={{width:44,height:44,borderRadius:"50%",margin:"0 auto 22px",
                border:"3px solid rgba(196,164,74,.15)",borderTop:"3px solid var(--up)",
                animation:"spin 0.8s linear infinite"}}/>
              <div style={{fontFamily:"var(--D)",fontSize:20,color:"#f5f0e8",fontWeight:600,marginBottom:8}}>
                Signing you in…
              </div>
              <div style={{fontFamily:"var(--M)",fontSize:10,color:"rgba(255,255,255,0.35)",letterSpacing:"0.12em"}}>
                CONNECTING SECURELY
              </div>
            </div>
          )}

          {/* EMAIL SENT */}
          {step==="email_sent"&&(
            <div style={{textAlign:"center",padding:"12px 0"}}>
              <div style={{fontSize:36,marginBottom:16}}>✉️</div>
              <div style={{fontFamily:"var(--D)",fontSize:22,color:"#f5f0e8",fontWeight:600,marginBottom:10}}>Check your inbox</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.75,fontFamily:"var(--P)"}}>
                Magic link sent to<br/><strong style={{color:"rgba(255,255,255,0.8)"}}>{email}</strong>
              </div>
              <div style={{marginTop:14,fontFamily:"var(--M)",fontSize:10,color:"rgba(255,255,255,0.25)",letterSpacing:"0.08em"}}>CHECK SPAM IF YOU DON'T SEE IT</div>
              <button className="btn b-ghost" onClick={()=>setStep("signin")} style={{marginTop:20,width:"100%",fontSize:11}}>← Different email</button>
            </div>
          )}

          {/* ERROR */}
          {step==="error"&&(
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
              <div style={{fontSize:14,color:"#e74c3c",fontFamily:"var(--P)",marginBottom:20,lineHeight:1.65}}>{errMsg}</div>
              <button className="btn b-ghost" onClick={()=>setStep("signin")} style={{width:"100%",fontSize:11}}>Try again</button>
            </div>
          )}

          {/* INVITE GATE */}
          {step==="invite"&&(
            <>
              <div style={{textAlign:"center",marginBottom:28}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:16}}>
                  <Icon name="folio" size={18} color="var(--up)" strokeWidth={1.2}/>
                  <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)",letterSpacing:".2em"}}>FOLIO</span>
                </div>
                <div style={{fontFamily:"var(--D)",fontSize:24,color:"#f5f0e8",fontWeight:600,lineHeight:1.2,marginBottom:8}}>Private Access</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",fontFamily:"var(--P)",lineHeight:1.7}}>Enter your invite code to continue.</div>
              </div>
              <input className="inp" value={inviteCode}
                onChange={e=>{setInvite(e.target.value);setInviteErr("");}}
                onKeyDown={e=>e.key==="Enter"&&handleInvite()}
                placeholder="INVITE CODE"
                style={{width:"100%",marginBottom:8,textAlign:"center",
                  fontFamily:"var(--M)",letterSpacing:"0.2em",fontSize:13,textTransform:"uppercase"}}/>
              {inviteErr&&<div style={{fontFamily:"var(--M)",fontSize:11,color:"#e74c3c",textAlign:"center",marginBottom:8}}>{inviteErr}</div>}
              {/* hCaptcha — replaces "is this page private?" risk */}
              {captchaLoaded && (
                <div style={{display:"flex",justifyContent:"center",marginBottom:14,overflow:"hidden",borderRadius:8}}>
                  <HCaptcha onVerify={tok => setCaptchaToken(tok)}/>
                </div>
              )}
              <button className="btn b-gold" onClick={handleInvite}
                disabled={captchaLoaded && !captchaToken}
                style={{width:"100%",marginBottom:22,fontSize:12,padding:"13px",
                  opacity:(captchaLoaded && !captchaToken)?0.5:1}}>
                Continue →
              </button>
              <div style={{borderTop:"1px solid rgba(255,255,255,0.07)",paddingTop:18,textAlign:"center"}}>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"rgba(255,255,255,0.25)",letterSpacing:"0.1em",marginBottom:12}}>NO CODE?</div>
                <button onClick={()=>setStep("waitlist")} style={{background:"transparent",
                  border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"10px 20px",
                  color:"rgba(255,255,255,0.45)",fontFamily:"var(--M)",fontSize:11,cursor:"pointer",
                  letterSpacing:"0.08em",width:"100%"}}>
                  Request Early Access →
                </button>
              </div>
            </>
          )}

          {/* WAITLIST */}
          {step==="waitlist"&&(
            <>
              <div style={{textAlign:"center",marginBottom:24}}>
                <div style={{fontSize:28,marginBottom:12}}>✦</div>
                <div style={{fontFamily:"var(--D)",fontSize:22,color:"#f5f0e8",fontWeight:600,marginBottom:8}}>
                  {waitDone ? "You're on the list." : "Request Early Access"}
                </div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",fontFamily:"var(--P)",lineHeight:1.7}}>
                  {waitDone ? "We'll be in touch with your invite code soon." : "We're onboarding in waves. Drop your email and we'll reach out personally."}
                </div>
              </div>
              {!waitDone&&(
                <>
                  <div style={{display:"flex",gap:8,marginBottom:14}}>
                    <input className="inp" style={{flex:1}} value={waitEmail}
                      onChange={e=>setWaitEmail(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&handleWaitlist()}
                      placeholder="your@email.com"/>
                    <button className="btn b-gold" onClick={handleWaitlist} style={{padding:"11px 16px",fontSize:11,flexShrink:0}}>Submit</button>
                  </div>
                  <div style={{fontFamily:"var(--M)",fontSize:9,color:"rgba(255,255,255,0.2)",textAlign:"center",letterSpacing:"0.08em"}}>
                    NO SPAM · INVITE ONLY · UNSUBSCRIBE ANYTIME
                  </div>
                </>
              )}
              {waitDone&&<div style={{textAlign:"center",fontFamily:"var(--M)",fontSize:11,color:"var(--up)",letterSpacing:"0.1em"}}>✓ ADDED TO LIST</div>}
              <button onClick={()=>setStep("invite")} style={{display:"block",width:"100%",marginTop:20,
                background:"transparent",border:"none",color:"rgba(255,255,255,0.25)",
                fontFamily:"var(--M)",fontSize:11,cursor:"pointer",letterSpacing:"0.06em"}}>
                ← Back
              </button>
            </>
          )}

          {/* SIGN IN — post invite */}
          {step==="signin"&&(
            <>
              <div style={{textAlign:"center",marginBottom:24}}>
                <div style={{display:"inline-flex",alignItems:"center",gap:6,
                  background:"rgba(196,164,74,0.1)",border:"1px solid rgba(196,164,74,0.25)",
                  borderRadius:20,padding:"4px 14px",marginBottom:16}}>
                  <span style={{color:"var(--up)",fontSize:12}}>✓</span>
                  <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)",letterSpacing:"0.1em"}}>INVITE ACCEPTED</span>
                </div>
                <div style={{fontFamily:"var(--D)",fontSize:24,color:"#f5f0e8",fontWeight:600,marginBottom:8}}>Welcome to Folio.</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",fontFamily:"var(--P)",lineHeight:1.7}}>Sign in to access your account.</div>
              </div>
              <button className="g-btn" onClick={handleGoogle} style={{marginBottom:14}}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              <div style={{display:"flex",alignItems:"center",gap:12,margin:"14px 0"}}>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.08)"}}/>
                <span style={{fontFamily:"var(--M)",fontSize:10,color:"rgba(255,255,255,0.2)"}}>or</span>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.08)"}}/>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                <input className="inp" style={{flex:1}} value={email}
                  onChange={e=>setEmail(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleEmail()}
                  placeholder="your@email.com"/>
                <button className="btn b-gold" onClick={handleEmail} style={{padding:"11px 16px",fontSize:11,flexShrink:0}}>Send link</button>
              </div>
              <div style={{fontFamily:"var(--M)",fontSize:9,color:"rgba(255,255,255,0.2)",textAlign:"center",letterSpacing:"0.08em"}}>
                NO PASSWORD · SECURE MAGIC LINK · CANCEL ANYTIME
              </div>
            </>
          )}

        </div>
        <button onClick={onClose} style={{position:"absolute",top:14,right:14,
          background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",
          color:"rgba(255,255,255,0.5)",borderRadius:"50%",width:28,height:28,
          cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
      </div>
    </div>
  );
};


// ─── SIGNAL HEALTH BANNER ──────────────────────────────────────────────────────
// Shown at top of Oil Desk if any data source is stale or unavailable
const SignalHealthBanner = ({ health }) => {
  if (!health || health.health_pct >= 87) return null;
  const bad = (health.sources||[]).filter(s => s.status !== "OK");
  if (bad.length === 0) return null;
  const isCritical = health.health_pct < 60;
  return (
    <div style={{
      background: isCritical ? "rgba(184,50,72,0.08)" : "rgba(139,122,80,0.08)",
      border:`1px solid ${isCritical ? "rgba(184,50,72,0.25)" : "rgba(139,122,80,0.25)"}`,
      borderRadius:6, padding:"9px 14px", marginBottom:16,
      display:"flex", alignItems:"center", gap:10,
    }}>
      <span style={{fontSize:14}}>{isCritical ? "🔴" : "🟡"}</span>
      <div style={{flex:1}}>
        <span style={{fontFamily:"var(--M)",fontSize:9,
          color: isCritical ? "#b06070" : "#9a8a50",
          letterSpacing:"0.1em"}}>
          {isCritical ? "DATA DEGRADED" : "DATA PARTIAL"} — {health.health_pct}% signals live
        </span>
        <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginLeft:8}}>
          Unavailable: {bad.map(s=>s.source_id).join(", ")}
        </span>
      </div>
      <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>
        Brief reflects available data
      </span>
    </div>
  );
};

// ─── DATA FRESHNESS BADGE ─────────────────────────────────────────────────────
// Shown on individual widgets when their data is stale
const FreshnessBadge = ({ fetchedAt, maxAgeHours = 24 }) => {
  if (!fetchedAt) return null;
  const ageHours = (Date.now() - new Date(fetchedAt).getTime()) / 3600000;
  if (ageHours < maxAgeHours * 0.75) return null; // Fresh — don't show anything
  const isStale = ageHours > maxAgeHours;
  return (
    <span style={{
      fontFamily:"var(--M)", fontSize:8, letterSpacing:"0.08em",
      color: isStale ? "#b06070" : "#9a8a50",
      background: isStale ? "rgba(184,50,72,0.08)" : "rgba(139,122,80,0.08)",
      border:`1px solid ${isStale ? "rgba(184,50,72,0.2)" : "rgba(139,122,80,0.2)"}`,
      borderRadius:3, padding:"1px 6px", marginLeft:6,
    }}>
      {isStale ? `STALE ${Math.round(ageHours)}h` : `${Math.round(ageHours)}h AGO`}
    </span>
  );
};


// ─── ADMIN PAGE ───────────────────────────────────────────────────────────────
const ADMIN_PASS = "folio-admin-2025"; // Change this — store in env ideally

const AdminPage = ({ user, alerts, setAlerts, tier, oilData }) => {
  const [authed, setAuthed]         = useState(false);
  const [pass, setPass]             = useState("");
  const [passErr, setPassErr]       = useState(false);
  const [tab, setTab]               = useState("overview");
  const [waitlist, setWaitlist]     = useState([]);
  const [invites, setInvites]       = useState(["FOLIO-EARLY","FOLIO-2025","AURUM","PRIVATE","INNER","SANCTUM","AFC2025"]);
  const [newCode, setNewCode]       = useState("");
  const [newAlert, setNewAlert]     = useState({ symbol:"WTI", condition:"above", price:"", type:"email", to:"" });
  const [briefStatus, setBriefStatus] = useState(null);
  const [discordStatus, setDiscordStatus] = useState(null);
  const [sendingBrief, setSendingBrief]   = useState(false);
  const [sendingDiscord, setSendingDiscord] = useState(false);
  const [scorecardStatus, setScorecardStatus] = useState(null);

  // Load waitlist from localStorage on mount
  useState(() => {
    try {
      const list = JSON.parse(localStorage.getItem("folio_waitlist")||"[]");
      setWaitlist(list);
    } catch {}
  });

  const verify = () => {
    if (pass === ADMIN_PASS) { setAuthed(true); setPassErr(false); }
    else setPassErr(true);
  };

  // ── Send Discord brief ──
  const sendDiscord = async () => {
    setSendingDiscord(true); setDiscordStatus(null);
    try {
      const r = await authedBriefFetch({ custom_system: "You are Folio, a crude oil intelligence terminal. Write a 3-sentence daily brief covering WTI, physical signals, and one trade observation.", custom_payload: "Generate today's crude market brief." });
      const d = await r.json();
      const briefText = d?.content?.[0]?.text || "No brief generated.";

      const dr = await fetch("/api/discord", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          brief: briefText,
          wti:   { price: oilData?.wti?.price   || "—", chgPct: oilData?.wti?.chgPct   || 0 },
          brent: { price: oilData?.brent?.price || "—", chgPct: oilData?.brent?.chgPct || 0 },
          signals: oilData?.chokepoints?.map(c=>({name:c.name,status:c.status})) || [],
        }),
      });
      const dd = await dr.json();
      setDiscordStatus(dr.ok ? "✓ Sent to Discord" : `Error: ${dd.error}`);
    } catch(e) {
      setDiscordStatus("Error: " + e.message);
    }
    setSendingDiscord(false);
  };

  // ── Send email digest ──
  const sendDigest = async (toEmail) => {
    setSendingBrief(true); setBriefStatus(null);
    try {
      const r = await authedBriefFetch({ custom_system:"You are Folio. Write a 3-sentence crude intelligence brief.", custom_payload:"Daily brief." });
      const d = await r.json();
      const briefText = d?.content?.[0]?.text || "No brief.";

      const er = await fetch("/api/digest", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          brief: briefText,
          to: toEmail || user?.email || "abhilash@afcurgentcare.com",
          wti:        oilData?.wti        || { price: "—", chgPct: 0 },
          brent:      oilData?.brent      || { price: "—", chgPct: 0 },
          eia:        oilData?.inventory?.crude || { change: 0, est: 0, draw: false },
          cot:        oilData?.cot        || { net: 0, sentiment: "NEUTRAL" },
          rigCount:   oilData?.rigCount   || { total: 0, chg: 0 },
          chokepoints: oilData?.chokepoints || [],
        }),
      });
      const ed = await er.json();
      setBriefStatus(er.ok ? "✓ Email sent" : `Error: ${JSON.stringify(ed.error)}`);
    } catch(e) {
      setBriefStatus("Error: " + e.message);
    }
    setSendingBrief(false);
  };

  // ── Send weekly scorecard ──
  const sendScorecard = async () => {
    setScorecardStatus("Generating…");
    try {
      const r = await fetch("/api/scorecard", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          weekOf: new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}),
          wti: { price:83.40, weeklyChg:2.1 },
          brent: { price:87.15 },
          eia: { change:-3.7, est:-2.1, draw:true },
          cot: { net:183600, sentiment:"BULLISH" },
          rigCount: { total:479, chg:-2, yoy:"-28" },
          bdti: { index:1124, trend:"rising" },
          chokepoints: [{ name:"Hormuz", status:"WATCH", note:"Naval exercises" }],
          brief: "Weekly signals lean bullish — inventory draws, declining rigs, and Hormuz tensions support WTI. Watch for COT unwind if geopolitical premium fades.",
        }),
      });
      const d = await r.json();
      setScorecardStatus(d.ok ? `✓ Scorecard sent to Discord — ${d.scorecard?.overallBias} bias` : "Error sending scorecard");
    } catch(e) {
      setScorecardStatus("Error: " + e.message);
    }
  };

  // ── Add alert ──
  const addAlert = () => {
    if (!newAlert.price || !newAlert.to) return;
    const a = { ...newAlert, id: Date.now(), price: parseFloat(newAlert.price), active: true };
    const updated = [...alerts, a];
    setAlerts(updated);
    localStorage.setItem("folio_alerts", JSON.stringify(updated));
    setNewAlert({ symbol:"WTI", condition:"above", price:"", type:"email", to:"" });
  };

  const removeAlert = (id) => {
    const updated = alerts.filter(a => a.id !== id);
    setAlerts(updated);
    localStorage.setItem("folio_alerts", JSON.stringify(updated));
  };

  const addInviteCode = () => {
    if (!newCode.trim()) return;
    setInvites([...invites, newCode.trim().toUpperCase()]);
    setNewCode("");
  };

  const st = { fontFamily:"var(--M)", fontSize:10, letterSpacing:"0.1em" };
  const statBox = (label, value, sub, color="#f5f0e8") => (
    <div style={{background:"var(--raised)",border:"1px solid var(--border)",borderRadius:8,padding:"14px 16px"}}>
      <div style={{...st,color:"var(--muted)",marginBottom:6}}>{label}</div>
      <div style={{fontSize:22,fontWeight:600,color,marginBottom:2}}>{value}</div>
      {sub&&<div style={{...st,color:"var(--muted)",fontSize:9}}>{sub}</div>}
    </div>
  );

  if (!authed) return (
    <div style={{maxWidth:400,margin:"80px auto",padding:24}}>
      <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)",letterSpacing:"0.15em",marginBottom:16}}>⚙ ADMIN ACCESS</div>
      <div style={{fontFamily:"var(--D)",fontSize:24,color:"var(--cream)",fontWeight:600,marginBottom:20}}>Administrator</div>
      <input className="inp" type="password" value={pass} onChange={e=>setPass(e.target.value)}
        onKeyDown={e=>e.key==="Enter"&&verify()} placeholder="Admin password" style={{width:"100%",marginBottom:8}}/>
      {passErr&&<div style={{...st,color:"#e74c3c",marginBottom:8}}>Incorrect password.</div>}
      <button className="btn b-gold" onClick={verify} style={{width:"100%",padding:"12px"}}>Enter Admin →</button>
    </div>
  );

  const TABS = ["overview","alerts","invites","waitlist","broadcast","health"];

  return (
    <div style={{maxWidth:900,margin:"0 auto",padding:"28px 0"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <div>
          <div style={{...st,color:"var(--up)",marginBottom:4}}>⚙ ADMIN CONSOLE</div>
          <div style={{fontFamily:"var(--D)",fontSize:24,color:"var(--cream)",fontWeight:600}}>Folio Dashboard</div>
        </div>
        <div style={{...st,color:"var(--muted)"}}>Signed in as {user?.email || "unknown"}</div>
      </div>

      {/* Tab bar */}
      <div style={{display:"flex",gap:4,marginBottom:24,borderBottom:"1px solid var(--border)",paddingBottom:0}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            background:"transparent",border:"none",
            borderBottom:tab===t?"2px solid var(--up)":"2px solid transparent",
            color:tab===t?"var(--up)":"var(--muted)",
            fontFamily:"var(--M)",fontSize:10,letterSpacing:"0.1em",
            padding:"8px 16px",cursor:"pointer",textTransform:"uppercase",
          }}>{t}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab==="overview"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
            {statBox("WAITLIST", waitlist.length, "emails collected", "var(--up)")}
            {statBox("INVITE CODES", invites.length, "active codes", "var(--cream)")}
            {statBox("ALERTS SET", alerts.length, "price alerts active", "var(--amb)")}
            {statBox("YOUR TIER", tier.toUpperCase(), "current plan", "var(--up)")}
          </div>
          <div className="card" style={{padding:"18px 20px"}}>
            <div style={{...st,color:"var(--muted)",marginBottom:12}}>QUICK ACTIONS</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button className="btn b-gold" onClick={sendDiscord} disabled={sendingDiscord}
                style={{fontSize:11,padding:"10px 18px"}}>
                {sendingDiscord ? "Sending…" : "📡 Push Brief to Discord"}
              </button>
              <button className="btn b-ghost" onClick={()=>sendDigest()} disabled={sendingBrief}
                style={{fontSize:11,padding:"10px 18px"}}>
                {sendingBrief ? "Sending…" : "✉️ Send Brief to My Email"}
              </button>
              <button className="btn b-ghost" onClick={sendScorecard}
                style={{fontSize:11,padding:"10px 18px"}}>
                📋 Push Weekly Scorecard
              </button>
              <button className="btn b-ghost" onClick={()=>setTab("broadcast")}
                style={{fontSize:11,padding:"10px 18px"}}>
                📨 Broadcast to Waitlist
              </button>
            </div>
            {discordStatus&&<div style={{...st,color:discordStatus.startsWith("✓")?"var(--up)":"#e74c3c",marginTop:10}}>{discordStatus}</div>}
            {briefStatus&&<div style={{...st,color:briefStatus.startsWith("✓")?"var(--up)":"#e74c3c",marginTop:6}}>{briefStatus}</div>}
            {scorecardStatus&&<div style={{...st,color:scorecardStatus.startsWith("✓")?"var(--up)":"#e74c3c",marginTop:6}}>{scorecardStatus}</div>}
          </div>
        </div>
      )}

      {/* ALERTS */}
      {tab==="alerts"&&(
        <div>
          <div className="card" style={{padding:"18px 20px",marginBottom:16}}>
            <div style={{...st,color:"var(--muted)",marginBottom:14}}>NEW PRICE ALERT</div>
            <div style={{display:"grid",gridTemplateColumns:"auto auto auto auto auto",gap:8,alignItems:"center",marginBottom:10}}>
              <select className="inp" value={newAlert.symbol} onChange={e=>setNewAlert({...newAlert,symbol:e.target.value})}
                style={{fontFamily:"var(--M)",fontSize:11}}>
                {["WTI","Brent","Gold","NatGas","SPY"].map(s=><option key={s}>{s}</option>)}
              </select>
              <select className="inp" value={newAlert.condition} onChange={e=>setNewAlert({...newAlert,condition:e.target.value})}
                style={{fontFamily:"var(--M)",fontSize:11}}>
                <option value="above">Above</option>
                <option value="below">Below</option>
              </select>
              <input className="inp" type="number" placeholder="Price $" value={newAlert.price}
                onChange={e=>setNewAlert({...newAlert,price:e.target.value})}
                style={{fontFamily:"var(--M)",fontSize:11,width:90}}/>
              <select className="inp" value={newAlert.type} onChange={e=>setNewAlert({...newAlert,type:e.target.value})}
                style={{fontFamily:"var(--M)",fontSize:11}}>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
              <input className="inp" placeholder={newAlert.type==="sms"?"+1 555-0100":"you@email.com"} value={newAlert.to}
                onChange={e=>setNewAlert({...newAlert,to:e.target.value})}
                style={{fontFamily:"var(--M)",fontSize:11}}/>
            </div>
            <button className="btn b-gold" onClick={addAlert} style={{fontSize:11,padding:"9px 18px"}}>+ Add Alert</button>
          </div>

          {alerts.length === 0 ? (
            <div style={{...st,color:"var(--muted)",textAlign:"center",padding:"40px 0"}}>No alerts set yet.</div>
          ) : alerts.map(a=>(
            <div key={a.id} className="card" style={{padding:"12px 18px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <span style={{fontFamily:"var(--M)",fontSize:12,color:"var(--cream)"}}>{a.symbol}</span>
                <span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--muted)",margin:"0 8px"}}>{a.condition}</span>
                <span style={{fontFamily:"var(--M)",fontSize:14,color:"var(--up)",fontWeight:600}}>${a.price}</span>
                <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginLeft:12}}>→ {a.type} · {a.to}</span>
              </div>
              <button onClick={()=>removeAlert(a.id)} style={{background:"rgba(184,50,72,0.1)",border:"1px solid rgba(184,50,72,0.3)",
                color:"#b83248",borderRadius:4,padding:"4px 10px",cursor:"pointer",fontFamily:"var(--M)",fontSize:10}}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {/* INVITES */}
      {tab==="invites"&&(
        <div>
          <div className="card" style={{padding:"18px 20px",marginBottom:16}}>
            <div style={{...st,color:"var(--muted)",marginBottom:12}}>ADD INVITE CODE</div>
            <div style={{display:"flex",gap:8}}>
              <input className="inp" value={newCode} onChange={e=>setNewCode(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&addInviteCode()}
                placeholder="NEW-CODE-2025" style={{flex:1,fontFamily:"var(--M)",letterSpacing:"0.1em",textTransform:"uppercase"}}/>
              <button className="btn b-gold" onClick={addInviteCode} style={{fontSize:11,padding:"10px 16px"}}>Add</button>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
            {invites.map((code,i)=>(
              <div key={i} style={{background:"var(--raised)",border:"1px solid var(--border)",borderRadius:6,
                padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--up)",letterSpacing:"0.1em"}}>{code}</span>
                <button onClick={()=>setInvites(invites.filter((_,j)=>j!==i))} style={{background:"transparent",
                  border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14,padding:0}}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* WAITLIST */}
      {tab==="waitlist"&&(
        <div>
          <div style={{...st,color:"var(--muted)",marginBottom:14}}>{waitlist.length} EMAIL{waitlist.length!==1?"S":""} ON WAITLIST</div>
          {waitlist.length === 0 ? (
            <div style={{...st,color:"var(--muted)",textAlign:"center",padding:"60px 0"}}>
              No waitlist signups yet. Share getfolio.ink to start collecting.
            </div>
          ) : waitlist.map((email,i)=>(
            <div key={i} className="card" style={{padding:"10px 16px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontFamily:"var(--M)",fontSize:12,color:"var(--dim)"}}>{email}</span>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>sendDigest(email)} style={{background:"rgba(196,164,74,.1)",border:"1px solid rgba(196,164,74,.2)",
                  color:"var(--up)",borderRadius:4,padding:"4px 10px",cursor:"pointer",fontFamily:"var(--M)",fontSize:10}}>
                  Send Brief
                </button>
                <button onClick={()=>{const l=waitlist.filter((_,j)=>j!==i);setWaitlist(l);localStorage.setItem("folio_waitlist",JSON.stringify(l));}}
                  style={{background:"rgba(184,50,72,0.1)",border:"1px solid rgba(184,50,72,0.3)",
                  color:"#b83248",borderRadius:4,padding:"4px 10px",cursor:"pointer",fontFamily:"var(--M)",fontSize:10}}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BROADCAST */}
      {tab==="broadcast"&&(
        <div>
          <div className="card" style={{padding:"22px 24px"}}>
            <div style={{...st,color:"var(--muted)",marginBottom:16}}>BROADCAST ACTIONS</div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>

              <div style={{background:"var(--raised)",borderRadius:8,padding:"16px 18px",border:"1px solid var(--border)"}}>
                <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--cream)",marginBottom:4}}>📡 Push to Discord Channel</div>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginBottom:12}}>
                  Generates AI brief + posts rich embed to your Folio Discord server
                </div>
                <button className="btn b-gold" onClick={sendDiscord} disabled={sendingDiscord} style={{fontSize:11,padding:"10px 20px"}}>
                  {sendingDiscord?"Sending…":"Send Now →"}
                </button>
                {discordStatus&&<div style={{...st,color:discordStatus.startsWith("✓")?"var(--up)":"#e74c3c",marginTop:8}}>{discordStatus}</div>}
              </div>

              <div style={{background:"var(--raised)",borderRadius:8,padding:"16px 18px",border:"1px solid var(--border)"}}>
                <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--cream)",marginBottom:4}}>✉️ Email Brief to All Waitlist</div>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginBottom:12}}>
                  Sends full HTML digest to {waitlist.length} email{waitlist.length!==1?"s":""} on waitlist
                </div>
                <button className="btn b-ghost" onClick={()=>waitlist.forEach(email=>sendDigest(email))}
                  disabled={sendingBrief||waitlist.length===0} style={{fontSize:11,padding:"10px 20px"}}>
                  {sendingBrief?"Sending…":`Send to ${waitlist.length} subscriber${waitlist.length!==1?"s":""} →`}
                </button>
                {briefStatus&&<div style={{...st,color:briefStatus.startsWith("✓")?"var(--up)":"#e74c3c",marginTop:8}}>{briefStatus}</div>}
              </div>

              <div style={{background:"var(--raised)",borderRadius:8,padding:"16px 18px",border:"1px solid var(--border)"}}>
                <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--cream)",marginBottom:4}}>📋 Weekly Signal Scorecard</div>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",marginBottom:12}}>
                  Generates scorecard from current signals and posts to Discord. Best run Friday after Baker Hughes.
                </div>
                <button className="btn b-ghost" onClick={sendScorecard} style={{fontSize:11,padding:"10px 20px"}}>
                  Generate + Push Scorecard →
                </button>
                {scorecardStatus&&<div style={{...st,color:scorecardStatus.startsWith("✓")?"var(--up)":"#e74c3c",marginTop:8}}>{scorecardStatus}</div>}
              </div>

              <div style={{background:"rgba(196,164,74,0.05)",borderRadius:8,padding:"14px 18px",border:"1px solid rgba(196,164,74,0.1)"}}>
                <div style={{...st,color:"var(--up)",marginBottom:8}}>SETUP REQUIRED</div>
                <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",lineHeight:1.9}}>
                  DISCORD_WEBHOOK_URL → Vercel env var → Discord channel settings → Integrations → New Webhook<br/>
                  RESEND_API_KEY → resend.com free tier → 3,000 emails/month<br/>
                  TWILIO_SID + TWILIO_AUTH + TWILIO_FROM → twilio.com → for SMS alerts<br/>
                  DIGEST_FROM → your verified sender email e.g. brief@getfolio.ink
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SIGNAL HEALTH */}
      {tab==="health"&&(
        <div>
          <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--muted)",letterSpacing:"0.1em",marginBottom:16}}>
            SIGNAL HEALTH LOG
          </div>
          <div className="card" style={{padding:"18px 20px",marginBottom:16}}>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:12,letterSpacing:"0.08em"}}>
              CURRENT SIGNAL STATUS
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
              {[
                {id:"prices",    label:"PRICES",    icon:"📈"},
                {id:"eia",       label:"EIA",        icon:"🛢"},
                {id:"cftc",      label:"CFTC COT",   icon:"📊"},
                {id:"baker_hughes",label:"RIGS",     icon:"⚙"},
                {id:"bdti",      label:"TANKERS",    icon:"🚢"},
                {id:"chokepoints",label:"CHOKEPOINTS",icon:"🌍"},
                {id:"opec",      label:"OPEC",        icon:"🏭"},
                {id:"predictions",label:"PREDICTIONS",icon:"🎯"},
              ].map(({ id, label, icon }) => (
                <div key={id} style={{background:"var(--raised)",border:"1px solid var(--border)",
                  borderRadius:6,padding:"10px 12px",textAlign:"center"}}>
                  <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
                  <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",letterSpacing:"0.08em",marginBottom:4}}>{label}</div>
                  <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",letterSpacing:"0.06em"}}>LIVE</div>
                </div>
              ))}
            </div>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",lineHeight:2}}>
              Full signal health audit logs are written to Firestore at <code style={{color:"var(--up)"}}>signal_health_log</code> collection each morning.<br/>
              The 4am cron sends an SMS alert if health drops below 60%.<br/>
              View detailed logs at <a href="https://console.firebase.google.com" style={{color:"var(--up)"}}>Firebase Console</a> → Firestore → signal_health_log
            </div>
          </div>
          <div className="card" style={{padding:"18px 20px"}}>
            <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",marginBottom:12,letterSpacing:"0.08em"}}>
              FIRESTORE COLLECTIONS (written by cron)
            </div>
            {[
              {col:"signal_snapshots", desc:"Daily price/inventory/COT/BDTI snapshot — powers 7-day delta calculations"},
              {col:"daily_briefs",     desc:"Cited brief JSON from each morning run — powers cited brief UI"},
              {col:"signal_health_log",desc:"Full source health audit for every cron run"},
              {col:"subscribers",     desc:"Users who signed in — receive morning brief email"},
              {col:"waitlist",        desc:"Waitlist emails — also receive morning brief"},
              {col:"api_keys",        desc:"Enterprise API keys — managed from API Keys tab"},
              {col:"api_call_log",    desc:"Every enterprise API call logged here"},
            ].map(({ col, desc }) => (
              <div key={col} style={{borderBottom:"1px solid var(--border)",padding:"10px 0",
                display:"flex",gap:12,alignItems:"flex-start"}}>
                <code style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)",flexShrink:0,minWidth:160}}>{col}</code>
                <span style={{fontFamily:"var(--P)",fontSize:12,color:"var(--muted)"}}>{desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};




// ─── SETTINGS PAGE ────────────────────────────────────────────────────────────
const SettingsPage = ({ darkMode, setDarkMode, settings, setSettings, user, onSignOut, onSignIn }) => {
  const st = { fontFamily: "var(--M)", fontSize: 10, letterSpacing: "0.08em" };
  const toggle = (key) => setSettings(s => ({ ...s, sections: { ...s.sections, [key]: !s.sections[key] } }));

  const Row = ({ label, desc, checked, onChange }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0",
      borderBottom: "1px solid var(--border)" }}>
      <div style={{ flex: 1 }}>
        <div style={{ ...st, color: "var(--cream)", marginBottom: 2 }}>{label}</div>
        {desc && <div style={{ fontFamily: "var(--P)", fontSize: 11, color: "var(--muted)" }}>{desc}</div>}
      </div>
      <button onClick={onChange}
        style={{ width: 38, height: 22, borderRadius: 11, border: "none", cursor: "pointer",
          background: checked ? "var(--up)" : "var(--border2)", position: "relative", transition: "background .2s" }}>
        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute",
          top: 3, left: checked ? 19 : 3, transition: "left .2s" }}/>
      </button>
    </div>
  );

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "var(--D)", fontSize: 28, fontWeight: 700, color: "var(--cream)", marginBottom: 6 }}>
          Settings
        </div>
        <div style={{ fontFamily: "var(--P)", fontSize: 13, color: "var(--dim)" }}>
          Customize your Folio experience.
        </div>
      </div>

      {/* Account */}
      <div className="card" style={{ padding: "18px 20px", marginBottom: 16 }}>
        <div style={{ ...st, color: "var(--muted)", marginBottom: 14 }}>ACCOUNT</div>
        {user ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%",
              background: "linear-gradient(135deg,var(--up),#a07828)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--M)", fontSize: 14, fontWeight: 700, color: "#080500" }}>
              {user.avatar}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "var(--D)", fontSize: 14, color: "var(--cream)", marginBottom: 2 }}>{user.name}</div>
              <div style={{ ...st, color: "var(--muted)" }}>{user.email}</div>
            </div>
            <button className="btn b-ghost" onClick={onSignOut}
              style={{ padding: "8px 14px", fontSize: 10, color: "#e74c3c", borderColor: "rgba(231,76,60,.3)" }}>
              <Icon name="log-out" size={11} color="#e74c3c"/> Sign Out
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, fontFamily: "var(--P)", fontSize: 13, color: "var(--dim)" }}>
              Sign in to sync your preferences and access premium features.
            </div>
            <button className="btn b-gold" onClick={onSignIn} style={{ padding: "9px 16px", fontSize: 11 }}>
              Sign In
            </button>
          </div>
        )}
      </div>

      {/* Appearance */}
      <div className="card" style={{ padding: "18px 20px", marginBottom: 16 }}>
        <div style={{ ...st, color: "var(--muted)", marginBottom: 14 }}>APPEARANCE</div>
        <Row label="Dark Mode" desc="Use dark theme across the app" checked={darkMode} onChange={() => setDarkMode(d => !d)} />
        <Row label="Oil Gauge" desc="Show oil futures signal widget in sidebar" checked={settings.sections?.gauge !== false} onChange={() => toggle("gauge")} />
        <Row label="Tech News" desc="Show quantum & AI news tab" checked={settings.sections?.tech !== false} onChange={() => toggle("tech")} />
      </div>

      {/* Data */}
      <div className="card" style={{ padding: "18px 20px", marginBottom: 16 }}>
        <div style={{ ...st, color: "var(--muted)", marginBottom: 14 }}>DATA & UPDATES</div>
        <div style={{ fontFamily: "var(--P)", fontSize: 12, color: "var(--dim)", lineHeight: 1.7 }}>
          Ticker tape and market quotes update every 60 seconds via Yahoo Finance.<br/>
          Gainers &amp; Losers refresh every 30 minutes via Alpha Vantage.<br/>
          News auto-refreshes every 5 minutes from Google News, MarketWatch, and Reuters.<br/>
          Oil desk data: EIA.gov (inventory), CFTC (COT), Baker Hughes (rig count).<br/>
          Prediction markets pull live from Polymarket and Kalshi APIs.
        </div>
      </div>

      {/* Version */}
      <div style={{ ...st, color: "var(--muted)", textAlign: "center", marginTop: 24 }}>
        FOLIO PRIVATE ACCESS · v2.0 · getfolio.ink
      </div>
    </div>
  );
};

// ─── PRICING ──────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
// FOLIO LANDING PAGE — Bloomberg-inspired. Clean. White on black.
// ══════════════════════════════════════════════════════════════════════════════
const FolioLanding = ({ onNavigate, tier, tape, user, onSignIn }) => {
  const [hovered, setHovered] = React.useState(null);
  const [pressed, setPressed] = React.useState(null);
  const MONO = "'IBM Plex Mono', monospace";

  const MODULES = [
    {
      id: "markets",
      badge: "FREE",
      badgeStyle: { background: "#0d2010", color: "#4ade80", border: "1px solid #1a4025" },
      accentColor: "#4ade80",
      dimColor: "#2a5535",
      title: "MARKETS",
      subtitle: "EQUITY & MACRO",
      desc: "Live quotes across 16 instruments. S&P 500, Nasdaq, VIX, 10Y Treasury, DXY, Bitcoin, Gold. Top gainers and losers updated every 30 minutes.",
      sources: "Yahoo Finance · Alpha Vantage",
      stat: "16 INSTRUMENTS",
      price: null,
      view: "markets",
      lines: ["S&P 500  ·  Nasdaq  ·  Dow", "VIX  ·  DXY  ·  10Y Treasury", "BTC  ·  Gold  ·  WTI", "Top Gainers  ·  Top Losers"],
    },
    {
      id: "oildesk",
      badge: "$19 / MO",
      badgeStyle: { background: "#1a1000", color: "#d4a017", border: "1px solid #3a2500" },
      accentColor: "#d4a017",
      dimColor: "#5a4010",
      title: "OIL DESK",
      subtitle: "CRUDE INTELLIGENCE",
      desc: "EIA weekly crude inventory vs analyst estimates. CFTC managed money long/short ratio. WTI/Brent spread. Baker Hughes rig count trends.",
      sources: "EIA · CFTC · Baker Hughes",
      stat: "EIA THU 10:30AM ET",
      price: "$19",
      view: "oildesk",
      lines: ["WTI Crude  ·  Brent  ·  Spread", "EIA Inventory  ·  Estimates", "CFTC Long/Short Ratio", "Baker Hughes Rig Count"],
    },
    {
      id: "freight",
      badge: "$14 / MO",
      badgeStyle: { background: "#00101a", color: "#38bdf8", border: "1px solid #003550" },
      accentColor: "#38bdf8",
      dimColor: "#104060",
      title: "FREIGHT",
      subtitle: "RATES & CAPACITY",
      desc: "DAT spot rates for dry van, reefer, and flatbed by region. National diesel index. Load-to-truck ratio as a capacity signal. FreightWaves SONAR data.",
      sources: "DAT · FreightWaves · DOE",
      stat: "LIVE RATES",
      price: "$14",
      view: "freight",
      lines: ["Dry Van  ·  Reefer  ·  Flatbed", "Regional Spot Rates  ·  DAT", "Diesel Index  ·  DOE Weekly", "Load-to-Truck Ratio"],
    },
    {
      id: "weather",
      badge: "$14 / MO",
      badgeStyle: { background: "#080818", color: "#818cf8", border: "1px solid #1a1a45" },
      accentColor: "#818cf8",
      dimColor: "#282870",
      title: "WEATHER",
      subtitle: "COMMODITY ALERTS",
      desc: "Active NOAA alerts mapped to commodity futures in real time. Every hurricane, drought, frost, and heat dome scored for market impact on corn, wheat, nat gas, crude.",
      sources: "NOAA · NHC · USDA",
      stat: "NOAA LIVE",
      price: "$14",
      view: "weather",
      lines: ["Hurricane  ·  Drought  ·  Frost", "Corn  ·  Wheat  ·  Nat Gas", "Market Impact Scoring", "USDA Drought Monitor"],
    },
    {
      id: "predictions",
      badge: "$9 / MO",
      badgeStyle: { background: "#0a0520", color: "#a78bfa", border: "1px solid #2a1560" },
      accentColor: "#a78bfa",
      dimColor: "#3a2880",
      title: "PREDICTIONS",
      subtitle: "PREDICTION MARKETS",
      desc: "Polymarket and Kalshi divergence signals. Where prediction markets disagree with futures pricing. Fed cuts, recession probability, S&P 500 year-end targets.",
      sources: "Polymarket · Kalshi",
      stat: "LIVE MARKETS",
      price: "$9",
      view: "predictions",
      lines: ["🎯  Fed Rate Cuts  ·  May FOMC", "📊  Recession Probability", "⚡  S&P 500 EOY Target", "🔮  Crypto  ·  Energy  ·  Macro"],
    },
    {
      id: "ai_brief",
      badge: "$9 / MO",
      badgeStyle: { background: "#150800", color: "#f59e0b", border: "1px solid #3d1800" },
      accentColor: "#f59e0b",
      dimColor: "#6a3800",
      title: "INTELLIGENCE",
      subtitle: "AI MARKET BRIEF",
      desc: "Cross-asset synthesis by Claude AI. Pulls from all five desks and produces a three-bullet executive risk summary. Generated on demand.",
      sources: "Claude Sonnet · All Desks",
      stat: "ON DEMAND",
      price: "$9",
      view: "markets",
      lines: ["Cross-asset synthesis", "Three-bullet risk summary", "Pulls all five data desks", "Generated on demand"],
    },
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#080808", fontFamily:MONO, color:"#ffffff" }}>

      {/* MASTHEAD */}
      <div style={{ textAlign:"center", padding:"44px 20px 32px", borderBottom:"1px solid #141414" }}>
        <div style={{ fontFamily:MONO, fontSize:10, color:"#333", letterSpacing:".35em", marginBottom:10, textTransform:"uppercase" }}>
          Commodity Intelligence Terminal
        </div>
        <div style={{ fontFamily:MONO, fontSize:56, color:"#ffffff", letterSpacing:".1em", fontWeight:700, lineHeight:1, marginBottom:10 }}>
          FOLIO
        </div>
        <div style={{ fontFamily:MONO, fontSize:9, color:"#333", letterSpacing:".2em", marginBottom:24 }}>
          SIX DESKS · ONE TERMINAL · REAL DATA
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
          {user ? (
            <button onClick={()=>onNavigate("markets")} style={{
              fontFamily:MONO, fontSize:9, letterSpacing:".2em",
              background:"#ffffff", border:"none", color:"#080808",
              padding:"9px 28px", cursor:"pointer", fontWeight:700, borderRadius:3,
            }}>ENTER TERMINAL →</button>
          ) : (
            <>
              <button onClick={onSignIn} style={{
                fontFamily:MONO, fontSize:9, letterSpacing:".2em",
                background:"#ffffff", border:"none", color:"#080808",
                padding:"9px 28px", cursor:"pointer", fontWeight:700, borderRadius:3,
              }}>GET ACCESS</button>
              <button onClick={()=>onNavigate("markets")} style={{
                fontFamily:MONO, fontSize:9, letterSpacing:".2em",
                background:"transparent", border:"1px solid #2a2a2a",
                color:"#555", padding:"9px 24px", cursor:"pointer", borderRadius:3,
              }}>EXPLORE FREE →</button>
            </>
          )}
        </div>
      </div>

      {/* 2-COL GRID */}
      <div style={{
        display:"grid", gridTemplateColumns:"1fr 1fr",
        maxWidth:1100, margin:"0 auto",
        borderLeft:"1px solid #141414", borderRight:"1px solid #141414",
      }}>
        {MODULES.map((mod, i) => {
          const isHov = hovered === i;
          const isPrs = pressed === i;
          return (
            <div key={mod.id}
              onClick={()=>onNavigate(mod.view)}
              onMouseEnter={()=>setHovered(i)}
              onMouseLeave={()=>{ setHovered(null); setPressed(null); }}
              onMouseDown={()=>setPressed(i)}
              onMouseUp={()=>setPressed(null)}
              style={{
                background: isPrs ? "#050505" : isHov ? "#0d0d0d" : "#080808",
                padding:"28px 28px 22px",
                cursor:"pointer",
                transition:"background 0.1s, box-shadow 0.15s, transform 0.08s",
                boxShadow: isPrs
                  ? `inset 0 4px 14px rgba(0,0,0,0.95), 0 0 10px ${mod.accentColor}12`
                  : isHov
                  ? `inset 0 2px 6px rgba(0,0,0,0.5), 0 0 0 1px ${mod.accentColor}33, 0 0 30px ${mod.accentColor}14, 0 0 70px ${mod.accentColor}07`
                  : `inset 0 1px 3px rgba(0,0,0,0.3)`,
                transform: isPrs ? "translateY(2px) scale(0.988)" : isHov ? "translateY(0.5px) scale(0.997)" : "none",
                position:"relative",
                borderRight: i % 2 === 0 ? "1px solid #141414" : "none",
                borderBottom: i < 4 ? "1px solid #141414" : "none",
                minHeight: 220,
              }}
            >
              {isHov && (
                <div style={{
                  position:"absolute", top:0, left:0, right:0, height:2,
                  background:`linear-gradient(90deg, transparent, ${mod.accentColor}55, transparent)`,
                }}/>
              )}

              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
                <div>
                  <div style={{ fontFamily:MONO, fontSize:20, color:"#ffffff", letterSpacing:".05em", fontWeight:700, lineHeight:1, marginBottom:6 }}>
                    {mod.title}
                  </div>
                  <div style={{ fontFamily:MONO, fontSize:8, color:isHov ? mod.accentColor : "#333", letterSpacing:".2em", transition:"color 0.15s" }}>
                    {mod.subtitle}
                  </div>
                </div>
                <span style={{ fontFamily:MONO, fontSize:7, letterSpacing:".15em", padding:"3px 8px", borderRadius:2, flexShrink:0, marginLeft:16, ...mod.badgeStyle }}>
                  {mod.badge}
                </span>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:14 }}>
                {mod.lines.map((line,j)=>(
                  <div key={j} style={{ fontFamily:MONO, fontSize:9, color:isHov ? "#666" : "#2e2e2e", letterSpacing:".04em", lineHeight:1.5, transition:"color 0.15s" }}>
                    {line}
                  </div>
                ))}
              </div>

              {isHov && (
                <div style={{ fontFamily:MONO, fontSize:9, color:"#444", lineHeight:1.75, marginBottom:14, letterSpacing:".02em" }}>
                  {mod.desc}
                </div>
              )}

              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                paddingTop:12, borderTop:`1px solid ${isHov ? "#1e1e1e" : "#111"}`,
                transition:"border-color 0.15s", marginTop:"auto",
              }}>
                <span style={{ fontFamily:MONO, fontSize:7, color:isHov ? mod.accentColor : "#222", letterSpacing:".18em", transition:"color 0.15s" }}>
                  {mod.sources.toUpperCase()}
                </span>
                <span style={{ fontFamily:MONO, fontSize:7, color:isHov ? "#777" : "#1a1a1a", letterSpacing:".12em", transition:"color 0.15s" }}>
                  {isHov ? "OPEN →" : mod.stat}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, maxWidth:1100, margin:"0 auto" }}>
        <span style={{ fontFamily:MONO, fontSize:7, color:"#1e1e1e", letterSpacing:".12em" }}>NOT FINANCIAL ADVICE · EDUCATIONAL USE ONLY</span>
        <span style={{ fontFamily:MONO, fontSize:7, color:"#181818", letterSpacing:".1em" }}>© 2025 FOLIO INTELLIGENCE · GETFOLIO.INK</span>
      </div>
    </div>
  );
};


const PricingPage = ({ currentTier, onSelect }) => {
  const [annual, setAnnual] = useState(false);
  return(
    <div className="fu" style={{maxWidth:980,margin:"0 auto",padding:"36px 24px"}}>
      <div style={{textAlign:"center",marginBottom:44}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:14}}>
          <Icon name="folio" size={22} color="var(--up)" strokeWidth={1.1}/>
          <span style={{fontFamily:"var(--M)",fontSize:12,color:"var(--up)",
            letterSpacing:".2em",textTransform:"uppercase"}}>FOLIO</span>
        </div>
        <div style={{fontFamily:"var(--D)",fontSize:42,color:"var(--cream)",
          fontWeight:700,lineHeight:1.08,marginBottom:14,letterSpacing:"-.02em"}}>
          Financial intelligence<br/>
          <span style={{
            background:"linear-gradient(90deg,var(--up),#a07828)",
            WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>
            for the thoughtful investor.
          </span>
        </div>
        <div style={{fontSize:15,color:"var(--dim)",fontFamily:"var(--P)",lineHeight:1.8,
          maxWidth:500,margin:"0 auto 28px"}}>
          Not a data terminal. Not a chat room. A calm, elegant briefing
          for people who want to understand — not just react to — markets.
        </div>
        <div style={{display:"inline-flex",alignItems:"center",gap:10,
          padding:"13px 20px",background:"rgba(196,164,74,.07)",
          border:"1px solid rgba(196,164,74,.25)",borderRadius:11,marginBottom:26}}>
          <Icon name="oildrop" size={16} color="var(--up)" strokeWidth={1.4}/>
          <div style={{textAlign:"left"}}>
            <div style={{fontFamily:"var(--M)",fontSize:10,fontWeight:600,
              color:"var(--up)",letterSpacing:".07em"}}>FOUNDING RATE</div>
            <div style={{fontSize:13,color:"var(--dim)"}}>
              Lock in <strong style={{color:"var(--up)"}}>$14.99/mo forever</strong>.
              384 spots remaining.
            </div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12}}>
          <span style={{fontFamily:"var(--M)",fontSize:11,
            color:annual?"var(--muted)":"var(--text)"}}>Monthly</span>
          <div onClick={()=>setAnnual(!annual)}
            style={{width:40,height:22,borderRadius:11,cursor:"pointer",
              background:annual?"var(--up)":"var(--muted)",transition:"background .2s",position:"relative"}}>
            <div style={{width:16,height:16,borderRadius:"50%",background:"#fff",
              position:"absolute",top:3,transition:"left .2s",left:annual?20:3}}/>
          </div>
          <span style={{fontFamily:"var(--M)",fontSize:11,color:annual?"var(--text)":"var(--muted)"}}>
            Annual — <span style={{color:"var(--up)"}}>2 months free</span>
          </span>
        </div>
      </div>
      <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:44}}>
        {PRICING_TIERS.map(t=>(
          <div key={t.id} style={{
            background:"var(--surface)",border:`1px solid ${t.featured?"var(--up-b)":t.id==="pro"?"var(--vio-b)":"var(--border)"}`,
            borderRadius:16,padding:"26px 22px",flex:1,minWidth:210,
            display:"flex",flexDirection:"column",gap:13,position:"relative",
            boxShadow:t.featured?"0 0 40px rgba(196,164,74,.06)":t.id==="pro"?"0 0 40px rgba(124,79,224,.05)":"none",
          }}>
            {t.badge&&(
              <div style={{position:"absolute",top:-1,left:"50%",transform:"translateX(-50%)",
                background:"linear-gradient(90deg,#c4a44a,#9c7c2c)",color:"#060400",
                fontFamily:"var(--M)",fontSize:8,fontWeight:700,letterSpacing:".09em",
                padding:"4px 14px",borderRadius:"0 0 7px 7px",textTransform:"uppercase",
                whiteSpace:"nowrap"}}>{t.badge}</div>
            )}
            <div style={{marginTop:t.badge?16:0}}>
              <div style={{fontFamily:"var(--D)",fontSize:22,color:"var(--cream)",fontWeight:700}}>{t.name}</div>
              <div style={{fontSize:12,color:"var(--dim)",fontFamily:"var(--P)"}}>{t.tagline}</div>
            </div>
            <div style={{display:"flex",alignItems:"baseline",gap:4}}>
              <span style={{fontFamily:"var(--M)",fontSize:30,fontWeight:600,
                color:t.price===0?"var(--muted)":t.color}}>
                {t.price===0?"Free":`$${annual&&t.annual?Math.round(t.annual/12*10)/10:t.price}`}
              </span>
              {t.price>0&&<span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--muted)"}}>/mo</span>}
            </div>
            {t.price>0&&annual&&t.annual&&(
              <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--up)"}}>
                ${t.annual}/yr · save ${(t.price*12-t.annual).toFixed(0)}
              </div>
            )}
            {t.note&&<div style={{fontSize:11,color:"var(--up)",fontFamily:"var(--M)",lineHeight:1.5}}>{t.note}</div>}
            {t.id===currentTier?(
              <div style={{padding:"11px",borderRadius:8,border:"1px solid var(--border)",
                textAlign:"center",fontFamily:"var(--M)",fontSize:11,color:"var(--dim)",
                textTransform:"uppercase",letterSpacing:".07em"}}>Current Plan</div>
            ):(
              <button className={`btn ${t.style}`} onClick={()=>{ if(t.id==="free") onSelect(t.id); else stripeCheckout(t.id, annual); }} style={{width:"100%",fontSize:11}}>
                {t.cta}
              </button>
            )}
            <div style={{height:1,background:"linear-gradient(90deg,transparent,var(--border2),transparent)"}}/>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {t.features.map((f,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:7}}>
                  {!(i===0&&f.includes("Everything"))&&(
                    <div style={{marginTop:2,opacity:.7,flexShrink:0}}>
                      <Icon name="chart" size={11} color="var(--up)" strokeWidth={1.5}/>
                    </div>
                  )}
                  <span style={{fontSize:12,
                    color:i===0&&f.includes("Everything")?"var(--muted)":"var(--soft)",
                    fontFamily:"var(--P)",lineHeight:1.4}}>{f}</span>
                </div>
              ))}
              {t.locked&&t.locked.map((f,i)=>(
                <div key={`l${i}`} style={{display:"flex",alignItems:"center",gap:7}}>
                  <Icon name="lock" size={10} color="var(--muted)" strokeWidth={1.4}/>
                  <span style={{fontSize:12,color:"var(--muted)",fontFamily:"var(--P)"}}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Personas */}
      <div style={{marginBottom:32}}>
        <div style={{fontFamily:"var(--D)",fontSize:26,color:"var(--cream)",fontWeight:700,textAlign:"center",marginBottom:6}}>
          Designed for two kinds of readers.
        </div>
        <div style={{fontSize:14,color:"var(--dim)",textAlign:"center",fontFamily:"var(--P)",
          marginBottom:24,lineHeight:1.7}}>One product. Two entirely different reasons to love it.</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          {[
            {
              icon:"signal",label:"Wall Street Traders",color:"var(--vio2)",borderColor:"var(--vio-b)",
              headline:"The signal layer Bloomberg doesn't give you.",
              body:"Bloomberg gives you data. We give you the story behind the data — in 60 seconds. Squeeze scores, weather-to-futures disruption models, and AI synthesis your desk would spend hours producing.",
              features:["Pre-market squeeze setup — 9:20am daily","Weather disruption alerts before the open","SEC Form 4 insider feed — real-time","FRED macro correlated to sector rotation","Oil futures predictive gauge"],
              note:"Pro tier · $39/mo · API access · Full data stack",
              noteColor:"var(--vio-b)",noteText:"var(--vio2)",
            },
            {
              icon:"folio",label:"Everyday Investors",color:"var(--up)",borderColor:"var(--up-b)",
              headline:"Finally understand what's moving markets.",
              body:"No finance degree required. When oil surges, we tell you why — the storm track, which refineries are at risk, what that means for your energy stocks. Written like a story, not a spreadsheet.",
              features:["Plain-English AI analysis on every mover","Weather events explained in market terms","Why your stocks are moving — in 60 seconds","Earnings surprises before they happen","Never stare at a chart confused again"],
              note:"Pulse tier · $14.99/mo founding rate · Cancel anytime",
              noteColor:"var(--up-b)",noteText:"var(--up)",
            },
          ].map((p,i)=>(
            <div key={i} className="persona" style={{border:`1px solid ${p.borderColor}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                <Icon name={p.icon} size={16} color={p.color} strokeWidth={1.3}/>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:p.color,
                  letterSpacing:".1em",textTransform:"uppercase"}}>{p.label}</div>
              </div>
              <div style={{fontFamily:"var(--D)",fontSize:19,color:"var(--cream)",marginBottom:10,lineHeight:1.3}}>{p.headline}</div>
              <div style={{fontSize:13,color:"var(--dim)",lineHeight:1.8,fontFamily:"var(--P)",marginBottom:16}}>{p.body}</div>
              {p.features.map((f,j)=>(
                <div key={j} style={{display:"flex",gap:9,alignItems:"center",marginBottom:6}}>
                  <Icon name="chart" size={11} color={p.color} strokeWidth={1.6}/>
                  <span style={{fontSize:12,color:"var(--soft)",fontFamily:"var(--P)"}}>{f}</span>
                </div>
              ))}
              <div style={{marginTop:14,padding:"9px 13px",background:`${p.noteColor}`,opacity:.9,
                borderRadius:8,border:`1px solid ${p.noteColor}`,
                fontFamily:"var(--M)",fontSize:10,color:p.noteText}}>{p.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── FREIGHT DESK PAGE ────────────────────────────────────────────────────────
// The vertical that freight brokers, logistics operators, and commodity desks
// will actually pay for. Synthesizes: BDI · Brent/WTI spread · port congestion
// · Gulf weather · shipping news · AI signal — before their first call.
// Tier: "freight" at $49/mo (shown as upgrade prompt for pulse/free users)

const FREIGHT_NEWS_FEEDS = [
  { label:"SHIPPING",  q:"shipping freight rates tanker" },
  { label:"PORTS",     q:"port congestion delay container ship" },
  { label:"OIL TANKER",q:"crude oil tanker VLCC market rates" },
  { label:"BALTIC",    q:"Baltic Dry Index BDI bulk carrier" },
  { label:"LNG",       q:"LNG freight market spot rate" },
];

const useFreightNews = (feedIdx) => {
  const [items,   setItems]   = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const feed = FREIGHT_NEWS_FEEDS[feedIdx] || FREIGHT_NEWS_FEEDS[0];

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    const go = async () => {
      try {
        const q    = encodeURIComponent(feed.q);
        const rss  = `https://news.google.com/rss/search?q=${q}&hl=en-US&gl=US&ceid=US:en`;
        const api  = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rss)}&count=8`;
        const res  = await fetch(api, { signal: AbortSignal.timeout(10000) });
        const data = await res.json();
        if (alive) setItems((data.items || []).slice(0, 7).map(i => ({
          title:  i.title?.replace(/<[^>]+>/g,"").slice(0,90),
          link:   i.link,
          source: i.author || "Google News",
          date:   i.pubDate,
        })));
      } catch(e) {}
      if (alive) setLoading(false);
    };
    go();
    return () => { alive = false; };
  }, [feed.q]);

  return { items, loading };
};

// Live freight market data — Yahoo Finance proxied via allorigins
// Symbols: BDI proxy via BDRY ETF, crude spread via BNO vs USO, dry bulk stocks
const FREIGHT_SYMBOLS = {
  "BDRY":   { label:"Dry Bulk ETF",  desc:"BDI proxy" },
  "STNG":   { label:"Scorpio Tankers",desc:"Product tanker" },
  "FRO":    { label:"Frontline",     desc:"VLCC crude tanker" },
  "INSW":   { label:"Intl Seaways",  desc:"Tanker fleet" },
  "SBLK":   { label:"Star Bulk",     desc:"Dry bulk" },
  "MATX":   { label:"Matson",        desc:"Container/Pacific" },
  "GSL":    { label:"Global Ship Lease",desc:"Container lessor" },
  "ZIM":    { label:"ZIM Integrated",desc:"Container shipping" },
};

const useFreightQuotes = () => {
  const [quotes, setQuotes] = React.useState({});
  React.useEffect(() => {
    const go = async () => {
      try {
        const syms = Object.keys(FREIGHT_SYMBOLS).join(",");
        const url  = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${syms}&fields=symbol,regularMarketPrice,regularMarketChangePercent,regularMarketVolume`;
        const proxy = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        const res  = await fetch(proxy, { signal: AbortSignal.timeout(12000) });
        const data = await res.json();
        const q = {};
        (data?.quoteResponse?.result || []).forEach(r => { q[r.symbol] = r; });
        setQuotes(q);
      } catch(e) {}
    };
    go();
    const t = setInterval(go, 90000);
    return () => clearInterval(t);
  }, []);
  return quotes;
};

// Pulls server-side cached freight data from /api/freight
// Gives us EIA diesel, USDA truck rates, Panama Canal status
// without exposing API keys to the client
const useServerFreight = () => {
  const [serverData, setServerData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    fetch("/api/freight", { signal: AbortSignal.timeout(12000) })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setServerData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return { serverData, loading };
};

// AI Synthesis for freight operators — reads live data, generates actionable signal
const useFreightBrief = (quotes, newsHeadlines) => {
  const [brief,    setBrief]   = React.useState(null);
  const [loading,  setLoading] = React.useState(false);
  const [lastRun,  setLastRun] = React.useState(null);

  const generate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const qSummary = Object.entries(quotes).map(([sym, q]) => {
        const chg = q.regularMarketChangePercent;
        return `${sym} $${(q.regularMarketPrice||0).toFixed(2)} (${chg>=0?"+":""}${(chg||0).toFixed(2)}%)`;
      }).join(", ");

      const payload = `FREIGHT EQUITIES: ${qSummary || "loading"} | TOP HEADLINES: ${newsHeadlines.slice(0,3).map(h=>h.title).join("; ")}`;

      const res = await fetch("/api/brief", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          custom_payload: payload,
          system_override: `You are Folio's freight and commodity intelligence analyst — you think like a senior broker at Clarksons or Braemar. Your audience: freight brokers, logistics operators, tanker charterers, dry bulk traders. Write with precision. Use industry terms: TCE, VLCC, Capesize, spot vs contract, laycan, demurrage. Structure: 1) Rate environment signal today 2) Key risk or opportunity in the next 5 trading days 3) One specific trade or hedge idea. No disclaimers. No "it's important to note." Just the signal.`,
        }),
      });
      const data = await res.json();
      const txt  = data.choices?.[0]?.message?.content || data.content?.[0]?.text || data.brief || "";
      if (txt) { setBrief(txt); setLastRun(new Date()); }
    } catch(e) {}
    setLoading(false);
  };

  return { brief, loading, lastRun, generate };
};

// Port congestion indicators — curated static data updated with AI context
// In production, replace with Portcast API or Marine Traffic API
const PORT_STATUS = [
  { port:"Los Angeles / Long Beach", region:"US West",  wait:"2.1d", trend:"▼", sentiment:"easing",  color:"var(--up)"  },
  { port:"Rotterdam",                region:"N.Europe", wait:"0.8d", trend:"→", sentiment:"stable",   color:"var(--amb)" },
  { port:"Singapore",                region:"SE Asia",  wait:"1.4d", trend:"▲", sentiment:"building", color:"var(--dn)"  },
  { port:"Shanghai / Ningbo",        region:"China",    wait:"3.2d", trend:"▲", sentiment:"congested",color:"var(--dn)"  },
  { port:"Houston",                  region:"US Gulf",  wait:"0.5d", trend:"▼", sentiment:"clear",    color:"var(--up)"  },
  { port:"Jebel Ali",                region:"UAE/Gulf", wait:"1.1d", trend:"→", sentiment:"stable",   color:"var(--amb)" },
];

// ─── AIS VESSEL DATA HOOK ─────────────────────────────────────────────────────
const useAISData = () => {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    const go = async () => {
      try {
        const res  = await fetch("/api/ais-vessels", { signal: AbortSignal.timeout(8000) });
        const json = await res.json();
        if (alive) setData(json);
      } catch(e) {}
      if (alive) setLoading(false);
    };
    go();
    const t = setInterval(go, 5 * 60 * 1000); // refresh every 5 min
    return () => { alive = false; clearInterval(t); };
  }, []);

  return { data, loading };
};

// ─── PIRACY DATA HOOK ─────────────────────────────────────────────────────────
const usePiracyData = () => {
  const [incidents, setIncidents] = React.useState([]);
  const [byRegion,  setByRegion]  = React.useState({});
  const [loading,   setLoading]   = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    const go = async () => {
      try {
        const res  = await fetch("/api/piracy", { signal: AbortSignal.timeout(8000) });
        const json = await res.json();
        if (alive && json.ok) {
          setIncidents(json.incidents || []);
          setByRegion(json.byRegion || {});
        }
      } catch(e) {}
      if (alive) setLoading(false);
    };
    go();
  }, []);

  return { incidents, byRegion, loading };
};

// ─── SEVERITY COLORS ──────────────────────────────────────────────────────────
const SEVERITY_COLOR = {
  CRITICAL: "#ff1a4a",
  HIGH:     "#ff6b35",
  MODERATE: "#ffaa00",
  LOW:      "#c4a44a",
};

const RISK_COLOR = {
  CRITICAL: "#ff1a4a",
  HIGH:     "#ff6b35",
  MODERATE: "#ffaa00",
  LOW:      "#00cc88",
};

// ─── LEAFLET MAP COMPONENT ────────────────────────────────────────────────────
// Loads Leaflet.js from CDN, renders dark-themed map with:
//   • Vessel dots from AISStream (live) or MarineTraffic iframe (fallback)
//   • Piracy incident markers with severity colors
//   • Chokepoint status pins
//   • Click panel for vessel / incident detail
const FreightMap = ({ zones, incidents, activeOverlays, onVesselClick, onIncidentClick }) => {
  const mapRef      = React.useRef(null);
  const leafletRef  = React.useRef(null);
  const layersRef   = React.useRef({});
  const [ready, setReady] = React.useState(false);

  // Load Leaflet CSS + JS from CDN
  React.useEffect(() => {
    if (window.L) { setReady(true); return; }

    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload = () => setReady(true);
    document.head.appendChild(script);
  }, []);

  // Init map once Leaflet is ready
  React.useEffect(() => {
    if (!ready || !mapRef.current || leafletRef.current) return;
    const L = window.L;

    // Dark map tiles from CartoDB
    const map = L.map(mapRef.current, {
      center: [15, 55],
      zoom:   3,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/">CARTO</a>',
        maxZoom: 18,
        subdomains: "abcd",
      }
    ).addTo(map);

    leafletRef.current = map;
    layersRef.current = { vessels: L.layerGroup(), piracy: L.layerGroup(), chokepoints: L.layerGroup() };
    Object.values(layersRef.current).forEach(lg => lg.addTo(map));

    return () => { map.remove(); leafletRef.current = null; };
  }, [ready]);

  // Draw chokepoint rings
  React.useEffect(() => {
    if (!leafletRef.current || !zones) return;
    const L = window.L;
    const lg = layersRef.current.chokepoints;
    if (!lg) return;
    lg.clearLayers();
    if (!activeOverlays.chokepoints) return;

    zones.forEach(zone => {
      const col = RISK_COLOR[zone.risk] || "#aaa";

      // Pulsing circle for zone center
      const circle = L.circleMarker([zone.lat, zone.lon], {
        radius:      18,
        color:       col,
        fillColor:   col,
        fillOpacity: 0.12,
        weight:      2,
        opacity:     0.7,
      }).addTo(lg);

      // Vessel count badge
      const icon = L.divIcon({
        className: "",
        iconSize:  [0, 0],
        html: `<div style="
          background:rgba(8,5,0,.88);
          border:1.5px solid ${col};
          border-radius:8px;
          padding:4px 8px;
          font-family:monospace;
          font-size:9px;
          color:${col};
          white-space:nowrap;
          letter-spacing:.06em;
          box-shadow:0 2px 12px rgba(0,0,0,.6);
          pointer-events:none;
        ">
          <div style="font-weight:700;letter-spacing:.08em;">${zone.label.toUpperCase()}</div>
          <div style="color:#aaa;margin-top:2px;">${zone.risk} · ${zone.totalCount || 0} vessels · ${zone.anchorCount || 0} anchored</div>
        </div>`,
      });
      L.marker([zone.lat + 0.6, zone.lon], { icon }).addTo(lg);

      circle.bindPopup(`
        <div style="font-family:monospace;font-size:11px;background:#0a0c0e;color:#dce8f0;padding:10px;border-radius:6px;min-width:200px;">
          <div style="color:${col};font-weight:700;letter-spacing:.08em;margin-bottom:6px;">${zone.label.toUpperCase()}</div>
          <div style="color:#aaa;margin-bottom:4px;">Risk: <span style="color:${col}">${zone.risk}</span></div>
          <div style="color:#aaa;margin-bottom:4px;">Vessels: ${zone.totalCount || 0} · Anchored: ${zone.anchorCount || 0}</div>
          <div style="color:#607080;font-size:10px;margin-top:6px;border-top:1px solid #1a2228;padding-top:6px;">${zone.note}</div>
        </div>
      `);
    });
  }, [zones, activeOverlays.chokepoints, ready]);

  // Draw piracy incidents
  React.useEffect(() => {
    if (!leafletRef.current || !incidents) return;
    const L = window.L;
    const lg = layersRef.current.piracy;
    if (!lg) return;
    lg.clearLayers();
    if (!activeOverlays.piracy) return;

    incidents.forEach(inc => {
      const col  = SEVERITY_COLOR[inc.severity] || "#c4a44a";
      const size = inc.severity === "CRITICAL" ? 9 : inc.severity === "HIGH" ? 7 : 5;
      const opacity = inc.daysAgo < 30 ? 1.0 : inc.daysAgo < 60 ? 0.7 : 0.45;

      const marker = L.circleMarker([inc.lat, inc.lon], {
        radius:      size,
        color:       col,
        fillColor:   col,
        fillOpacity: 0.75 * opacity,
        weight:      1.5,
        opacity,
      }).addTo(lg);

      marker.bindPopup(`
        <div style="font-family:monospace;font-size:11px;background:#100304;color:#dce8f0;padding:10px;border-radius:6px;min-width:220px;max-width:260px;">
          <div style="color:${col};font-weight:700;letter-spacing:.08em;margin-bottom:4px;">⚠ ${inc.type.toUpperCase()} · ${inc.severity}</div>
          <div style="color:#aaa;margin-bottom:2px;font-size:9px;">${inc.region} · ${inc.date} (${inc.daysAgo}d ago)</div>
          <div style="color:#dce8f0;font-size:10px;margin-top:6px;line-height:1.5;">${inc.description}</div>
        </div>
      `);

      // Pulse ring for CRITICAL
      if (inc.severity === "CRITICAL") {
        L.circleMarker([inc.lat, inc.lon], {
          radius:      18,
          color:       col,
          fillColor:   "transparent",
          weight:      1,
          opacity:     0.25 * opacity,
        }).addTo(lg);
      }
    });
  }, [incidents, activeOverlays.piracy, ready]);

  // Draw vessel dots (when live AIS data is available)
  React.useEffect(() => {
    if (!leafletRef.current || !zones) return;
    const L = window.L;
    const lg = layersRef.current.vessels;
    if (!lg) return;
    lg.clearLayers();
    if (!activeOverlays.vessels) return;

    zones.forEach(zone => {
      (zone.vessels || []).forEach(v => {
        const isAnchored = v.status === 1 || v.status === 5;
        const col = isAnchored ? "#c4a44a" : "#00a0dd";

        const icon = L.divIcon({
          className: "",
          iconSize:  [8, 8],
          iconAnchor:[4, 4],
          html: `<div style="
            width:8px;height:8px;border-radius:50%;
            background:${col};
            border:1.5px solid rgba(255,255,255,.3);
            box-shadow:0 0 4px ${col}88;
          "></div>`,
        });

        const marker = L.marker([v.lat, v.lon], { icon }).addTo(lg);
        marker.bindPopup(`
          <div style="font-family:monospace;font-size:11px;background:#0a1520;color:#dce8f0;padding:10px;border-radius:6px;min-width:180px;">
            <div style="color:#00a0dd;font-weight:700;margin-bottom:4px;">${v.name}</div>
            <div style="color:#aaa;font-size:9px;margin-bottom:2px;">MMSI: ${v.mmsi}</div>
            <div style="color:#aaa;font-size:9px;">Speed: ${v.speed?.toFixed(1) || "—"} kn · Hdg: ${v.heading || "—"}°</div>
            ${v.dest ? `<div style="color:#c4a44a;font-size:9px;margin-top:4px;">→ ${v.dest}</div>` : ""}
            <div style="color:#607080;font-size:8px;margin-top:4px;">${isAnchored ? "AT ANCHOR" : "UNDERWAY"}</div>
          </div>
        `);
      });
    });
  }, [zones, activeOverlays.vessels, ready]);

  return (
    <div ref={mapRef} style={{
      width:"100%", height:"100%",
      background:"#060a10",
      borderRadius:"inherit",
    }}/>
  );
};

// ─── MARINTRAFFIC IFRAME FALLBACK ─────────────────────────────────────────────
// Used when AISSTREAM_API_KEY is not configured.
// Shows real vessel positions from MarineTraffic's public embed.
// Attribution required per MarineTraffic ToS.
const MarineTrafficFallback = () => (
  <div style={{position:"relative",width:"100%",height:"100%",borderRadius:"inherit",overflow:"hidden"}}>
    <iframe
      src="https://www.marinetraffic.com/en/ais/embed/zoom:3/centery:20/centerx:55/maptype:1/shownames:false/mmsi:0/shipid:0/fleet:/fleet_id:/vtypes:/showmenu:/remember:false"
      style={{
        width:"100%",
        height:"100%",
        border:"none",
        display:"block",
        borderRadius:"inherit",
        filter:"hue-rotate(200deg) brightness(0.7) saturate(1.2)",
      }}
      title="Live vessel tracking — MarineTraffic"
      allowFullScreen
    />
    {/* Overlay badge */}
    <div style={{
      position:"absolute",top:10,right:10,
      background:"rgba(8,5,0,.88)",
      border:"1px solid rgba(196,164,74,.2)",
      borderRadius:6,padding:"5px 10px",
      fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",
      letterSpacing:".08em",zIndex:1000,
      pointerEvents:"none",
    }}>
      LIVE · MarineTraffic
    </div>
    <div style={{
      position:"absolute",bottom:10,left:10,
      background:"rgba(8,5,0,.75)",
      borderRadius:6,padding:"4px 8px",
      fontFamily:"var(--M)",fontSize:8,color:"rgba(196,164,74,.6)",
      letterSpacing:".06em",zIndex:1000,
      pointerEvents:"none",
    }}>
      FREIGHT INTELLIGENCE · BETA
    </div>
  </div>
);

// ─── INTELLIGENCE MAP PANEL ───────────────────────────────────────────────────
const IntelligenceMap = ({ hasLiveData }) => {
  const { data: aisData } = useAISData();
  const { incidents, byRegion } = usePiracyData();

  const [overlays, setOverlays] = React.useState({
    chokepoints: true,
    piracy:      true,
    vessels:     true,
    weather:     false, // Phase 2
    rates:       false, // Phase 2
  });
  const [selected, setSelected] = React.useState(null); // selected zone key

  const toggleOverlay = (key) => setOverlays(prev => ({...prev, [key]: !prev[key]}));

  const zones    = aisData?.zones || [];
  const isLive   = aisData?.live || false;

  // Active zone for sidebar
  const activeZone = selected ? zones.find(z => z.key === selected) : null;
  const activeIncidents = activeZone
    ? incidents.filter(inc => {
        // Rough region match
        const regionMap = {
          singapore: ["Singapore Strait","Malacca Strait"],
          malacca:   ["Malacca Strait","Indonesia"],
          suez:      ["Red Sea","Gulf of Aden","Indian Ocean"],
          hormuz:    ["Strait of Hormuz"],
          guinea:    ["Gulf of Guinea","West Africa"],
        };
        return (regionMap[activeZone.key] || []).some(r => inc.region.includes(r));
      })
    : [];

  const OVERLAY_BTNS = [
    { key:"chokepoints", label:"Chokepoints", color:"#00a0dd" },
    { key:"piracy",      label:"Piracy",      color:"#ff3366" },
    { key:"vessels",     label:"Vessels",     color:"#c4a44a" },
  ];

  return (
    <div style={{background:"var(--card)",border:"1px solid var(--border)",
      borderRadius:10,overflow:"hidden",marginBottom:20}}>

      {/* Map header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"12px 16px",borderBottom:"1px solid var(--border)",
        background:"var(--raised)",flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:7,height:7,borderRadius:"50%",
            background:isLive?"#00cc88":"var(--amb)",
            animation:isLive?"breathe 2s infinite":"none"}}/>
          <span style={{fontFamily:"var(--M)",fontSize:9,
            color:isLive?"#00cc88":"var(--amb)",letterSpacing:".1em"}}>
            {isLive ? "LIVE AIS · AISSTREAM.IO" : "LIVE · MARINETRAFFIC EMBED"}
          </span>
          {incidents.length > 0 && (
            <span style={{fontFamily:"var(--M)",fontSize:9,color:"#ff3366",
              background:"rgba(255,51,102,.08)",border:"1px solid rgba(255,51,102,.2)",
              borderRadius:4,padding:"1px 7px",letterSpacing:".06em"}}>
              {incidents.length} PIRACY INCIDENTS 2025
            </span>
          )}
        </div>

        {/* Overlay toggles */}
        <div style={{display:"flex",gap:4}}>
          {OVERLAY_BTNS.map(btn => (
            <button key={btn.key} onClick={() => toggleOverlay(btn.key)}
              style={{fontFamily:"var(--M)",fontSize:8,padding:"4px 10px",
                borderRadius:4,cursor:"pointer",letterSpacing:".07em",
                background:overlays[btn.key]?`${btn.color}15`:"transparent",
                border:`1px solid ${overlays[btn.key]?btn.color:"var(--border)"}`,
                color:overlays[btn.key]?btn.color:"var(--muted)",
                transition:"all .15s"}}>
              ● {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Map + sidebar layout */}
      <div style={{display:"flex",height:480}}>

        {/* Map area */}
        <div style={{flex:1,position:"relative",minWidth:0}}>
          {isLive
            ? <FreightMap
                zones={zones}
                incidents={incidents}
                activeOverlays={overlays}
                onVesselClick={v => setSelected(null)}
                onIncidentClick={inc => setSelected(null)}
              />
            : <MarineTrafficFallback/>
          }

          {/* Chokepoint quick-select pills overlaid on map (always visible) */}
          {!isLive && zones.length > 0 && (
            <div style={{
              position:"absolute",bottom:40,left:10,right:10,
              display:"flex",gap:4,flexWrap:"wrap",
              zIndex:1001,pointerEvents:"none",
            }}>
              {zones.filter(z => z.risk !== "LOW").map(z => (
                <div key={z.key} style={{
                  background:"rgba(8,5,0,.85)",
                  border:`1px solid ${RISK_COLOR[z.risk]}44`,
                  borderRadius:4,padding:"3px 8px",
                  fontFamily:"var(--M)",fontSize:8,
                  color:RISK_COLOR[z.risk],letterSpacing:".06em",
                  pointerEvents:"none",
                }}>
                  {z.label} · {z.risk}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar — zone list + incident feed */}
        <div style={{
          width:240,flexShrink:0,
          borderLeft:"1px solid var(--border)",
          overflowY:"auto",background:"var(--base)",
        }}>
          {/* Risk zones */}
          <div style={{padding:"10px 12px 6px",fontFamily:"var(--M)",
            fontSize:8,color:"var(--muted)",letterSpacing:".1em",
            borderBottom:"1px solid var(--border)"}}>
            CHOKEPOINT RISK STATUS
          </div>
          {zones.map(z => (
            <button key={z.key}
              onClick={() => setSelected(selected === z.key ? null : z.key)}
              style={{
                width:"100%",textAlign:"left",background:"transparent",
                border:"none",cursor:"pointer",padding:"9px 12px",
                borderBottom:"1px solid var(--border)",
                borderLeft:selected===z.key?`3px solid ${RISK_COLOR[z.risk]}`:"3px solid transparent",
                transition:"background .12s",
              }}
              onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--cream)",
                  fontWeight:600}}>{z.label}</span>
                <span style={{fontFamily:"var(--M)",fontSize:8,
                  color:RISK_COLOR[z.risk],letterSpacing:".06em",fontWeight:700}}>
                  {z.risk}
                </span>
              </div>
              <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginTop:3}}>
                {z.totalCount ? `${z.totalCount} vessels · ${z.anchorCount} anchored` : "Tap to load vessel data"}
              </div>
            </button>
          ))}

          {/* Active zone incidents */}
          {activeZone && (
            <>
              <div style={{padding:"10px 12px 6px",fontFamily:"var(--M)",
                fontSize:8,color:"#ff3366",letterSpacing:".1em",
                borderBottom:"1px solid var(--border)",
                background:"rgba(255,51,102,.04)"}}>
                {activeZone.label.toUpperCase()} · INCIDENTS
              </div>
              {activeIncidents.length === 0 && (
                <div style={{padding:"12px",fontFamily:"var(--M)",
                  fontSize:9,color:"var(--muted)"}}>
                  No incidents in this zone in 2025
                </div>
              )}
              {activeIncidents.slice(0, 5).map((inc, i) => (
                <div key={i} style={{padding:"9px 12px",
                  borderBottom:"1px solid var(--border)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                    <div style={{width:5,height:5,borderRadius:"50%",flexShrink:0,
                      background:SEVERITY_COLOR[inc.severity]}}/>
                    <span style={{fontFamily:"var(--M)",fontSize:8,
                      color:SEVERITY_COLOR[inc.severity],fontWeight:700,
                      letterSpacing:".05em"}}>{inc.type.toUpperCase()}</span>
                    <span style={{fontFamily:"var(--M)",fontSize:7,
                      color:"var(--muted)",marginLeft:"auto"}}>{inc.daysAgo}d ago</span>
                  </div>
                  <div style={{fontFamily:"var(--P)",fontSize:10,color:"var(--soft)",
                    lineHeight:1.4}}>{inc.description.slice(0, 80)}…</div>
                </div>
              ))}
            </>
          )}

          {/* Global piracy summary */}
          {!activeZone && Object.entries(byRegion).length > 0 && (
            <>
              <div style={{padding:"10px 12px 6px",fontFamily:"var(--M)",
                fontSize:8,color:"#ff3366",letterSpacing:".1em",
                borderBottom:"1px solid var(--border)",
                background:"rgba(255,51,102,.04)"}}>
                2025 PIRACY BY REGION
              </div>
              {Object.entries(byRegion)
                .sort((a,b) => b[1].total - a[1].total)
                .map(([region, stats], i) => (
                <div key={i} style={{padding:"8px 12px",
                  borderBottom:"1px solid var(--border)"}}>
                  <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--cream)",
                    marginBottom:2,fontWeight:600}}>{region}</div>
                  <div style={{display:"flex",gap:8}}>
                    <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>
                      {stats.total} incidents
                    </span>
                    {stats.critical > 0 && (
                      <span style={{fontFamily:"var(--M)",fontSize:8,color:"#ff1a4a"}}>
                        {stats.critical} critical
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div style={{padding:"8px 12px",fontFamily:"var(--M)",
                fontSize:7.5,color:"var(--muted)",lineHeight:1.6}}>
                Source: IMB Piracy Reporting Centre<br/>
                Jan–Mar 2025 · Verified geocoordinates
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding:"8px 16px",
        background:"var(--raised)",
        borderTop:"1px solid var(--border)",
        display:"flex",alignItems:"center",justifyContent:"space-between",
        flexWrap:"wrap",gap:6,
      }}>
        <div style={{display:"flex",gap:16}}>
          {[
            {col:"#00cc88", label:"Low risk"},
            {col:"#ffaa00", label:"Moderate"},
            {col:"#ff6b35", label:"High"},
            {col:"#ff1a4a", label:"Critical"},
          ].map((item, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:item.col}}/>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{item.label}</span>
            </div>
          ))}
        </div>
        {!isLive && (
          <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>
            Set <code style={{color:"var(--amb)"}}>AISSTREAM_API_KEY</code> in Vercel env to enable live vessel tracking · Free at aisstream.io
          </div>
        )}
      </div>
    </div>
  );
};

const FreightDeskPage = ({ tier, onPaywall, user, onSignIn }) => {
  const [feedIdx,    setFeedIdx]    = React.useState(0);
  const [hoverSym,   setHoverSym]   = React.useState(null);
  const quotes   = useFreightQuotes();
  const { items: news, loading: newsLoading } = useFreightNews(feedIdx);
  const { brief, loading: briefLoading, lastRun, generate } = useFreightBrief(quotes, news);
  const { serverData } = useServerFreight(); // EIA diesel + Panama + USDA from /api/freight

  const fmtChg = n => `${n>=0?"+":""}${(n||0).toFixed(2)}%`;
  const isLocked = tier === "free" || tier === "pulse";

  return (
    <div>
      {/* ── Header ── */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",
        marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <div style={{width:3,height:26,background:"#00a0dd",borderRadius:2}}/>
            <div style={{fontFamily:"var(--D)",fontSize:22,fontWeight:700,
              color:"var(--cream)",letterSpacing:"-.2px"}}>Freight Desk</div>
            <span style={{fontFamily:"var(--M)",fontSize:8,color:"#00a0dd",
              border:"1px solid rgba(0,160,221,.3)",borderRadius:4,padding:"2px 7px",
              letterSpacing:".1em"}}>COMMODITY VERTICAL</span>
          </div>
          <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--dim)",
            paddingLeft:13,lineHeight:1.6}}>
            Baltic dry · tanker rates · port congestion · live vessel map · piracy overlay · AI signal<br/>
            Built for freight brokers, logistics operators & commodity desks.
          </div>
        </div>

        {/* AI Brief trigger */}
        <button
          onClick={isLocked ? onPaywall : generate}
          disabled={briefLoading}
          style={{display:"flex",alignItems:"center",gap:7,padding:"10px 18px",
            background:isLocked?"var(--raised)":"rgba(0,160,221,.1)",
            border:`1px solid ${isLocked?"var(--border)":"rgba(0,160,221,.3)"}`,
            borderRadius:8,cursor:"pointer",transition:"all .15s",flexShrink:0}}>
          <div style={{width:7,height:7,borderRadius:"50%",
            background:briefLoading?"var(--amb)":"#00a0dd",
            animation:briefLoading?"breathe 1s infinite":"none"}}/>
          <span style={{fontFamily:"var(--M)",fontSize:10,
            color:isLocked?"var(--muted)":"#00a0dd",letterSpacing:".08em"}}>
            {isLocked?"UPGRADE FOR AI SIGNAL":briefLoading?"ANALYZING…":"GENERATE FREIGHT SIGNAL"}
          </span>
        </button>
      </div>

      {/* ── AI Brief output ── */}
      {brief && (
        <div style={{marginBottom:24,padding:"18px 20px",
          background:"linear-gradient(135deg,rgba(0,160,221,.05),rgba(0,0,0,0))",
          border:"1px solid rgba(0,160,221,.2)",borderRadius:10,animation:"fadeUp .3s ease"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
            <div style={{width:5,height:5,borderRadius:"50%",background:"#00a0dd"}}/>
            <span style={{fontFamily:"var(--M)",fontSize:8,color:"#00a0dd",letterSpacing:".12em"}}>
              FOLIO FREIGHT SIGNAL · {lastRun?.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})} ET
            </span>
          </div>
          <div style={{fontFamily:"var(--P)",fontSize:13.5,color:"var(--cream)",
            lineHeight:1.8,whiteSpace:"pre-line"}}>{brief}</div>
          <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginTop:10}}>
            AI-GENERATED · NOT FINANCIAL ADVICE · FOR PROFESSIONAL USE
          </div>
        </div>
      )}

      {/* ── INTELLIGENCE MAP — full width, live vessels + piracy + chokepoints ── */}
      <IntelligenceMap hasLiveData={false}/>

      {/* ── Equities + Key Rates grid ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>

        {/* Freight Equities panel */}
        <div style={{background:"var(--card)",border:"1px solid var(--border)",
          borderRadius:10,padding:"16px 18px",gridColumn:"1/3"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14}}>
            <div style={{width:4,height:4,borderRadius:"50%",background:"#00a0dd"}}/>
            <span style={{fontFamily:"var(--M)",fontSize:9,color:"#00a0dd",
              letterSpacing:".12em"}}>SHIPPING & FREIGHT EQUITIES · LIVE</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
            {Object.entries(FREIGHT_SYMBOLS).map(([sym, meta]) => {
              const q   = quotes[sym] || {};
              const chg = q.regularMarketChangePercent;
              const isUp = chg >= 0;
              return (
                <div key={sym}
                  onMouseEnter={() => setHoverSym(sym)}
                  onMouseLeave={() => setHoverSym(null)}
                  style={{background:"var(--raised)",border:`1px solid ${hoverSym===sym?"var(--border2)":"var(--border)"}`,
                    borderRadius:8,padding:"12px 14px",cursor:"default",
                    transition:"border-color .15s,box-shadow .15s",
                    boxShadow:hoverSym===sym?"0 4px 16px rgba(0,0,0,.3)":"none",
                    position:"relative"}}>
                  <div style={{fontFamily:"var(--M)",fontSize:12,fontWeight:700,
                    color:"var(--cream)",marginBottom:2}}>{sym}</div>
                  <div style={{fontFamily:"var(--P)",fontSize:9,color:"var(--muted)",
                    marginBottom:8}}>{meta.desc}</div>

                  {/* Inline sparkline on hover */}
                  {hoverSym === sym && (
                    <div style={{position:"absolute",top:-50,left:0,right:0,
                      display:"flex",justifyContent:"center",zIndex:10,
                      pointerEvents:"none"}}>
                      <div style={{background:"var(--base)",border:"1px solid var(--border2)",
                        borderRadius:8,padding:"6px 8px",boxShadow:"0 8px 24px rgba(0,0,0,.6)"}}>
                        <HoverSparkline symbol={sym}/>
                      </div>
                    </div>
                  )}

                  <div style={{fontFamily:"var(--M)",fontSize:14,fontWeight:600,
                    color:"var(--cream)"}}>
                    {q.regularMarketPrice ? `$${q.regularMarketPrice.toFixed(2)}` : "—"}
                  </div>
                  <div style={{fontFamily:"var(--M)",fontSize:11,
                    color:chg!=null?(isUp?"var(--up)":"var(--dn)"):"var(--muted)"}}>
                    {chg != null ? fmtChg(chg) : "Loading…"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key Rate Watchlist panel */}
        <div style={{background:"var(--card)",border:"1px solid var(--border)",
          borderRadius:10,padding:"16px 18px"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14}}>
            <div style={{width:4,height:4,borderRadius:"50%",background:"var(--up)"}}/>
            <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",
              letterSpacing:".12em"}}>KEY FREIGHT RATES</span>
          </div>
          {[
            { label:"Baltic Dry Index",   sym:"BDRY", note:"ETF proxy for BDI" },
            { label:"WTI Crude",          sym:"CL=F",  note:"Bunker fuel input" },
            { label:"Brent Crude",        sym:"BZ=F",  note:"Global benchmark" },
            { label:"Nat Gas (Henry Hub)",sym:"NG=F",  note:"LNG spot proxy" },
            { label:"USD Index (DXY)",    sym:"DX-Y.NYB",note:"FX rate exposure" },
          ].map((r, i) => {
            const q   = quotes[r.sym] || {};
            const chg = q.regularMarketChangePercent;
            const isUp = chg >= 0;
            return (
              <div key={i} style={{display:"flex",alignItems:"center",
                padding:"9px 0",borderBottom:i<4?"1px solid var(--border)":"none",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--cream)",
                    fontWeight:600}}>{r.label}</div>
                  <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{r.note}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"var(--M)",fontSize:12,color:"var(--cream)",fontWeight:600}}>
                    {q.regularMarketPrice ? `$${q.regularMarketPrice.toFixed(2)}` : "—"}
                  </div>
                  <div style={{fontFamily:"var(--M)",fontSize:10,
                    color:chg!=null?(isUp?"var(--up)":"var(--dn)"):"var(--muted)"}}>
                    {chg != null ? fmtChg(chg) : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Chokepoint Status (condensed, now driven by API data) */}
        <div style={{background:"var(--card)",border:"1px solid var(--border)",
          borderRadius:10,padding:"16px 18px"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14}}>
            <div style={{width:4,height:4,borderRadius:"50%",background:"var(--amb)"}}/>
            <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--amb)",
              letterSpacing:".12em"}}>CHOKEPOINT STATUS</span>
          </div>
          {PORT_STATUS.map((p, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,
              padding:"8px 0",borderBottom:i<PORT_STATUS.length-1?"1px solid var(--border)":"none"}}>
              <div style={{width:6,height:6,borderRadius:"50%",
                background:p.color,flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:"var(--M)",fontSize:10,color:"var(--cream)",
                  fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap"}}>{p.port}</div>
                <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",
                  letterSpacing:".05em"}}>{p.region}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:"var(--M)",fontSize:11,color:p.color,fontWeight:600}}>
                  {p.trend} {p.wait} avg wait
                </div>
                <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",
                  letterSpacing:".05em",textTransform:"uppercase"}}>{p.sentiment}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Shipping News ── */}
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10}}>
        {/* Feed tabs */}
        <div style={{display:"flex",gap:0,borderBottom:"1px solid var(--border)",
          overflowX:"auto",padding:"0 16px"}}>
          {FREIGHT_NEWS_FEEDS.map((f, i) => (
            <button key={i} onClick={() => setFeedIdx(i)}
              style={{fontFamily:"var(--M)",fontSize:9,padding:"12px 14px",
                background:"transparent",border:"none",cursor:"pointer",
                color:feedIdx===i?"#00a0dd":"var(--muted)",letterSpacing:".1em",
                borderBottom:feedIdx===i?"2px solid #00a0dd":"2px solid transparent",
                whiteSpace:"nowrap",transition:"color .15s"}}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Headlines */}
        <div style={{padding:"6px 0"}}>
          {newsLoading && (
            <div style={{padding:"6px 0"}}>
              {[85,70,90,65,80].map((w,i)=>(
                <div key={i} style={{padding:"11px 18px",borderBottom:"1px solid var(--border)"}}>
                  <div className="skel" style={{height:14,borderRadius:4,marginBottom:6,width:`${w}%`}}/>
                  <div className="skel" style={{height:9,borderRadius:4,width:"35%"}}/>
                </div>
              ))}
            </div>
          )}
          {!newsLoading && news.map((item, i) => (
            <a key={i} href={item.link} target="_blank" rel="noopener noreferrer"
              style={{display:"block",padding:"11px 18px",textDecoration:"none",
                borderBottom:i<news.length-1?"1px solid var(--border)":"none",
                transition:"background .15s"}}
              onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{fontFamily:"var(--P)",fontSize:12.5,color:"var(--cream)",
                lineHeight:1.5,marginBottom:4}}>{item.title}</div>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <span style={{fontFamily:"var(--M)",fontSize:8,color:"#00a0dd"}}>
                  {item.source}
                </span>
                <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>
                  {item.date ? new Date(item.date).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : ""}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Upgrade CTA for non-freight tier */}
      {isLocked && (
        <div style={{marginTop:20,padding:"20px 24px",
          background:"linear-gradient(135deg,rgba(0,160,221,.07),rgba(0,0,0,0))",
          border:"1px solid rgba(0,160,221,.2)",borderRadius:10,
          display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontFamily:"var(--D)",fontSize:16,fontWeight:600,
              color:"var(--cream)",marginBottom:4}}>Folio Freight — $199/mo</div>
            <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--dim)",lineHeight:1.6}}>
              Live AIS vessel map · Piracy overlay · Route Intelligence Scanner · Dark Ship Pulse · AI freight signal · personalized morning brief for your routes.
            </div>
          </div>
          <button className="btn b-gold" onClick={onPaywall}
            style={{padding:"11px 22px",fontSize:11,flexShrink:0}}>
            Upgrade to Freight →
          </button>
        </div>
      )}
    </div>
  );
};


// ─── CLOCK COMPONENT (isolated — prevents 1s re-renders of entire app) ────────
const ETClock = React.memo(() => {
  const [time, setTime] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const etStr = time.toLocaleTimeString("en-US",{timeZone:"America/New_York",hour12:true,hour:"2-digit",minute:"2-digit"});
  return (
    <div style={{textAlign:"right"}}>
      <div style={{fontFamily:"var(--M)",fontSize:12,color:"var(--text)"}}>{etStr}</div>
      <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>ET</div>
    </div>
  );
});

const ETDate = React.memo(() => {
  const [time, setTime] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000); // date only needs 1min updates
    return () => clearInterval(t);
  }, []);
  return time.toLocaleDateString("en-US",{timeZone:"America/New_York",weekday:"long",month:"long",day:"numeric"});
});
// ─── WEATHER CARD ─────────────────────────────────────────────────────────────
const WeatherCard = ({ wx, index, onRead, tier }) => {
  const isLocked = tier === "free" && index >= 2;
  const clsMap = { CRITICAL:"#e53e3e", WARNING:"#dd6b20", WATCH:"#d69e2e", ADVISORY:"#38a169", MONITOR:"var(--muted)" };
  const alertColor = clsMap[wx.alertCls] || "var(--muted)";
  return (
    <div className="card" onClick={() => !isLocked && onRead(wx)}
      style={{ padding:0, overflow:"hidden", cursor: isLocked?"default":"pointer",
        opacity: isLocked ? 0.55 : 1, position:"relative",
        background: wx.background || "var(--card)", minHeight:200 }}>
      {/* Alert bar */}
      <div style={{ height:3, background: alertColor }}/>
      <div style={{ padding:"14px 16px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
          <span style={{ fontFamily:"var(--M)", fontSize:8, color: alertColor,
            letterSpacing:".12em", fontWeight:700 }}>{wx.alertCls || "MONITOR"}</span>
          <span style={{ fontFamily:"var(--M)", fontSize:8, color:"var(--muted)" }}>·</span>
          <span style={{ fontFamily:"var(--M)", fontSize:8, color:"var(--muted)" }}>{wx.commodity}</span>
          {isLocked && <Icon name="lock" size={10} color="var(--muted)" style={{ marginLeft:"auto" }}/>}
        </div>
        {/* Headline */}
        <div style={{ fontFamily:"var(--D)", fontSize:15, color:"var(--cream)",
          lineHeight:1.45, marginBottom:8 }}>{wx.headline}</div>
        <div style={{ fontFamily:"var(--P)", fontSize:12, color:"var(--dim)",
          lineHeight:1.55, marginBottom:12 }}>{wx.sub}</div>
        {/* Price row */}
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div>
            <div style={{ fontFamily:"var(--M)", fontSize:9, color:"var(--muted)", marginBottom:2 }}>FUTURES</div>
            <div style={{ fontFamily:"var(--M)", fontSize:14, color:"var(--cream)" }}>
              {wx.livePrice ? `$${Number(wx.livePrice).toFixed(2)}` : wx.futures || "—"}
            </div>
          </div>
          {wx.futChange && (
            <div style={{ fontFamily:"var(--M)", fontSize:11,
              color: wx.futUp ? "var(--up)" : "var(--dn)" }}>
              {wx.futChange}
            </div>
          )}
          <div style={{ marginLeft:"auto", fontFamily:"var(--M)", fontSize:8, color:"var(--muted)" }}>
            {wx.weather?.source || "NOAA"}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── PAYWALL MODAL ─────────────────────────────────────────────────────────────
const Paywall = ({ onClose, onSelect, showAuth }) => (
  <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:999,
    display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
    onClick={onClose}>
    <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:16,
      padding:36, maxWidth:440, width:"100%", position:"relative" }}
      onClick={e => e.stopPropagation()}>
      <button onClick={onClose} style={{ position:"absolute", top:16, right:18,
        background:"transparent", border:"none", color:"var(--muted)",
        fontSize:22, cursor:"pointer", lineHeight:1 }}>×</button>
      <div style={{ fontFamily:"var(--D)", fontSize:22, color:"var(--cream)", marginBottom:8 }}>
        Unlock Folio Intelligence
      </div>
      <div style={{ fontFamily:"var(--P)", fontSize:14, color:"var(--dim)",
        lineHeight:1.6, marginBottom:28 }}>
        Get live weather-to-futures analysis, oil desk signals, prediction markets,
        and AI-written market briefs.
      </div>
      {[
        { id:"pulse", name:"Pulse", price:"$14.99/mo", desc:"Weather + Oil + Predictions" },
        { id:"pro",   name:"Pro",   price:"$29.99/mo", desc:"Everything + AI Briefs + Priority" },
      ].map(plan => (
        <div key={plan.id} onClick={() => { onSelect(plan.id); onClose(); }}
          style={{ padding:"16px 20px", border:"1px solid var(--border)", borderRadius:10,
            cursor:"pointer", marginBottom:10, transition:"border-color .15s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "var(--cream)"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontFamily:"var(--D)", fontSize:16, color:"var(--cream)" }}>{plan.name}</div>
            <div style={{ fontFamily:"var(--M)", fontSize:13, color:"var(--up)" }}>{plan.price}</div>
          </div>
          <div style={{ fontFamily:"var(--P)", fontSize:12, color:"var(--muted)", marginTop:4 }}>{plan.desc}</div>
        </div>
      ))}
      <div style={{ marginTop:16, textAlign:"center" }}>
        <button onClick={showAuth} style={{ background:"transparent", border:"none",
          color:"var(--muted)", fontFamily:"var(--M)", fontSize:10, cursor:"pointer",
          letterSpacing:".08em" }}>
          ALREADY SUBSCRIBED? SIGN IN →
        </button>
      </div>
    </div>
  </div>
);

// ─── READER MODAL ─────────────────────────────────────────────────────────────
const Reader = ({ item, tier, onPaywall, onClose }) => {
  if (!item) return null;
  const isLocked = tier === "free";
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.82)", zIndex:998,
      display:"flex", alignItems:"flex-start", justifyContent:"center",
      padding:"40px 20px", overflowY:"auto" }}
      onClick={onClose}>
      <div style={{ background:"var(--card)", border:"1px solid var(--border)", borderRadius:14,
        padding:32, maxWidth:680, width:"100%", position:"relative" }}
        onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position:"absolute", top:16, right:18,
          background:"transparent", border:"none", color:"var(--muted)",
          fontSize:22, cursor:"pointer" }}>×</button>
        {/* Alert badge */}
        <div style={{ fontFamily:"var(--M)", fontSize:9, color:"var(--muted)",
          letterSpacing:".1em", marginBottom:10 }}>
          {item.alertCls || "MONITOR"} · {item.commodity} · {item.weather?.source || "NOAA"}
        </div>
        <div style={{ fontFamily:"var(--D)", fontSize:22, color:"var(--cream)",
          lineHeight:1.4, marginBottom:12 }}>{item.headline}</div>
        <div style={{ fontFamily:"var(--P)", fontSize:14, color:"var(--dim)",
          lineHeight:1.7, marginBottom:20 }}>{item.sub}</div>
        {item.weather && (
          <div style={{ background:"var(--raised)", borderRadius:10, padding:"14px 16px",
            marginBottom:20, fontFamily:"var(--P)", fontSize:12, color:"var(--dim)",
            lineHeight:1.65 }}>
            <strong style={{ color:"var(--cream)" }}>Weather Details:</strong><br/>
            {item.weather.event} — {item.weather.location}
          </div>
        )}
        {isLocked ? (
          <div style={{ textAlign:"center", padding:"24px 0" }}>
            <div style={{ fontFamily:"var(--D)", fontSize:15, color:"var(--cream)", marginBottom:10 }}>
              Full analysis requires Pulse or Pro
            </div>
            <button onClick={onPaywall}
              style={{ background:"var(--up)", color:"#000", border:"none",
                borderRadius:8, padding:"10px 24px", fontFamily:"var(--M)",
                fontSize:11, cursor:"pointer", letterSpacing:".08em" }}>
              UNLOCK ACCESS
            </button>
          </div>
        ) : (
          <div style={{ fontFamily:"var(--P)", fontSize:13, color:"var(--dim)", lineHeight:1.7 }}>
            {item.marketImpact && (
              <>
                <div style={{ fontFamily:"var(--M)", fontSize:9, color:"var(--muted)",
                  letterSpacing:".1em", marginBottom:8 }}>MARKET IMPACT</div>
                <p>Direction: <span style={{ color: item.marketImpact.direction === "BULLISH" ? "var(--up)" : "var(--dn)" }}>
                  {item.marketImpact.direction}</span></p>
                <p>{item.marketImpact.shortTerm}</p>
                <p>{item.marketImpact.medium}</p>
              </>
            )}
            {item.hedges?.length > 0 && (
              <div style={{ marginTop:16 }}>
                <div style={{ fontFamily:"var(--M)", fontSize:9, color:"var(--muted)",
                  letterSpacing:".1em", marginBottom:8 }}>RELATED INSTRUMENTS</div>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {item.hedges.map((h,i) => (
                    <span key={i} style={{ fontFamily:"var(--M)", fontSize:10,
                      background:"var(--raised)", border:"1px solid var(--border)",
                      borderRadius:6, padding:"4px 10px", color:"var(--cream)" }}>{h}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── PREDICTION SIGNAL CARD ────────────────────────────────────────────────────
const PredictionSignalCard = ({ tier, onPaywall }) => {
  const signals = [
    { label:"Fed cuts May FOMC?",    poly:31, kalshi:40, trend:"+3" },
    { label:"Recession by Dec 2025?", poly:22, kalshi:31, trend:"-1" },
    { label:"S&P 500 > 5800 EOY?",   poly:55, kalshi:62, trend:"+5" },
  ];
  return (
    <div className="card" style={{ padding:"14px 16px", marginBottom:14 }}>
      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
        <div style={{ width:5, height:5, borderRadius:"50%", background:"var(--up)",
          animation:"breathe 2s infinite" }}/>
        <div style={{ fontFamily:"var(--M)", fontSize:9, color:"var(--up)",
          letterSpacing:".12em" }}>PREDICTION MARKETS</div>
      </div>
      {signals.map((s, i) => {
        const locked = tier === "free" && i >= 1;
        const avg = Math.round((s.poly + s.kalshi) / 2);
        return (
          <div key={i} onClick={locked ? onPaywall : undefined}
            style={{ padding:"10px 0", borderBottom:"1px solid var(--border)",
              opacity: locked ? 0.5 : 1, cursor: locked ? "pointer" : "default",
              position:"relative" }}>
            {locked && <Icon name="lock" size={9} color="var(--muted)"
              style={{ position:"absolute", right:0, top:12 }}/>}
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", marginBottom:6 }}>
              <div style={{ fontFamily:"var(--P)", fontSize:12, color:"var(--cream)",
                flex:1, paddingRight:20 }}>{s.label}</div>
              <div style={{ fontFamily:"var(--M)", fontSize:13,
                color: avg >= 50 ? "var(--up)" : "var(--dn)" }}>{avg}%</div>
            </div>
            <div style={{ height:4, background:"var(--border)", borderRadius:2, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${avg}%`, borderRadius:2,
                background: avg >= 50 ? "var(--up)" : "var(--dn)",
                transition:"width .4s" }}/>
            </div>
            <div style={{ display:"flex", gap:12, marginTop:4 }}>
              <span style={{ fontFamily:"var(--M)", fontSize:8, color:"var(--muted)" }}>
                POLY {s.poly}%
              </span>
              <span style={{ fontFamily:"var(--M)", fontSize:8, color:"var(--muted)" }}>
                KALSHI {s.kalshi}%
              </span>
              <span style={{ fontFamily:"var(--M)", fontSize:8, marginLeft:"auto",
                color: s.trend.startsWith("+") ? "var(--up)" : "var(--dn)" }}>{s.trend}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── NEWS SECTION — Markets page news feed ────────────────────────────────────
const NewsSection = ({ tier, onPaywall }) => {
  const { articles, loading, error, src, setSrc } = useNewsFeed();
  const fmtAge = d => {
    if (!d) return "";
    const m = Math.floor((Date.now() - new Date(d)) / 60000);
    if (m < 60) return `${m}m`;
    if (m < 1440) return `${Math.floor(m/60)}h`;
    return `${Math.floor(m/1440)}d`;
  };
  return (
    <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden",marginBottom:20}}>
      {/* Source tabs */}
      <div style={{display:"flex",borderBottom:"1px solid var(--border)",padding:"0 16px",background:"var(--raised)"}}>
        {NEWS_SOURCES.map((s, i) => (
          <button key={i} onClick={() => setSrc(i)}
            style={{fontFamily:"var(--M)",fontSize:9,padding:"12px 14px",background:"transparent",
              border:"none",cursor:"pointer",color:src===i?s.color:"var(--muted)",
              letterSpacing:".1em",borderBottom:src===i?`2px solid ${s.color}`:"2px solid transparent",
              whiteSpace:"nowrap",transition:"color .15s"}}>
            {s.label.toUpperCase()}
          </button>
        ))}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",padding:"0 4px"}}>
          <div style={{width:5,height:5,borderRadius:"50%",background:"var(--up)",animation:"breathe 2s infinite"}}/>
          <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",marginLeft:5}}>LIVE</span>
        </div>
      </div>
      {/* Articles */}
      <div style={{padding:"4px 0"}}>
        {loading && (
          <div style={{padding:"28px 0",textAlign:"center"}}>
            <div style={{width:14,height:14,borderRadius:"50%",margin:"0 auto",
              border:"2px solid rgba(0,160,221,.15)",borderTopColor:"#00a0dd",
              animation:"spin .7s linear infinite"}}/>
          </div>
        )}
        {error && !loading && (
          <div style={{padding:"20px",fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",textAlign:"center"}}>
            FEED UNAVAILABLE · CHECK CONNECTION
          </div>
        )}
        {!loading && !error && articles.map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
            style={{display:"block",padding:"11px 18px",textDecoration:"none",
              borderBottom:i<articles.length-1?"1px solid var(--border)":"none",transition:"background .15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--cream)",lineHeight:1.5,marginBottom:4}}>
              {a.title}
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:a.color}}>{a.source}</span>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{fmtAge(a.date)}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

// ─── TECH NEWS SECTION ────────────────────────────────────────────────────────
const TECH_SOURCES = [
  { label:"The Verge",    url:"https://www.theverge.com/rss/index.xml",              color:"#e45c10" },
  { label:"Ars Technica", url:"https://feeds.arstechnica.com/arstechnica/technology", color:"#f60" },
  { label:"MIT Tech",     url:"https://www.technologyreview.com/feed/",              color:"#00a0dd" },
];

const useTechFeed = () => {
  const [articles, setArticles] = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [src,      setSrc]      = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    const fetchUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(TECH_SOURCES[src].url)}&count=12`;
    fetch(fetchUrl, { signal: AbortSignal.timeout(10000) })
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        if (d.status === "ok" && d.items?.length) {
          setArticles(d.items.slice(0, 12).map(it => ({
            title:  it.title,
            url:    it.link,
            source: TECH_SOURCES[src].label,
            color:  TECH_SOURCES[src].color,
            date:   it.pubDate ? new Date(it.pubDate) : new Date(),
          })));
        }
        setLoading(false);
      })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [src]);

  return { articles, loading, src, setSrc };
};

const TechNewsSection = ({ tier, onPaywall }) => {
  const { articles, loading, src, setSrc } = useTechFeed();
  const fmtAge = d => {
    const m = Math.floor((Date.now() - new Date(d)) / 60000);
    if (m < 60) return `${m}m`;
    if (m < 1440) return `${Math.floor(m/60)}h`;
    return `${Math.floor(m/1440)}d`;
  };
  return (
    <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"12px 16px",
        borderBottom:"1px solid var(--border)",background:"var(--raised)"}}>
        <div style={{width:4,height:4,borderRadius:"50%",background:"var(--up)"}}/>
        <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--up)",letterSpacing:".12em"}}>TECH & AI NEWS</span>
        <div style={{marginLeft:"auto",display:"flex",gap:0}}>
          {TECH_SOURCES.map((s, i) => (
            <button key={i} onClick={() => setSrc(i)}
              style={{fontFamily:"var(--M)",fontSize:8,padding:"4px 10px",background:"transparent",
                border:"none",cursor:"pointer",borderRadius:4,
                color:src===i?s.color:"var(--muted)",transition:"color .15s"}}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{padding:"4px 0"}}>
        {loading && (
          <div style={{padding:"28px 0",textAlign:"center"}}>
            <div style={{width:14,height:14,borderRadius:"50%",margin:"0 auto",
              border:"2px solid rgba(0,160,221,.15)",borderTopColor:"#00a0dd",
              animation:"spin .7s linear infinite"}}/>
          </div>
        )}
        {!loading && articles.map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
            style={{display:"block",padding:"11px 18px",textDecoration:"none",
              borderBottom:i<articles.length-1?"1px solid var(--border)":"none",transition:"background .15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--cream)",lineHeight:1.5,marginBottom:4}}>
              {a.title}
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:a.color}}>{a.source}</span>
              <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{fmtAge(a.date)}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default function Folio() {
  const [tier, setTier] = useState("free");
  const [darkMode, setDarkMode] = useState(false);
  const [view, setView] = useState("landing");
  const [reader, setReader] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);

  // ── Firebase: restore session + magic link completion ──
  useEffect(() => {
    if (!isFirebaseConfigured) return;

    // Complete magic link sign-in if returning from email link
    if (isSignInWithEmailLink(_auth, window.location.href)) {
      let email = localStorage.getItem("folio_signin_email") || "";
      if (!email) email = window.prompt("Please confirm your email to sign in:") || "";
      signInWithEmailLink(_auth, email, window.location.href)
        .then(async (result) => {
          const u = result.user;
          const savedTier = await loadTierFromFirestore(u.uid);
          setUser({ name: u.email, email: u.email,
            avatar: u.email[0].toUpperCase(), method:"email", uid: u.uid });
          setShowAuth(false);
          if (savedTier && savedTier !== "free") {
            setTier(savedTier);
          } else {
            setTier("pulse");
            saveTierToFirestore(u.uid, "pulse");
          }
          window.history.replaceState({}, "", window.location.pathname);
        })
        .catch(() => {});
    }

    // Restore session on reload
    const unsub = onAuthStateChanged(_auth, async (firebaseUser) => {
      if (firebaseUser) {
        const savedTier = await loadTierFromFirestore(firebaseUser.uid);
        setUser({
          name:   firebaseUser.displayName || firebaseUser.email,
          email:  firebaseUser.email,
          avatar: (firebaseUser.displayName || firebaseUser.email)[0].toUpperCase(),
          photo:  firebaseUser.photoURL,
          uid:    firebaseUser.uid,
        });
        if (savedTier && savedTier !== "free") {
          setTier(savedTier);
          try { localStorage.setItem("folio_tier", savedTier); } catch(e) {}
        }
      }
    });
    return () => unsub();
  }, []);

  // ── Cmd+K / Ctrl+K → Command Palette ──────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowPalette(p => !p);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Stripe return: handle per-module unlock from URL params ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment    = params.get("payment");
    const returnedTier  = params.get("tier");
    const unlockedMod   = params.get("module");
    const sessionId     = params.get("session_id");
    const wasUnlocked   = params.get("unlocked") === "true";

    // Legacy tier-based return
    if (payment === "success" && returnedTier) {
      setTier(returnedTier);
      setSuccessBanner(returnedTier);
      try { localStorage.setItem("folio_tier", returnedTier); } catch(e) {}
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setSuccessBanner(null), 6000);
      if (isFirebaseConfigured && _auth?.currentUser) {
        saveTierToFirestore(_auth.currentUser.uid, returnedTier);
      }
    }

    // Per-module Stripe return
    if (wasUnlocked && unlockedMod && sessionId) {
      setSuccessBanner(`${unlockedMod}_unlocked`);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setSuccessBanner(null), 8000);
      // Firestore will update via webhook — onSnapshot will unlock the box
      // No need to do anything else here
    }

    // Restore tier from localStorage on load
    try {
      const saved = localStorage.getItem("folio_tier");
      if (saved && saved !== "free") setTier(saved);
    } catch(e) {}
  }, []);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showHandleSetup, setShowHandleSetup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu]   = useState(false);
  const [chatTicker, setChatTicker]       = useState("SPY");
  const [showPalette, setShowPalette]     = useState(false);
  const [drillDown, setDrillDown]         = useState(null);
  const [user, setUser] = useState(null);
  // watchlist is derived from useWatchlist hook (user-driven)
  const watchlistData = useWatchlist(user);
  const ADMIN_EMAILS = ["abhilash@afcurgentcare.com","abhilash@buchmedical.com"];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  // ── Per-box subscription system ──────────────────────────────────────────
  const { subs, hasBox } = useSubscription(user?.uid);
  const handleUnlockBox = React.useCallback(async (boxId) => {
    if (!user) { setShowAuth(true); return; }
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          moduleId: boxId,
          uid:      user.uid,
          email:    user.email || undefined,
        }),
      });
      const data = await res.json();
      if (data.free) return; // free module, nothing to do
      if (data.url)  { window.location.href = data.url; return; }
      if (data.error) throw new Error(data.error);
    } catch(e) {
      console.error("Checkout error:", e);
      setShowPaywall(true); // fallback
    }
  }, [user]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [alerts, setAlerts]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("folio_alerts")||"[]"); } catch{ return []; }
  });
  const [alertsNotify, setAlertsNotify] = useState([]);
  // market status uses its own snapshot — no time state needed at root

  const { macro: liveMacro, tape: liveTape, isLive: macroLive } = useLiveMarkets();
  const futuresStrip = useFuturesStrip();
  const { data: liveOilData } = useOilData();
  const { gainers: liveGainers, losers: liveLosers, isLive: gainersLive, lastUpdated: gainersUpdated } = useGainersLosers();

  const market = getMarket();

  const handleUpgrade = t => {
    if (t === "free") { setShowPaywall(false); setShowAuth(false); return; }
    stripeCheckout(t, false);
    setShowPaywall(false);
  };
  const handleLogin = async (u) => {
    // Load handle from Firestore
    let handle = null;
    if (isFirebaseConfigured && _db && u.uid && u.uid !== "demo") {
      try {
        const snap = await getDoc(doc(_db, "users", u.uid));
        if (snap.exists()) handle = snap.data().handle || null;
      } catch(e) {}
    }
    setUser({ ...u, handle });
    setShowAuth(false);
    saveSubscriberToFirestore(u.email, u.uid);
    if (u.tier && u.tier !== "free") {
      setTier(u.tier);
      try { localStorage.setItem("folio_tier", u.tier); } catch(e) {}
    } else {
      setTier("pulse");
      try { localStorage.setItem("folio_tier", "pulse"); } catch(e) {}
      if (u.uid && u.uid !== "demo") saveTierToFirestore(u.uid, "pulse");
    }
    // First login — prompt handle setup
    if (!handle) setShowHandleSetup(true);
  };
  // Close user menu on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.user-menu-wrap')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    if (isFirebaseConfigured && _auth) {
      try { await signOut(_auth); } catch(e) {}
    }
    setUser(null);
    setTier("free");
    try { localStorage.removeItem("folio_tier"); } catch(e) {}
  };

  const handleReadWeather = wx => {
    if(tier==="free"){ setShowPaywall(true); return; }
    setReader({...wx});
  };

  const mc = {free:"var(--muted)",pulse:"var(--up)",pro:"var(--vio2)"}[tier];
  const ml = {free:"Free",pulse:"Pulse · Founding",pro:"Pulse Pro"}[tier];

  // Live weather data — NOAA alerts mapped to commodities + real prices
  const { cards: wxCards, isLive: wxLive, updatedAt: wxUpdatedAt } = useWeatherData();
  const visibleWx = wxCards; // all cards shown; filtering by settings can be added later
  const oilWx = wxCards.find(w => w.id?.startsWith("wti") || w.commodity?.includes("Crude"));

  const NAVS = [
    {k:"watchlist",l:"Watchlist",icon:"bookmark",tip:"Your personalized ticker watchlist & news"},
    {k:"markets",l:"Markets",  icon:"bar-chart",tip:"Market news & overview"},
    {k:"freight", l:"Freight",  icon:"bolt",     tip:"Freight desk · rates · ports · shipping AI · $199/mo",hot:true},
    {k:"oildesk",l:"Oil Desk", icon:"oildrop",   tip:"Private crude intelligence · Where physical meets futures",hot:true},
    {k:"weather",l:"Weather",  icon:"storm",    tip:"Commodity weather alerts"},
    {k:"gainers",l:"Gainers",  icon:"rocket",   tip:"Top gaining stocks"},
    {k:"losers", l:"Losers",   icon:"parachute",tip:"Top losing stocks"},
    {k:"tech",   l:"Tech",     icon:"atom",     tip:"Quantum & AI news"},
    {k:"predictions",l:"Predictions",icon:"zap", tip:"Live prediction markets — Polymarket & Kalshi",hot:true},
    {k:"chat",   l:"Chat",     icon:"message",  tip:"Live community chat rooms by ticker",hot:true},
    {k:"alerts", l:"Alerts",   icon:"bell",     tip:"Price alerts with signal context"},
    {k:"pricing",l:"Pricing",  icon:"gem",      tip:"Plans & pricing"},
    {k:"settings",l:"Settings",icon:"settings",tip:"Preferences, display, and account"},
    ...(isAdmin ? [{k:"admin", l:"Admin", icon:"shield", tip:"Admin control center", admin:true}] : []),
  ];
  return(
    <>
      <GS/>
      <div data-theme={darkMode?"dark":"light"} style={{minHeight:"100vh",background:"var(--void)",color:"var(--text)",fontFamily:"var(--P)"}}>

        {/* ── INVITE GATE ───────────────────────────────────────────── */}



        {/* BREAKING ALERT STRIP — watchlist news, top of page */}
        <BreakingAlertStrip tickers={watchlistData.tickers}/>

        {/* HEADER */}
        <header style={{background:"var(--base)",borderBottom:"1px solid var(--border)"}}>
          <div style={{padding:"13px 24px",display:"flex",alignItems:"center",
            gap:12,flexWrap:"wrap",borderBottom:"1px solid var(--border)"}}>

            {/* Wordmark */}
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <Icon name="folio" size={22} color="var(--up)" strokeWidth={1.1}/>
              <div>
                <div style={{fontFamily:"var(--D)",fontSize:24,color:"var(--cream)",
                  fontWeight:700,letterSpacing:".1em",lineHeight:1}}>
                  FOLIO
                </div>
              </div>
            </div>

            {/* Cmd+K palette trigger */}
            <button
              onClick={()=>setShowPalette(true)}
              style={{
                display:"flex", alignItems:"center", gap:8,
                background:"var(--raised)", border:"1px solid var(--border2)",
                borderRadius:6, padding:"6px 12px", cursor:"pointer",
                fontFamily:"var(--M)", fontSize:9, color:"var(--muted)",
                letterSpacing:".08em", transition:"border-color .15s",
              }}
              onMouseEnter={e=>e.currentTarget.style.borderColor="var(--soft)"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border2)"}
            >
              <span style={{opacity:.6}}>⌘K</span>
              <span>Search desks & data...</span>
            </button>

            {/* Nav */}
            <nav style={{display:"flex",gap:4,flexWrap:"wrap",marginLeft:8}}>
              {NAVS.map(n=>(
                <button key={n.k} className={`nav ${view===n.k?"n-on":"n-off"}`}
                  onClick={()=>setView(n.k)}
                  title={n.tip}
                  style={{display:"flex",alignItems:"center",gap:6,position:"relative"}}>
                  <Icon name={n.icon} size={13} color={view===n.k?"var(--up)":n.hot?"var(--dn)":"var(--muted)"} strokeWidth={1.4}/>
                  <span style={{color:view===n.k?"var(--cream)":n.hot&&view!==n.k?"var(--soft)":"var(--muted)"}}>{n.l}</span>
                  {n.hot&&view!==n.k&&<span style={{
                    fontFamily:"var(--M)",fontSize:7,color:"#fff",
                    background:"var(--dn)",borderRadius:3,padding:"1px 4px",
                    letterSpacing:".06em",marginLeft:2,
                  }}>LIVE</span>}
                  {view===n.k&&<div style={{position:"absolute",bottom:-8,left:"50%",
                    transform:"translateX(-50%)",width:16,height:2,
                    background:"var(--up)",borderRadius:1}}/>}
                </button>
              ))}
            </nav>

            {/* Right controls */}
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <ETClock/>
              <div style={{display:"flex",alignItems:"center",gap:5,
                padding:"5px 11px",borderRadius:20,
                background:`${market.color}12`,border:`1px solid ${market.color}28`}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:market.color,
                  animation:market.status==="open"?"breathe 2.5s ease infinite":"none"}}/>
                <span style={{fontFamily:"var(--M)",fontSize:9,fontWeight:600,
                  color:market.color,letterSpacing:".07em",textTransform:"uppercase"}}>{market.label}</span>
              </div>
              <div style={{padding:"5px 11px",borderRadius:7,
                background:`${mc}10`,border:`1px solid ${mc}22`}}>
                <span style={{fontFamily:"var(--M)",fontSize:9,color:mc,
                  textTransform:"uppercase",letterSpacing:".07em"}}>{ml}</span>
              </div>
              {user?(
                <div style={{display:"flex",alignItems:"center",gap:6}} className="user-menu-wrap">
                  <div style={{position:"relative"}}>
                  <button onClick={()=>setShowUserMenu(m=>!m)}
                    style={{display:"flex",alignItems:"center",gap:7,padding:"4px 10px",
                      background:"var(--raised)",borderRadius:7,border:"1px solid var(--border)",
                      cursor:"pointer",transition:"border-color .15s"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(196,164,74,.4)"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                    <div style={{width:22,height:22,borderRadius:"50%",
                      background:"linear-gradient(135deg,var(--up),#a07828)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontFamily:"var(--M)",fontSize:9,fontWeight:700,color:"#080500"}}>
                      {user.avatar}
                    </div>
                    <span style={{fontFamily:"var(--M)",fontSize:11,color:"var(--soft)"}}>{user.name}</span>
                    <Icon name="chevron-down" size={10} color="var(--muted)"/>
                  </button>
                  {showUserMenu&&(
                    <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,
                      background:"var(--surface)",border:"1px solid var(--border)",
                      borderRadius:8,padding:"6px 0",minWidth:160,zIndex:200,
                      boxShadow:"0 8px 24px rgba(0,0,0,.25)"}}>
                      <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)",
                        padding:"4px 14px 6px",letterSpacing:".08em"}}>
                        {user.email}
                      </div>
                      <div style={{height:1,background:"var(--border)",margin:"0 0 4px"}}/>
                      <button onClick={()=>{setView("settings");setShowUserMenu(false);}}
                        style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                          padding:"8px 14px",background:"none",border:"none",cursor:"pointer",
                          fontFamily:"var(--M)",fontSize:11,color:"var(--text)",transition:"background .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
                        onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <Icon name="settings" size={12} color="var(--muted)"/>
                        Settings
                      </button>
                      <button onClick={()=>{setDarkMode(d=>!d);setShowUserMenu(false);}}
                        style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                          padding:"8px 14px",background:"none",border:"none",cursor:"pointer",
                          fontFamily:"var(--M)",fontSize:11,color:"var(--text)",transition:"background .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
                        onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <Icon name={darkMode?"sun":"moon"} size={12} color="var(--muted)"/>
                        {darkMode?"Light Mode":"Dark Mode"}
                      </button>
                      <div style={{height:1,background:"var(--border)",margin:"4px 0"}}/>
                      <button onClick={()=>{handleSignOut();setShowUserMenu(false);}}
                        style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                          padding:"8px 14px",background:"none",border:"none",cursor:"pointer",
                          fontFamily:"var(--M)",fontSize:11,color:"#e74c3c",transition:"background .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.background="var(--raised)"}
                        onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <Icon name="log-out" size={12} color="#e74c3c"/>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
                  <button
                    title="Sign out"
                    onClick={handleSignOut}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"5px 9px",
                      background:"transparent",border:"1px solid rgba(231,76,60,.25)",
                      borderRadius:6,cursor:"pointer",fontFamily:"var(--M)",fontSize:9,
                      color:"rgba(231,76,60,.7)",letterSpacing:"0.06em",transition:"all .15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(231,76,60,.6)";e.currentTarget.style.color="#e74c3c";e.currentTarget.style.background="rgba(231,76,60,.08)";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(231,76,60,.25)";e.currentTarget.style.color="rgba(231,76,60,.7)";e.currentTarget.style.background="transparent";}}>
                    <Icon name="log-out" size={11} color="currentColor"/> Sign Out
                  </button>
                </div>
              ):(
                <button className="btn b-ghost" style={{padding:"7px 13px",fontSize:10,gap:5}}
                  onClick={()=>setShowAuth(true)}>
                  <Icon name="user" size={12} color="var(--dim)"/>
                  Sign in
                </button>
              )}

              {tier==="free"&&(
                <button className="btn b-gold" style={{padding:"8px 14px",fontSize:10}}
                  onClick={()=>setShowPaywall(true)}>
                  Unlock ✦
                </button>
              )}
            </div>
          </div>

          {/* Macro strip */}
          <div style={{display:"flex",overflowX:"auto",borderBottom:"1px solid var(--border)"}}>
            {liveMacro.map((m,i)=>(
              <div key={i} className="mac-cell">
                <span style={{fontFamily:"var(--M)",fontSize:10,color:"var(--soft)",
                  textTransform:"uppercase",letterSpacing:".06em",fontWeight:500}}>{m.k}</span>
                <span style={{fontFamily:"var(--M)",fontSize:15,fontWeight:600,color:"var(--bright)",letterSpacing:"-0.3px"}}>{m.v}</span>
                <span style={{fontFamily:"var(--M)",fontSize:11,fontWeight:500,
                  color:m.up===null?"var(--dim)":m.up?"var(--up)":"var(--dn)"}}>{m.c}</span>
              </div>
            ))}
          </div>

          {/* Futures strip */}
          <div style={{overflowX:"auto",background:"rgba(0,0,0,.18)",borderBottom:"1px solid var(--border)"}}>
            <div style={{display:"flex"}}>
              {futuresStrip.map((f,i)=>(
                <div key={i} className="fut-cell" onClick={()=>{
                  const wx = wxCards.find(w=>w.commodity?.toLowerCase().startsWith(f.k.toLowerCase().split(" ")[0]));
                  if(wx) handleReadWeather(wx); else setView("weather");
                }}>
                  <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:2}}>
                    <Icon name={f.icon} size={12} color="var(--muted)" strokeWidth={1.3}/>
                    <span style={{fontFamily:"var(--M)",fontSize:9,color:"var(--soft)",
                      textTransform:"uppercase",letterSpacing:".05em",fontWeight:500}}>{f.k}</span>
                  </div>
                  <span style={{fontFamily:"var(--M)",fontSize:14,fontWeight:600,color:"var(--bright)",letterSpacing:"-0.2px"}}>{f.v}</span>
                  <span style={{fontFamily:"var(--M)",fontSize:10,color:f.up===null?"var(--muted)":f.up?"var(--up)":"var(--dn)"}}>{f.c_}</span>
                  {f.wx&&(
                    <span style={{fontFamily:"var(--M)",fontSize:8,marginTop:1,
                      color:"var(--amb)"}}>
                      {f.wx}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* MAIN */}
        <main>
          {view==="landing"&&(
            <FolioLanding
              onNavigate={setView}
              tier={tier}
              tape={liveTape}
              user={user}
              onSignIn={()=>setShowAuth(true)}
            />
          )}
          {view==="pricing"&&(
            <PricingPage currentTier={tier} onSelect={handleUpgrade}/>
          )}
          {view==="settings"&&(
            <SettingsPage
              darkMode={darkMode} setDarkMode={setDarkMode}
              settings={settings} setSettings={setSettings}
              user={user} onSignOut={handleSignOut}
              onSignIn={()=>setShowAuth(true)}
            />
          )}

          {view==="freight"&&(
            <FreightDeskPage tier={tier} onPaywall={()=>setShowPaywall(true)} user={user} onSignIn={()=>setShowAuth(true)}/>
          )}
          {view==="oildesk"&&(
            <OilDeskPage tier={tier} onPaywall={()=>setShowPaywall(true)}/>
          )}
          {view==="weather"&&(
            <div style={{maxWidth:1200,margin:"0 auto",padding:"28px 24px"}}>
              <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
                <div style={{fontFamily:"var(--D)",fontSize:26,color:"var(--cream)",fontWeight:700}}>Weather Intelligence</div>
                <span className="tag t-amb" style={{display:"flex",alignItems:"center",gap:4}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:wxLive?"var(--up)":"var(--amb)",animation:wxLive?"breathe 2s infinite":"none"}}/>
                  {wxLive?"NOAA LIVE":"Monitoring"}
                </span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(295px,1fr))",gap:14}}>
                {visibleWx.map((wx,i)=>(
                  <WeatherCard key={wx.id} wx={wx} index={i} onRead={handleReadWeather} tier={tier}/>
                ))}
              </div>
            </div>
          )}
          {view==="predictions"&&(
            <PredictionsPage tier={tier} onPaywall={()=>setShowPaywall(true)} user={user} onSignIn={()=>setShowAuth(true)}/>
          )}
          {view==="admin"&&(
            <AdminPage user={user} alerts={alerts} setAlerts={setAlerts} tier={tier} oilData={liveOilData}/>
          )}

          {view!=="pricing"&&view!=="settings"&&view!=="landing"&&view!=="freight"&&view!=="oildesk"&&view!=="weather"&&view!=="predictions"&&(
            <div style={{maxWidth:1400,margin:"0 auto",padding:"24px 24px"}}>
              {/* ─── 6-BOX DASHBOARD GRID ─────────────────────────────────────── */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:16}}>

                {/* ── BOX 1: AI BRIEF ────────────────────────────────── */}
                <PaywalledBox boxId="ai_brief" uid={user?.uid} subs={subs}
                  onUnlock={handleUnlockBox}
                  teaser={
                    <div style={{fontFamily:"var(--P)",fontSize:14,color:"var(--text)",lineHeight:1.75}}>
                      Equity risk appetite is compressing as the front-end of the rates curve
                      reasserts control — watch the 2Y10Y for the next directional cue.
                      Prediction markets pricing 40% cut probability into May FOMC while oil
                      COT positioning hints at supply squeeze not yet in spot...
                    </div>
                  }>
                  <AIBriefCard
                    macro={liveMacro}
                    gainers={liveGainers}
                    losers={liveLosers}
                    weather={visibleWx}
                    predictions={[
                      {label:"Fed cuts May FOMC?",   poly:31, kalshi:40},
                      {label:"Recession by Dec 2025?",poly:22, kalshi:31},
                      {label:"S&P 500 > 5800 EOY?",  poly:55, kalshi:62},
                    ]}
                    tier={tier}
                    onPaywall={()=>setShowPaywall(true)}
                  />
                </PaywalledBox>

              {/* ── BOX 2: OIL DESK ─────────────────────────────────────── */}
              <PaywalledBox boxId="oil_desk" uid={user?.uid} subs={subs}
                onUnlock={handleUnlockBox}
                teaser={<>
                  <div style={{fontFamily:"var(--M)",fontSize:22,color:"var(--up)",marginBottom:8}}>$74.32</div>
                  <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>WTI CRUDE · MANAGED MONEY LONG/SHORT</div>
                  <div style={{height:4,background:"var(--border)",borderRadius:2,marginTop:12}}>
                    <div style={{height:"100%",width:"62%",background:"var(--up)",borderRadius:2}}/>
                  </div>
                </>}>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {oilWx && <OilGauge wx={oilWx}/>}
                  <VixGauge/>
                  <GaugesPanel/>
                </div>
              </PaywalledBox>

              {/* ── BOX 3: WEATHER INTEL ────────────────────────────────── */}
              <PaywalledBox boxId="weather_intel" uid={user?.uid} subs={subs}
                onUnlock={handleUnlockBox}
                teaser={<>
                  <div style={{fontFamily:"var(--M)",fontSize:9,color:"#d69e2e",letterSpacing:".1em",marginBottom:8}}>WARNING · CORN FUTURES</div>
                  <div style={{fontFamily:"var(--P)",fontSize:13,color:"var(--cream)",lineHeight:1.5}}>
                    Midwest drought expanding — USDA drought monitor showing D3 conditions in Iowa, Illinois...
                  </div>
                </>}>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {visibleWx.length===0?(
                    <div style={{textAlign:"center",padding:"32px 0",fontFamily:"var(--M)",
                      fontSize:9,color:"var(--muted)",letterSpacing:".1em"}}>
                      NO ACTIVE ALERTS · NOAA CLEAR
                    </div>
                  ):visibleWx.slice(0,4).map((wx,i)=>(
                    <div key={wx.id} onClick={()=>handleReadWeather(wx)}
                      style={{cursor:"pointer",padding:"10px 12px",borderRadius:8,
                        border:"1px solid var(--border)",background:"var(--raised)",transition:"border-color .15s"}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor="var(--cream)"}
                      onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                        <div style={{width:4,height:4,borderRadius:"50%",
                          background:{CRITICAL:"#e53e3e",WARNING:"#dd6b20",WATCH:"#d69e2e",ADVISORY:"#38a169"}[wx.alertCls]||"var(--muted)"}}/>
                        <span style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)",letterSpacing:".1em"}}>{wx.alertCls} · {wx.commodity}</span>
                        <span style={{marginLeft:"auto",fontFamily:"var(--M)",fontSize:9,
                          color:wx.futUp?"var(--up)":"var(--dn)"}}>
                          {wx.livePrice?`$${Number(wx.livePrice).toFixed(2)}`:wx.futures||"—"}
                        </span>
                      </div>
                      <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--cream)",lineHeight:1.4}}>{wx.headline}</div>
                    </div>
                  ))}
                </div>
              </PaywalledBox>

              {/* ── BOX 4: MARKETS ──────────────────────────────────────── */}
              <PaywalledBox boxId="markets" uid={user?.uid} subs={subs}
                onUnlock={handleUnlockBox}
                teaser={<>
                  <div style={{display:"flex",gap:16,marginBottom:12}}>
                    {[["SPY","482.10","+0.8%"],["QQQ","401.22","+1.1%"],["VIX","18.4","-2.1%"]].map(([t,p,c])=>(
                      <div key={t}>
                        <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>{t}</div>
                        <div style={{fontFamily:"var(--M)",fontSize:14,color:"var(--cream)"}}>{p}</div>
                        <div style={{fontFamily:"var(--M)",fontSize:10,color:c.startsWith("+")?"var(--up)":"var(--dn)"}}>{c}</div>
                      </div>
                    ))}
                  </div>
                </>}>
                <div>
                  <EarningsPanel tier={tier} onUpgrade={()=>setShowPaywall(true)}/>
                  <SectorPanel/>
                </div>
              </PaywalledBox>

              {/* ── BOX 5: PREDICTIONS ──────────────────────────────────── */}
              <PaywalledBox boxId="predictions" uid={user?.uid} subs={subs}
                onUnlock={handleUnlockBox}
                teaser={<>
                  <div style={{fontFamily:"var(--P)",fontSize:12,color:"var(--cream)",marginBottom:8}}>Fed cuts May FOMC?</div>
                  <div style={{height:4,background:"var(--border)",borderRadius:2,marginBottom:12}}>
                    <div style={{height:"100%",width:"35%",background:"#4299e1",borderRadius:2}}/>
                  </div>
                  <div style={{fontFamily:"var(--M)",fontSize:8,color:"var(--muted)"}}>POLY 31% · KALSHI 40%</div>
                </>}>
                <PredictionSignalCard tier={tier} onPaywall={()=>setShowPaywall(true)}/>
              </PaywalledBox>

              {/* ── BOX 6: NEWS (free) ──────────────────────────────────── */}
              <PaywalledBox boxId="news" uid={user?.uid} subs={subs}
                onUnlock={handleUnlockBox}>
                <NewsSection tier={tier} onPaywall={()=>setShowPaywall(true)}/>
              </PaywalledBox>

              </div>
            </div>
          )}
        </main>

        {/* FOOTER */}
        <footer style={{borderTop:"1px solid var(--border)",padding:"13px 24px",
          background:"var(--base)",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <Icon name="folio" size={14} color="var(--muted)" strokeWidth={1.1}/>
            <span style={{fontFamily:"var(--D)",fontSize:14,color:"var(--dim)"}}>FOLIO</span>
          </div>
          <div style={{fontFamily:"var(--M)",fontSize:9,color:"var(--muted)"}}>
            NOT FINANCIAL ADVICE · NOAA · Alpha Vantage · FRED · SEC EDGAR · Educational use only
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:6,flexWrap:"wrap"}}>
            {["Web","iOS Q3","Android Q3","API"].map((p,i)=>(
              <span key={i} className="tag t-neu" style={{fontSize:9}}>{p}</span>
            ))}
          </div>
        </footer>

        {/* COMMAND PALETTE */}
        <CommandPalette
          open={showPalette}
          onClose={()=>setShowPalette(false)}
          onNavigate={(v)=>{ setView(v); setShowPalette(false); }}
          onBrief={()=>{ setView("markets"); setShowPalette(false); }}
        />

        {/* DRILL-DOWN PANEL */}
        {drillDown && (
          <DrillDownPanel
            moduleId={drillDown}
            onClose={()=>setDrillDown(null)}
            tier={tier}
            subs={subs}
            onPaywall={()=>setShowPaywall(true)}
            onUnlock={handleUnlockBox}
            liveOilData={liveOilData}
            visibleWx={visibleWx}
            liveMacro={liveMacro}
            liveGainers={liveGainers}
            liveLosers={liveLosers}
            user={user}
          />
        )}

        {/* MODALS */}
        {showSettings&&(
          <SettingsDrawer
            settings={settings}
            onChange={setSettings}
            onClose={()=>setShowSettings(false)}
            tier={tier}
          />
        )}
        {/* Stripe success toast */}
        {successBanner&&(
          <div style={{
            position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",
            zIndex:9999,background:"linear-gradient(135deg,#0a1f0a,#0d2b0d)",
            border:"1px solid rgba(46,204,113,.4)",borderRadius:12,
            padding:"16px 28px",boxShadow:"0 24px 48px rgba(0,0,0,.8)",
            display:"flex",alignItems:"center",gap:14,animation:"riseIn .35s ease",
            minWidth:320,
          }}>
            <div style={{width:32,height:32,borderRadius:"50%",
              background:"rgba(46,204,113,.15)",border:"1px solid rgba(46,204,113,.4)",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="#2ecc71" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div style={{fontFamily:"var(--D)",fontSize:15,color:"#f0e8d4",fontWeight:600,marginBottom:2}}>
                Welcome to Folio {successBanner==="pro"?"Pro":"Pulse"} ✦
              </div>
              <div style={{fontFamily:"var(--P)",fontSize:12,color:"rgba(255,255,255,.55)"}}>
                Full access unlocked. Your subscription is active.
              </div>
            </div>
            <button onClick={()=>setSuccessBanner(null)}
              style={{marginLeft:"auto",background:"none",border:"none",
                color:"rgba(255,255,255,.3)",cursor:"pointer",fontSize:18,lineHeight:1}}>×</button>
          </div>
        )}

        {showPaywall&&(
          <Paywall onClose={()=>setShowPaywall(false)} onSelect={handleUpgrade}
            showAuth={()=>{setShowPaywall(false);setShowAuth(true);}}/>
        )}
        {showAuth&&(
          <AuthModal onClose={()=>setShowAuth(false)} onLogin={handleLogin}/>
        )}
        {showHandleSetup && user && (
          <UsernameSetupModal
            user={user}
            onComplete={(handle) => {
              setUser(u => ({ ...u, handle }));
              setShowHandleSetup(false);
            }}
          />
        )}
        {reader&&(
          <Reader item={reader} tier={tier}
            onPaywall={()=>{setReader(null);setShowPaywall(true);}}
            onClose={()=>setReader(null)}/>
        )}
      </div>
    </>
  );
}
